import { appConfirm, appPrompt } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
import { isSopCascade, SopCascadeConfig, SopCascadeDetails } from './ComplianceSopCascade';
import { isSopManagement, SopManagementConfig, SopManagementDetails } from './ComplianceSopManagement';
import ComplianceNextDue from './ComplianceNextDue';
import { TrainingSettings, TrainingResults } from './ComplianceTraining';
import { VERSION as CRITERIA_VERSION, catalogue as JURISDICTION_CATALOGUE, rowApplicability, locationOf, detailsFor, decorateGroups, badge, niGuidanceValue } from './complianceJurisdiction';
import NearMissPharmacyDialog from "./NearMissPharmacyDialog";
// src/ComplianceWorkingGrid.js
// =============================================================================
// Regulatory compliance → WORKING GRID (2026-10)
//
//   Rows    = drilled-down data items: every data element on the compliance
//             page (header → sub-header → element) plus rows you add yourself
//             (e.g. "Public liability certificate displayed").
//   Columns = branches.
//   Cell    = where that item's evidence comes from at that branch:
//               • Pharmace module  — live status from the Pharmace data
//               • Task             — sent to a named team member, who confirms
//                                    and attaches a photo/document in the app
//               • Link             — another source (spreadsheet, portal…)
//               • Not applicable
//
// Each row says what evidence it needs and how long it lasts (no expiry, N
// days, or until an expiry date such as an insurance policy's). When evidence
// is about to expire, or has expired, the backend sends the recipient a
// renewal task automatically (compliance_working_grid.js).
// =============================================================================
import React, { useCallback, useEffect, useMemo, useState } from "react";
import InspectorResources, { InspectorSopPopup, sortInspectorStaff } from "./InspectorResources";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
const API_BASE = apiUrl("");
const GRID = `${API_BASE}/api/headoffice/compliance-grid`;
async function post(token, url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? {
        Authorization: `Bearer ${token}`
      } : {})
    },
    body: JSON.stringify(body || {})
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
  return d;
}
function contextualRow(row, branch, data) {
  const detail = detailsFor(row, locationOf(branch, data));
  return {
    ...row,
    regulator: detail.regulator,
    help: detail.help,
    explanation: detail.help || row.explanation,
    link: detail.link,
    reference: detail.reference
  };
}
const DAY = 86400000;
// "Held in another system": the Link box is optional. Text typed there that
// isn't a web address is tidied rather than rejected — "www.x.com" becomes
// "https://www.x.com", and plain words ("CDRX") become the description.
function normLink(url, label) {
  let u = String(url || "").trim(),
    l = String(label || "").trim();
  if (u && !/^https?:\/\//i.test(u)) {
    if (/^[^\s/]+\.[a-z]{2,}(\/\S*)?$/i.test(u)) u = `https://${u}`;else {
      if (!l) l = u;
      u = "";
    }
  }
  return {
    url: u,
    label: l
  };
}
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-GB") : "—";
const isoDay = d => d ? new Date(d).toISOString().slice(0, 10) : "";
const daysBetween = (a, b) => Math.round((new Date(b).getTime() - new Date(a).getTime()) / DAY);
const userName = () => localStorage.getItem("userName") || "";
const EVIDENCE_LABEL = {
  photo: "📷 Photo",
  document: "📄 Document",
  confirm: "✔ Confirm only"
};
export const STATUS_STYLE = {
  current: {
    bg: "#F6FFED",
    fg: "#389E0D",
    label: "Current"
  },
  "pharmace-ok": {
    bg: "#F6FFED",
    fg: "#389E0D",
    label: "Pharmace ✓"
  },
  "due-soon": {
    bg: "#FFF7E6",
    fg: "#AD4E00",
    label: "Due soon"
  },
  chasing: {
    bg: "#FFE58F",
    fg: "#AD4E00",
    label: "Renewal task sent"
  },
  awaiting: {
    bg: "#E6F7FF",
    fg: "#0958D9",
    label: "Awaiting staff"
  },
  "needs-expiry": {
    bg: "#FFF7E6",
    fg: "#AD4E00",
    label: "Set expiry date"
  },
  expired: {
    bg: "#FFF1F0",
    fg: "#A8071A",
    label: "Expired"
  },
  missing: {
    bg: "#FFF1F0",
    fg: "#A8071A",
    label: "No evidence"
  },
  "pharmace-no": {
    bg: "#FFF1F0",
    fg: "#A8071A",
    label: "Pharmace ✗"
  },
  "pharmace-lapsed": {
    bg: "#FFF7E6",
    fg: "#AD4E00",
    label: "Pharmace lapsed"
  },
  "pharmace-never": {
    bg: "#FFF7E6",
    fg: "#AD4E00",
    label: "Live — not yet used"
  },
  linked: {
    bg: "#F9F0FF",
    fg: "#531DAB",
    label: "Linked"
  },
  "log-gaps": {
    bg: "#FFF1F0",
    fg: "#A8071A",
    label: "Log has gaps"
  },
  "log-lapsed": {
    bg: "#FFF7E6",
    fg: "#AD4E00",
    label: "Log lapsed"
  },
  pharmace: {
    bg: "#F5F5F5",
    fg: "#434343",
    label: "Pharmace"
  },
  "not-applicable": {
    bg: "#f1f5f9",
    fg: "#64748b",
    label: "Not applicable"
  },
  "location-unknown": {
    bg: "#fff7ed",
    fg: "#9a3412",
    label: "Location unresolved"
  },
  na: {
    bg: "#FAFAFA",
    fg: "#8C8C8C",
    label: "N/A"
  },
  unset: {
    bg: "#ffffff",
    fg: "#8C8C8C",
    label: "Not set"
  }
};

// The source a branch uses when it hasn't been set individually: the row's
// own choice, else Task for a row that has a renewal period set up (that is
// what a renewal period is for), else automatic.
function defaultSourceOf(row) {
  if (!row) return "";
  if (row.defaultSource) return row.defaultSource;
  return row.validity && row.validity.mode && row.validity.mode !== "none" ? "task" : "";
}
function validitySummary(row) {
  const v = row?.validity || {};
  if (row?.defaultSource === "team") {
    const off = row.teamOffsetDays ?? 30;
    return `👥 Task to every ${row.teamKind === "technician" ? "technician" : "pharmacist"} · next task ${Math.abs(off)} days ${off >= 0 ? "after" : "before"} their expiry date`;
  }
  const ev = row?.defaultSource === "sop" ? "📘 Pharmace SOP" : row?.uploadKind === "documents" ? "📄 Document upload" : row?.uploadKind || row?.defaultSource === "upload" ? "📊 Spreadsheet upload" : EVIDENCE_LABEL[row?.evidenceType] || EVIDENCE_LABEL.photo;
  const life = v.mode === "days" ? `lasts ${v.days} days` : v.mode === "expiry-date" ? "lasts until its expiry date" : "no expiry";
  const chase = v.mode && v.mode !== "none" ? ` · task ${v.warnDays ? `${v.warnDays}d before expiry` : "on expiry"}` : "";
  return `${ev} · ${life}${chase}`;
}

// Items made of several parts, each set up as its own row under the item.
// The item is in place at a branch only when every part is.
export const COMPLAINT_PARTS = [{
  id: "complaints--sop",
  label: "1 · Complaints SOP"
}, {
  id: "complaints--system",
  label: "2 · Feedback system, complaints switched on"
}, {
  id: "complaints--poster",
  label: "3 · Complaints / feedback poster displayed"
}, {
  id: "complaints--records",
  label: "4 · Complaints recorded and resolved"
}];
// Speak up / whistleblowing — GPhC standards 2.4 & 2.5; NHS Terms of Service (England).
export const SPEAKUP_PARTS = [{
  id: "speakup--policy",
  label: "1 · Speak-up / whistleblowing policy"
},
// The Guardian also puts up the speak-up poster and photographs it (this
// replaced a separate "contacts shown to staff" part).
{
  id: "speakup--guardian",
  label: "2 · Freedom to Speak Up Guardian named & poster up"
}, {
  id: "speakup--briefed",
  label: "3 · Staff and locums briefed"
}, {
  id: "speakup--handling",
  label: "4 · Concerns investigator / assessor named"
}];
// Premises temperature — GPhC 3 & 4.3 (medicines stored as the manufacturer says, mostly ≤25°C)
// and the Workplace Regulations (reasonable for staff, normally ≥16°C).
export const TEMP_PARTS = [{
  id: "temp--thermometer",
  label: "1 · Min/max thermometer in place"
}, {
  id: "temp--records",
  label: "2 · Temperatures recorded and in range"
}];
// CD cabinet — Misuse of Drugs (Safe Custody) Regulations 1973 (reg 3 & Schedule 2).
export const CDCAB_PARTS = [{
  id: "cdcab--cabinet",
  label: "1 · Cabinet meets the Safe Custody Regulations"
}, {
  id: "cdcab--keys",
  label: "2 · CD keys held by the pharmacist and logged"
}, {
  id: "cdcab--segregation",
  label: "3 · Out-of-date and returned CDs kept separate"
}];
// Parts whose Pharmace data comes from another item's feed: the CD key log is
// evidenced by Pharmace RP sign-in records (the RP in charge holds the CD keys).
// "name" is the Pharmace record shown in the part's note.
export const LIVE_ALIAS = {
  "cdcab--keys": {
    elementId: "rp-log",
    groupId: "governance",
    itemId: "rp-record",
    name: "RP sign-in"
  },
  "cdao--discrepancies": {
    elementId: "cdao--discrepancies",
    groupId: "medicines",
    itemId: "cd",
    name: "CD register"
  }
};
// Essential Services (NHS Terms of Service): a yearly confirmation plus the SOP.
export const DISPOSAL_PARTS = [{
  id: "disposal--offered",
  label: "1 · Unwanted medicines accepted from patients (yearly confirmation)"
}, {
  id: "disposal--sop",
  label: "2 · Disposal of unwanted medicines SOP"
}];
export const SIGNPOST_PARTS = [{
  id: "signpost--offered",
  label: "1 · Signposting and self-care support given (yearly confirmation)"
}, {
  id: "signpost--sop",
  label: "2 · Signposting / self-care SOP"
}];
// Patient privacy notice — UK GDPR articles 13–14 (right to be informed).
export const PRIVACY_PARTS = [{
  id: "privacy--display",
  label: "1 · Privacy notice on display for patients (photo)"
}, {
  id: "privacy--notice",
  label: "2 · Current privacy notice (document or web page)"
}];
// PMR access — NHS smartcard terms and conditions; DSPT (access control); GPhC 4.2/1.1.
export const ITPMR_PARTS = [{
  id: "itpmr--users",
  label: "1 · Own login / smartcard for every user, leavers removed"
}, {
  id: "itpmr--sop",
  label: "2 · IT access / information governance SOP"
}];
// CD Accountable Officer — Controlled Drugs (Supervision of Management and Use) Regulations 2013.
export const CDAO_PARTS = [{
  id: "cdao--contact",
  label: "1 · CD Accountable Officer’s contact details known to staff"
}, {
  id: "cdao--discrepancies",
  label: "2 · CD discrepancies resolved or escalated within 7 days"
}];
// POM areas — GPhC 3.4 (premises safeguarded from unauthorised access) and 4.3 (medicines stored securely).
export const POM_PARTS = [{
  id: "pom--arrangements",
  label: "1 · Access arrangements in place (photo)"
}, {
  id: "pom--sop",
  label: "2 · Access / security SOP"
}];
// P-medicine display — GPhC statement on facilitated self-selection (2024, updated 2026); RPS guidance (2025).
export const PMED_PARTS = [{
  id: "pmed--display",
  label: "1 · How P medicines are displayed (photo)"
}, {
  id: "pmed--risk",
  label: "2 · Risk assessment / P-medicine sales SOP"
}];
// PQS 2026/27 Domain 2 — Pharmacy First clinical audit, in four parts.
export const PQS_AUDIT_PARTS = [{
  id: "pqsaudit--data",
  label: "1 · Audit of 10+ patients, data submitted on MYS"
}, {
  id: "pqsaudit--plan",
  label: "2 · Action plan from the findings"
}, {
  id: "pqsaudit--peer",
  label: "3 · Peer discussion with a buddy pharmacist"
}, {
  id: "pqsaudit--sop",
  label: "4 · SOP updated where the findings need it"
}];
export const PARTS_OF = {
  "tos-disposal": DISPOSAL_PARTS,
  "tos-signposting": SIGNPOST_PARTS,
  "gdpr-privacy": PRIVACY_PARTS,
  "it-pmr-secure": ITPMR_PARTS,
  "cd-officer": CDAO_PARTS,
  complaints: COMPLAINT_PARTS,
  "wb-speakup": SPEAKUP_PARTS,
  "env-temperature": TEMP_PARTS,
  "sec-cd-cabinet": CDCAB_PARTS,
  "sec-pom-access": POM_PARTS,
  "sec-pmedicine": PMED_PARTS,
  "pqs-pf-audit": PQS_AUDIT_PARTS
};
export const PARTS_PARENTS = Object.keys(PARTS_OF);
// Pharmace patient feedback data → the "live" status of the two parts it evidences.
const FEEDBACK_MODULE = {
  "complaints--system": "Patient feedback",
  "complaints--records": "Patient feedback (complaints)"
};
export function feedbackPartLive(id, f) {
  if (!f) return {
    live: false
  };
  if (id === "complaints--system") {
    const miss = [!f.inUse && "not in use (no feedback received)", f.complaintsEnabled === false && "complaints switched off", f.recipientSet === false && "no complaint recipient set"].filter(Boolean);
    const unknown = f.complaintsEnabled == null;
    return {
      live: true,
      noData: !f.inUse,
      done: f.inUse && !miss.length && !unknown,
      usageNote: miss.length ? `Needs: ${miss.join(", ")}` : unknown ? "In use — couldn’t read the complaints settings" : `In use · complaints on · recipient set${f.lastSubmissionAt ? ` · last feedback ${fmtDate(f.lastSubmissionAt)}` : ""}`
    };
  }
  if (id === "complaints--records") {
    return {
      live: true,
      noData: !f.inUse,
      done: f.inUse && !f.overdueOpen,
      usageNote: !f.inUse ? "Feedback system not in use" : !f.complaints ? "No complaints received" : `${f.complaints} complaint${f.complaints === 1 ? "" : "s"} · ${f.open} open${f.overdueOpen ? ` (${f.overdueOpen} over ${f.targetDays} days)` : ""}${f.avgDaysToClose != null ? ` · ${f.avgDaysToClose} days to close` : ""}`
    };
  }
  return {
    live: false
  };
}
const OK_STATES = new Set(["current", "pharmace-ok", "linked", "na", "due-soon"]);
const RED_STATES = new Set(["expired", "missing", "pharmace-no", "log-gaps"]);
// For the Compliance view: each complaints part at one branch, from /load.
export function complaintPartsStatus(load, dbName) {
  return partsStatus(load, dbName, "complaints");
}
export function partsStatus(load, dbName, parentId, liveFor = null) {
  const PARTS = PARTS_OF[parentId];
  if (!load || !PARTS) return null;
  const rows = new Map((load.rows || []).map(r => [r.rowId, r]));
  const cells = new Map((load.cells || []).map(c => [`${c.rowId}|${c.dbName}`, c]));
  if (!PARTS.some(p => rows.has(`el:${p.id}`) || cells.has(`el:${p.id}|${dbName}`))) return null;
  return PARTS.map(p => {
    // Same rule as the grid: a part fed by Pharmace that hasn't been set up yet uses Pharmace automatically.
    const aliasLive = LIVE_ALIAS[p.id] && liveFor ? liveFor(p.id, dbName) : null;
    const rowId = `el:${p.id}`,
      c = cells.get(`${rowId}|${dbName}`);
    // A Pharmace-fed part uses Pharmace where the branch has the data, else the row's other source.
    const rowSrc = rows.get(rowId)?.defaultSource,
      rowFallback = rows.get(rowId)?.fallbackSource;
    const src = c?.source || (LIVE_ALIAS[p.id] ? aliasLive?.live && !aliasLive.noData ? "pharmace" : rowSrc && rowSrc !== "pharmace" ? rowSrc : rowFallback || "" : rowSrc || (FEEDBACK_MODULE[p.id] ? "pharmace" : ""));
    if (!src) return {
      id: p.id,
      label: p.label,
      state: "unset",
      note: "Not set up yet"
    };
    if (src === "pharmace" && LIVE_ALIAS[p.id]) {
      const l = aliasLive || {};
      const nm = LIVE_ALIAS[p.id].name || "RP sign-in";
      return {
        id: p.id,
        label: p.label,
        state: l.done ? "ok" : "bad",
        note: l.live ? `Pharmace ${nm}: ${l.usageNote || (l.done ? "in use" : "not up to date")}` : `No Pharmace ${nm} data`
      };
    }
    if (src === "pharmace") {
      const l = feedbackPartLive(p.id, load.feedback?.[dbName]);
      return {
        id: p.id,
        label: p.label,
        state: l.done ? "ok" : "bad",
        note: l.usageNote || "No Pharmace data"
      };
    }
    const st = c?.status || "missing";
    const sopM = c?.sop?.matched || [];
    const unsigned = c?.sop?.requireSigned ? sopM.filter(x => x.held && x.inDate && !(x.assigned > 0 && x.signed >= x.assigned)) : [];
    const note = src === "sop" ? !sopM.length ? "No SOP linked yet" : unsigned.length ? `Not everyone has read & signed: ${unsigned.map(x => `${x.title} ${x.signed || 0}/${x.assigned || 0}`).join(", ")}` : OK_STATES.has(st) ? `Pharmace SOP in place: ${sopM.map(x => x.title).join(", ")}` : sopM.some(x => !x.held) ? `SOP not held: ${sopM.filter(x => !x.held).map(x => x.title).join(", ")}` : "SOP past its review date" : src === "na" ? "Not applicable" : src === "link" ? `Linked: ${c?.linkLabel || c?.linkUrl || "source"}` : c?.evidence?.at ? `Evidenced ${fmtDate(c.evidence.at)}${c.expiresAt ? `, until ${fmtDate(c.expiresAt)}` : ""}` : st === "awaiting" ? `Requested ${fmtDate(c?.task?.sentAt)}` : (src === "task" || src === "upload") && !c?.recipientStaffId && !c?.task ? `No recipient chosen yet — choose who receives the ${src === "upload" ? "upload request" : "task"} in the Working grid` : "No evidence yet";
    return {
      id: p.id,
      label: p.label,
      state: OK_STATES.has(st) ? "ok" : RED_STATES.has(st) ? "bad" : "pending",
      note,
      analysis: c?.evidence?.analysis || null,
      reports: c?.evidence?.reports || null
    };
  });
}

// -----------------------------------------------------------------------------
// Pure model pieces (2026-10), shared by the grid and the Overview page so
// both show exactly the same statuses. The grid wraps them in hooks below.
// -----------------------------------------------------------------------------
export const liveModuleFrom = rawLiveModule => id => FEEDBACK_MODULE[id] || (LIVE_ALIAS[id] ? rawLiveModule?.(LIVE_ALIAS[id].elementId) || null : GUIDANCE[id]?.pharmace === false ? null : rawLiveModule?.(id) || null);
export const liveStatusFrom = (data, liveStatus) => (pharmacyName, groupId, itemId, elementId) => {
  if (FEEDBACK_MODULE[elementId]) {
    const b = (data?.branches || []).find(x => x.pharmacyName === pharmacyName);
    return feedbackPartLive(elementId, b ? data?.feedback?.[b.dbName] : null);
  }
  const al = LIVE_ALIAS[elementId];
  if (al) return liveStatus?.(pharmacyName, al.groupId, al.itemId, al.elementId);
  return liveStatus?.(pharmacyName, groupId, itemId, elementId);
};
// Status for one cell, including live Pharmace status and the implicit
// "Pharmace (auto)" source for elements already wired to a module.
export function cellViewOf(row, b, {
  cells,
  rowCfg,
  liveModule,
  liveStatusX,
  data
}) {
  const check = rowApplicability(row, locationOf(b, data));
  if (!check.applicable) return {
    applicable: false,
    source: 'na',
    status: check.unknown ? 'location-unknown' : 'not-applicable',
    line1: check.unknown ? 'Location unresolved' : 'Not applicable',
    line2: check.reason
  };
  const c = cells.get(`${row.rowId}|${b.dbName}`);
  if (c?.sopCascade) {
    const mode = c.sopCascade.mode;
    return {
      c,
      source: c.source,
      status: c.status,
      line1: mode === 'cascade' ? 'SOPs cascade managed by Pharmace' : mode === 'sop' ? 'Selected Pharmace SOPs' : mode === 'link' ? 'External SOP/process link' : mode === 'upload' ? 'Uploaded SOP and evidence' : 'SOP evidence task',
      line2: ['cascade', 'sop'].includes(mode) ? `${c.sopDocuments?.length || 0} SOPs · ${c.status}` : c.evidence?.at ? `Received ${fmtDate(c.evidence.at)}` : c.status
    };
  }
  if (c?.sopShared) return {
    c,
    source: c.source,
    status: c.status,
    line1: c.source === 'sop' ? 'Pharmace SOP system' : c.scopeMasterDbName ? 'Estate scope review' : c.source === 'link' ? 'External SOP evidence' : 'Supporting document',
    line2: c.scopeMasterDbName ? `${c.recipientName} · ${c.task?.status || c.status}` : c.source === 'sop' ? `${c.sopDocuments?.length || 0} SOPs · ${c.status}` : c.evidence?.at ? `Received ${fmtDate(c.evidence.at)}` : c.status
  };
  // A branch not set individually uses the row's default source; failing
  // that, an element already wired to a Pharmace feed shows it automatically.
  const def = defaultSourceOf(rowCfg.get(row.rowId));
  const autoModule = row.elementId && !c?.source && !def ? liveModule?.(row.elementId) : null;
  const source = c?.source || def || (autoModule ? "pharmace" : GUIDANCE[row.elementId]?.web ? "web" : GUIDANCE[row.elementId]?.team ? "team" : null);
  let status = c?.status || (autoModule ? "pharmace" : "unset");
  let line1 = "Not set",
    line2 = "";
  if (source === "pharmace") {
    const live = row.elementId ? liveStatusX(b.pharmacyName, row.groupId, row.itemId, row.elementId) : null;
    const dated = ["expired", "due-soon", "chasing"].includes(c?.status);
    if (!dated && live && live.live) status = live.noData || live.usage === "never" ? "pharmace-never" : live.done ? "pharmace-ok" : live.usage === "lapsed" ? "pharmace-lapsed" : "pharmace-no";
    line1 = `Pharmace · ${c?.moduleName || autoModule || liveModule?.(row.elementId) || "module"}${!c?.source ? " (auto)" : ""}`;
    line2 = live && live.live ? live.usageNote || (live.noData ? "Live — no entries recorded yet" : live.done ? "Done" : "Not done") : "No live data";
    if (c?.status === "expired") line2 = `Review overdue ${fmtDate(c.expiresAt)}`;else if (c?.status === "due-soon") line2 = `Review by ${fmtDate(c.expiresAt)}`;else if (c?.status === "chasing") line2 = `Review task sent ${fmtDate(c.task?.sentAt)}`;else if (c?.validUntil) line2 += ` · until ${fmtDate(c.validUntil)}`;
  } else if (source === "task") {
    line1 = c?.recipientName ? `Task → ${c.recipientName}` : "Task → choose person";
  } else if (source === "sop") {
    const m = c?.sop?.matched || [];
    line1 = `📘 SOP${m.length > 1 ? `s (${m.length})` : ""}${m[0] ? ` · ${m[0].title}` : ""}`;
    line2 = !m.length ? "No SOP linked" : m.every(x => x.held && x.inDate) ? `In place${m[0].assigned ? ` · ${m.reduce((t, x) => t + (x.signed || 0), 0)}/${m.reduce((t, x) => t + (x.assigned || 0), 0)} read` : ""}` : m.some(x => !x.held) ? `Missing: ${m.filter(x => !x.held).map(x => x.title).join(", ")}` : "Past review date";
  } else if (source === "upload") {
    line1 = c?.recipientName ? `📧 Upload → ${c.recipientName}` : "📧 Upload → choose person";
  } else if (source === "link") {
    line1 = `${c?.linkUrl ? "🔗" : "🗄"} ${c?.linkLabel || "Link"}`;
  } else if (source === "na") {
    line1 = "Not applicable";
  } else if (source === "team") {
    const t = c?.team;
    const tw = GUIDANCE[row.elementId]?.teamKind === "technician" ? "technician" : "pharmacist";
    line1 = `👥 ${t ? `${t.total} ${tw}${t.total === 1 ? "" : "s"}` : `Every ${tw}`}`;
    line2 = !t ? "Not sent yet" : !t.total ? "Nobody with the role here" : t.confirmed === t.total ? `All confirmed${t.nextExpiry ? ` · next expiry ${fmtDate(t.nextExpiry)}` : ""}` : `${t.confirmed} of ${t.total} confirmed${t.people.some(p => p.status === "needs-expiry") ? " · check needed" : t.people.some(p => p.status === "awaiting" || p.status === "chasing") ? " · awaiting copies" : ""}`;
  } else if (source === "web") {
    const w = webRowStatus(row.elementId, data?.sites?.[b.dbName]);
    status = w.status;
    line1 = "🌐 Website review";
    line2 = w.note;
  }
  if ((source === "task" || source === "link" || source === "upload") && c) {
    const exp = c.expiresAt;
    if (status === "current") line2 = exp ? `Until ${fmtDate(exp)}` : `Evidenced ${fmtDate(c.evidence?.at)}`;else if (status === "due-soon") line2 = `Expires in ${daysBetween(new Date(), exp)}d`;else if (status === "expired") line2 = `Expired ${fmtDate(exp)}`;else if (status === "chasing") line2 = `Renewal sent ${fmtDate(c.task?.sentAt)}`;else if (status === "awaiting") line2 = `${source === "upload" ? "Requested" : "Sent"} ${fmtDate(c.task?.sentAt)}`;
    if (source === "upload" && c.evidence?.files?.length && !c.evidence.analysis && (status === "current" || status === "due-soon")) line2 = `Uploaded ${fmtDate(c.evidence.at)} · ${c.evidence.files.length} file${c.evidence.files.length === 1 ? "" : "s"}`;
    if (source === "upload" && LOG_TEXT[c.evidence?.analysis?.kind] && (status === "current" || status === "due-soon")) {
      const an = c.evidence.analysis;
      line2 = an.kind === "cleaning" ? `${c.sheetLink?.url ? "🔄 " : ""}${an.pctOnTimeYear ?? "—"}% on time · ${an.missedCleans} missed${an.overdueAreas ? ` · ${an.overdueAreas} overdue` : ""}` : `${c.sheetLink?.url ? "🔄 " : ""}${an.total} ${LOG_TEXT[an.kind].many} · ${an.kind === "lfpse" ? `${an.lfpse?.pct ?? 0}% to LFPSE` : `${an.last30} in 30d`}`;
      // An upload in date can still show a log with gaps, or one that has lapsed.
      if (c.evidence.analysis.status === "gaps") status = "log-gaps";else if (c.evidence.analysis.status === "lapsed") status = "log-lapsed";
    } else if (source === "upload" && c.evidence?.analysis && (status === "current" || status === "due-soon")) line2 = `${c.sheetLink?.url ? "🔄 " : ""}Uploaded ${fmtDate(c.evidence.at)} · ${c.evidence.analysis.daysCovered}/${c.evidence.analysis.tradingDays} days`;else if (status === "needs-expiry") line2 = "Evidence in · add expiry";else if (status === "missing") line2 = c.recipientName ? "No evidence yet" : "";
    // Speak up Guardian: the named person has (or hasn't yet) accepted the role.
    const rg = GUIDANCE[row.elementId];
    if (rg?.guardian && source === "task") {
      line1 = `${rg.roleIcon} ${rg.roleShort || rg.roleTitle} → ${c.recipientName || "choose person"}`;
      if ((status === "current" || status === "due-soon") && c.evidence?.at) line2 = `✔ Accepted ${fmtDate(c.evidence.at)}${status === "due-soon" ? " · renewal due" : ""}`;else if (status === "awaiting" || status === "chasing") line2 = `${rg.posterGuardian ? "Awaiting poster photo" : "Awaiting acceptance"} · sent ${fmtDate(c.task?.sentAt)}`;
    }
  }
  return {
    c,
    source,
    status,
    line1,
    line2
  };
}
// Every outstanding line (see outstandingFor) across all groups and branches.
// all: true also lists every item that is in place (or not applicable), with
// a summary of its evidence — the inspector view.
export function buildOutstanding({
  data,
  groups,
  branches,
  isDsp,
  cellView,
  all = false
}) {
  if (!data) return [];
  const customs = (data.rows || []).filter(r => r.custom);
  const out = [];
  for (const g of groups) for (const it of g.items || []) {
    const rows = [];
    for (const el of it.elements || []) {
      // The same row shape as the grid's, so "Open" shows the usual cell dialog.
      const common = {
        requirement: el.requirement,
        dsp: !!g.dsp,
        groupId: g.id,
        itemId: it.id,
        groupName: g.name,
        itemName: it.name
      };
      if (PARTS_OF[el.id]) for (const p of PARTS_OF[el.id]) rows.push({
        ...common,
        rowId: `el:${p.id}`,
        elementId: p.id,
        label: p.label,
        part: true,
        parentLabel: el.label,
        help: GUIDANCE[p.id]?.explanation || firstPara(GUIDANCE[p.id]?.satisfies),
        link: ""
      });else rows.push({
        ...common,
        rowId: `el:${el.id}`,
        elementId: el.id,
        label: el.label,
        help: el.help,
        link: el.link || "",
        auto: !!GUIDANCE[el.id]?.auto
      });
    }
    for (const c of customs.filter(x => x.groupId === g.id && x.itemId === it.id)) rows.push({
      ...c,
      dsp: !!g.dsp,
      groupName: g.name,
      itemName: it.name
    });
    for (const row of rows) {
      if (row.auto) continue;
      for (const b of branches) {
        if (row.dsp && !isDsp(b)) continue;
        const v = cellView(row, b);
        if (v.applicable === false) continue;
        let lines = outstandingFor(row, b, v);
        if (all && !lines.length) lines = [inPlaceLine(v)];
        for (const e of lines) out.push({
          ...e,
          key: `${row.rowId}|${b.dbName}|${e.staffId || ""}|${out.length}`,
          row,
          branch: b,
          groupName: g.name,
          itemName: it.name,
          element: row.part ? `${row.parentLabel} — ${row.label}` : row.label
        });
      }
    }
  }
  return out;
}
export const groupsFor = (baseGroups, hasDsp, branches = [], sites = {}) => decorateGroups([...baseGroups, ...PQS_GROUPS, ...(hasDsp ? DSP_GROUPS : [])], branches.map(b => locationOf(b, {
  sites
})));

// -----------------------------------------------------------------------------
// Data filter categories for a cell's status (2026-10).
const DATA_CAT = st => ["current", "pharmace-ok", "due-soon", "linked", "na"].includes(st) ? "in" : ["pharmace-no", "log-gaps"].includes(st) ? "out" : st === "unset" ? "unset" : "none";
export default function InspectorWorkingGrid({
  initialBranch = "",
  groups: baseGroups,
  token,
  liveStatus,
  liveModule: rawLiveModule,
  displayName = n => n,
  masked = false,
  renderDetail = null,
  rollup = null,
  renderItemDetail = null
}) {
  // Items Pharmace can never evidence (e.g. RP notice displayed) never use a module.
  const liveModule = useMemo(() => liveModuleFrom(rawLiveModule), [rawLiveModule]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [area, setArea] = useState("all");
  const [search, setSearch] = useState("");
  const [hideUnset, setHideUnset] = useState(true);
  // Row filter: "all" | "set" (set up) | "unset" (not set up yet).
  const [setFilter, setSetFilter] = useState("all");
  const onlySetUp = setFilter !== "all";
  const [editRow, setEditRow] = useState(null); // row object (or {new:true})
  const [openCell, setOpenCell] = useState(null); // { row, branch }
  const [sitesOpen, setSitesOpen] = useState(false);
  const [assistOpen, setAssistOpen] = useState(false); // Setup assistant
  const [outOpen, setOutOpen] = useState(false); // Outstanding list
  const [dataFilter, setDataFilter] = useState("all"); // all | none | out | in
  const [detail, setDetail] = useState(null); // { row, branch } — the data behind a cell
  const [itemDetail, setItemDetail] = useState(null); // { g, it, b, metricId? } — a whole item at one branch
  // Each new inspection starts collapsed; expansion is local to this window.
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const toggleKey = k => setOpenKeys(s => {
    const n = new Set(s);
    if (n.has(k)) n.delete(k);else n.add(k);
    return n;
  });
  const load = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      setData(await post(token, `${GRID}/load`, {}));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [token]);
  useEffect(() => {
    load();
  }, [load]);
  const rowCfg = useMemo(() => new Map((data?.rows || []).map(r => [r.rowId, r])), [data]);
  const cells = useMemo(() => new Map((data?.cells || []).map(c => [`${c.rowId}|${c.dbName}`, c])), [data]);
  const [selectedBranch, setSelectedBranch] = useState(String(initialBranch));
  const allBranches = data?.branches || [];
  const selected = allBranches.find(b => String(b.dbName) === selectedBranch) || allBranches[0];
  const branches = selected ? [selected] : [];
  useEffect(() => {
    setDetail(null);
    setItemDetail(null);
  }, [selectedBranch]);
  // Distance selling pharmacies: their criteria only appear once a branch is flagged.
  const sites = data?.sites || {};
  const isDsp = useCallback(b => !!sites[b.dbName]?.dsp, [sites]);
  const dspBranches = useMemo(() => branches.filter(isDsp), [branches, isDsp]);
  const groups = useMemo(() => groupsFor(baseGroups, dspBranches.length > 0, branches, sites), [baseGroups, dspBranches.length, branches, sites]);
  // Live status: Pharmace patient feedback for the complaints parts, else the page's own.
  const liveStatusX = useMemo(() => liveStatusFrom(data, liveStatus), [data, liveStatus]);

  // Header → sub-header → rows, with custom rows placed under their sub-header.
  const sections = useMemo(() => {
    const customs = (data?.rows || []).filter(r => r.custom);
    const q = search.trim().toLowerCase();
    const keep = row => {
      if (q && !`${row.label} ${row.parentLabel || ""} ${row.groupName} ${row.itemName}`.toLowerCase().includes(q)) return false;
      if (setFilter !== "all") {
        // A row is set up once its settings are saved or any branch has a source;
        // an item made of parts counts as set up when every part is.
        const one = rid => rowCfg.has(rid) || branches.some(b => cells.get(`${rid}|${b.dbName}`)?.source);
        const isSetUp = row.hasParts ? (PARTS_OF[row.elementId] || []).every(p => one(`el:${p.id}`)) : one(row.rowId) || !!GUIDANCE[row.elementId]?.auto;
        if (setFilter === "set" ? !isSetUp : isSetUp) return false;
      }
      return true;
    };
    return groups.filter(g => area === "all" || g.id === area).map(g => {
      const itemIds = new Set((g.items || []).map(i => i.id));
      const items = (g.items || []).map(it => {
        const base = (it.elements || []).flatMap(el => {
          const main = {
            rowId: `el:${el.id}`,
            elementId: el.id,
            label: el.label,
            hasParts: !!PARTS_OF[el.id],
            dsp: !!g.dsp,
            requirement: el.requirement,
            help: el.help,
            link: el.link || "",
            groupId: g.id,
            itemId: it.id,
            groupName: g.name,
            itemName: it.name
          };
          const parts = (PARTS_OF[el.id] || []).map(p => ({
            rowId: `el:${p.id}`,
            elementId: p.id,
            label: p.label,
            part: true,
            parentLabel: el.label,
            requirement: el.requirement,
            help: GUIDANCE[p.id]?.explanation || firstPara(GUIDANCE[p.id]?.satisfies),
            link: "",
            groupId: g.id,
            itemId: it.id,
            groupName: g.name,
            itemName: it.name
          }));
          return [main, ...parts];
        });
        const extra = customs.filter(c => c.groupId === g.id && c.itemId === it.id).map(c => ({
          ...c,
          groupName: g.name,
          itemName: it.name
        }));
        return {
          ...it,
          rows: [...base, ...extra].filter(keep)
        };
      });
      const orphans = customs.filter(c => c.groupId === g.id && !itemIds.has(c.itemId)).map(c => ({
        ...c,
        groupName: g.name,
        itemName: "Other"
      }));
      if (orphans.length) items.push({
        id: "_other",
        name: "Other",
        rows: orphans.filter(keep)
      });
      return {
        ...g,
        items: items.filter(it => it.rows.length || !q && !onlySetUp)
      };
    }).filter(g => g.items.length);
  }, [groups, data, area, search, onlySetUp, setFilter, rowCfg, cells, branches]);

  // Status for one cell, including live Pharmace status and the implicit
  // "Pharmace (auto)" source for elements already wired to a module.
  const cellView = useCallback((row, b) => cellViewOf(row, b, {
    cells,
    rowCfg,
    liveModule,
    liveStatusX,
    data
  }), [cells, rowCfg, liveModule, liveStatusX, data]);

  // Is there Pharmace data for this item, and at which branches?
  const detect = useCallback(row => {
    const module = row?.elementId ? liveModule?.(row.elementId) : null;
    if (!module) return {
      module: null,
      withData: []
    };
    const withData = branches.filter(b => {
      const l = liveStatusX(b.pharmacyName, row.groupId, row.itemId, row.elementId);
      return l && l.live && !l.noData;
    }).map(b => b.dbName);
    return {
      module,
      withData
    };
  }, [branches, liveModule, liveStatusX]);

  // An item made of parts: its cell sums up its parts' cells.
  const partsView = useCallback((row, b) => {
    if (!rowApplicability(row, locationOf(b, data)).applicable) return cellView(row, b);
    const parts = PARTS_OF[row.elementId].map((p, i) => ({
      n: i + 1,
      v: cellView({
        ...row,
        rowId: `el:${p.id}`,
        elementId: p.id
      }, b)
    }));
    const ok = parts.filter(x => OK_STATES.has(x.v.status)).length;
    const red = parts.filter(x => RED_STATES.has(x.v.status));
    const unset = parts.filter(x => x.v.status === "unset");
    const status = red.length ? "missing" : unset.length === parts.length ? "unset" : unset.length ? "unset" : ok === parts.length ? "current" : "due-soon";
    const needs = parts.filter(x => !OK_STATES.has(x.v.status)).map(x => x.n);
    return {
      status,
      line1: `${ok} of ${parts.length} parts in place`,
      line2: needs.length ? `${unset.length === parts.length ? "Set up parts" : "Needs part"} ${needs.join(", ")}` : "All parts in place"
    };
  }, [cellView]);
  const counts = useMemo(() => {
    const n = {};
    for (const g of sections) for (const it of g.items) for (const row of it.rows) for (const b of branches) {
      if (row.hasParts || row.dsp && !isDsp(b) || !rowApplicability(row, locationOf(b, data)).applicable) continue;
      const s = cellView(row, b).status;
      n[s] = (n[s] || 0) + 1;
    }
    return n;
  }, [sections, branches, cellView, isDsp]);

  // Outstanding (2026-10): every row × branch a manager has to chase — not set
  // up, no evidence, a request not answered, expired or due, Pharmace data
  // not up to date, a registrant who hasn't sent their copy… Built from the
  // same cell views as the grid (ignoring the grid's own search and filters).
  const outstanding = useMemo(() => buildOutstanding({
    data,
    groups,
    branches,
    isDsp,
    cellView
  }), [data, groups, branches, isDsp, cellView]);
  // Every item with what holds it (the inspector view) — only worked out while the list is open.
  const evidenceAll = useMemo(() => outOpen && data ? buildOutstanding({
    data,
    groups,
    branches,
    isDsp,
    cellView,
    all: true
  }) : null, [outOpen, data, groups, branches, isDsp, cellView]);

  // Data filter: keep rows where at least one branch's cell is in the chosen state.
  const shown = useMemo(() => {
    const isVisible = (row, b) => {
      if (row.dsp && !isDsp(b)) return false;
      const view = cellView(row, b);
      // Built-in source suggestions (e.g. "Every technician") do not mean
      // the item has been configured. Respect each cell's actual setup status.
      // Configured missing/expired/awaiting evidence remains visible.
      if (hideUnset) {
        if (view.status === "unset") return false;
        const configured = view.c?.source || defaultSourceOf(rowCfg.get(row.rowId));
        if (!configured) {
          const live = row.elementId ? liveStatusX(b.pharmacyName, row.groupId, row.itemId, row.elementId) : null;
          if (!live?.live) return false;
          // A wired Pharmace feed with no entries is amber and may be inspected.
          // Unconfigured feedback sub-parts remain hidden until explicitly set.
          if (live.noData && (view.source !== "pharmace" || FEEDBACK_MODULE[row.elementId])) return false;
        }
      }
      return dataFilter === "all" || DATA_CAT(view.status) === dataFilter;
    };
    const hit = row => branches.some(b => {
      if (row.hasParts) return (PARTS_OF[row.elementId] || []).some(p => isVisible({
        ...row,
        rowId: `el:${p.id}`,
        elementId: p.id,
        hasParts: false
      }, b));
      return isVisible(row, b);
    });
    if (!hideUnset && dataFilter === "all") return sections;
    return sections.map(g => ({
      ...g,
      items: g.items.map(it => ({
        ...it,
        rows: it.rows.filter(hit)
      })).filter(it => it.rows.length)
    })).filter(g => g.items.length);
  }, [sections, hideUnset, dataFilter, branches, cellView, isDsp, rowCfg, liveStatusX]);

  // Filter panel → "Reset to default".
  const filtersChanged = area !== "all" || !!search || setFilter !== "all" || dataFilter !== "all";
  const resetFilters = () => {
    setArea("all");
    setSearch("");
    setSetFilter("all");
    setDataFilter("all");
  };
  // Any search or filter shows every matching row, so nothing is hidden by a collapsed heading.
  const filtering = !!search.trim() || setFilter !== "all" || dataFilter !== "all";
  const gOpen = g => filtering || area !== "all" || openKeys.has(`g:${g.id}`);
  const iOpen = (g, it) => filtering || openKeys.has(`i:${g.id}|${it.id}`);
  const expandAll = () => setOpenKeys(new Set(shown.flatMap(g => [`g:${g.id}`, ...g.items.map(it => `i:${g.id}|${it.id}`)])));
  const collapseAll = () => setOpenKeys(new Set());
  const showMetrics = !!rollup && !filtering;
  const chip = (pct, {
    onClick,
    title,
    dark,
    band
  } = {}) => {
    const b = band || null;
    const bg = b ? b.bg : pct == null ? dark ? "#5D5D5D" : "#F5F5F5" : rollup.bg(pct);
    const fg = b ? b.fg : pct == null ? dark ? "#D9D9D9" : "#5D5D5D" : "#262626";
    const txt = pct == null ? b ? "No data" : "—" : `${Math.round(pct)}%`;
    const st = {
      display: "block",
      width: "100%",
      textAlign: "center",
      background: bg,
      color: fg,
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: 6,
      padding: "3px 4px",
      fontSize: 12,
      fontWeight: 700,
      cursor: onClick ? "pointer" : "default"
    };
    return onClick ? <button onClick={onClick} title={title} style={st}>{txt}</button> : <div title={title} style={st}>{txt}</div>;
  };
  const runCheck = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await post(token, `${GRID}/run-expiry-check`, {});
      setNotice((r.chased.length ? `Refresh & send: ${r.chased.length} task${r.chased.length === 1 ? "" : "s"} sent (${r.chased.map(c => `${c.to} at ${c.pharmacyName}`).join(", ")})${r.reminded?.length ? `; ${r.reminded.length} overdue reminder${r.reminded.length === 1 ? "" : "s"} emailed` : ""}.` : `Refresh & send: replies picked up — nothing new to send${r.reminded?.length ? `; ${r.reminded.length} overdue reminder${r.reminded.length === 1 ? "" : "s"} emailed` : ""} (${r.checked} cell${r.checked === 1 ? "" : "s"} checked).`) + (r.errors.length ? ` ${r.errors.length} could not be sent: ${r.errors[0].error}` : ""));
      await load();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  const th = {
    position: "sticky",
    top: 0,
    background: "#262626",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    padding: "8px 6px",
    borderRight: "1px solid #434343",
    zIndex: 2,
    textAlign: "left"
  };
  const firstCol = {
    position: "sticky",
    left: 0,
    zIndex: 1
  };
  return <div className="inspector-grid" style={{
    marginTop: 16
  }}> 
      <style>{`
        .cwg-filter-panel { margin: 0 0 12px; padding: 12px 16px 16px; min-width: 0; border: 1px solid #D9D9D9; border-radius: 8px; background: #fff; }
        .cwg-filter-panel legend { padding: 0 6px; color: #5D5D5D; font-size: 12px; font-weight: 600; }
        .cwg-filter-fields { display: grid; grid-template-columns: minmax(150px, 1fr) minmax(200px, 1.4fr) minmax(270px, 1.4fr) minmax(190px, 1.2fr) auto; gap: 12px 16px; align-items: start; }
        .cwg-filter-reset { box-sizing: border-box; height: 36px; padding: 6px 14px; font-size: 13px; white-space: nowrap; }
        .cwg-filter-field { display: flex; flex-direction: column; gap: 6px; margin: 0; padding: 0; min-width: 0; font-size: 12px; color: #5D5D5D; }
        .cwg-filter-field > select, .cwg-filter-field > input { box-sizing: border-box; width: 100%; min-width: 0; height: 36px; padding: 6px 10px; font-size: 13px; color: #262626; }
        .cwg-setup-options { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; min-height: 36px; }
        .cwg-setup-option { display: inline-flex; align-items: center; gap: 5px; padding: 0; margin: 0; white-space: nowrap; cursor: pointer; font-size: 13px; color: #262626; }
        .cwg-setup-option > input { width: 14px; height: 14px; margin: 0; padding: 0; flex-shrink: 0; }
        .cwg-grid-toolbar { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 10px 16px; align-items: center; margin-bottom: 8px; }
        .cwg-grid-toolbar-group { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
        @media (max-width: 1200px) { .cwg-filter-fields { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 650px) { .cwg-filter-fields { grid-template-columns: minmax(0, 1fr); } }
      `}</style>
      <header style={{
      position: "sticky",
      top: -16,
      zIndex: 20,
      background: "#fff",
      padding: "16px 0 12px",
      borderBottom: "1px solid #D6E4FF",
      marginBottom: 12
    }}>
        <h1 style={{
        fontSize: 22,
        margin: "0 0 12px"
      }}>Regulatory compliance — {selected ? badge(locationOf(selected, data)) : ""} — inspection view for {selected ? displayName(selected.pharmacyName, masked) : "selected pharmacy"}</h1>
        <div style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 12
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          maxWidth: "100%"
        }}>
          <label htmlFor="inspector-item-search">Search</label>
          <div style={{
            position: "relative",
            width: 360,
            maxWidth: "70vw"
          }}>
            <input id="inspector-item-search" aria-label="Search inspection items" placeholder="Search data items…" value={search} onChange={e => setSearch(e.target.value)} style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 38px 10px 10px"
            }} />
            {search !== "" && <button type="button" aria-label="Clear search filter" title="Clear search filter" onClick={e => {
              setSearch("");
              e.currentTarget.parentElement.querySelector("input")?.focus();
            }} style={{
              position: "absolute",
              right: 4,
              top: "50%",
              transform: "translateY(-50%)",
              width: 28,
              height: 28,
              minHeight: 0,
              padding: 0,
              margin: 0,
              border: 0,
              borderRadius: 4,
              background: "transparent",
              color: "#595959",
              fontSize: 22,
              lineHeight: "28px",
              cursor: "pointer"
            }}>×</button>}
          </div>
        </div>
        <label style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13
        }}>
          <input type="checkbox" checked={hideUnset} onChange={e => setHideUnset(e.target.checked)} />
          Hide unset items
        </label>
        <button type="button" onClick={expandAll} disabled={filtering || !data} title={filtering ? "Matching headings are already expanded while searching" : "Expand every compliance heading and item"}>Expand all</button>
        <button type="button" onClick={collapseAll} disabled={filtering || !data} title={filtering ? "Clear the search to collapse headings" : "Collapse every compliance heading and item"}>Collapse all</button>
        </div>
      </header>
      <p>Click a coloured heading to expand or collapse it. Click an evidence cell to see documents, dates and contributors.</p>
      {selected && <InspectorResources key={selected.dbName} branch={selected} token={token} />}
      <style>{`.inspector-grid button:hover, .inspector-grid a:hover { outline: 2px solid #1677ff; }`}</style>
      {data && branches.some(b => !locationOf(b, data)?.nation) && <div role="status" style={{
      padding: 10,
      background: '#fff7ed',
      color: '#9a3412',
      margin: '8px 0'
    }}>Location unresolved for {branches.filter(b => !locationOf(b, data)?.nation).map(b => displayName(b.pharmacyName, masked)).join(', ')}. Check the postcode in the pharmacy record, then reload. These branches are excluded from readiness scores and new compliance requests.</div>}
      {error && <div role="alert" style={{
      color: "#A8071A",
      margin: "6px 0"
    }}>{error}</div>}
      {notice && <div role="status" style={{
      color: "#389E0D",
      margin: "6px 0"
    }}>{notice}</div>}
      {busy && !data && <div style={{
      color: "#5D5D5D"
    }}>Loading working grid…</div>}

      {data && <div style={{
      overflow: "auto",
      border: "1px solid #F0F0F0",
      borderRadius: 8
    }}>
          <table style={{
        borderCollapse: "separate",
        borderSpacing: 0,
        width: "100%",
        tableLayout: "fixed"
      }}>
            <thead><tr>
              <th style={{
              ...th,
              ...firstCol,
              zIndex: 3,
              width: "100%"
            }}>Data item</th>

            </tr></thead>
            <tbody>
              {!shown.length && <tr><td style={{
              padding: 16,
              color: "#5D5D5D"
            }}>No inspection items match the current filters{hideUnset ? ". Uncheck “Hide unset items” to include items that are not configured." : "."}</td></tr>}
              {shown.map(g => <React.Fragment key={g.id}>
                  {!rollup ? <tr><td colSpan={1} style={{
                background: "#434343",
                color: "#fff",
                padding: "7px 10px",
                fontWeight: 700,
                fontSize: 13
              }}>
                    <span style={{
                  position: "sticky",
                  left: 10
                }}>{g.name} <span style={{
                    fontWeight: 400,
                    opacity: 0.75
                  }}>· {g.subtitle}</span>
                      {g.dsp && <span style={{
                    fontWeight: 400,
                    fontSize: 11,
                    marginLeft: 8,
                    background: "#722ED1",
                    borderRadius: 8,
                    padding: "1px 8px"
                  }}>applies to {dspBranches.length} DSP site{dspBranches.length === 1 ? "" : "s"} only</span>}</span>
                  </td></tr> : <tr>
                    <td onClick={() => !filtering && area === "all" && toggleKey(`g:${g.id}`)} title={filtering || area !== "all" ? "" : gOpen(g) ? "Click to close" : "Click to open"} style={{
                ...firstCol,
                background: "#434343",
                color: "#fff",
                padding: "7px 10px",
                fontWeight: 700,
                fontSize: 13,
                borderTop: "2px solid #fff",
                cursor: filtering || area !== "all" ? "default" : "pointer"
              }}>
                      <div className="inspector-heading-summary" style={{
                  display: "grid",
                  gridTemplateColumns: "12px minmax(0, 1fr) 60px",
                  gap: 8,
                  alignItems: "center",
                  width: "100%",
                  maxWidth: 640
                }}> 
                        <span style={{
                    width: 12
                  }} aria-hidden="true">{gOpen(g) ? "▾" : "▸"}</span>
                        <div style={{
                    flex: 1,
                    whiteSpace: "normal",
                    overflowWrap: "anywhere"
                  }}>{g.name} <span style={{
                      fontWeight: 400,
                      opacity: 0.75
                    }}>· {g.subtitle}</span>
                          {g.dsp && <span style={{
                      fontWeight: 400,
                      fontSize: 11,
                      marginLeft: 8,
                      background: "#722ED1",
                      borderRadius: 8,
                      padding: "1px 8px"
                    }}>applies to {dspBranches.length} DSP site{dspBranches.length === 1 ? "" : "s"} only</span>}</div>
                        <span style={{
                    minWidth: 52
                  }}>{chip(rollup.pct(g.id, null, selected?.pharmacyName), {
                      title: `${g.name} — selected branch`,
                      dark: true
                    })}</span>
                      </div>
                    </td>

                  </tr>}
                  {(!rollup || gOpen(g)) && g.items.map(it => {
              const metrics = showMetrics && it.id !== "_other" ? rollup.metrics(g.id, it.id) : [];
              const open = !rollup || iOpen(g, it);
              return <React.Fragment key={it.id}>
                      {!rollup ? <tr><td colSpan={1} style={{
                    background: "#F0F0F0",
                    padding: "5px 10px",
                    fontWeight: 600,
                    fontSize: 12,
                    color: "#262626"
                  }}>
                        <span style={{
                      position: "sticky",
                      left: 10
                    }}>{it.name}</span>
                        {!it.rows.length && <span style={{
                      fontWeight: 400,
                      color: "#5D5D5D"
                    }}> — tracked by Pharmace effectiveness metrics on the Compliance view</span>}
                      </td></tr> : <tr>
                        <td onClick={() => !filtering && toggleKey(`i:${g.id}|${it.id}`)} title={filtering ? "" : open ? "Click to close" : "Click to open"} style={{
                    ...firstCol,
                    background: "#F0F0F0",
                    padding: "5px 10px 5px 22px",
                    fontWeight: 600,
                    fontSize: 12,
                    color: "#262626",
                    borderBottom: "1px solid #D9D9D9",
                    cursor: filtering ? "default" : "pointer"
                  }}>
                          <div className="inspector-heading-summary" style={{
                      display: "grid",
                      gridTemplateColumns: "12px minmax(0, 1fr) 60px",
                      gap: 8,
                      alignItems: "center",
                      width: "100%",
                      maxWidth: 628
                    }}> 
                            <span style={{
                        width: 12
                      }} aria-hidden="true">{open ? "▾" : "▸"}</span>
                            <div style={{
                        flex: 1,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere"
                      }}>{it.name}
                              <span style={{
                          fontWeight: 400,
                          color: "#5D5D5D"
                        }}> · {it.rows.filter(r => !r.part).length} item{it.rows.filter(r => !r.part).length === 1 ? "" : "s"}{metrics.length ? ` · ${metrics.length} measure${metrics.length === 1 ? "" : "s"}` : ""}</span></div>
                            <span style={{
                        minWidth: 52
                      }}>{chip(it.id === "_other" ? null : rollup.pct(g.id, it.id, selected?.pharmacyName), {
                          title: `${it.name} — selected branch`
                        })}</span>
                          </div>
                        </td>

                      </tr>}
                      {open && metrics.map(m => <tr key={`m:${m.id}`}>
                          <td style={{
                    ...firstCol,
                    background: "#E6F7FF",
                    borderBottom: "1px solid #F0F0F0",
                    padding: "6px 8px",
                    verticalAlign: "top"
                  }}>
                            <div style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#262626"
                    }} title={m.help || ""}>📈 {m.label}</div>
                            <div style={{
                      fontSize: 11,
                      color: "#5D5D5D"
                    }}>12-month effectiveness measure · from Pharmace · click the measure below for detail</div>
                          {branches.map(b => {
                      if (g.dsp && !isDsp(b)) return <div key={b.dbName} style={{
                        borderBottom: "1px solid #F0F0F0"
                      }} />;
                      const v = rollup.metric(m, b.pharmacyName);
                      const band = rollup.band(v.pct, v.threshold ?? 90);
                      return <div key={b.dbName} style={{
                        borderBottom: "1px solid #F0F0F0",
                        borderRight: "1px solid #F5F5F5",
                        padding: 3,
                        verticalAlign: "top"
                      }}>
                              {chip(v.pct, {
                          band,
                          title: `${m.label} — ${b.pharmacyName}: ${band.label}${v.threshold != null ? ` (target ${v.threshold}%)` : ""}`,
                          onClick: renderItemDetail ? () => setItemDetail({
                            g,
                            it,
                            b,
                            metricId: m.id
                          }) : undefined
                        })}
                              <div style={{
                          fontSize: 10,
                          color: band.fg,
                          textAlign: "center",
                          marginTop: 2
                        }}>{band.label}{v.threshold != null ? ` · target ${v.threshold}%` : ""}</div>
                            </div>;
                    })}
                          </td>
                        </tr>)}
                      {open && it.rows.map(row => {
                  const saved = rowCfg.get(row.rowId) || {};
                  const cfg = {
                    ...row,
                    ...saved,
                    label: row.label,
                    help: row.help,
                    explanation: niGuidanceValue(row, "explanation", saved.explanation) || GUIDANCE[row.elementId]?.explanation || row.help || "",
                    link: saved.link || row.link || ""
                  };
                  return <tr key={row.rowId}>
                            <td style={{
                      ...firstCol,
                      background: row.part ? "#FAFAFA" : "#fff",
                      borderBottom: "1px solid #F0F0F0",
                      padding: row.part ? "5px 8px 5px 26px" : "6px 8px",
                      verticalAlign: "top"
                    }}>
                              <div style={{
                        display: "flex",
                        gap: 6,
                        alignItems: "flex-start"
                      }}>
                                <div style={{
                          flex: 1
                        }}>
                                  <div style={{
                            fontSize: row.part ? 12 : 13,
                            fontWeight: 600,
                            color: "#262626"
                          }} title={row.help || ""}>{row.part && <span style={{
                              color: "#8C8C8C"
                            }}>↳ </span>}{row.label}
                                    {row.custom && <span style={{
                              fontSize: 10,
                              color: "#722ED1",
                              marginLeft: 6
                            }}>added</span>}</div>
                                  <div style={{
                            fontSize: 11,
                            color: rowCfg.has(row.rowId) || row.hasParts ? "#5D5D5D" : "#8C8C8C"
                          }}>
                                    {row.hasParts ? `Made of ${PARTS_OF[row.elementId].length} parts — set up each one below` : rowCfg.has(row.rowId) ? validitySummary(cfg) : "Evidence rules not set"}</div>
                                </div>
                                {false && <button onClick={() => setEditRow(cfg)} title="Evidence and validity for this row" style={{
                          fontSize: 13
                        }}>⚙</button>}
                              </div>
                            {row.hasParts && branches.map(b => {
                        const v = partsView(row, b),
                          st = STATUS_STYLE[v.status] || STATUS_STYLE.unset;
                        return <div key={b.dbName} style={{
                          borderBottom: "1px solid #F0F0F0",
                          borderRight: "1px solid #F5F5F5",
                          padding: 3,
                          verticalAlign: "top"
                        }}>
                                <div title={`${row.label} — ${b.pharmacyName}: click to see each part’s data`} onClick={() => v.applicable !== false && renderDetail && setDetail({
                            row: contextualRow(cfg, b, data),
                            branch: b
                          })} style={{
                            minHeight: 44,
                            background: st.bg,
                            color: st.fg,
                            border: v.status === "unset" ? "1px dashed #D9D9D9" : "1px solid rgba(0,0,0,0.06)",
                            borderRadius: 6,
                            padding: "4px 6px",
                            fontSize: 11,
                            lineHeight: 1.3,
                            cursor: renderDetail ? "pointer" : "default"
                          }}>
                                  <div style={{
                              fontWeight: 700
                            }}>{v.line1}</div><div>{v.line2}</div></div></div>;
                      })}
                            {!row.hasParts && branches.map(b => {
                        if (row.dsp && !isDsp(b)) return <div key={b.dbName} style={{
                          borderBottom: "1px solid #F0F0F0",
                          borderRight: "1px solid #F5F5F5",
                          padding: 3,
                          verticalAlign: "top"
                        }}>
                                <div title="Not a distance selling pharmacy — change under Sites" style={{
                            minHeight: 44,
                            background: "#FAFAFA",
                            color: "#D9D9D9",
                            borderRadius: 6,
                            padding: "4px 6px",
                            fontSize: 11
                          }}>— not a DSP</div></div>;
                        const v = cellView(row, b),
                          st = STATUS_STYLE[v.status] || STATUS_STYLE.unset;
                        return <div key={b.dbName} style={{
                          borderBottom: "1px solid #F0F0F0",
                          borderRight: "1px solid #F5F5F5",
                          padding: 3,
                          verticalAlign: "top"
                        }}>
                                  <div style={{
                            position: "relative"
                          }}>
                                  <button disabled={v.applicable === false} onClick={() => renderDetail ? setDetail({
                              row: contextualRow(cfg, b, data),
                              branch: b
                            }) : setOpenCell({
                              row: contextualRow(cfg, b, data),
                              branch: b
                            })} title={`${row.label} — ${b.pharmacyName}: ${st.label}${renderDetail ? " — click to see evidence, date and contributor" : ""}`} style={{
                              width: "100%",
                              minHeight: 44,
                              textAlign: "left",
                              background: st.bg,
                              color: st.fg,
                              border: v.status === "unset" ? "1px dashed #D9D9D9" : "1px solid rgba(0,0,0,0.06)",
                              borderRadius: 6,
                              padding: "4px 24px 4px 6px",
                              cursor: "pointer",
                              fontSize: 11,
                              lineHeight: 1.3
                            }}>
                                    <div style={{
                                fontWeight: 600,
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "none"
                              }}>{v.line1}</div>
                                    <div>{v.line2 || st.label}</div>{v.applicable !== false && <div style={{
                                fontSize: 10,
                                marginTop: 3
                              }}>{detailsFor(row, locationOf(b, data)).reference}</div>}
                                  </button>
                                  {false && <button onClick={e => {
                              e.stopPropagation();
                              setOpenCell({
                                row: cfg,
                                branch: b
                              });
                            }} aria-label={`Settings — ${row.label} at ${b.pharmacyName}`} title="Change the settings for this branch" style={{
                              position: "absolute",
                              top: 3,
                              right: 3,
                              width: 20,
                              height: 20,
                              padding: 0,
                              border: "1px solid rgba(0,0,0,0.12)",
                              borderRadius: 4,
                              background: "rgba(255,255,255,0.85)",
                              cursor: "pointer",
                              fontSize: 12,
                              lineHeight: "18px",
                              color: "#434343"
                            }}>⚙</button>}
                                  </div>
                                </div>;
                      })}
                            </td>
                          </tr>;
                })}
                    </React.Fragment>;
            })}
                </React.Fragment>)}
            </tbody>
          </table>
        </div>}

      {detail?.row.elementId === "near-miss" && <NearMissPharmacyDialog pharmacyName={detail.branch.pharmacyName} pharmacyId={detail.branch.dbName} token={token} onClose={() => setDetail(null)} />}
      {detail && detail.row.elementId !== "near-miss" && renderDetail && <div role="dialog" aria-modal="true" aria-label={`Data — ${detail.row.label} — ${detail.branch.pharmacyName}`} onClick={e => {
      if (e.target === e.currentTarget) setDetail(null);
    }} style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,42,0.5)",
      zIndex: 10050,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      padding: "4vh 12px",
      overflow: "auto"
    }}>
        <div style={{
        background: "#fff",
        borderRadius: 10,
        width: "100%",
        maxWidth: 1180,
        boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
        overflow: "hidden"
      }}>
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          borderBottom: "1px solid #F0F0F0"
        }}>
            <div style={{
            flex: 1
          }}><b>{displayName(detail.branch.pharmacyName, masked)}</b> <span style={{
              color: "#5D5D5D"
            }}>· {detail.row.groupName} › {detail.row.itemName}{detail.row.parentLabel ? ` › ${detail.row.parentLabel}` : ""}</span></div>

            <button onClick={() => setDetail(null)} aria-label="Close">✕</button>
          </div>
          <div style={{
          padding: 12,
          maxHeight: "80vh",
          overflow: "auto"
        }}><OutstandingDetail x={{
            row: detail.row,
            branch: detail.branch,
            view: cellView(detail.row, detail.branch),
            cell: cellView(detail.row, detail.branch).c,
            type: "ok"
          }} token={token} readOnly />
            {renderDetail(detail.row, detail.branch.pharmacyName)}</div>
        </div>
      </div>}
      {itemDetail && renderItemDetail && <div role="dialog" aria-modal="true" aria-label={`${itemDetail.it.name} — ${itemDetail.b.pharmacyName}`} onClick={e => {
      if (e.target === e.currentTarget) setItemDetail(null);
    }} style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,42,0.5)",
      zIndex: 10050,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      padding: "4vh 12px",
      overflow: "auto"
    }}>
        <div style={{
        background: "#fff",
        borderRadius: 10,
        width: "100%",
        maxWidth: 1000,
        boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
        overflow: "hidden"
      }}>
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          borderBottom: "1px solid #F0F0F0"
        }}>
            <div style={{
            flex: 1
          }}><b>{displayName(itemDetail.b.pharmacyName, masked)}</b> <span style={{
              color: "#5D5D5D"
            }}>· {itemDetail.g.name} › {itemDetail.it.name}</span></div>
            {itemDetail.metricId && <button onClick={() => setItemDetail({
            ...itemDetail,
            metricId: null
          })} title="Show every element of this item at this branch">Whole item</button>}
            <button onClick={() => setItemDetail(null)} aria-label="Close">✕</button>
          </div>
          <div style={{
          padding: "0 12px 12px",
          maxHeight: "82vh",
          overflow: "auto"
        }}>{renderItemDetail(itemDetail.g.id, itemDetail.it.id, itemDetail.b.pharmacyName, itemDetail.metricId || null)}</div>
        </div>
      </div>}
      {outOpen && data && <OutstandingDialog items={outstanding} allItems={evidenceAll} initialMode={outOpen === "all" ? "all" : "outstanding"} initialSort={outOpen === "all" ? "item" : "branch"} token={token} branches={branches} displayName={displayName} masked={masked} sites={sites} onShowData={renderDetail ? (row, branch) => setDetail({
      row,
      branch
    }) : null} onOpenCell={(row, branch) => setOpenCell({
      row,
      branch
    })} onClose={() => setOutOpen(false)} onChanged={load} />}
      {assistOpen && data && <SetupAssistant groups={groups} rowCfg={rowCfg} cells={cells} customs={(data.rows || []).filter(r => r.custom)} branches={branches} dspBranches={dspBranches} cellView={cellView} displayName={displayName} masked={masked} onOpenRow={cfg => setEditRow(cfg)} onClose={() => setAssistOpen(false)} />}
      {sitesOpen && <SitesDialog branches={branches} sites={sites} token={token} displayName={displayName} masked={masked} onClose={() => setSitesOpen(false)} onChanged={async msg => {
      if (msg) setNotice(msg);
      await load();
    }} />}
      {editRow && <RowDialog row={editRow} groups={groups} token={token} branches={editRow.dsp ? dspBranches : branches} detect={detect} sites={sites} cellOf={db => cells.get(`${editRow.rowId}|${db}`) || null} onClose={() => setEditRow(null)} onSaved={async msg => {
      setEditRow(null);
      if (msg) setNotice(msg);
      await load();
    }} />}
      {openCell && <CellDialog row={openCell.row} branch={openCell.branch} branches={branches} view={cellView(openCell.row, openCell.branch)} token={token} liveModule={liveModule} displayName={displayName} masked={masked} feedback={data?.feedback?.[openCell.branch.dbName]} site={sites[openCell.branch.dbName]} guardian={data?.guardians?.[openCell.branch.dbName] || null} onClose={() => setOpenCell(null)} onSaved={async msg => {
      if (msg) setNotice(msg);
      await load();
    }} />}
    </div>;
}

// -----------------------------------------------------------------------------
// OUTSTANDING (2026-10). One line per thing to chase: branch, item, staff
// member, what's wrong. Sort by branch, item or staff member; filter; export
// to Excel; email a reminder (one line, or every ticked line).
// -----------------------------------------------------------------------------
const OUT_OK = new Set(["current", "pharmace-ok", "linked", "na", "pharmace"]);
const CHASE_SOURCES = new Set(["task", "upload", "link", "pharmace"]);
export const OUT_TYPES = [["ok", "In place"], ["na", "Not applicable"], ["unset", "Not set up"], ["nobody", "No one chosen"], ["awaiting", "Not answered"], ["missing", "No evidence"], ["expired", "Expired"], ["due", "Renewal due"], ["office", "Head office to act"], ["data", "Data not up to date"], ["signed", "Not read & signed by all"], ["mhra", "MHRA alert not read"]];
const OUT_TYPE_LABEL = Object.fromEntries(OUT_TYPES);
const reqWord = t => t?.kind === "both" ? "Task + upload email" : t?.kind === "upload" ? "Upload request" : "Task";
const overdueText = d => d && new Date(d) < new Date() ? ` — overdue since ${fmtDate(d)}` : d ? ` — due ${fmtDate(d)}` : "";
// The line for an item that is in order (inspector view): what holds it.
function inPlaceLine(v) {
  const c = v.c || null,
    ev = c?.evidence;
  const nFiles = ev?.files?.length || (ev?.fileUrl ? 1 : 0);
  const what = v.status === "na" ? "Not applicable at this branch" : [v.line1, v.line2, ev?.at && !String(v.line2 || "").includes(fmtDate(ev.at)) ? `evidenced ${fmtDate(ev.at)}${ev.by ? ` by ${ev.by}` : ""}` : "", nFiles ? `${nFiles} file${nFiles === 1 ? "" : "s"}` : ""].filter(Boolean).join(" · ");
  return {
    type: v.status === "na" ? "na" : "ok",
    what,
    status: v.status,
    staffName: ev?.by || c?.recipientName || "",
    staffId: null,
    remind: null,
    demo: !!c?.artificial,
    cell: c,
    view: v
  };
}
// The outstanding lines for one row at one branch (none when it's in order).
function outstandingFor(row, b, v) {
  const c = v.c || null,
    st = v.status,
    src = v.source;
  const line = (type, what, extra = {}) => ({
    type,
    what,
    status: st,
    staffName: "",
    staffId: null,
    remind: null,
    demo: !!c?.artificial,
    cell: c,
    view: v,
    ...extra
  });
  if (st === "unset" || !src) return [line("unset", "Not set up — choose how this is evidenced at this branch")];
  if (src === "team") {
    const t = c?.team;
    if (!t) return [line("awaiting", "No registration requests sent yet")];
    if (!t.total) return [line("data", "Nobody at the branch has the chosen role")];
    return (t.people || []).filter(p => p.status !== "current").map(p => {
      const open = p.task?.status === "open";
      const base = {
        person: p,
        staffName: p.name,
        staffId: p.staffId,
        lastReminderAt: p.task?.lastReminderAt || null,
        since: p.task?.sentAt || null,
        remind: p.status === "needs-expiry" ? null : {
          kind: "cell",
          rowId: row.rowId,
          dbName: b.dbName,
          staffId: p.staffId,
          label: row.label,
          resend: !open
        }
      };
      if (p.status === "needs-expiry") return line("office", "Registration copy received — head office to check it", {
        ...base,
        status: p.status
      });
      if (p.status === "expired") return line("expired", `Registration expired ${fmtDate(p.expiresAt)}${open ? ` · renewal request sent ${fmtDate(p.task.sentAt)}, not answered` : ""}`, {
        ...base,
        status: p.status
      });
      if (p.status === "due-soon") return line("due", `Registration renewal due — expires ${fmtDate(p.expiresAt)}`, {
        ...base,
        status: p.status
      });
      if (open) return line("awaiting", `Task + upload email sent ${fmtDate(p.task.sentAt)}, no copy sent yet${overdueText(p.task.dueDate)}`, {
        ...base,
        status: p.status
      });
      return line("missing", p.lastRejection?.length ? `Copy not accepted: ${p.lastRejection.join("; ")}` : "No copy of their registration yet", {
        ...base,
        status: p.status
      });
    });
  }
  if (src === "sop" && OUT_OK.has(st)) {
    const short = (c?.sop?.matched || []).filter(m => m.held && m.assigned > m.signed);
    return short.length ? [line("signed", `Not read & signed by everyone: ${short.map(m => `${m.title} ${m.signed || 0}/${m.assigned}`).join(", ")}`, {
      staffName: "Staff at the branch",
      sopTitles: short.map(m => m.title)
    })] : [];
  }
  if (OUT_OK.has(st)) return [];
  const task = c?.task?.status === "open" ? c.task : null;
  const who = task?.recipientName || c?.recipientName || "";
  const canChase = CHASE_SOURCES.has(src) && !!c?.recipientStaffId;
  const extra = {
    staffName: who,
    staffId: task?.recipientStaffId || c?.recipientStaffId || null,
    since: task?.sentAt || null,
    lastReminderAt: task?.lastReminderAt || null,
    remind: canChase ? {
      kind: "cell",
      rowId: row.rowId,
      dbName: b.dbName,
      label: row.label,
      resend: !task
    } : null
  };
  if (st === "awaiting" || st === "chasing") return [line("awaiting", `${st === "chasing" ? "Renewal — " : ""}${reqWord(task)} sent ${fmtDate(task?.sentAt)}, not answered${overdueText(task?.dueDate)}`, extra)];
  if ((src === "task" || src === "upload") && !c?.recipientStaffId) return [line("nobody", `No one chosen to receive the ${src === "upload" ? "upload request" : "task"}`)];
  if (st === "needs-expiry") return [line("office", "Evidence received — enter its expiry date", {
    ...extra,
    remind: null
  })];
  if (st === "expired") return [line("expired", src === "pharmace" ? `Pharmace: ${v.line2}` : `Evidence expired ${fmtDate(c?.expiresAt)}`, extra)];
  if (st === "due-soon") return [line("due", src === "pharmace" ? `Pharmace: ${v.line2}` : `Renewal due — evidence expires ${fmtDate(c?.expiresAt)}`, extra)];
  if (st === "missing") return [line("missing", src === "sop" || src === "web" ? v.line2 || "Not in place" : CHASE_SOURCES.has(src) ? "No evidence yet — request not sent" : v.line2 || "No evidence yet", extra)];
  if (String(st).startsWith("pharmace-")) return [line("data", `Pharmace: ${v.line2 || STATUS_STYLE[st]?.label || st}`)];
  if (st === "log-gaps" || st === "log-lapsed") return [line("data", `${STATUS_STYLE[st].label}: ${v.line2}`, extra)];
  return [line("missing", `${STATUS_STYLE[st]?.label || st}${v.line2 ? ` — ${v.line2}` : ""}`, extra)];
}

// The details behind one Outstanding line: what's held, what's missing, and
// who — every request and email, the evidence on file, the people involved.
const EVENT_TEXT = {
  "task-sent": "Request sent",
  "upload-requested": "Upload link emailed",
  "reminder-emailed": "Reminder emailed",
  "task-completed": "Completed in the staff app",
  uploaded: "Uploaded",
  "report-uploaded": "Report uploaded",
  "rejected-no-attachment": "Completed without the photo / document — returned",
  "rejected-check": "Copy not accepted",
  "evidence-recorded": "Evidence recorded by head office",
  configured: "Set up",
  "registration-confirmed": "Registration confirmed",
  "registration-needs-review": "Registration needs checking",
  "role-holder-changed": "Person changed",
  "sheet-reread": "Shared spreadsheet re-read",
  "sheet-reread-monthly": "Shared spreadsheet re-read (monthly)"
};
const EMAIL_TEXT = {
  sent: "email sent",
  no_address: "NOT emailed — no email address on file",
  failed: "email FAILED"
};
// The evidence behind one line — for things in place as well as things missing,
// so a manager or an inspector can check what the evidence is and who has seen
// it: the documents (open them), who has read & signed, every request and
// email, the people involved, the website checks, the data.
const isImg = u => /\.(jpe?g|png|gif|webp)(\?|#|$)/i.test(String(u || ""));
function FileList({
  files
}) {
  if (!files?.length) return null;
  return <div style={{
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    margin: "4px 0"
  }}>
    {files.slice(0, 12).map((f, i) => isImg(f.fileUrl) ? <a key={i} href={f.fileUrl} target="_blank" rel="noreferrer" data-filename={f.filename || "Photo"} title={`Open ${f.filename || "the photo"}`}>
          <img src={f.fileUrl} alt={f.filename || "Evidence photo"} style={{
        height: 70,
        maxWidth: 120,
        objectFit: "cover",
        borderRadius: 6,
        border: "1px solid #D9D9D9"
      }} /></a> : <a key={i} href={f.fileUrl} target="_blank" rel="noreferrer" data-filename={f.filename || "Document"} style={{
      alignSelf: "center"
    }}>📄 {f.filename || "Open the document"}</a>)}
  </div>;
}
function MhraAlertsHeld({
  token,
  dbName,
  h,
  tbl,
  tcell,
  st
}) {
  const [rows, setRows] = useState(null),
    [err, setErr] = useState(""),
    [open, setOpen] = useState(null),
    [who, setWho] = useState({});
  useEffect(() => {
    let off = false;
    post(token, `${API_BASE}/api/headoffice/mhra/site`, {
      dbName
    }).then(r => {
      if (!off) setRows(r.rows || []);
    }).catch(e => {
      if (!off) setErr(e.message);
    });
    return () => {
      off = true;
    };
  }, [token, dbName]);
  const toggle = a => {
    setOpen(open === a.mrhaAlertId ? null : a.mrhaAlertId);
    if (!who[a.mrhaAlertId] && a.staffNotifiedCount) post(token, `${API_BASE}/api/headoffice/mhra/cascade-detail`, {
      dbName,
      mrhaAlertId: a.mrhaAlertId
    }).then(r => setWho(m => ({
      ...m,
      [a.mrhaAlertId]: r.items || []
    }))).catch(() => setWho(m => ({
      ...m,
      [a.mrhaAlertId]: []
    })));
  };
  if (err) return <div style={{
    color: "#A8071A"
  }}>Couldn’t load the MHRA alerts: {err}</div>;
  if (!rows) return <div>Loading the MHRA alerts…</div>;
  const recent = rows.slice(0, 40);
  return <>
    <div style={h}>MHRA alerts — the response recorded and who has read them</div>
    <table style={tbl}><thead><tr><th style={tcell}>Alert</th><th style={tcell}>Response</th><th style={tcell}>Sent to staff</th></tr></thead>
      <tbody>{recent.map(a => <React.Fragment key={a.mrhaAlertId}>
        <tr>
          <td style={tcell}>{a.articleUrl ? <a href={a.articleUrl} target="_blank" rel="noreferrer" data-no-viewer="1">{a.title}</a> : a.title} <span style={{
                color: "#8C8C8C"
              }}>{a.alertId} · {fmtDate(a.alertDate)}</span></td>
          <td style={tcell}>{a.actioned ? <><span style={{
                  color: "#389E0D",
                  fontWeight: 700
                }}>✓</span> {fmtDate(a.action?.date)}{a.action?.isAffected ? ` · affected: ${a.action.affectedStockDetails || "yes"}` : a.action?.notAffectedReason ? ` · ${a.action.notAffectedReason}` : ""}{a.action?.actionTakenByStaff ? ` · by ${a.action.actionTakenByStaff}` : ""}</> : <span style={{
                color: "#A8071A",
                fontWeight: 600
              }}>No response recorded</span>}</td>
          <td style={tcell}>{a.staffNotifiedCount ? <button type="button" onClick={() => toggle(a)} style={{
                fontSize: 11
              }} title="Show who it was sent to and who has read it">
            {a.staffAcknowledgedCount}/{a.staffNotifiedCount} read {open === a.mrhaAlertId ? "▾" : "▸"}</button> : <span style={{
                color: "#8C8C8C"
              }}>not sent to staff</span>}</td>
        </tr>
        {open === a.mrhaAlertId && <tr><td colSpan={3} style={{
              ...tcell,
              background: "#FAFAFA"
            }}>
          {!who[a.mrhaAlertId] ? "Loading…" : who[a.mrhaAlertId].map((p, i) => <span key={i} style={{
                marginRight: 14,
                whiteSpace: "nowrap"
              }}>{p.staffName} {p.acknowledged ? <>{st("read")} {fmtDate(p.completedAt)}</> : st("pending")}</span>)}
        </td></tr>}
      </React.Fragment>)}</tbody></table>
    {rows.length > recent.length && <div style={{
      color: "#5D5D5D"
    }}>Showing the latest {recent.length} of {rows.length} alerts — “Show the data behind this” lists them all.</div>}
  </>;
}
// Inspector-only prose derived from the same effective source as the evidence cell.
export function inspectorMethodology(row = {}, view = {}, guidance = {}) {
  const c = view.c || {},
    source = view.source || c.source;
  const validity = row.validity || {};
  const recipient = c.recipientName || c.task?.recipientName;
  const person = recipient || "the nominated member of staff (not yet selected)";
  const schedule = validity.mode === "days" && Number(validity.days) > 0 ? ` Evidence is renewed every ${validity.days} days from the evidence date.` : validity.mode === "expiry-date" ? " Evidence is renewed against the expiry date recorded on the document." : validity.mode === "none" ? " No recurring renewal is configured." : " No renewal schedule has been configured.";
  const renewal = ["task", "upload", "link", "pharmace"].includes(source) && validity.mode && validity.mode !== "none" ? c.autoChase === false ? " Automatic renewal requests are switched off." : !c.recipientStaffId && !recipient ? " A recipient must be selected before renewal requests can be sent." : ` Renewal tasks are sent to ${person}${Number(validity.warnDays) > 0 ? ` ${validity.warnDays} days before expiry` : " when renewal is due"}.` : "";
  if (row.hasParts) return "This item is evidenced through its configured parts below. Each part uses its own evidence source, recipient and renewal arrangements.";
  if (view.applicable === false || source === "na") return `This item is not applicable at this pharmacy.${view.line2 ? ` ${view.line2}` : ""}`;
  if (c.sopCascade?.mode === "cascade") return "This item is managed through the Pharmace SOP cascade. The view uses the branch’s live SOP documents, versions, review dates and staff reading/signing records.";
  if (c.scopeMasterDbName) return `This item is covered by an estate-wide scope review${recipient ? ` assigned to ${recipient}` : ""}. The branch uses the shared review and its supporting evidence.${c.scopeStatement ? ` ${c.scopeStatement}` : ""}${schedule}${renewal}`;
  if (source === "pharmace" && row.elementId === "wb-staffing") return "This data is obtained automatically from active staff members’ 14-day shift schedules in Pharmace, grouped by role and weekday, alongside the pharmacy’s opening hours. Split shifts are added, daily breaks deducted once, and Week 1 and Week 2 averaged. Click a daily role total to see staff names and both weeks’ shift hours.";
  if (source === "pharmace") {
    const moduleName = c.moduleName || guidance.pharmaceName || (view.line1 || "").replace(/^Pharmace\s*·\s*/, "").replace(/\s*\(auto\)$/, "") || "the relevant Pharmace module";
    const automatic = /clean/i.test(moduleName) || /clean/i.test(row.elementId || "") ? "This data is obtained automatically from the cleaning records staff enter in the cleaning module on Pharmace." : `This data is obtained automatically from records entered in the ${moduleName} module on Pharmace.`;
    return automatic + (c.validUntil ? ` The arrangement is due for review on ${fmtDate(c.validUntil)}.` : "") + renewal;
  }
  if (source === "task" || source === "upload") {
    const action = source === "upload" ? "An upload request is emailed" : "A task is sent in the Pharmace staff app";
    const evidence = row.evidenceType || "photo";
    const completion = source === "upload" ? "upload the requested document(s) or records using the secure upload link" : evidence === "confirm" ? "acknowledge and confirm that the requirement has been met" : evidence === "document" ? "upload the requested document and mark the task complete" : "attach the requested photo and mark the task complete";
    return `${action} to ${person}, who must ${completion}.${schedule}${renewal}${c.sheetLink?.url ? " The linked shared spreadsheet is also re-read automatically on the first of each month." : ""}`;
  }
  if (source === "sop") return `This item uses ${c.sopCascade ? "the selected" : "linked"} SOP documents in Pharmace, with their versions and review dates.${c.sop?.requireSigned || c.sopShared || c.sopCascade ? " Staff reading and signing records are checked for the assigned SOPs." : ""}${schedule}`;
  if (source === "link") return `Evidence is held in another system${c.linkLabel ? ` (${c.linkLabel})` : ""}${c.linkUrl ? " and is accessed through the saved link" : "; no web link has been supplied"}.${schedule}${renewal}`;
  if (source === "team") return `Tasks are sent to every ${row.teamKind || guidance.teamKind || "pharmacist"} at this branch to confirm their professional registration and provide the requested copy. Each person’s evidence and expiry are tracked separately.${row.teamOffsetDays != null ? ` The next request is scheduled ${Math.abs(row.teamOffsetDays)} days ${row.teamOffsetDays >= 0 ? "after" : "before"} their expiry date.` : ""}${c.autoChase === false ? " Automatic requests are switched off." : ""}`;
  if (source === "web") return "This data is obtained through the Pharmace website review, which checks the pharmacy’s configured website for the required information.";
  return "No evidence source has been configured for this item at this pharmacy.";
}
function OutstandingDetail({
  x,
  token,
  site,
  onShowData,
  onChanged,
  readOnly = false
}) {
  const c = x.cell || {},
    v = x.view || {};
  const g = GUIDANCE[x.row?.elementId] || {};
  const [sops, setSops] = useState(null),
    [mh, setMh] = useState(null),
    [err, setErr] = useState("");
  const [done, setDone] = useState({});
  const [openSop, setOpenSop] = useState(null);
  const sopTitles = useMemo(() => x.sopTitles?.length ? x.sopTitles : (c.sop?.matched || []).map(m => m.title), [x.sopTitles, c.sop]);
  useEffect(() => {
    let off = false;
    if (v.source === "sop" && sopTitles.length) post(token, `${GRID}/sop-staff`, {
      dbName: x.branch.dbName,
      titles: sopTitles
    }).then(r => {
      if (!off) setSops(r.sops || []);
    }).catch(e => {
      if (!off) setErr(e.message);
    });
    if (x.type === "mhra" && x.mhraRow) post(token, `${API_BASE}/api/headoffice/mhra/cascade-detail`, {
      dbName: x.branch.dbName,
      mrhaAlertId: x.mhraRow.mrhaAlertId
    }).then(r => {
      if (!off) setMh(r.items || []);
    }).catch(e => {
      if (!off) setErr(e.message);
    });
    return () => {
      off = true;
    };
  }, [x, token, v.source, sopTitles]);
  const box = {
    background: "#fff",
    border: "1px solid #D6E4FF",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 12,
    lineHeight: 1.5
  };
  const h = {
    fontSize: 11,
    fontWeight: 700,
    color: "#5D5D5D",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    margin: "10px 0 3px"
  };
  const bad = {
    color: "#A8071A",
    fontWeight: 600
  };
  const good = {
    color: "#237804",
    fontWeight: 600
  };
  const tbl = {
    borderCollapse: "collapse",
    fontSize: 12,
    width: "100%"
  };
  const tcell = {
    padding: "3px 6px",
    borderTop: "1px solid #F0F0F0",
    textAlign: "left",
    verticalAlign: "top"
  };
  const st = s0 => s0 === "signed" || s0 === "read" ? <span style={{
    color: "#389E0D",
    fontWeight: 700
  }}>✓ {s0 === "read" ? "Read" : "Signed"}</span> : s0 === "overdue" ? <span style={bad}>✗ Overdue</span> : <span style={{
    color: "#AD6800",
    fontWeight: 700
  }}>● Not yet</span>;
  const sopRemind = async (title, p) => {
    const k = `${title}|${p.staffId}`;
    setDone(m => ({
      ...m,
      [k]: "Sending…"
    }));
    try {
      const r = await post(token, `${GRID}/sop-remind`, {
        dbName: x.branch.dbName,
        rowId: x.row?.rowId,
        title,
        staffId: p.staffId
      });
      setDone(m => ({
        ...m,
        [k]: r.alreadySigned ? "Already signed" : r.email === "sent" ? "Reminder emailed" : r.email === "no_address" ? "No email address on file" : "Email failed"
      }));
    } catch (e) {
      setDone(m => ({
        ...m,
        [k]: `Not sent: ${e.message}`
      }));
    }
  };
  const task = c.task || null,
    ev = c.evidence || null;
  const files = ev?.files?.length ? ev.files : ev?.fileUrl ? [{
    filename: ev.filename || "Evidence",
    fileUrl: ev.fileUrl
  }] : [];
  const required = g.explanation || firstPara(g.satisfies) || x.row?.help || x.row?.explanation || "";
  const team = v.source === "team" ? c.team : null;
  const earlier = (c.uploads || []).filter(u => u.fileUrl && !files.some(f => f.fileUrl === u.fileUrl)).slice(0, 10);
  const PSTATE = {
    current: ["✓ Current", "#237804"],
    "due-soon": ["Renewal due", "#AD6800"],
    "needs-expiry": ["Check needed", "#AD6800"],
    awaiting: ["Requested", "#AD6800"],
    chasing: ["Renewal requested", "#AD6800"],
    missing: ["No copy", "#A8071A"],
    expired: ["Expired", "#A8071A"]
  };
  return <div style={box}>
      {openSop && <InspectorSopPopup sop={openSop} onClose={() => setOpenSop(null)} />}
      <ComplianceNextDue cell={c} rowId={x.row?.rowId} dbName={x.branch?.dbName} token={token} readOnly={readOnly} onChanged={onChanged} />
      <TrainingResults cell={c} />
      {required && <><div style={{
        ...h,
        marginTop: 0
      }}>What’s required</div><div>{required}</div></>}
      {readOnly && <section aria-label="How it is achieved" style={{
      background: "#F0F7FF",
      border: "1px solid #BAD7FF",
      borderRadius: 6,
      padding: "8px 10px",
      marginTop: 10
    }}>
        <div style={{
        ...h,
        marginTop: 0
      }}>How it is achieved</div>
        <div>{inspectorMethodology(x.row, {
          ...v,
          c
        }, g)}</div>
      </section>}
      {(c.inspectorNote || x.row?.inspectorNote) && <><div style={h}>Note for the inspector</div><div style={{
        fontStyle: "italic"
      }}>{c.inspectorNote || x.row?.inspectorNote}</div></>}

      {x.type === "unset" && <><div style={h}>Shortcoming</div><div style={bad}>Nothing has been chosen for how this is evidenced at {x.branch.pharmacyName}.</div>
        <div>Options: {sourcesOf(g).map(k => (SOURCE_WORDS[k] || k).split(" — ")[0]).join(" · ")}. Use <b>Open</b> to choose.</div></>}
      {x.type === "nobody" && <><div style={h}>Shortcoming</div><div style={bad}>The item is set to a {v.source === "upload" ? "document request" : "task"}, but nobody at {x.branch.pharmacyName} has been chosen to receive it — so nothing has been sent.</div><div>Use <b>Open</b> to pick a person.</div></>}
      {x.type === "na" && <><div style={h}>Not applicable</div><div>Marked not applicable at {x.branch.pharmacyName}{c.updatedAt ? ` (${fmtDate(c.updatedAt)})` : ""}.</div></>}

      {/* SOPs: the document, and everyone it's assigned to — signed and not */}
      {v.source === "sop" && <><div style={h}>The SOP{sopTitles.length > 1 ? "s" : ""} and who has read & signed</div>
        {!sopTitles.length ? <div style={bad}>No SOP has been linked to this item yet.</div> : err ? <div style={bad}>Couldn’t load the SOP records: {err}</div> : !sops ? <div>Loading…</div> : sops.map(sp => {
        const signed = sp.people.filter(p => p.status === "signed").length;
        return <div key={sp.title} style={{
          marginBottom: 10
        }}>
            <div style={{
            fontWeight: 700
          }}><button title="Open SOP contents and review details" onClick={() => setOpenSop(sp)}>{sp.title}</button>{sp.version ? ` v${sp.version}` : ""}
              {sp.held && <span style={{
              fontWeight: 400,
              color: "#5D5D5D"
            }}>{sp.issuedAt ? ` · issued ${fmtDate(sp.issuedAt)}` : ""}{sp.expiryDate ? ` · review by ${fmtDate(sp.expiryDate)}` : ""}</span>}</div>
            {!sp.held ? <div style={bad}>Not held at this branch.</div> : <>
              <div>{sp.fileUrl ? <a href={sp.fileUrl} target="_blank" rel="noreferrer" data-filename={`${sp.title}${sp.version ? ` v${sp.version}` : ""}`}>📄 View the SOP document</a> : <span style={{
                color: "#8C8C8C"
              }}>No document file on the SOP record.</span>}
                {sp.expiryDate && new Date(sp.expiryDate) < new Date() && <span style={bad}> · past its review date</span>}
                {sp.versionNotes && <span style={{
                color: "#5D5D5D"
              }}> · “{sp.versionNotes}”</span>}</div>
              <div style={signed === sp.people.length && sp.people.length ? good : signed ? {
              fontWeight: 600
            } : bad}>{signed} of {sp.people.length} staff have read & signed it</div>
              {sp.people.length > 0 && <table style={tbl}><thead><tr><th style={tcell}>Staff member</th><th style={tcell}>Assigned</th><th style={tcell}>Due</th><th style={tcell}>Read & signed</th><th style={tcell} /></tr></thead>
                <tbody>{sortInspectorStaff(sp.people).map(p => {
                  const k = `${sp.title}|${p.staffId}`;
                  return <tr key={k}>
                  <td style={tcell}>{p.name}{p.left ? <span style={{
                        color: "#8C8C8C"
                      }}> (no longer active)</span> : ""}</td>
                  <td style={tcell}>{fmtDate(p.assignedAt)}</td><td style={tcell}>{fmtDate(p.dueDate)}</td>
                  <td style={tcell}>{p.status === "signed" ? <>{st("signed")} {fmtDate(p.signedAt)}{p.signatureUrl && <> · <a href={p.signatureUrl} target="_blank" rel="noreferrer" data-filename={`Signature — ${p.name}`}>signature</a></>}</> : st(p.status)}</td>
                  <td style={{
                      ...tcell,
                      textAlign: "right"
                    }}>{p.status === "signed" ? null : done[k] ? <span style={{
                        fontWeight: 600,
                        color: /Not|fail|No email/i.test(done[k]) ? "#A8071A" : "#237804"
                      }}>{done[k]}</span> : x.demo ? <span style={{
                        color: "#8C8C8C"
                      }}>demo data</span> : !readOnly && p.hasEmail && !p.left ? <button onClick={() => sopRemind(sp.title, p)}>✉ Remind</button> : !p.hasEmail ? <span style={{
                        color: "#8C8C8C"
                      }}>no email</span> : null}</td></tr>;
                })}</tbody></table>}
            </>}
          </div>;
      })}</>}

      {x.type === "mhra" && <><div style={h}>Everyone this alert was sent to at {x.branch.pharmacyName}</div>
        {x.mhraRow?.title && <div>{x.mhraRow.title}{x.mhraRow.alertId ? ` (${x.mhraRow.alertId})` : ""}</div>}
        {err ? <div style={bad}>Couldn’t load: {err}</div> : !mh ? <div>Loading…</div> : <table style={tbl}><thead><tr><th style={tcell}>Staff member</th><th style={tcell}>Sent</th><th style={tcell}>Read</th></tr></thead>
          <tbody>{mh.map((p, i) => <tr key={i}><td style={tcell}>{p.staffName}</td><td style={tcell}>{fmtDate(p.createdAt)}</td>
            <td style={tcell}>{p.acknowledged ? <>{st("read")} {fmtDate(p.completedAt)}</> : st(x.mhraRow?.overdue && p.staffId === x.staffId ? "overdue" : "pending")}</td></tr>)}</tbody></table>}</>}
      {x.row?.elementId === "sup-mhra" && <MhraAlertsHeld token={token} dbName={x.branch.dbName} h={h} tbl={tbl} tcell={tcell} st={st} />}

      {/* professional registrations: every registrant, with their copy */}
      {x.person && x.person.status === "needs-expiry" && <div style={{
      ...bad,
      marginTop: 8
    }}>{x.person.name}’s copy needs checking by head office (the automatic read couldn’t confirm it) — use Open.</div>}
      {team && <><div style={h}>Registrations held — every {GUIDANCE[x.row?.elementId]?.teamKind === "technician" ? "pharmacy technician" : "pharmacist"} at the branch</div>
        {!team.total ? <div style={bad}>Nobody at the branch has the chosen role.</div> : <table style={tbl}><thead><tr><th style={tcell}>Name</th><th style={tcell}>State</th><th style={tcell}>Registration no.</th><th style={tcell}>Expires</th><th style={tcell}>Copy</th></tr></thead>
          <tbody>{(team.people || []).map(p => {
            const [lab, col] = PSTATE[p.status] || [p.status, "#5D5D5D"];
            return <tr key={p.staffId} style={x.person?.staffId === p.staffId ? {
              background: "#FFFBE6"
            } : undefined}>
            <td style={tcell}>{p.name}{p.roleName ? <span style={{
                  color: "#8C8C8C"
                }}> · {p.roleName}</span> : ""}</td>
            <td style={{
                ...tcell,
                color: col,
                fontWeight: 700
              }}>{lab}{p.lastRejection?.length ? <div style={{
                  fontWeight: 400
                }}>Not accepted: {p.lastRejection.join("; ")}</div> : ""}</td>
            <td style={tcell}>{p.evidence?.registrationNumber || "—"}</td><td style={tcell}>{fmtDate(p.expiresAt)}</td>
            <td style={tcell}>{p.evidence?.fileUrl ? <a href={p.evidence.fileUrl} target="_blank" rel="noreferrer" data-filename={`professional registration — ${p.name}`}>view{p.evidence.at ? ` (${fmtDate(p.evidence.at)})` : ""}</a> : p.task?.status === "open" ? <span style={{
                  color: "#AD6800"
                }}>requested {fmtDate(p.task.sentAt)}</span> : "—"}</td>
          </tr>;
          })}</tbody></table>}</>}

      {/* Requests and evidence (task, upload, link, Pharmace review) */}
      {!team && x.row && CHASE_SOURCES.has(v.source) && <>
        {v.source === "link" && <><div style={h}>Held in another system</div><div>{c.linkLabel || "—"}{c.linkUrl && <> · <a href={c.linkUrl} target="_blank" rel="noreferrer" data-no-viewer="1">open ↗</a></>}</div></>}
        {(task || ["awaiting", "missing", "expired", "due", "office"].includes(x.type)) && <><div style={h}>Request</div>
        {task ? <div>{reqWord(task)} {task.status === "open" ? "open" : task.status} — sent {fmtDate(task.sentAt)} to <b>{task.recipientName || c.recipientName || "—"}</b>{task.dueDate ? `, due ${fmtDate(task.dueDate)}` : ""}{task.completedAt ? ` · completed ${fmtDate(task.completedAt)}${task.completedVia === "upload" ? " (through the upload link)" : task.completedVia === "task" ? " (in the staff app)" : ""}` : ""}
          {task.dueDate && task.status === "open" && new Date(task.dueDate) < new Date() && <span style={bad}> · overdue {daysBetween(task.dueDate, new Date())} day{daysBetween(task.dueDate, new Date()) === 1 ? "" : "s"}</span>}
          {task.email && <div style={task.email.status === "sent" ? undefined : bad}>First email: {EMAIL_TEXT[task.email.status] || task.email.status}{task.email.address ? ` (${task.email.address})` : ""}{task.email.error ? ` — ${task.email.error}` : ""}</div>}
          {(task.remindedAt || task.lastReminderAt) && <div>Reminders: {[task.remindedAt && `automatic ${fmtDate(task.remindedAt)}${task.reminderEmail && task.reminderEmail !== "sent" ? ` (${EMAIL_TEXT[task.reminderEmail] || task.reminderEmail})` : ""}`, task.lastReminderAt && `manual ${fmtDate(task.lastReminderAt)}`].filter(Boolean).join(" · ")}</div>}
          {task.status === "rejected" && <div style={bad}>Last response was completed without the photo / document, so it was returned.</div>}</div> : <div style={c.recipientName ? undefined : bad}>{c.recipientName ? `Nothing open — ${c.recipientName} receives the next request.` : "No request has been sent."}</div>}</>}
        {(v.source !== "pharmace" || ev?.at) && <><div style={h}>Evidence on file</div>
        {ev?.at ? <div>{fmtDate(ev.at)}{ev.by ? ` by ${ev.by}` : ""}{ev.via ? ` (${ev.via === "task" ? "staff app" : ev.via === "manual" ? "recorded by head office" : "uploaded"})` : ""}{c.expiresAt ? ` · ${new Date(c.expiresAt) < new Date() ? "expired" : "valid until"} ${fmtDate(c.expiresAt)}` : c.status === "current" ? " · no expiry" : ""}
          <FileList files={files} />
          {!files.length && <div style={{
            color: "#8C8C8C"
          }}>{ev.via === "task" ? "Confirmed in the staff app — no file needed for this item." : "No file attached."}</div>}
          {ev.note && <div style={{
            color: "#5D5D5D"
          }}>“{ev.note}”</div>}
          {ev.analysis?.rowsRead != null && <div>Spreadsheet read: {ev.analysis.rowsRead} rows{ev.analysis.periodFrom ? `, ${fmtDate(ev.analysis.periodFrom)} to ${fmtDate(ev.analysis.periodTo)}` : ""}.</div>}</div> : v.source === "link" ? <div style={{
          color: "#5D5D5D"
        }}>The evidence is held in the other system above.</div> : <div style={bad}>None received yet.</div>}
        {earlier.length > 0 && <div style={{
          marginTop: 4
        }}><span style={{
            color: "#5D5D5D"
          }}>Earlier uploads: </span>{earlier.map((u, i) => <a key={i} href={u.fileUrl} target="_blank" rel="noreferrer" data-filename={u.filename || "Upload"} style={{
            marginRight: 10
          }}>{fmtDate(u.at)} {u.filename || "file"}</a>)}</div>}
        </>}
        {x.type === "office" && <div style={bad}>Waiting on head office: enter the expiry date shown on the evidence (use Open).</div>}</>}

      {/* Pharmace data */}
      {v.source === "pharmace" && <><div style={h}>What the Pharmace data shows</div><div>{v.line1}{v.line2 ? ` — ${v.line2}` : ""}</div>
        {c.evidence?.analysis?.problems?.length > 0 && <div style={bad}>{c.evidence.analysis.problems.slice(0, 6).join("; ")}</div>}
        {onShowData && x.row && <div style={{
        marginTop: 4
      }}><button onClick={() => onShowData(x.row, x.branch)}>Show the data behind this</button></div>}</>}
      {x.type === "data" && v.source !== "pharmace" && !team && <><div style={h}>What the data shows</div><div>{v.line1}{v.line2 ? ` — ${v.line2}` : ""}</div>
        {onShowData && x.row && <div style={{
        marginTop: 4
      }}><button onClick={() => onShowData(x.row, x.branch)}>Show the data behind this</button></div>}</>}

      {/* Website: every check, passed and failed, with the wording found */}
      {v.source === "web" && (() => {
      const keys = WEB_ROW_CHECKS[x.row?.elementId] || [];
      const checks = (site?.webReview?.checks || []).filter(k => keys.includes(k.key));
      return <><div style={h}>Website review</div><div>{site?.websiteUrl ? <a href={site.websiteUrl} target="_blank" rel="noreferrer" data-no-viewer="1">{site.websiteUrl}</a> : <span style={bad}>No website address — add it under Sites.</span>}{site?.webReview?.reviewedAt ? ` · reviewed ${fmtDate(site.webReview.reviewedAt)}` : ""}</div>
          {checks.map(k => <div key={k.key} style={k.found ? undefined : bad}>{k.found ? <span style={{
            color: "#389E0D",
            fontWeight: 700
          }}>✓ </span> : "✗ "}{k.label || WEB_SHORT[k.key] || k.key}{k.quote ? ` — “${k.quote}”` : ""}{k.url ? <span style={{
            color: "#8C8C8C"
          }}> ({k.url})</span> : ""}</div>)}</>;
    })()}

      {(c.history || []).length > 0 && <><div style={h}>History</div>
        <div>{(c.history || []).slice(-8).reverse().map((e, i) => <div key={i}>{fmtDate(e.at)} — {EVENT_TEXT[e.event] || e.event}{e.to ? ` → ${e.to}` : e.by ? ` by ${e.by}` : ""}{e.email && e.email !== "sent" ? <span style={bad}> ({EMAIL_TEXT[e.email] || e.email})</span> : ""}{e.reason && e.event === "rejected-check" ? `: ${e.reason}` : ""}</div>)}</div></>}
    </div>;
}
export function OutstandingDialog({
  items,
  allItems = null,
  token,
  branches,
  displayName,
  masked,
  sites = {},
  onOpenCell = null,
  onShowData = null,
  onClose,
  onChanged,
  initialBranch = "all",
  initialType = "all",
  initialSort = "branch",
  initialQuery = "",
  initialMode = "outstanding"
}) {
  // "outstanding": only what needs chasing. "all": every item, with what holds
  // it — the inspector view (needs allItems).
  const [mode, setMode] = useState(allItems ? initialMode : "outstanding");
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleOpen = k => setExpanded(s0 => {
    const n = new Set(s0);
    if (n.has(k)) n.delete(k);else n.add(k);
    return n;
  });
  const [mhra, setMhra] = useState(null); // MHRA alerts cascaded but not read
  const [mhraErr, setMhraErr] = useState("");
  const [sortBy, setSortBy] = useState(initialSort);
  const [branchF, setBranchF] = useState(initialBranch);
  const [typeF, setTypeF] = useState(initialType);
  const [q, setQ] = useState(initialQuery);
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState({}); // key -> result text
  const [msg, setMsg] = useState("");
  useEffect(() => {
    let off = false;
    post(token, `${API_BASE}/api/headoffice/mhra/unread`, {
      dbNames: branches.map(b => b.dbName)
    }).then(r => {
      if (!off) setMhra(r.rows || []);
    }).catch(e => {
      if (!off) {
        setMhra([]);
        setMhraErr(e.message);
      }
    });
    return () => {
      off = true;
    };
  }, [token, branches]);
  const all = useMemo(() => {
    const byDb = new Map(branches.map(b => [b.dbName, b]));
    const m = (mhra || []).filter(r => byDb.has(r.dbName)).map((r, i) => ({
      key: `mhra|${r.dbName}|${r.mrhaAlertId}|${r.staffId}|${i}`,
      type: "mhra",
      status: r.overdue ? "expired" : "awaiting",
      branch: byDb.get(r.dbName),
      groupName: "Medicines & Services",
      itemName: "Safe & effective supply",
      element: `MHRA alert: ${r.title}${r.alertId ? ` (${r.alertId})` : ""}`,
      mhraRow: r,
      staffName: r.staffName,
      staffId: r.staffId,
      since: r.sentAt,
      what: `Alert sent as a staff-app task ${fmtDate(r.sentAt)}, not read yet${overdueText(r.dueDate)}`,
      remind: r.staffId ? {
        kind: "mhra",
        dbName: r.dbName,
        mrhaAlertId: r.mrhaAlertId,
        staffId: r.staffId
      } : null
    }));
    return [...(mode === "all" && allItems ? allItems : items), ...m];
  }, [items, allItems, mode, mhra, branches]);
  const bName = x => displayName(x.branch.pharmacyName, masked);
  const shownList = useMemo(() => {
    const nm = x => displayName(x.branch.pharmacyName, masked);
    const needle = q.trim().toLowerCase();
    const list = all.filter(x => (branchF === "all" || x.branch.dbName === branchF) && (typeF === "all" || x.type === typeF) && (!needle || `${x.branch.pharmacyName} ${x.groupName} ${x.itemName} ${x.element} ${x.staffName} ${x.what}`.toLowerCase().includes(needle)));
    const k = {
      branch: x => [nm(x), x.groupName, x.itemName, x.element, x.staffName],
      item: x => [x.groupName, x.itemName, x.element, nm(x), x.staffName],
      staff: x => [x.staffName ? 0 : 1, x.staffName, nm(x), x.groupName, x.element]
    }[sortBy];
    return list.slice().sort((a, b) => {
      const ka = k(a),
        kb = k(b);
      for (let i = 0; i < ka.length; i++) {
        const c = typeof ka[i] === "number" ? ka[i] - kb[i] : String(ka[i] || "").localeCompare(String(kb[i] || ""));
        if (c) return c;
      }
      return 0;
    });
  }, [all, branchF, typeF, q, sortBy, masked, displayName]);
  const typeCounts = useMemo(() => {
    const n = {};
    for (const x of all) if (branchF === "all" || x.branch.dbName === branchF) n[x.type] = (n[x.type] || 0) + 1;
    return n;
  }, [all, branchF]);
  const remindable = shownList.filter(x => x.remind && !x.demo);
  const toggle = k => setPicked(s => {
    const n = new Set(s);
    if (n.has(k)) n.delete(k);else n.add(k);
    return n;
  });
  const allPicked = remindable.length > 0 && remindable.every(x => picked.has(x.key));
  const remindOne = async x => {
    const r = x.remind;
    const res = r.kind === "mhra" ? await post(token, `${API_BASE}/api/headoffice/mhra/remind`, {
      dbName: r.dbName,
      mrhaAlertId: r.mrhaAlertId,
      staffId: r.staffId
    }) : await post(token, `${GRID}/cell/remind`, {
      rowId: r.rowId,
      dbName: r.dbName,
      staffId: r.staffId,
      label: r.label
    });
    const e = res.email;
    return res.alreadyRead ? "Already read" : e === "no_address" ? `${res.action === "sent" ? "Request sent in the staff app" : "Not emailed"} — no email address on file` : e === "failed" ? "Email failed" : res.action === "sent" ? "Request sent again (task + email)" : "Reminder emailed";
  };
  const send = async list => {
    if (!list.length) return;
    if (list.length > 1 && !(await appConfirm(`Email ${list.length} reminder${list.length === 1 ? "" : "s"}?`))) return;
    setBusy(true);
    setMsg("");
    let ok = 0;
    for (const x of list) {
      try {
        const t = await remindOne(x);
        setSent(m => ({
          ...m,
          [x.key]: t
        }));
        if (!/fail|Not emailed/i.test(t)) ok++;
      } catch (e) {
        setSent(m => ({
          ...m,
          [x.key]: `Not sent: ${e.message}`
        }));
      }
    }
    setBusy(false);
    setPicked(new Set());
    setMsg(`${ok} of ${list.length} reminder${list.length === 1 ? "" : "s"} sent.`);
    if (list.some(x => x.remind?.kind === "cell")) onChanged?.();
  };
  const exportXlsx = () => {
    const rows = shownList.map(x => ({
      Branch: bName(x),
      Area: x.groupName,
      Item: x.itemName,
      Element: x.element,
      "Staff member": x.staffName || "",
      Type: OUT_TYPE_LABEL[x.type] || x.type,
      "What's wrong": x.what,
      "Request sent": x.since ? fmtDate(x.since) : "",
      "Last reminder": x.lastReminderAt ? fmtDate(x.lastReminderAt) : "",
      ...(sent[x.key] ? {
        "This session": sent[x.key]
      } : {})
    }));
    const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{
      Branch: "Nothing outstanding"
    }]);
    ws["!cols"] = [{
      wch: 24
    }, {
      wch: 22
    }, {
      wch: 28
    }, {
      wch: 46
    }, {
      wch: 22
    }, {
      wch: 20
    }, {
      wch: 70
    }, {
      wch: 13
    }, {
      wch: 13
    }, {
      wch: 30
    }];
    ws["!autofilter"] = {
      ref: ws["!ref"]
    };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Outstanding");
    const out = XLSX.write(wb, {
      type: "array",
      bookType: "xlsx"
    });
    saveAs(new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    }), `Compliance_outstanding_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const th = {
    position: "sticky",
    top: 0,
    background: "#262626",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    padding: "7px 8px",
    textAlign: "left",
    zIndex: 1,
    whiteSpace: "nowrap"
  };
  const sortTh = (k, label) => <th style={{
    ...th,
    cursor: "pointer"
  }} onClick={() => setSortBy(k)} title={`Sort by ${label.toLowerCase()}`}>{label}{sortBy === k ? " ▾" : ""}</th>;
  const td = {
    padding: "6px 8px",
    fontSize: 12,
    verticalAlign: "top",
    borderTop: "1px solid #F0F0F0"
  };
  const tone = x => x.type === "ok" ? STATUS_STYLE.current : x.type === "na" ? STATUS_STYLE.na : ["expired", "missing", "nobody"].includes(x.type) ? STATUS_STYLE.expired : x.type === "unset" ? {
    bg: "#F5F5F5",
    fg: "#5D5D5D"
  } : x.type === "data" ? STATUS_STYLE["pharmace-no"] : STATUS_STYLE["due-soon"];
  return <div role="dialog" aria-modal="true" aria-label={mode === "all" ? "Evidence" : "Outstanding"} onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    zIndex: 10000,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "3vh 12px",
    overflow: "auto"
  }}>
      <div style={{
      background: "#fff",
      borderRadius: 10,
      width: "100%",
      maxWidth: 1400,
      boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
      overflow: "hidden"
    }}>
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 16px",
        borderBottom: "1px solid #F0F0F0"
      }}>
          <div style={{
          flex: 1
        }}>
            <div style={{
            fontWeight: 800,
            fontSize: 16
          }}>{mode === "all" ? "🔍 Evidence — every item" : "⚑ Outstanding"} <span style={{
              fontWeight: 400,
              color: "#5D5D5D",
              fontSize: 13
            }}>· {shownList.length}{shownList.length !== all.length ? ` of ${all.length}` : ""} {mode === "all" ? "lines" : "to chase"}{mhra == null ? " · checking MHRA alerts…" : ""}</span></div>
            <div style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>{mode === "all" ? "Everything held for every item at every branch — open ▸ Details to see the documents, who has read & signed them, the requests and the history. Ready for an inspector." : "Items not set up, requests not answered, documents not uploaded, evidence expired or due, Pharmace data not up to date, and MHRA alerts staff haven’t read."}</div>
          </div>
          {allItems && <div role="radiogroup" aria-label="What to list" style={{
          display: "flex",
          border: "1px solid #D9D9D9",
          borderRadius: 6,
          overflow: "hidden"
        }}>
            {[["outstanding", "⚑ Outstanding"], ["all", "🔍 Everything (inspector view)"]].map(([k, l]) => <button key={k} role="radio" aria-checked={mode === k} onClick={() => {
            setMode(k);
            setTypeF("all");
            setPicked(new Set());
            setExpanded(new Set());
            if (k === "all") setSortBy("item");
          }} style={{
            border: "none",
            borderRadius: 0,
            padding: "6px 10px",
            background: mode === k ? "#0958D9" : "#fff",
            color: mode === k ? "#fff" : "#262626",
            fontWeight: mode === k ? 700 : 400
          }}>{l}</button>)}
          </div>}
          <button onClick={exportXlsx} disabled={!shownList.length}>⬇ Export to Excel</button>
          <button onClick={() => send(remindable.filter(x => picked.has(x.key)))} disabled={busy || !picked.size} style={{
          fontWeight: 700,
          background: picked.size ? "#0958D9" : undefined,
          color: picked.size ? "#fff" : undefined,
          border: "none",
          borderRadius: 6,
          padding: "6px 12px"
        }}>
            ✉ Email reminders{picked.size ? ` (${picked.size})` : ""}</button>
          <button onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        alignItems: "center",
        padding: "10px 16px",
        background: "#FAFAFA",
        borderBottom: "1px solid #F0F0F0",
        fontSize: 13
      }}>
          <label>Branch <select value={branchF} onChange={e => setBranchF(e.target.value)}>
            <option value="all">All branches</option>
            {branches.map(b => <option key={b.dbName} value={b.dbName}>{displayName(b.pharmacyName, masked)}</option>)}</select></label>
          <label>What <select value={typeF} onChange={e => setTypeF(e.target.value)}>
            <option value="all">Everything</option>
            {OUT_TYPES.filter(([k]) => typeCounts[k]).map(([k, l]) => <option key={k} value={k}>{l} ({typeCounts[k]})</option>)}</select></label>
          <label>Sort by <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="branch">Branch</option><option value="item">Item</option><option value="staff">Staff member</option></select></label>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" style={{
          minWidth: 200
        }} aria-label="Search the outstanding list" />
          {mhraErr && <span style={{
          color: "#A8071A"
        }}>MHRA alerts couldn’t be checked: {mhraErr}</span>}
          {msg && <span style={{
          color: "#237804",
          fontWeight: 600
        }}>{msg}</span>}
        </div>
        <div style={{
        maxHeight: "72vh",
        overflow: "auto"
      }}>
          {!shownList.length ? <div style={{
          padding: 30,
          textAlign: "center",
          color: "#389E0D",
          fontWeight: 600
        }}>{all.length ? "Nothing matches the filters." : "Nothing outstanding — everything is set up and up to date."}</div> : <table style={{
          borderCollapse: "collapse",
          width: "100%"
        }}>
            <thead><tr>
              <th style={{
                ...th,
                width: 28
              }}><input type="checkbox" checked={allPicked} disabled={!remindable.length} title="Tick every line that can be emailed a reminder" onChange={e => setPicked(e.target.checked ? new Set(remindable.map(x => x.key)) : new Set())} /></th>
              {sortTh("branch", "Branch")}{sortTh("item", "Item")}{sortTh("staff", "Staff member")}<th style={th}>What’s wrong</th><th style={th} />
            </tr></thead>
            <tbody>{shownList.map(x => {
              const t = tone(x);
              const can = !!x.remind && !x.demo;
              const isOpen = expanded.has(x.key);
              return <React.Fragment key={x.key}>
              <tr style={isOpen ? {
                  background: "#F0F7FF"
                } : undefined}>
                <td style={td}>{can && <input type="checkbox" checked={picked.has(x.key)} onChange={() => toggle(x.key)} aria-label="Pick for a reminder" />}</td>
                <td style={{
                    ...td,
                    fontWeight: 600,
                    whiteSpace: "nowrap"
                  }}>{bName(x)}</td>
                <td style={td}><div style={{
                      color: "#5D5D5D",
                      fontSize: 11
                    }}>{x.groupName} › {x.itemName}</div><div>{x.element}</div></td>
                <td style={{
                    ...td,
                    whiteSpace: "nowrap"
                  }}>{x.staffName || <span style={{
                      color: "#8C8C8C"
                    }}>—</span>}</td>
                <td style={{
                    ...td,
                    cursor: "pointer"
                  }} onClick={() => toggleOpen(x.key)} title={isOpen ? "Hide the details" : "Show the details: what's held and what's missing"}>
                  <span style={{
                      display: "inline-block",
                      background: t.bg,
                      color: t.fg,
                      borderRadius: 8,
                      padding: "1px 8px",
                      fontSize: 11,
                      fontWeight: 700,
                      marginRight: 6
                    }}>{OUT_TYPE_LABEL[x.type] || x.type}</span>{x.what}
                  <span style={{
                      marginLeft: 6,
                      color: "#0958D9",
                      fontSize: 11,
                      fontWeight: 600,
                      whiteSpace: "nowrap"
                    }}>{isOpen ? "▾ Hide details" : "▸ Details"}</span>
                  {x.lastReminderAt && <div style={{
                      fontSize: 11,
                      color: "#5D5D5D"
                    }}>Last reminder {fmtDate(x.lastReminderAt)}</div>}
                  {sent[x.key] && <div style={{
                      fontSize: 11,
                      color: /Not|fail/i.test(sent[x.key]) ? "#A8071A" : "#237804",
                      fontWeight: 600
                    }}>{sent[x.key]}</div>}</td>
                <td style={{
                    ...td,
                    whiteSpace: "nowrap",
                    textAlign: "right"
                  }}>
                  {can && <button disabled={busy} onClick={() => send([x])} title={x.remind.resend ? "Nothing is open for this, so the request (task + email) is sent again" : "Email a reminder about the open request"}>
                    ✉ {x.remind.resend ? "Send request" : "Remind"}</button>}
                  {x.demo && <span style={{
                      fontSize: 11,
                      color: "#8C8C8C"
                    }}>demo data</span>}
                  {x.row && onOpenCell && <button onClick={() => onOpenCell(x.row, x.branch)} style={{
                      marginLeft: 6
                    }} title="Open this item at this branch">Open</button>}
                </td>
              </tr>
              {isOpen && <tr><td />
                <td colSpan={5} style={{
                    padding: "4px 8px 12px",
                    background: "#F0F7FF",
                    borderTop: "none"
                  }}>
                  <OutstandingDetail x={x} token={token} site={sites[x.branch.dbName]} onShowData={onShowData} onChanged={onChanged} />
                </td></tr>}
              </React.Fragment>;
            })}</tbody>
          </table>}
        </div>
      </div>
    </div>;
}

// -----------------------------------------------------------------------------
// SETUP ASSISTANT (2026-10). Steps the manager through every item (each part
// of a multi-part item is its own step), in the order of the compliance page:
//   top    — how many are set out of the total, a progress bar, back / next,
//            "next not set", and a jump list;
//   left   — a navigator-style note: what the item is about, what counts,
//            what to do, and a button that opens the item's settings (the
//            usual Settings dialog, which opens on top of the assistant);
//   right  — a preview of the task / email staff will receive, built from the
//            item's saved wording (or the built-in wording until it is saved).
// An item counts as "set" once its settings are saved, or any branch has a
// source chosen for it. Nothing is saved or sent by the assistant itself.
// -----------------------------------------------------------------------------
// Items where staff provide a document or photo get BOTH a task in the staff
// app and an email with a secure upload link (compliance_working_grid.js
// isDual). Whichever they do first closes the other. Analysed spreadsheet
// logs stay email-link only; confirm-only tasks stay a task with an email.
const DUAL_NOTE = "This has also been sent to you as a task in the Pharmace staff app. Complete either one — not both: if you upload with the link first, the task is removed from the staff app; if you complete the task first, this link stops working.";
// Mirrors dualEmailContent() in compliance_working_grid.js (first request).
function dualEmailPreview({
  label,
  todo,
  explanation,
  link,
  word = "document"
}) {
  const topic = `Compliance check: ${label}`;
  const due = new Date(Date.now() + 7 * DAY).toLocaleDateString("en-GB");
  return [`Subject: ${topic}`, "Evidence requested by head office", `Head office needs this for your pharmacy by ${due}.`, `WHAT IS REQUIRED:\n${todo}`, `WHY: ${explanation || "This is part of the pharmacy’s GPhC / statutory compliance record."}`, `TWO WAYS TO SEND IT — DO ONE OR THE OTHER:\n1. Upload it with the secure link below — it works on a phone or a computer, and you can attach several files at once.\n   [ Upload the ${word} ]  ← secure link\n2. Or open the task “${topic}” in the Pharmace staff app, attach the ${word} and mark it complete.`, DUAL_NOTE, `Please don’t reply to this email with the ${word} — use the link or the staff app.`, link ? `More information: ${link}` : ""].filter(Boolean).join("\n\n");
}
function docUploadEmailPreview(label, ask, explanation = "") {
  return dualEmailPreview({
    label,
    todo: ask || "Attach the current document(s) for this item (or a clear photo of each page).",
    explanation,
    word: "document"
  });
}
// A document upload request duplicates the (now combined) task, so where an
// item offers both, only the task is listed.
const dedupeDocs = (list, guide) => guide.upload === "documents" && list.includes("task") ? list.filter(x => x !== "upload") : list;
const SOURCE_WORDS = {
  pharmace: "Pharmace data — read automatically where the branch records it in Pharmace",
  task: "Task in the staff app + email to a named team member — for a photo or document the email carries an upload link, and either way counts",
  upload: "Email with a secure upload link — for documents a staff-app task goes too, and either way counts",
  sop: "Link the SOP held in Pharmace’s SOP module",
  link: "Held in another system — a note of where (a link is optional)",
  na: "Not applicable at that branch",
  team: "A task to every registrant at the branch",
  web: "Automatic review of the branch’s website"
};
function sourcesOf(guide) {
  const others = dedupeDocs(guide.sources || [guide.web ? "web" : null, guide.sop ? "sop" : null, guide.task === false ? null : "task", guide.upload ? "upload" : null, "link", "na"].filter(Boolean), guide);
  return guide.pharmace === false ? others : ["pharmace", ...others.filter(x => x !== "pharmace")];
}
function assistantPreviews(row, cfg, guide) {
  const label = row.label.replace(/^\d · /, "");
  const explanation = cfg.explanation || guide.explanation || row.help || "";
  const srcs = sourcesOf(guide);
  const out = [];
  if (guide.team) {
    const kind = guide.teamKind === "technician" ? "pharmacy technician" : "pharmacist";
    out.push({
      btn: "Preview task",
      title: `Task to every ${kind}`,
      text: [`Compliance check: ${row.label}`, `WHY THIS MATTERS:\n${explanation || `Every ${kind} working at the pharmacy must be on the GPhC register.`}`, `WHAT TO DO:\n${cfg.instructions ? `${cfg.instructions}\n` : ""}Attach a copy of your current GPhC ${kind} registration — a screenshot or PDF of your registration confirmation from myGPhC, or of your entry on the GPhC online register — showing your name, registration number and the expiry / renewal date. Then mark the task complete.`].join("\n\n")
    });
  } else if (srcs.includes("task")) {
    const evidenceType = guide.evidenceOnly || cfg.evidenceType || guide.defaultEvidence || "photo";
    const instructions = (guide.posterGuardian ? "" : niGuidanceValue(row, "instructions", cfg.instructions)) || guide.instructions || "";
    out.push({
      src: "task",
      btn: "Preview task",
      title: guide.guardian ? `Task to the ${guide.roleTitle} (staff app)` : "Task in the staff app",
      text: taskPreview({
        label,
        explanation,
        instructions,
        link: cfg.link || "",
        evidenceType
      })
    });
    out.push({
      src: "task",
      btn: "Preview email",
      title: guide.posterGuardian ? "Email to the Guardian (always sent, with the poster PDF and the upload link)" : evidenceType === "confirm" ? "Email that goes with the task" : "Email that goes with the task (with the upload link)",
      text: guide.posterGuardian ? guardianEmailPreview() : emailPreview({
        label,
        explanation,
        instructions,
        link: cfg.link || "",
        evidenceType
      })
    });
  }
  if (guide.upload && srcs.includes("upload")) {
    const ask = cfg.uploadAsk || guide.uploadAsk || "";
    const docsBoth = !LOG_TEXT[guide.upload] && guide.upload === "documents";
    out.push({
      src: "upload",
      btn: docsBoth ? "Preview email" : "Preview upload email",
      title: docsBoth ? "Email with the upload link (a staff-app task goes too)" : "Upload request email",
      text: LOG_TEXT[guide.upload] ? nmEmailPreview(label, ask, guide.upload) : docsBoth ? docUploadEmailPreview(label, ask, explanation) : uploadEmailPreview(label, ask)
    });
  }
  // The suggested source's messages first.
  const def = guide.fallbackDefault;
  out.sort((a, b) => (b.src === def) - (a.src === def));
  if (!out.length) out.push({
    title: "Nothing is sent",
    text: srcs.includes("pharmace") || guide.auto ? "This item is read automatically — no task or email goes to staff. Branches without the data can be noted as held elsewhere, or marked not applicable." : "No task or email goes to staff for this item: it is set from a link, a note of where the record is held, or marked not applicable."
  });
  return out;
}
function SetupAssistant({
  groups,
  rowCfg,
  cells,
  customs = [],
  branches,
  dspBranches,
  cellView,
  displayName,
  masked,
  onOpenRow,
  onClose
}) {
  // Every step, in compliance-page order; parts replace their parent item.
  const steps = useMemo(() => {
    const out = [];
    for (const g of groups) for (const it of g.items || []) {
      const base = {
        groupId: g.id,
        itemId: it.id,
        groupName: g.name,
        itemName: it.name,
        dsp: !!g.dsp
      };
      for (const el of it.elements || []) {
        const parts = PARTS_OF[el.id];
        if (parts) parts.forEach((p, i) => out.push({
          ...base,
          rowId: `el:${p.id}`,
          elementId: p.id,
          label: p.label,
          part: true,
          partNo: i + 1,
          partOf: parts.length,
          parentLabel: el.label,
          requirement: el.requirement,
          help: GUIDANCE[p.id]?.explanation || firstPara(GUIDANCE[p.id]?.satisfies),
          link: ""
        }));else out.push({
          ...base,
          rowId: `el:${el.id}`,
          elementId: el.id,
          label: el.label,
          requirement: el.requirement,
          help: el.help,
          link: el.link || ""
        });
      }
      for (const c of customs.filter(x => x.groupId === g.id && x.itemId === it.id)) out.push({
        ...base,
        ...c
      });
    }
    return out;
  }, [groups, customs]);
  const branchesFor = row => (row.dsp ? dspBranches : branches).filter(b => rowApplicability(row, b.jurisdiction).applicable);
  const isSet = row => GUIDANCE[row.elementId]?.auto || rowCfg.has(row.rowId) || branchesFor(row).some(b => cells.get(`${row.rowId}|${b.dbName}`)?.source);
  const setCount = steps.filter(isSet).length;
  const firstUnset = Math.max(0, steps.findIndex(r => !isSet(r)));
  const [i, setI] = useState(() => {
    try {
      const k = Number(localStorage.getItem("cwgAssistStep"));
      if (k >= 0 && k < steps.length) return k;
    } catch {/* storage unavailable */}
    return firstUnset;
  });
  useEffect(() => {
    try {
      localStorage.setItem("cwgAssistStep", String(i));
    } catch {/* storage unavailable */}
  }, [i]);
  const [prevTab, setPrevTab] = useState(-1);
  useEffect(() => setPrevTab(-1), [i]);
  const row = steps[Math.min(i, steps.length - 1)];
  if (!row) return null;
  const saved = rowCfg.get(row.rowId) || {};
  const guide = GUIDANCE[row.elementId] || {};
  const cfg = {
    ...row,
    ...saved,
    label: row.label,
    help: row.help,
    explanation: niGuidanceValue(row, "explanation", saved.explanation) || guide.explanation || row.help || "",
    link: saved.link || row.link || ""
  };
  const done = isSet(row);
  const pct = steps.length ? Math.round(setCount / steps.length * 100) : 0;
  const nextUnset = () => {
    for (let k = 1; k <= steps.length; k++) {
      const j = (i + k) % steps.length;
      if (!isSet(steps[j])) {
        setI(j);
        return;
      }
    }
  };
  const srcs = sourcesOf(guide);
  const def = guide.fallbackDefault;
  const previews = assistantPreviews(row, cfg, guide);
  const bs = branchesFor(row);
  // Per branch: the source in use and what it currently holds.
  const atBranch = bs.map(b => {
    const c = cells.get(`${row.rowId}|${b.dbName}`) || null;
    const v = cellView(row, b);
    const src = c?.source || defaultSourceOf(saved) || (String(v.status).startsWith("pharmace") ? "pharmace" : GUIDANCE[row.elementId]?.auto ? "auto" : "");
    return {
      b,
      c,
      v,
      src
    };
  });
  const statusCounts = {};
  for (const x of atBranch) statusCounts[x.v.status] = (statusCounts[x.v.status] || 0) + 1;
  const usedSrcs = new Set(atBranch.map(x => x.src).filter(Boolean));
  // Right panel tabs: what the data source holds, then example messages.
  // Previews open on demand (prevTab = index of the open preview, -1 none).
  const msgPreviews = previews.filter(p => p.btn);
  const openPv = prevTab >= 0 ? msgPreviews[prevTab] : null;
  const what = cfg.explanation || guide.explanation || row.help || firstPara(guide.satisfies) || "";
  const refs = (saved.references && saved.references.length ? saved.references : guide.references) || [];
  const links = [...refs, ...(row.link && !refs.some(r => r.url === row.link) ? [{
    label: "Guidance",
    url: row.link
  }] : [])];
  const rowSrc = defaultSourceOf(saved);
  const btn = {
    border: "1px solid #D9D9D9",
    background: "#fff",
    color: "#262626",
    borderRadius: 6,
    padding: "6px 12px",
    cursor: "pointer",
    fontSize: 13
  };
  const card = {
    border: "1px solid #F0F0F0",
    borderRadius: 10,
    padding: "12px 14px",
    background: "#fff"
  };
  const h = {
    fontWeight: 700,
    fontSize: 13,
    color: "#003A8C",
    margin: "0 0 6px"
  };
  const fileLinks = c => {
    const ev = c?.evidence;
    if (!ev) return [];
    const fs = ev.files?.length ? ev.files : ev.fileUrl ? [{
      filename: ev.filename || "Evidence",
      fileUrl: ev.fileUrl
    }] : [];
    return [...fs, ...(ev.reports || [])].filter(f => f && f.fileUrl);
  };
  const branchContent = ({
    b,
    c,
    v,
    src
  }) => {
    if (src === "link") return <>{c?.linkLabel || "Held elsewhere"}{c?.linkUrl && <> · <a href={c.linkUrl} target="_blank" rel="noreferrer">open ↗</a></>}</>;
    if (src === "na") return <>Not applicable{rowCfg.get(row.rowId)?.naReason ? `: ${rowCfg.get(row.rowId).naReason}` : ""}</>;
    if (src === "sop") return <>{(c?.sop?.matched || []).map(m => `“${m.title}”${m.signed != null ? ` ${m.signed}/${m.assigned} signed` : ""}`).join(", ") || v.line2 || "No SOP linked yet"}</>;
    const files = fileLinks(c);
    return <>{v.line2 || v.line1}{c?.evidence?.analysis?.status && <span> · log {c.evidence.analysis.status}</span>}
      {files.length > 0 && <div>{files.slice(0, 4).map(f => <a key={f.fileUrl} href={f.fileUrl} target="_blank" rel="noreferrer" style={{
          marginRight: 8
        }}>📎 {f.filename || "file"}</a>)}</div>}</>;
  };
  return <div role="dialog" aria-modal="true" aria-label="Setup assistant" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    zIndex: 10000,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "3vh 12px",
    overflow: "auto"
  }}>
      <div style={{
      background: "#FAFAFA",
      borderRadius: 12,
      width: "100%",
      maxWidth: 1400,
      boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
      overflow: "hidden"
    }}>
        {/* ---- Navigation ---- */}
        <div style={{
        background: "#262626",
        color: "#fff",
        padding: "12px 16px"
      }}>
          <div style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 10
        }}>
            <div style={{
            fontWeight: 800,
            fontSize: 16
          }}>🧭 Setup assistant</div>
            <div style={{
            fontSize: 13,
            opacity: 0.85
          }}>Item {i + 1} of {steps.length} · <b>{setCount}</b> of {steps.length} set ({pct}%)</div>
            <span style={{
            flex: 1
          }} />
            <select value={i} onChange={e => setI(Number(e.target.value))} aria-label="Jump to an item" style={{
            maxWidth: 340,
            fontSize: 12
          }}>
              {groups.map(g => {
              const opts = steps.map((r, k) => ({
                r,
                k
              })).filter(x => x.r.groupId === g.id);
              return opts.length ? <optgroup key={g.id} label={g.name}>{opts.map(({
                  r,
                  k
                }) => <option key={r.rowId} value={k}>{isSet(r) ? "✓ " : "○ "}{r.part ? `${r.parentLabel} — ${r.label}` : r.label}</option>)}</optgroup> : null;
            })}
            </select>
            <button onClick={onClose} aria-label="Close" style={{
            ...btn,
            background: "transparent",
            color: "#fff",
            borderColor: "#5D5D5D"
          }}>✕</button>
          </div>
          <div style={{
          marginTop: 10,
          height: 10,
          background: "#434343",
          borderRadius: 6,
          overflow: "hidden"
        }} aria-label={`${pct}% set`}>
            <div style={{
            width: `${pct}%`,
            height: "100%",
            background: "#52C41A",
            transition: "width .3s"
          }} />
          </div>
          <div style={{
          display: "flex",
          gap: 8,
          marginTop: 10,
          flexWrap: "wrap"
        }}>
            <button style={btn} disabled={i === 0} onClick={() => setI(i - 1)}>← Back</button>
            <button style={{
            ...btn,
            fontWeight: 700,
            background: done ? "#389E0D" : "#fff",
            color: done ? "#fff" : "#262626",
            borderColor: done ? "#389E0D" : "#D9D9D9"
          }} disabled={i >= steps.length - 1} onClick={() => setI(i + 1)}>Next →</button>
            <button style={btn} disabled={setCount === steps.length} onClick={nextUnset}>Next not set ⇥</button>
          </div>
        </div>

        <div style={{
        padding: "12px 16px 0"
      }}>
          <div style={{
          fontSize: 12,
          color: "#5D5D5D"
        }}>{row.groupName} › {row.itemName}{row.part ? ` › ${row.parentLabel}` : ""}</div>
          <h3 style={{
          margin: "2px 0 0",
          fontSize: 18
        }}>{row.part ? `Part ${row.partNo} of ${row.partOf}: ` : ""}{row.label.replace(/^\d · /, "")}
            <span style={{
            marginLeft: 8,
            fontSize: 11,
            verticalAlign: "middle",
            borderRadius: 10,
            padding: "2px 8px",
            background: done ? "#F6FFED" : "#FFF7E6",
            color: done ? "#389E0D" : "#AD4E00"
          }}>{done ? "✓ Set" : "Not set yet"}</span></h3>
        </div>

        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: 14,
        padding: 16
      }}>
          {/* ---- 1. Explanation ---- */}
          <div style={card}>
            <div style={panelTitle}>1 · What it is</div>
            <div style={h}>What this is about</div>
            <div style={{
            fontSize: 13,
            lineHeight: 1.5
          }}>{what || "A compliance item for every branch."}</div>
            {row.part && <div style={{
            fontSize: 12,
            color: "#5D5D5D",
            marginTop: 6
          }}>“{row.parentLabel}” is in place at a branch only when all {row.partOf} parts are.</div>}
            {guide.satisfies && <>
              <div style={{
              ...h,
              marginTop: 12
            }}>What counts as evidence</div>
              <div style={{
              whiteSpace: "pre-wrap",
              fontSize: 12.5,
              lineHeight: 1.5,
              maxHeight: 280,
              overflow: "auto"
            }}>{guide.satisfies}</div></>}
            {links.length > 0 && <>
              <div style={{
              ...h,
              marginTop: 12
            }}>Guidance and regulations</div>
              <ul style={{
              margin: 0,
              paddingLeft: 18,
              fontSize: 12.5,
              lineHeight: 1.6
            }}>
                {links.map(r => <li key={r.url + r.label}><a href={r.url} target="_blank" rel="noreferrer">{r.label}</a></li>)}
              </ul></>}
          </div>

          {/* ---- 2. Choices and current setting ---- */}
          <div style={card}>
            <div style={panelTitle}>2 · Where the evidence comes from</div>
            <div style={h}>Available</div>
            {guide.auto ? <div style={{
            fontSize: 13
          }}>{guide.onlyReason || "This comes automatically — nothing to choose."}</div> : <ul style={{
            margin: 0,
            paddingLeft: 0,
            listStyle: "none",
            fontSize: 13,
            lineHeight: 1.45
          }}>
                {srcs.map(x => {
              const on = rowSrc ? rowSrc === x || rowSrc === "pharmace" && saved.fallbackSource === x : usedSrcs.has(x);
              return <li key={x} style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: "5px 8px",
                marginBottom: 4,
                borderRadius: 6,
                background: on ? "#E6F7FF" : "transparent",
                border: `1px solid ${on ? "#91D5FF" : "#F5F5F5"}`
              }}>
                    <span style={{
                  width: 16
                }}>{on ? "●" : "○"}</span>
                    <span style={{
                  flex: 1
                }}>{SOURCE_WORDS[x] || x}{x === "pharmace" && guide.pharmaceName ? ` (${guide.pharmaceName})` : ""}
                      {x === def && <span style={{
                    fontSize: 11,
                    color: "#389E0D",
                    marginLeft: 6
                  }}>suggested</span>}
                      {rowSrc === "pharmace" && saved.fallbackSource === x && <span style={{
                    fontSize: 11,
                    color: "#0958D9",
                    marginLeft: 6
                  }}>for branches without Pharmace data</span>}</span>
                  </li>;
            })}
              </ul>}
            {guide.onlyReason && !guide.auto && <div style={{
            fontSize: 12,
            color: "#5D5D5D",
            marginTop: 6,
            fontStyle: "italic"
          }}>{guide.onlyReason}</div>}
            <div style={{
            ...h,
            marginTop: 12
          }}>Currently set to</div>
            <div style={{
            fontSize: 13,
            lineHeight: 1.5
          }}>{guide.auto ? "Automatic" : rowCfg.has(row.rowId) ? (() => {
              const short = x => (SOURCE_WORDS[x] || x || "").split(" — ")[0];
              const fb = rowSrc === "pharmace" ? saved.fallbackSource : null;
              const msg = [rowSrc, fb].some(x => ["task", "upload", "team"].includes(x));
              return <>{rowSrc === "pharmace" ? <><b>Pharmace{guide.pharmaceName ? ` (${guide.pharmaceName})` : ""}</b> where the branch has the data{saved.fallbackTask === false ? "; nothing for the others" : fb ? <>; otherwise <b>{short(fb)}</b></> : ""}</> : <b>{short(rowSrc) || "Chosen per branch"}</b>}
                {msg && <div style={{
                  fontSize: 12,
                  color: "#5D5D5D"
                }}>{validitySummary(cfg)}</div>}</>;
            })() : <span style={{
              color: "#AD4E00"
            }}>Not set up yet</span>}</div>
            <div style={{
            fontSize: 12,
            color: "#5D5D5D",
            marginTop: 8
          }}>
              {Object.entries(statusCounts).map(([st, n]) => <span key={st} style={{
              display: "inline-block",
              margin: "2px 4px 2px 0",
              background: (STATUS_STYLE[st] || STATUS_STYLE.unset).bg,
              color: (STATUS_STYLE[st] || STATUS_STYLE.unset).fg,
              border: "1px solid #F0F0F0",
              borderRadius: 10,
              padding: "1px 8px"
            }}>{(STATUS_STYLE[st] || STATUS_STYLE.unset).label} · {n}</span>)}
              {!bs.length && <span>No branches{row.dsp ? " — none is marked as distance selling (Sites)" : ""}</span>}
            </div>
            <div style={{
            maxHeight: 220,
            overflow: "auto",
            marginTop: 6
          }}>
              {atBranch.map(({
              b,
              v
            }) => <div key={b.dbName} style={{
              display: "flex",
              gap: 8,
              fontSize: 12,
              padding: "3px 0",
              borderTop: "1px solid #F5F5F5"
            }}>
                <span style={{
                flex: "0 0 40%",
                fontWeight: 600
              }}>{displayName(b.pharmacyName, masked)}</span>
                <span style={{
                flex: 1,
                color: (STATUS_STYLE[v.status] || STATUS_STYLE.unset).fg
              }}>{v.line1}</span></div>)}
            </div>
            {!guide.auto && <button onClick={() => onOpenRow(cfg)} style={{
            marginTop: 12,
            fontWeight: 700,
            background: "#0E8CFF",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "8px 14px",
            cursor: "pointer"
          }}>
              {done ? "Change settings" : "Set up this item"}</button>}
            <div style={{
            fontSize: 12,
            color: "#5D5D5D",
            marginTop: 8,
            lineHeight: 1.5
          }}>
              {guide.auto ? "Nothing to do — press Next." : done ? "✓ Set. Press Next to move on, or change the settings." : guide.sop ? "Choose the Pharmace SOP(s) that cover this item and save — that covers every branch that holds them, so no cascade is needed. Then press Next." : "Set the source, wording and renewal, then Save & cascade — you see every branch before anything is sent. Then press Next."}</div>
          </div>

          {/* ---- 3. What the source holds; previews on demand ---- */}
          <div style={card}>
            <div style={panelTitle}>3 · What the data source holds</div>
            {msgPreviews.length > 0 && <div style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            marginBottom: 8
          }}>
              {msgPreviews.map((p, k) => <button key={p.btn} onClick={() => setPrevTab(k)} title={p.title} style={{
              ...btn,
              fontSize: 12,
              padding: "4px 10px"
            }}>👁 {p.btn}</button>)}
            </div>}
            {!msgPreviews.length && <div style={{
            ...help,
            marginBottom: 8
          }}>{previews[0]?.text}</div>}
            <div style={{
            maxHeight: "55vh",
            overflow: "auto"
          }}>
              {!atBranch.length && <div style={{
              fontSize: 13,
              color: "#5D5D5D"
            }}>No branches.</div>}
              {atBranch.map(x => <div key={x.b.dbName} style={{
              fontSize: 12.5,
              lineHeight: 1.45,
              padding: "6px 0",
              borderTop: "1px solid #F5F5F5"
            }}>
                <div style={{
                fontWeight: 700
              }}>{displayName(x.b.pharmacyName, masked)} <span style={{
                  fontWeight: 400,
                  color: "#5D5D5D"
                }}>· {x.src ? (SOURCE_WORDS[x.src] || x.src).split(" — ")[0] : "not set"}</span></div>
                <div style={{
                color: "#434343"
              }}>{x.src ? branchContent(x) : "Nothing set up at this branch yet."}</div>
              </div>)}
            </div>
          </div>
        </div>
        {openPv && <div role="dialog" aria-modal="true" aria-label={openPv.title} onClick={e => {
        if (e.target === e.currentTarget) setPrevTab(-1);
      }} style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.5)",
        zIndex: 10150,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "6vh 12px",
        overflow: "auto"
      }}>
          <div style={{
          background: "#fff",
          borderRadius: 10,
          width: "100%",
          maxWidth: 640,
          boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
          overflow: "hidden"
        }}>
            <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#262626",
            color: "#fff",
            padding: "8px 12px"
          }}>
              <div style={{
              fontWeight: 700,
              fontSize: 14,
              flex: 1
            }}>{openPv.title} — {row.label.replace(/^\d · /, "")}</div>
              {msgPreviews.length > 1 && msgPreviews.map((p, k) => k !== prevTab && <button key={p.btn} onClick={() => setPrevTab(k)} style={{
              ...btn,
              fontSize: 12,
              padding: "3px 10px",
              background: "transparent",
              color: "#fff",
              borderColor: "#5D5D5D"
            }}>{p.btn}</button>)}
              <button onClick={() => setPrevTab(-1)} aria-label="Close preview" style={{
              ...btn,
              background: "transparent",
              color: "#fff",
              borderColor: "#5D5D5D"
            }}>✕</button>
            </div>
            <pre style={{
            whiteSpace: "pre-wrap",
            fontFamily: "inherit",
            fontSize: 13,
            lineHeight: 1.55,
            margin: 0,
            padding: 16,
            maxHeight: "70vh",
            overflow: "auto"
          }}>{openPv.text}</pre>
            <div style={{
            ...help,
            padding: "0 16px 12px"
          }}>{rowCfg.has(row.rowId) ? "Built from this item’s saved wording." : "Built from the suggested wording — you can change it in the settings before anything is sent."} Dates are examples.</div>
          </div>
        </div>}
      </div>
    </div>;
}
// -----------------------------------------------------------------------------
// EVIDENCE VIEWER (2026-10). Clicking any evidence file on the compliance
// pages opens it in a popup instead of downloading it: images and PDFs
// directly, spreadsheets as tables (read with the xlsx library), text as text,
// and Word / PowerPoint through Microsoft's online viewer. A click handler on
// the page (evidenceClickCapture) catches every evidence link — files under
// /cg/ and anything with a document or image extension — so each section
// doesn't need its own wiring. Guidance links (web pages) open as before.
// -----------------------------------------------------------------------------
const EVIDENCE_EXT = /\.(jpe?g|png|gif|webp|bmp|pdf|xlsx?|xlsm|csv|docx?|pptx?|txt)(?:[?#]|$)/i;
const extOf = u => {
  const m = /\.([a-z0-9]{2,5})(?:[?#]|$)/i.exec(String(u || ""));
  return m ? m[1].toLowerCase() : "";
};
// File stores that staff-app photos and uploads live on (no extension needed).
const FILE_HOSTS = /^https?:\/\/[^/]*(amazonaws\.com|firebasestorage\.googleapis\.com|storage\.googleapis\.com|blob\.core\.windows\.net|cloudinary\.com)\//i;
export const isEvidenceUrl = u => {
  const x = String(u || "");
  return /\/cg\/[A-Za-z0-9_-]{22}(?:\.[a-z0-9]{1,10})?(?:[?#]|$)/.test(x) || EVIDENCE_EXT.test(x.split(/[?#]/)[0]) || FILE_HOSTS.test(x);
};
export function openEvidence(url, name) {
  try {
    window.dispatchEvent(new CustomEvent("pharmace:view-evidence", {
      detail: {
        url,
        name
      }
    }));
  } catch {
    window.open(url, "_blank", "noopener");
  }
}
export function evidenceClickCapture(e) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
  const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
  if (!a || a.hasAttribute("data-no-viewer")) return;
  if (!isEvidenceUrl(a.getAttribute("href"))) return;
  e.preventDefault();
  e.stopPropagation();
  const name = (a.getAttribute("data-filename") || a.getAttribute("title") || a.textContent || "").replace(/[^\p{L}\p{N}\s._()'’,-]/gu, "").trim();
  openEvidence(a.href, name);
}
export function EvidenceViewerHost() {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    const on = ev => setDoc(ev.detail || null);
    window.addEventListener("pharmace:view-evidence", on);
    return () => window.removeEventListener("pharmace:view-evidence", on);
  }, []);
  return doc ? <EvidenceViewer url={doc.url} name={doc.name} onClose={() => setDoc(null)} /> : null;
}
function EvidenceViewer({
  url,
  name,
  onClose
}) {
  const ext = extOf(url) || extOf(name);
  const isImg = /^(jpe?g|png|gif|webp|bmp)$/.test(ext);
  const isOffice = /^(docx?|pptx?)$/.test(ext);
  const [state, setState] = useState({
    loading: !isImg && !isOffice
  });
  useEffect(() => {
    const k = e => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  useEffect(() => {
    if (isImg || isOffice) return undefined;
    let off = false,
      objectUrl = null;
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const type = (res.headers.get("content-type") || "").toLowerCase();
        const kind = /^(xlsx?|xlsm|csv)$/.test(ext) || /spreadsheet|excel|csv/.test(type) ? "sheet" : ext === "pdf" || type.includes("pdf") ? "pdf" : /^image\//.test(type) ? "image" : ext === "txt" || /^text\//.test(type) ? "text" : "other";
        if (kind === "sheet") {
          const buf = await res.arrayBuffer();
          const XLSX = await import("xlsx");
          const wb = ext === "csv" || type.includes("csv") ? XLSX.read(new TextDecoder().decode(buf), {
            type: "string",
            raw: true
          }) : XLSX.read(buf, {
            type: "array",
            cellDates: true
          });
          const sheets = wb.SheetNames.slice(0, 12).map(n => ({
            name: n,
            rows: XLSX.utils.sheet_to_json(wb.Sheets[n], {
              header: 1,
              blankrows: false,
              defval: "",
              raw: false,
              dateNF: "dd/mm/yyyy"
            }).slice(0, 500)
          }));
          if (!off) setState({
            kind,
            sheets,
            sheet: 0
          });
        } else if (kind === "text") {
          const text = await res.text();
          if (!off) setState({
            kind,
            text: text.slice(0, 200000)
          });
        } else {
          const blob = await res.blob();
          const typed = kind === "pdf" && !blob.type.includes("pdf") ? new Blob([blob], {
            type: "application/pdf"
          }) : blob;
          objectUrl = URL.createObjectURL(typed);
          if (!off) setState({
            kind,
            objectUrl
          });
        }
      } catch (e) {
        // Couldn't read it from here (e.g. the file host doesn't allow it):
        // show it directly where the browser can, else offer to open it.
        if (!off) setState({
          kind: ext === "pdf" ? "pdf" : "other",
          objectUrl: ext === "pdf" ? url : null,
          error: e.message
        });
      }
    })();
    return () => {
      off = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, ext, isImg, isOffice]);
  const title = name || decodeURIComponent(String(url).split(/[?#]/)[0].split("/").pop() || "Evidence");
  const bar = {
    border: "1px solid #5D5D5D",
    background: "transparent",
    color: "#fff",
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: 12,
    textDecoration: "none",
    cursor: "pointer"
  };
  const st = state;
  return <div role="dialog" aria-modal="true" aria-label={`Evidence — ${title}`} onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.8)",
    zIndex: 10200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16
  }}>
      <div style={{
      background: "#fff",
      borderRadius: 10,
      width: "min(1200px, 100%)",
      height: "min(90vh, 100%)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: "0 10px 40px rgba(0,0,0,0.5)"
    }}>
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "#262626",
        color: "#fff",
        padding: "8px 12px"
      }}>
          <div style={{
          fontWeight: 700,
          fontSize: 14,
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }} title={title}>📎 {title}</div>
          <a href={url} target="_blank" rel="noreferrer" data-no-viewer style={bar}>Open in new tab ↗</a>
          <a href={url} download data-no-viewer style={bar}>Download</a>
          <button onClick={onClose} aria-label="Close" style={{
          ...bar,
          fontSize: 14
        }}>✕</button>
        </div>
        <div style={{
        flex: 1,
        overflow: "auto",
        background: "#F5F5F5",
        display: "flex",
        alignItems: isImg || st.kind === "image" ? "center" : "stretch",
        justifyContent: "center"
      }}>
          {isImg ? <img src={url} alt={title} style={{
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain"
        }} /> : isOffice ? <iframe title={title} src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`} style={{
          border: 0,
          width: "100%",
          height: "100%",
          background: "#fff"
        }} /> : st.loading ? <div style={{
          margin: "auto",
          color: "#5D5D5D"
        }}>Loading…</div> : st.kind === "image" ? <img src={st.objectUrl} alt={title} style={{
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain"
        }} /> : st.kind === "pdf" && st.objectUrl ? <iframe title={title} src={st.objectUrl} style={{
          border: 0,
          width: "100%",
          height: "100%",
          background: "#fff"
        }} /> : st.kind === "text" ? <pre style={{
          margin: 0,
          padding: 16,
          whiteSpace: "pre-wrap",
          fontSize: 12,
          background: "#fff",
          width: "100%"
        }}>{st.text}</pre> : st.kind === "sheet" ? <div style={{
          width: "100%",
          background: "#fff"
        }}>
                {st.sheets.length > 1 && <div style={{
            display: "flex",
            gap: 4,
            padding: 8,
            flexWrap: "wrap",
            borderBottom: "1px solid #F0F0F0"
          }}>
                  {st.sheets.map((sh, k) => <button key={sh.name} onClick={() => setState({
              ...st,
              sheet: k
            })} style={{
              fontSize: 12,
              padding: "3px 10px",
              borderRadius: 6,
              border: "1px solid #D9D9D9",
              background: k === st.sheet ? "#262626" : "#fff",
              color: k === st.sheet ? "#fff" : "#262626"
            }}>{sh.name}</button>)}
                </div>}
                <table style={{
            borderCollapse: "collapse",
            fontSize: 12
          }}><tbody>
                  {(st.sheets[st.sheet]?.rows || []).map((r, ri) => <tr key={ri} style={{
                background: ri === 0 ? "#FAFAFA" : ri % 2 ? "#fff" : "#FCFCFC",
                fontWeight: ri === 0 ? 700 : 400
              }}>
                    <td style={{
                  color: "#8C8C8C",
                  padding: "3px 6px",
                  borderRight: "1px solid #F0F0F0",
                  textAlign: "right"
                }}>{ri + 1}</td>
                    {r.map((v, ci) => <td key={ci} style={{
                  padding: "3px 8px",
                  border: "1px solid #EDF2F7",
                  whiteSpace: "nowrap",
                  maxWidth: 320,
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>{String(v)}</td>)}</tr>)}
                </tbody></table>
                {(st.sheets[st.sheet]?.rows || []).length >= 500 && <div style={{
            padding: 8,
            fontSize: 12,
            color: "#5D5D5D"
          }}>Showing the first 500 rows — download the file to see the rest.</div>}
              </div> : <div style={{
          margin: "auto",
          textAlign: "center",
          color: "#5D5D5D",
          fontSize: 14,
          padding: 20
        }}>
                This file can’t be shown here{st.error ? ` (${st.error})` : ""}.<br />Use <b>Open in new tab</b> or <b>Download</b> above.</div>}
        </div>
      </div>
    </div>;
}

// -----------------------------------------------------------------------------
function Modal({
  title,
  onClose,
  children,
  width = 560
}) {
  return <div role="dialog" aria-modal="true" aria-label={title} onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    zIndex: 10050,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "6vh 12px",
    overflow: "auto"
  }}>
      <div style={{
      background: "#fff",
      borderRadius: 10,
      width: "100%",
      maxWidth: width,
      padding: 18,
      boxShadow: "0 10px 40px rgba(0,0,0,0.25)"
    }}>
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        marginBottom: 10
      }}>
          <h3 style={{
          margin: 0,
          fontSize: 16
        }}>{title}</h3>
          <button onClick={onClose} aria-label="Close">✕</button>
        </div>
        {children}
      </div>
    </div>;
}
const field = {
  display: "block",
  margin: "10px 0 4px",
  fontSize: 12,
  fontWeight: 600,
  color: "#434343"
};
const box = {
  border: "1px solid #F0F0F0",
  borderRadius: 8,
  padding: 10,
  marginTop: 12
};

// What the staff member's task will say. Mirrors taskText() in
// compliance_working_grid.js (first-time wording).
function defaultAsk(evidenceType) {
  return evidenceType === "document" ? "Attach the current document (or a clear photo of it) when you complete the task." : evidenceType === "confirm" ? "Check this is in place, then complete the task to confirm." : "Take a clear photo showing this and attach it when you complete the task.";
}
// Mirrors todoText() in compliance_working_grid.js: a confirm-only task with its own wording needs no generic line.
const todoText = (instructions, evidenceType) => instructions && evidenceType === "confirm" ? instructions : `${instructions ? `${instructions}\n` : ""}${defaultAsk(evidenceType)}`;
// The email each Freedom to Speak Up Guardian receives (mirrors the backend's guardianEmailContent).
const GUARDIAN_READING = ["National Guardian’s Office — about the Guardian role: https://nationalguardian.org.uk/", "NHS England — Freedom to Speak Up development and support: https://www.england.nhs.uk/ourwork/freedom-to-speak-up/freedom-to-speak-up-development-and-support/", "NHS England — Freedom to Speak Up in primary care (guidance): https://www.england.nhs.uk/wp-content/uploads/2016/11/whistleblowing-guidance.pdf", "Community Pharmacy England — Raising concerns (whistleblowing): https://cpe.org.uk/quality-and-regulations/clinical-governance/raising-concerns-whistle-blowing/", "GPhC — Whistleblowing: https://www.pharmacyregulation.org/patients-and-public/reporting-concerns/how-we-deal-concerns/guidance-pharmacy-professionals-facing-concern/whistleblowing", "Protect — free, confidential whistleblowing advice: https://protect-advice.org.uk/"];
export function guardianEmailPreview(pharmacyName) {
  const branch = pharmacyName || "your pharmacy";
  return [`Subject: Thank you for being our Freedom to Speak Up Guardian — ${branch}`, "Dear …,", `Thank you for agreeing to be the Freedom to Speak Up Guardian for ${branch}. It is an important role, and we are grateful to you for taking it on.`, "WHAT THE ROLE INVOLVES:\n• Be someone colleagues can go to — in confidence — when they are worried about something at work: patient safety, the way people are treated, or anything else that doesn’t feel right.\n• Stay independent of the person’s line management: you are there to listen, not to judge or to investigate yourself.\n• Help them decide what to do next and point them to the right route — inside the company, or outside it (the GPhC, NHS England, Protect).\n• Escalate concerns that aren’t being dealt with, and make sure nobody is treated badly for speaking up.\n• Keep a simple, confidential note of what was raised and what happened, so the company can learn from it.", `THE POSTER:\nThe speak-up poster for ${branch} is attached as a PDF, with your name and contact details as the Guardian. Please print it — unless the branch already has its own speak-up poster showing you as the Guardian.`, "WHAT TO DO:\n1. Print the attached poster (or use the branch’s own, if it already has one naming you).\n2. Put it up in the staff room, where everyone — including locums — will see it.\n3. Open the Pharmace staff app, find the Whistleblowing / speak-up task you have been sent, take a photo of the staff room with the poster visible, attach it and mark the task complete.", "If you can’t use the staff app, you can upload the photo with the secure link below instead. Do one or the other — whichever you do first closes the other.\n[Upload the photo — secure link]", "Completing the task confirms you accept the role. If you’d rather not, please tell your line manager. You will be asked to confirm again each year.", `BACKGROUND READING:\n${GUARDIAN_READING.map(x => `• ${x}`).join("\n")}`, "📎 Attached: Speak-up-poster-<branch>.pdf"].join("\n\n");
}
function taskPreview({
  label,
  explanation,
  instructions,
  link,
  evidenceType,
  linkUrl,
  validUntil,
  review
}) {
  const issue = validUntil ? review ? `This is due to be reviewed by ${fmtDate(validUntil)}.` : `The current evidence for this runs out on ${fmtDate(validUntil)}. Please renew it before then.` : "Head office needs evidence that this is in place at your pharmacy.";
  return [`Compliance check: ${label}`, `WHAT: ${label}`, `WHY THIS MATTERS:\n${explanation || "This is part of the pharmacy’s GPhC / statutory compliance record."}`, `THE ISSUE:\n${issue}`, `WHAT TO DO:\n${todoText(instructions, evidenceType)}`, linkUrl ? `WHERE TO LOOK: ${linkUrl}` : "", link ? `MORE INFORMATION: ${link}` : ""].filter(Boolean).join("\n\n");
}
function TaskWording({
  w,
  setW,
  label,
  evidenceType,
  linkUrl,
  validUntil,
  review,
  note
}) {
  return <div style={box}>
    <div style={{
      fontWeight: 600,
      fontSize: 13
    }}>Task wording for staff {note && <span style={{
        fontWeight: 400,
        color: "#5D5D5D"
      }}>· {note}</span>}</div>
    <label style={field}>Why this matters — what the issue is about</label>
    <textarea value={w.explanation} onChange={e => setW({
      ...w,
      explanation: e.target.value
    })} rows={3} style={{
      width: "100%",
      fontFamily: "inherit"
    }} placeholder="e.g. Drug alerts and recalls must be acted on promptly so affected stock is removed and patients are protected." />
    <label style={field}>What needs to be done</label>
    <textarea value={w.instructions} onChange={e => setW({
      ...w,
      instructions: e.target.value
    })} rows={3} style={{
      width: "100%",
      fontFamily: "inherit"
    }} placeholder="e.g. Check every MHRA alert received this month has an outcome recorded in Pharmace." />
    <label style={field}>Guidance link (optional)</label>
    <input value={w.link} onChange={e => setW({
      ...w,
      link: e.target.value
    })} placeholder="https://…" style={{
      width: "100%"
    }} />
    <details style={{
      marginTop: 8
    }}>
      <summary style={{
        fontSize: 12,
        cursor: "pointer"
      }}>Preview the task as the staff member will see it</summary>
      <pre style={{
        whiteSpace: "pre-wrap",
        fontFamily: "inherit",
        fontSize: 12,
        background: "#FAFAFA",
        border: "1px solid #F0F0F0",
        borderRadius: 6,
        padding: 8,
        margin: "6px 0 0"
      }}>
        {taskPreview({
          label,
          ...w,
          evidenceType,
          linkUrl,
          validUntil,
          review
        })}</pre>
    </details>
  </div>;
}

// -----------------------------------------------------------------------------
// ROW SETTINGS (2026-10, v4) — three panels, written for a branch manager:
//   left   — what the item is, what satisfies it, references
//   middle — where the evidence comes from (Pharmace data detected / task /
//            link / not applicable), each explained; Pharmace is greyed out
//            when Pharmace holds no data for this item at any branch
//   right  — the settings for the chosen source
// "Save & cascade" saves, then shows a dry run of what every branch will get
// (source, recipient by role, first task + email) before anything is sent.
// -----------------------------------------------------------------------------
const GPHC_STANDARDS = "https://www.pharmacyregulation.org/pharmacies/standards-and-guidance-registered-pharmacies";
// Built-in guidance for items, used until a manager edits it.
const GUIDANCE = {
  "rp-displayed": {
    // Pharmace can't evidence a notice on the wall: always a photo from the branch.
    pharmace: false,
    noPharmaceReason: "Pharmace has no way to record whether the RP notice is on display. Other RP records (sign-in, absences) don’t show this, so the evidence has to be a photo from the branch.",
    satisfies: "A notice is on display where patients and the public can see it, showing the name and professional registration number of the pharmacist who is the Responsible Pharmacist (RP) at that moment, and that they are in charge of the pharmacy. It must be changed whenever the RP changes, including at shift handover.\n\nGood evidence: a clear photo of the notice in place, with the name and registration number readable.",
    references: [{
      label: "GPhC — Responsible pharmacist guidance",
      url: "https://www.pharmacyregulation.org/pharmacies/responsible-pharmacist"
    }, {
      label: "The Medicines (Pharmacies) (Responsible Pharmacist) Regulations 2008",
      url: "https://www.legislation.gov.uk/uksi/2008/2789/contents"
    }, {
      label: "GPhC — Standards for registered pharmacies",
      url: GPHC_STANDARDS
    }]
  }
};
// RP absence: evidenced by the SOP covering it (Pharmace SOP module, or an
// upload) — the same for every branch; nothing to send. Not Pharmace data and
// not a staff-app task.
GUIDANCE["rp-absence"] = {
  pharmace: false,
  task: false,
  sop: true,
  upload: "documents",
  sources: ["sop", "upload"],
  fallbackDefault: "sop",
  forceValidity: true,
  noPharmaceReason: "No Pharmace module records RP absences against the two-hour rule; the evidence is the SOP that sets out the arrangements.",
  noTaskReason: "Not a staff-app task: the evidence is the SOP covering RP absence, linked from Pharmace’s SOP module.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload your current SOP covering Responsible Pharmacist absence — when the RP may be absent (no more than two hours in total during business hours), how they stay contactable and able to return promptly, what staff may and may not do while they are away, and how the absence is recorded — with its signature sheet if you have one.",
  explanation: "The Responsible Pharmacist may be absent from the pharmacy for up to two hours in total during its business hours, but only if arrangements are in place: they stay contactable and able to return promptly (or another pharmacist is available), staff know what can and cannot happen while they are away, and the absence is recorded in the RP record.",
  satisfies: "A current SOP (or section of one) covering RP absence: absence limited to two hours in total during the pharmacy’s business hours; the RP remains contactable and able to return with reasonable promptness, or arranges for another pharmacist to be contactable; what staff may do while the RP is away (no supply of P or POM medicines, no handing out of checked prescriptions unless the SOP allows it); and the times of absence entered in the RP record. Staff have read and signed it, and it is reviewed regularly.\n\nGood evidence: the SOP linked from Pharmace’s SOP module — in date, held at the branch, with staff read & signed. One link covers every branch that holds the SOP.",
  references: [{
    label: "GPhC — Responsible pharmacist guidance",
    url: "https://www.pharmacyregulation.org/pharmacies/responsible-pharmacist"
  }, {
    label: "The Medicines (Pharmacies) (Responsible Pharmacist) Regulations 2008",
    url: "https://www.legislation.gov.uk/uksi/2008/2789/contents"
  }]
};
// GP notified of relevant supplies: Pharmace holds no data for this, so the
// Pharmace option isn't offered; task / held elsewhere / N/A stay as before.
GUIDANCE["sup-gp-notify"] = {
  pharmace: false,
  noPharmaceReason: "Pharmace doesn’t record notifications sent to GPs."
};
GUIDANCE["rp-log"] = {
  // The staff app can't evidence this; where Pharmace has no RP sign-in data
  // the branch uploads the record it keeps, which is analysed automatically.
  task: false,
  noTaskReason: "A staff-app task can’t evidence a whole RP record. Use Pharmace RP sign-in data, or ask the branch to upload the record it keeps.",
  upload: "rp-signin",
  validity: {
    mode: "days",
    days: 30,
    warnDays: 3
  },
  uploadAsk: "Please upload your Responsible Pharmacist record for at least the last 3 months. A spreadsheet is fine: one row per RP session with the pharmacist’s name, registration number, date, time they signed in and time they signed out.",
  satisfies: "A record of which pharmacist was the Responsible Pharmacist, and when, for every period the pharmacy is open: their name, professional registration number, the date and the times they took on and gave up responsibility, plus any absences. It can be electronic or paper, and must be kept for at least 5 years.\n\nGood evidence: Pharmace RP sign-in records showing every trading day covered, or an uploaded spreadsheet of the record the branch keeps.",
  references: [{
    label: "GPhC — Responsible pharmacist guidance",
    url: "https://www.pharmacyregulation.org/pharmacies/responsible-pharmacist"
  }, {
    label: "The Medicines (Pharmacies) (Responsible Pharmacist) Regulations 2008",
    url: "https://www.legislation.gov.uk/uksi/2008/2789/contents"
  }, {
    label: "GPhC — Standards for registered pharmacies",
    url: GPHC_STANDARDS
  }]
};
GUIDANCE["sop-dispensing"] = {
  // Pharmace modules never evidence this; it is evidenced by the SOP itself.
  pharmace: false,
  task: false,
  noTaskReason: "For an SOP, use “Email request to upload documents” instead of a staff-app task — staff can attach several files at once.",
  noPharmaceReason: "No Pharmace module records whether the dispensing SOP covers transposition checks. Link the SOP held in Pharmace’s SOP module, link to an external copy, or ask the branch to upload it.",
  sop: true,
  upload: "documents",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload the current, signed copy of your dispensing SOP(s) — the ones covering the accuracy/clinical check and transposition checks. You can attach several files (for example the SOP and its signature sheet).",
  satisfies: "A current dispensing SOP that includes an accuracy (and clinical) check step, with transposition checks — making sure what is dispensed matches the prescription and is labelled correctly — and capture of near misses. Staff involved in dispensing have read and signed it, and it is reviewed regularly.\n\nGood evidence: the SOP linked from Pharmace’s SOP module (in date, with staff read & signed), or a current signed copy uploaded by the branch.",
  references: [{
    label: "GPhC — Standards for registered pharmacies (Principle 4: services)",
    url: GPHC_STANDARDS
  }, {
    label: "GPhC — Guidance on standard operating procedures",
    url: "https://www.pharmacyregulation.org/standards/guidance"
  }]
};
GUIDANCE["near-miss"] = {
  // Pharmace's near-miss tool where branches use it; elsewhere the branch
  // uploads its own log (analysed automatically), links to it, or it is N/A.
  task: false,
  noTaskReason: "A staff-app task isn’t a suitable way to evidence a near-miss log. Use Pharmace’s near-miss recording, an upload request, a link to where the log is kept, or mark it not applicable.",
  pharmaceName: "near-miss recording",
  upload: "near-miss",
  fallbackDefault: "upload",
  validity: {
    mode: "days",
    days: 31,
    warnDays: 5
  },
  uploadAsk: "Please upload your near-miss log: the spreadsheet where near misses are recorded (date, type of error, drug, who spotted it, action/learning). If your system also produces summary reports (monthly reviews, trend reports, patient-safety reports), add those too.",
  satisfies: "Near misses (dispensing errors caught before they reach the patient) are recorded routinely, reviewed regularly — usually monthly — and the learning is acted on and shared, so the same errors don’t recur.\n\nGood evidence: Pharmace near-miss records in use every month, or the branch’s own near-miss log uploaded regularly, with entries every month, error types recorded and actions/learning written down; plus any monthly review or trend reports.",
  references: [{
    label: "GPhC — Standards for registered pharmacies (Principle 1: risks are managed)",
    url: GPHC_STANDARDS
  }, {
    label: "NHS England — Learn from patient safety events (LFPSE)",
    url: "https://www.england.nhs.uk/patient-safety/learn-from-patient-safety-events-service/"
  }]
};
GUIDANCE["lfpse"] = {
  // Pharmace patient safety records where branches use them; elsewhere the
  // branch uploads its incident log (analysed: harm, referral to LFPSE…).
  task: false,
  noTaskReason: "A staff-app task isn’t a suitable way to evidence patient safety incident records. Use Pharmace, an upload request, a link to where the log is kept, or mark it not applicable.",
  pharmaceName: "patient safety recording",
  upload: "lfpse",
  fallbackDefault: "upload",
  validity: {
    mode: "days",
    days: 31,
    warnDays: 5
  },
  uploadAsk: "Please upload your patient safety incident log: the spreadsheet where incidents are recorded (date, type of incident, medicine, degree of harm, whether it was reported to LFPSE and the reference, action taken). If you have LFPSE submission confirmations or incident reviews, add those too.",
  satisfies: "Patient safety incidents are recorded, the degree of harm is assessed, incidents are reported to the national Learn from Patient Safety Events (LFPSE) service — always for moderate harm or worse — and the learning is acted on.\n\nGood evidence: Pharmace patient safety records, or the branch’s incident log uploaded regularly, showing incidents logged, the degree of harm recorded, LFPSE references for those reported, and actions taken; plus any LFPSE submission confirmations.",
  references: [{
    label: "NHS England — Learn from patient safety events (LFPSE)",
    url: "https://www.england.nhs.uk/patient-safety/learn-from-patient-safety-events-service/"
  }, {
    label: "GPhC — Standards for registered pharmacies (Principle 1: risks are managed)",
    url: GPHC_STANDARDS
  }]
};
// -----------------------------------------------------------------------------
// P-MEDICINE DISPLAY — two parts (see PMED_PARTS). GPhC statement on
// self-selection of pharmacy medicines (20 June 2024, updated Feb 2026):
// "facilitated self-selection" conflicts with its standards unless key
// safeguards are in place — the legal requirement for pharmacist supervision
// still applies, with assurance that arrangements keep patients safe; checked
// at inspection. RPS guidance (July 2025) adds a risk assessment that leaves out
// higher-risk P medicines (misuse-prone, harmful in overdose, theft-prone,
// recent POM-to-P switches). Most pharmacies keep P medicines behind the counter.
// -----------------------------------------------------------------------------
const PMED_REFS = [{
  label: "GPhC — Statement on self-selection of pharmacy medicines (2024, updated 2026)",
  url: "https://www.pharmacyregulation.org/about-us/news-and-updates/statement-self-selection-pharmacy-medicines"
}, {
  label: "GPhC — Standards for registered pharmacies (4.3 medicines stored securely; 4.2 services managed safely)",
  url: GPHC_STANDARDS
}, {
  label: "RPS — position on facilitated self-selection of P medicines (July 2025)",
  url: "https://www.thepharmacist.co.uk/news/rps-changes-stance-and-signals-support-for-facilitated-self-selection-of-p-meds/"
}];
GUIDANCE["pmed--display"] = {
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record how pharmacy medicines are displayed; a photo shows where they are and that the public can’t simply help themselves.",
  onlyReason: "Once a year a named person photographs where P medicines are kept.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take a photo of where pharmacy (P) medicines are kept — behind the counter, or, if the pharmacy uses facilitated self-selection, the area where they are displayed, showing how it is supervised (e.g. next to the counter, in view of the pharmacist).",
  explanation: "Pharmacy (P) medicines may only be sold by or under the supervision of a pharmacist. The GPhC says letting customers select them themselves conflicts with its standards unless safeguards are in place, so the display must keep every sale under the pharmacist’s supervision.",
  satisfies: "Either:\n• P medicines are kept behind the counter (or in locked cases) so a member of staff hands them over and the sale can be supervised — the usual arrangement; or\n• the pharmacy uses facilitated self-selection, with the display next to the counter and in view of the pharmacist, only the P medicines its risk assessment (Part 2) found suitable on display, and every sale still made at the counter under the pharmacist’s supervision.\nHigher-risk P medicines — those liable to misuse (e.g. codeine/opioid painkillers, laxatives, pseudoephedrine, sleep aids, cyclizine), those with a serious risk in overdose, theft-prone or high-value lines, and recent POM-to-P switches — are never on self-selection.\n\nGood evidence: a yearly photo of where P medicines are kept, matching the arrangement described in the risk assessment / SOP.",
  references: PMED_REFS
};
GUIDANCE["pmed--risk"] = {
  pharmace: false,
  task: false,
  sop: true,
  upload: "documents",
  sources: ["sop", "upload"],
  fallbackDefault: "sop",
  forceValidity: true,
  noPharmaceReason: "No Pharmace module records this. Link the SOP held in Pharmace’s SOP module, or ask the branch to upload its risk assessment / SOP.",
  noTaskReason: "For an SOP or risk assessment, use an email request to upload documents instead of a staff-app task.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload your SOP for the sale of pharmacy (P) medicines and, if any P medicines are on facilitated self-selection, your written risk assessment for that display (which products are included and excluded, and how sales are supervised). You can attach several files.",
  explanation: "A written procedure makes sure every P-medicine sale is supervised by the pharmacist and the right questions are asked. If any P medicines are on display for customers to pick up, a risk assessment must show which products are safe to display and how the pharmacist stays in control of every sale.",
  satisfies: "A current SOP for selling P medicines — questions asked (e.g. WWHAM), when to refer to the pharmacist, refusing a sale, and medicines liable to misuse. If any P medicines are on facilitated self-selection, a written risk assessment that: lists the products displayed and those excluded (misuse-prone, harmful in overdose, theft-prone or high-value, recent POM-to-P switches); explains how the pharmacist supervises every sale and can intervene; covers staff training and when to refer; and is reviewed regularly and after any incident. Staff have read and signed it.\n\nGood evidence: the SOP linked from Pharmace’s SOP module (in date, with staff read & signed), or the SOP and risk assessment uploaded by the branch.",
  references: PMED_REFS
};

// -----------------------------------------------------------------------------
// POM AREAS ACCESS-CONTROLLED — two parts (see POM_PARTS). GPhC standards:
// 3.4 "premises are secure and safeguarded from unauthorised access"; 4.3
// medicines are "stored securely; safeguarded from unauthorised access"
// (inspectors mark POMs kept where the public could reach them as an area for
// improvement). POMs may only be supplied against a prescription (Human
// Medicines Regulations 2012), and P medicines are sold under a pharmacist's
// supervision, so neither may be open to self-selection.
// -----------------------------------------------------------------------------
const POM_REFS = [{
  label: "GPhC — Standards for registered pharmacies (3.4 premises secure; 4.3 medicines stored securely)",
  url: GPHC_STANDARDS
}, {
  label: "The Human Medicines Regulations 2012 — regulation 214 (sale or supply of prescription only medicines)",
  url: "https://www.legislation.gov.uk/uksi/2012/1916/regulation/214"
}];
GUIDANCE["pom--arrangements"] = {
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record how the dispensary is laid out; a photo shows the arrangements that keep POMs away from the public.",
  onlyReason: "Once a year a named person photographs the arrangements that keep the dispensary and POM storage to authorised staff.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take a photo showing how the dispensary and prescription-only medicines are kept away from the public — for example the counter flap, door or barrier into the dispensary, with POM shelving behind it out of reach, and P medicines behind the counter rather than on open shelves.",
  explanation: "Prescription-only medicines may only be supplied against a prescription, and pharmacy (P) medicines only under a pharmacist’s supervision, so the public must not be able to reach either. The GPhC expects premises and medicines to be safeguarded from unauthorised access.",
  satisfies: "Prescription-only medicines are kept in the dispensary or another area the public can’t get into — behind the counter, a door, flap or barrier — and only authorised staff go in. Pharmacy (P) medicines are behind the counter or in locked cases, not on self-selection. Medicines awaiting collection, returned medicines and prescriptions are kept out of the public’s reach. Outside opening hours the premises are locked and alarmed, and keys are held by named people.\n\nGood evidence: a yearly photo of the dispensary entrance and POM storage showing the public can’t reach them, alongside the SOP (Part 2) that says who may enter and how access is controlled.",
  references: POM_REFS
};
GUIDANCE["pom--sop"] = {
  pharmace: false,
  task: false,
  sop: true,
  upload: "documents",
  sources: ["sop", "upload"],
  fallbackDefault: "sop",
  forceValidity: true,
  noPharmaceReason: "No Pharmace module records the access arrangements themselves. Link the SOP held in Pharmace’s SOP module, or ask the branch to upload it.",
  noTaskReason: "For an SOP, use an email request to upload documents (several files at once) instead of a staff-app task.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload your current SOP covering access to the dispensary and security of medicines — who may enter the dispensary, how POMs and P medicines are kept from the public, and how the premises and keys are secured — with its signature sheet if you have one. You can attach several files.",
  explanation: "A written procedure makes sure every member of staff — including locums — knows who may go into the dispensary and how medicines and the premises are kept secure.",
  satisfies: "A current SOP (or section of one) that sets out: who is allowed into the dispensary and areas where POMs are stored, and how visitors and contractors are supervised; how P medicines are kept from self-selection; how prescriptions, medicines awaiting collection and returns are kept secure; and opening, closing, alarm and key-holding arrangements. Staff have read and signed it, and it is reviewed regularly.\n\nGood evidence: the SOP linked from Pharmace’s SOP module (in date, with staff read & signed), or a current copy uploaded by the branch.",
  references: POM_REFS
};

// -----------------------------------------------------------------------------
// CD CABINET — three parts (see CDCAB_PARTS). Misuse of Drugs (Safe Custody)
// Regulations 1973: reg 3(2) CDs needing safe custody are kept in a locked
// safe, cabinet or room built to Schedule 2 (or covered by a police
// certificate, reg 4, renewed yearly). Safe custody applies to Schedule 2 and
// most Schedule 3 CDs (not e.g. tramadol, midazolam, phenobarbital, pregabalin,
// gabapentin). Key control, and keeping out-of-date / patient-returned CDs
// apart until destroyed, are standard practice in CD guidance and SOPs.
// -----------------------------------------------------------------------------
const CDCAB_REFS = [{
  label: "Misuse of Drugs (Safe Custody) Regulations 1973 — regulation 3 and Schedule 2",
  url: "https://www.legislation.gov.uk/uksi/1973/798/made"
}, {
  label: "GPhC — Standards for registered pharmacies (Principle 4.3: medicines stored securely)",
  url: GPHC_STANDARDS
}, {
  label: "NHS — Controlled drugs: top 10 things to remember (key control, destruction)",
  url: "https://www.communitypharmacy.scot.nhs.uk/media/6972/cd-top-10-things-to-remember-080223.pdf"
}];
GUIDANCE["cdcab--cabinet"] = {
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record the CD cabinet itself; a photo shows it is in place, fixed and locked.",
  onlyReason: "Once a year a named person photographs the CD cabinet (or the police certificate, if the pharmacy has one instead).",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take a photo of the CD cabinet with the door open, showing the lock, the hinges and the bolts fixing it to the wall or floor. If the pharmacy has a police certificate for its CD storage instead, photograph the current certificate.",
  explanation: "Schedule 2 and most Schedule 3 controlled drugs must be kept in a locked safe, cabinet or room built to the standard set in the Safe Custody Regulations — or one the police have certified as giving the same security.",
  satisfies: "Controlled drugs that need safe custody — all Schedule 2 and most Schedule 3 (not, for example, tramadol, midazolam, phenobarbital, pregabalin or gabapentin) — are kept in a locked cabinet, safe or room that meets Schedule 2 of the Misuse of Drugs (Safe Custody) Regulations 1973:\n• steel construction (mild sheet steel no lighter than 16 gauge, or steel mesh) with no more than 3 mm gap round the door;\n• a lock with at least 5 levers (or 6 pins) and at least 1,000 key differs — two locks on a large door;\n• fixed to the wall or floor with at least two rag-bolts through steel anchor plates, with no fixings removable from the outside;\n• at least two hinges, and nothing on the outside showing that drugs are kept in it.\nAlternatively, the police have issued a certificate that the storage gives an adequate degree of security (valid one year, renewable).\nThe cabinet is kept locked except when in use, and is used only for CDs.\n\nGood evidence: a yearly photo of the cabinet open, showing the lock, hinges and fixings — or of the current police certificate.",
  references: CDCAB_REFS
};
GUIDANCE["cdcab--keys"] = {
  // Pharmace RP sign-in records where the branch uses them (the RP in charge
  // holds the CD keys); elsewhere the pharmacist is emailed a link to upload
  // the key log kept on paper or in another system.
  task: false,
  pharmaceName: "RP sign-in",
  upload: "documents",
  sources: ["upload"],
  fallbackDefault: "upload",
  forceValidity: true,
  noTaskReason: "A staff-app task isn’t used for this: Pharmace RP sign-in records show who held the keys, or the pharmacist is emailed a link to upload the key log.",
  onlyReason: "Sites without Pharmace RP sign-in: the pharmacist is emailed a secure link to upload the CD key log every three months.",
  validity: {
    mode: "days",
    days: 91,
    warnDays: 7
  },
  uploadAsk: "Please upload the CD key log for the last three months — the record of who held the controlled drugs cabinet keys each day, including any hand-overs between pharmacists. A photo or scan of the paper log, or an export from the system you use, is fine. You can attach several files.",
  explanation: "Only the pharmacist (or someone they authorise) should be able to open the CD cabinet. Keeping the keys separate from the shop keys, and recording who holds them, shows access is controlled. Where the branch signs in its Responsible Pharmacist in Pharmace, that record shows who held the keys each day.",
  satisfies: "The CD cabinet keys are kept separate from the premises keys, held by the pharmacist in charge (or a person they authorise), never left in the lock or in an unlocked drawer, and there is a record of who holds them each day — including hand-overs between pharmacists. Overnight the keys are secured (for example in a sealed, signed envelope or a key safe). Spare keys are kept securely off the shop floor.\n\nGood evidence: Pharmace RP sign-in records covering every trading day (the Responsible Pharmacist in charge holds the CD keys), or — where another system is used — the CD key log uploaded every three months by the pharmacist.",
  references: CDCAB_REFS
};
GUIDANCE["cdcab--segregation"] = {
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t show how stock is laid out in the cabinet; a photo shows out-of-date and returned CDs are kept apart.",
  onlyReason: "Every three months a named person photographs the separate, labelled area in the CD cabinet.",
  validity: {
    mode: "days",
    days: 91,
    warnDays: 7
  },
  instructions: "Take a photo inside the CD cabinet showing the clearly labelled separate shelf, box or bag for out-of-date and patient-returned controlled drugs awaiting destruction.",
  explanation: "Out-of-date stock and controlled drugs returned by patients must stay in the CD cabinet until they are destroyed, but kept apart from stock in use so they can never be supplied by mistake.",
  satisfies: "Out-of-date CD stock and patient-returned CDs are kept in the CD cabinet, apart from stock in use, in a clearly labelled shelf, box or bag (“Out of date — awaiting destruction”, “Patient returns”), until they are destroyed. Out-of-date Schedule 2 stock is only destroyed in front of an authorised witness; patient returns are recorded and destroyed using a denaturing kit.\n\nGood evidence: a photo every three months of the labelled separate area inside the cabinet.",
  references: CDCAB_REFS
};

// -----------------------------------------------------------------------------
// CD REGISTER BALANCES (2026-10). Pharmace CD register where the branch uses it:
// in place when every active register has been balance-checked within the
// site's interval (weekly unless the site sets its own) and no discrepancy is
// open; a register never balance-checked counts as NOT in place. Elsewhere a
// named person photographs the latest balance checks every three months, or
// the branch notes the system that holds the register (no link needed).
// Running balances aren't a legal requirement but are expected good practice.
// -----------------------------------------------------------------------------
const CDREG_REFS = [{
  label: "CQC — Controlled drugs guidance",
  url: "https://www.cqc.org.uk/guidance-providers/controlled-drugs"
}, {
  label: "The Misuse of Drugs Regulations 2001 (CD registers: regulations 19–20)",
  url: "https://www.legislation.gov.uk/uksi/2001/3998/contents"
}, {
  label: "The Controlled Drugs (Supervision of Management and Use) Regulations 2013",
  url: "https://www.legislation.gov.uk/uksi/2013/373/contents"
}, {
  label: "GPhC — Standards for registered pharmacies (Principle 4: medicines managed safely)",
  url: GPHC_STANDARDS
}];
GUIDANCE["cd-register"] = {
  pharmaceName: "CD register",
  sources: ["task", "link", "na"],
  fallbackDefault: "task",
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  forceValidity: true,
  validity: {
    mode: "days",
    days: 90,
    warnDays: 7
  },
  linkPlaceholder: "e.g. Information held by CD-RX system",
  onlyReason: "Sites not using Pharmace’s CD register: a named person photographs the latest balance checks every three months — or note the system the register is kept in (e.g. “Information held by CD-RX system”).",
  instructions: "Take a photo of the most recent balance check for each Schedule 2 controlled drug held: the register page (or e-register screen) showing the running balance, the date it was checked against the stock and who checked it. If you keep a separate balance-check log, a photo of that is fine.",
  explanation: "The running balance in the CD register should match the stock in the cabinet. Checking it regularly — weekly is standard practice — means a loss, theft or recording error is found quickly, investigated and, where needed, reported to the CD Accountable Officer.",
  satisfies: "A running balance is kept for every Schedule 2 controlled drug in the CD register (paper or electronic), and it is checked against the physical stock regularly — weekly unless the site has set a different interval — and whenever a discrepancy is suspected. Each check is dated and initialled. Any discrepancy is investigated and recorded, and one that can’t be resolved is reported to the CD Accountable Officer. Running balances aren’t a legal requirement in themselves, but they are expected good practice and inspectors look for them.\n\nGood evidence: the Pharmace CD register showing every active register balance-checked within the interval and no open discrepancies (a register that has never been balance-checked counts as not in place); or, where the register is kept in another system, a photo of the latest balance checks every three months, or a note of the system that holds it.",
  references: CDREG_REFS
};

// CD DESTRUCTION (2026-10). Pharmace CD register where used: in place when
// every destruction in the last 12 months names who destroyed it and a
// witness. Elsewhere: a 3-monthly photo task, an emailed upload request, or a
// note of the system that holds the record.
GUIDANCE["cd-destruction"] = {
  pharmaceName: "CD register",
  sources: ["task", "upload", "link", "na"],
  upload: "documents",
  fallbackDefault: "task",
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  forceValidity: true,
  validity: {
    mode: "days",
    days: 91,
    warnDays: 7
  },
  linkPlaceholder: "e.g. Information held by CD-RX system",
  onlyReason: "Sites not using Pharmace’s CD register: every three months a named person photographs the destruction records, or is emailed a link to upload them — or note the system that holds them.",
  instructions: "Take a photo of the controlled drug destruction records for the last three months: the CD register entries for out-of-date stock destroyed (with the authorised witness’s name) and the patient-returns destruction record (with who destroyed and who witnessed each one). If nothing was destroyed in that time, photograph the last entries and say so.",
  uploadAsk: "Please upload the controlled drug destruction records for the last three months — the CD register entries for out-of-date stock destroyed in front of an authorised witness, and the patient-returns destruction record showing who destroyed and who witnessed each one. Photos, scans or an export from your system are fine. You can attach several files.",
  explanation: "Controlled drugs must be destroyed so they can’t be retrieved — using a denaturing kit — and every destruction recorded. Out-of-date Schedule 2 stock may only be destroyed in front of an authorised witness; patient-returned CDs should be witnessed by a second member of staff.",
  satisfies: "Every destruction of a controlled drug is recorded, with the date, drug, strength, form and quantity, who destroyed it and who witnessed it, and the drug is denatured (a CD denaturing kit) so it can’t be retrieved or reused:\n• out-of-date or unwanted Schedule 2 stock — destroyed only in front of a witness authorised by the CD Accountable Officer, and recorded in the CD register;\n• patient-returned CDs — no authorised witness is needed by law, but they are destroyed in front of a second member of staff and recorded in a patient-returns destruction record;\n• Schedule 3 and 4 (part 1) stock — denatured before disposal.\n\nGood evidence: Pharmace CD register destructions over the last 12 months, every one naming who destroyed it and a witness; or, where another system is used, the destruction records photographed or uploaded every three months, or a note of the system that holds them.",
  references: CDREG_REFS
};
// CD SOPs — same set-up as the other SOP items.
GUIDANCE["cd-sop"] = {
  pharmace: false,
  task: false,
  sop: true,
  upload: "documents",
  sources: ["sop", "upload"],
  fallbackDefault: "sop",
  forceValidity: true,
  noPharmaceReason: "No Pharmace module records the CD procedures themselves. Link the SOPs held in Pharmace’s SOP module, or ask the branch to upload them.",
  noTaskReason: "For an SOP, use an email request to upload documents (several files at once) instead of a staff-app task.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload your current controlled drugs SOP(s) — covering ordering and receipt, safe custody and keys, register entries and balance checks, supply and ID checks, patient returns, destruction and reporting concerns — with the signature sheets if you have them. You can attach several files.",
  explanation: "Pharmacies must have standard operating procedures for every stage of handling controlled drugs, so all staff — including locums — do it the same, safe way.",
  satisfies: "Current, in-date SOPs covering the whole controlled drug process: ordering and receipt (including requisitions); safe custody and who holds the keys; CD register entries and running balances; balance checks and investigating discrepancies; supply, including checking the identity of whoever collects Schedule 2 CDs; patient returns; destruction (authorised witness for stock, denaturing kits); and reporting incidents and concerns to the CD Accountable Officer. Staff who handle CDs have read and signed them, and they are reviewed regularly (at least every two years, or after an incident or change in the law).\n\nGood evidence: the SOPs linked from Pharmace’s SOP module (in date, with staff read & signed), or current signed copies uploaded by the branch.",
  references: CDREG_REFS
};
// CD ACCOUNTABLE OFFICER — two parts (see CDAO_PARTS).
const CDAO_REFS = [{
  label: "Controlled Drug Reporting — report a CD incident or concern to your CD Accountable Officer (England)",
  url: "https://www.cdreporting.co.uk/"
}, {
  label: "The Controlled Drugs (Supervision of Management and Use) Regulations 2013",
  url: "https://www.legislation.gov.uk/uksi/2013/373/contents"
}, {
  label: "CQC — Controlled drugs guidance",
  url: "https://www.cqc.org.uk/guidance-providers/controlled-drugs"
}];
GUIDANCE["cdao--contact"] = {
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record who your area’s CD Accountable Officer is; a photo shows the contact details are where staff can find them.",
  onlyReason: "Once a year a named person photographs where the CD Accountable Officer’s contact details are kept for staff.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take a photo of where the contact details of your area’s Controlled Drugs Accountable Officer (CDAO) are kept for staff — for example a notice in the dispensary or the page of the CD SOP — showing the team or name and how to report a concern or incident (the reporting website, email or phone).",
  explanation: "CD incidents and concerns — losses, thefts, discrepancies that can’t be resolved, errors involving CDs, worries about a colleague — must be reported to the area’s CD Accountable Officer. Staff can only do that if they know who it is and how to contact them.",
  satisfies: "Staff know who the area’s NHS Controlled Drugs Accountable Officer is and how to report to them: the contact details and reporting route (in England, usually the online Controlled Drug Reporting site) are on display in the dispensary or in the CD SOP, and are checked each year to make sure they’re still current.\n\nGood evidence: a yearly photo of the contact details where staff can see them.",
  references: CDAO_REFS
};
GUIDANCE["cdao--discrepancies"] = {
  pharmaceName: "CD register",
  sources: ["task", "upload", "link", "na"],
  upload: "documents",
  fallbackDefault: "task",
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  forceValidity: true,
  validity: {
    mode: "days",
    days: 91,
    warnDays: 7
  },
  linkPlaceholder: "e.g. Information held by CD-RX system",
  onlyReason: "Sites not using Pharmace’s CD register: every three months a named person photographs the CD discrepancy / incident log, or is emailed a link to upload it — or note the system that holds it.",
  instructions: "Take a photo of your CD discrepancy / incident log for the last three months, showing each discrepancy, what was found and when it was resolved — or, if it couldn’t be resolved, when it was reported to the CD Accountable Officer. If there were none, photograph the log and say so.",
  uploadAsk: "Please upload your controlled drug discrepancy / incident log for the last three months, showing each discrepancy, how it was resolved, and any report made to the CD Accountable Officer (with the reference). Photos, scans or an export from your system are fine.",
  explanation: "A CD discrepancy — the register balance not matching the stock — must be investigated straight away. Most are a missed entry or a miscount; any that can’t be explained quickly has to be reported to the CD Accountable Officer.",
  satisfies: "Every CD discrepancy is investigated promptly and either resolved — with the cause and correction recorded — or, if it can’t be resolved, reported to the CD Accountable Officer (and to the police if theft is suspected). None is left open for more than 7 days.\n\nGood evidence: the Pharmace CD register with no discrepancy open for more than 7 days; or, where another system is used, the discrepancy / incident log photographed or uploaded every three months, or a note of the system that holds it.",
  references: CDAO_REFS
};

// -----------------------------------------------------------------------------
// CALIBRATION & MAINTENANCE (2026-10) — GPhC Principle 5: equipment is
// suitable, properly maintained and, where needed, calibrated. Pharmace holds
// no data for these, so each is a yearly task to a named person (no periodic
// tasks); the thermometer can also be noted as held elsewhere, and the sealer
// marked not applicable where no compliance aids are made.
// -----------------------------------------------------------------------------
const CAL_REFS = [{
  label: "GPhC — Standards for registered pharmacies (Principle 5: equipment and facilities)",
  url: GPHC_STANDARDS
}];
GUIDANCE["cal-thermometer"] = {
  pharmace: false,
  defaultEvidence: "photo",
  sources: ["task", "link", "na"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace records fridge readings, not whether the thermometer itself is accurate; a yearly check or certificate shows that.",
  onlyReason: "Once a year a named person sends a photo of the thermometer checked against a reference, or its calibration certificate — or note where the certificate is held (e.g. the data-logger supplier’s portal).",
  linkPlaceholder: "e.g. Calibration certificates held on the data-logger supplier’s portal",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Check each fridge thermometer (or data-logger probe) against a calibrated reference thermometer placed beside it, and take a photo showing both readings — they should agree within ±0.5°C. If your thermometer or data logger came with a calibration certificate, attach the current certificate instead. If one reads wrongly, replace it and record what you did.",
  explanation: "Fridge readings are only as good as the thermometer taking them. A thermometer that reads a degree or two out can hide a fridge running outside 2–8°C, so vaccines and insulin could be damaged without anyone knowing.",
  satisfies: "Every medicines fridge has a maximum/minimum thermometer or data logger whose accuracy is checked at least once a year — against a calibrated reference thermometer, or by a calibration certificate from the supplier — and it is replaced or recalibrated if it reads out of tolerance (usually ±0.5°C). The probe is placed in the centre of the fridge, not against the walls.\n\nGood evidence: a yearly photo of each thermometer beside a reference thermometer showing matching readings, or the current calibration certificate.",
  references: [...CAL_REFS, {
    label: "UKHSA — Protocol for ordering, storing and handling vaccines (thermometers and data loggers)",
    url: "https://www.gov.uk/government/publications/protocol-for-ordering-storing-and-handling-vaccines"
  }]
};
GUIDANCE["cal-scales"] = {
  // Same choices as the fridge thermometer: a yearly task (photo or
  // certificate), a note of where the records are held, or not applicable.
  pharmace: false,
  defaultEvidence: "photo",
  sources: ["task", "link", "na"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record dispensary equipment; a yearly check or certificate shows the scales and measures are suitable and accurate.",
  onlyReason: "Once a year a named person sends a photo of the scales and measures with the record of the accuracy check, or the scales’ verification / calibration certificate — or note where the records are held (e.g. the scales supplier’s service portal).",
  linkPlaceholder: "e.g. Calibration certificates held on the scales supplier’s service portal",
  naPlaceholder: "e.g. This branch does no measuring or weighing (no extemporaneous dispensing or liquid measuring).",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take photos of: the dispensary scales showing the approval / verification mark; the liquid measures (conical or cylindrical) showing their stamp or mark; and this year’s accuracy check — the scales checked with test weights, with the date, the readings and who checked. If the scales were serviced or calibrated by the supplier, attach the current certificate instead of the accuracy-check photo. Make sure the equipment is clean, and replace any measure that is chipped, cracked or has worn markings.",
  explanation: "Liquids and powders measured with inaccurate scales or worn measures give patients the wrong dose. Approved equipment that is checked regularly and kept clean makes sure what is measured is what is prescribed.",
  satisfies: "The pharmacy has the measuring and weighing equipment its services need: scales of a suitable class that are approved / verified for use, checked for accuracy at least once a year with test weights (and the check recorded); glass liquid measures that are stamped or marked, in a range of sizes, with separate measures kept for cytotoxic or sensitising medicines; and all equipment kept clean, in good repair and replaced when damaged or worn.\n\nGood evidence: a yearly photo of the scales and measures with their marks visible and the dated accuracy-check record, or the current calibration certificate — or a note of where the records are held.",
  references: CAL_REFS
};
GUIDANCE["cal-cmp"] = {
  // Same choices as the fridge thermometer: a yearly task (photo or
  // certificate), a note of where the records are held, or not applicable.
  pharmace: false,
  defaultEvidence: "photo",
  sources: ["task", "link", "na"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record the compliance-aid sealer; a yearly check or service record shows it is looked after.",
  onlyReason: "Once a year a named person sends a photo of the sealer’s service label or maintenance log, or its service certificate — or note where the records are held (e.g. the sealer supplier’s service portal). Branches that don’t make up compliance aids are marked not applicable.",
  linkPlaceholder: "e.g. Service records held on the sealer supplier’s portal",
  naPlaceholder: "e.g. This branch does not make up compliance aids.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take a photo of the sealing machine used for compliance aids (blister packs / MDS) showing its service label, or the page of its maintenance log — showing when it was last serviced or checked and that it is cleaned and working (seals complete, no burns or gaps). If the supplier serviced it, attach the current service certificate instead.",
  explanation: "A poorly maintained sealer can leave packs that don’t seal properly, so tablets fall out, mix or are exposed to moisture — and patients who rely on compliance aids may then take the wrong doses.",
  satisfies: "Where the pharmacy makes up compliance aids, the sealing equipment is used and maintained as the manufacturer says: it is cleaned regularly, its temperature and sealing are checked, faults are reported and repaired, and it is serviced at the interval the manufacturer recommends — with a record kept. Branches that don’t make up compliance aids mark this not applicable.\n\nGood evidence: a yearly photo of the sealer’s service label or maintenance log, or the current service certificate — or a note of where the records are held.",
  references: CAL_REFS
};

// -----------------------------------------------------------------------------
// CLINICAL IT & CONTINUITY (2026-10). Pharmace holds no data for these.
//   it-pmr-secure — two parts: a yearly confirmation task, and the access SOP.
//   it-backup     — the business continuity plan: Pharmace SOP or upload.
//   it-encryption — a yearly confirmation task (or a note of the IT record).
// -----------------------------------------------------------------------------
const IT_REFS = [{
  label: "NHS England — Data Security and Protection Toolkit",
  url: "https://www.dsptoolkit.nhs.uk/"
}, {
  label: "NHS England — Registration Authorities and Smartcards",
  url: "https://digital.nhs.uk/services/registration-authorities-and-smartcards"
}, {
  label: "GPhC — Standards for registered pharmacies",
  url: GPHC_STANDARDS
}];
GUIDANCE["itpmr--users"] = {
  // A yearly confirmation task, or the SOP(s) setting out logins, smartcards
  // and leavers, linked from Pharmace's SOP module.
  pharmace: false,
  sop: true,
  defaultEvidence: "confirm",
  evidenceOnly: "confirm",
  sources: ["task", "sop"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t manage PMR logins or smartcards; the branch manager confirms the arrangements each year.",
  onlyReason: "Once a year the branch manager confirms, in the staff app, that PMR access is controlled — or link the SOP(s) covering logins, smartcards and removing leavers from Pharmace’s SOP module.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Please confirm that: every person who uses the PMR / NHS systems has their own login and, where needed, their own NHS smartcard; smartcards and passwords are never shared or left in a reader unattended; access levels match each person’s role; and people who have left have had their PMR login disabled and their smartcard role removed (via your Registration Authority). Confirm only when all of this is true.",
  explanation: "Each person’s login and smartcard is what records who looked at or changed a patient’s record. Shared cards or logins left active for leavers mean the pharmacy can’t show who accessed patient data, and break the NHS smartcard terms.",
  satisfies: "Access to the PMR and NHS systems (Spine, SCR, EPS) is limited to authorised staff: everyone has their own login and smartcard, with access matching their role; smartcards and passwords are never shared and cards are removed when the user leaves the terminal; screens lock when unattended; and logins and smartcard roles are removed promptly when someone leaves.\n\nGood evidence: a yearly confirmation by the branch manager.",
  references: IT_REFS
};
GUIDANCE["itpmr--sop"] = {
  pharmace: false,
  task: false,
  sop: true,
  upload: "documents",
  sources: ["sop", "upload"],
  fallbackDefault: "sop",
  forceValidity: true,
  noPharmaceReason: "No Pharmace module records the IT access procedure itself. Link the SOP held in Pharmace’s SOP module, or ask the branch to upload it.",
  noTaskReason: "For an SOP, use an email request to upload documents (several files at once) instead of a staff-app task.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload your current SOP covering access to the PMR and NHS systems — logins and smartcards, access levels, screen locking, and removing access when someone leaves — or the information governance policy that covers it, with its signature sheet if you have one.",
  explanation: "A written procedure makes sure every member of staff, including locums, uses the PMR and smartcards the same safe way, and that access is removed when people leave.",
  satisfies: "A current SOP or information governance policy covering: issuing logins and smartcards; access levels by role; never sharing cards or passwords; locking screens and removing cards when away from the terminal; and removing access promptly for leavers. Staff have read and signed it, and it is reviewed regularly.\n\nGood evidence: the SOP linked from Pharmace’s SOP module (in date, with staff read & signed), or a current copy uploaded by the branch.",
  references: IT_REFS
};
GUIDANCE["it-backup"] = {
  pharmace: false,
  task: false,
  sop: true,
  upload: "documents",
  sources: ["sop", "upload"],
  fallbackDefault: "sop",
  forceValidity: true,
  noPharmaceReason: "No Pharmace module records the business continuity plan. Link it from Pharmace’s SOP module, or ask the branch to upload it.",
  noTaskReason: "For a plan or policy, use an email request to upload documents (several files at once) instead of a staff-app task.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload your current business continuity plan for this pharmacy — what you would do if you lost the PMR / NHS systems, power, internet, the premises or key staff, with the contact numbers you would need. You can attach several files.",
  explanation: "If the PMR, the internet, power or the premises are lost, the pharmacy still has to look after patients. A written, current plan means staff know what to do and who to call, and NHS England expects one for each premises.",
  satisfies: "A current, written business continuity plan for the premises covering: loss of the PMR and NHS systems (including how prescriptions will be dispensed and recorded), loss of power, internet or phones, loss of access to the premises, staff shortages, and fridge failure; with key contacts (IT supplier, NHS England, landlord, utilities) and how patients and the NHS will be told. It is reviewed at least yearly and staff know where it is.\n\nGood evidence: the plan linked from Pharmace’s SOP module (in date, with staff read & signed), or a current copy uploaded by the branch.",
  references: IT_REFS
};
GUIDANCE["it-encryption"] = {
  pharmace: false,
  defaultEvidence: "confirm",
  evidenceOnly: "confirm",
  sources: ["task", "link", "na"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t manage the pharmacy’s devices; the branch manager confirms each year that they are encrypted.",
  onlyReason: "Once a year the branch manager confirms in the staff app that devices are encrypted — or note where your IT provider’s device record is held.",
  linkPlaceholder: "e.g. Device encryption report held by our IT provider",
  naPlaceholder: "e.g. No laptops, tablets or phones at this branch hold patient data.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Please confirm that every laptop, tablet, phone and USB stick at the pharmacy that holds or can access patient information is encrypted and protected by a password or PIN, and that none is used without it. Confirm only when this is true.",
  explanation: "A lost or stolen laptop, tablet or phone that isn’t encrypted is a data breach — anyone who finds it can read the patient information on it. Encryption is required by the Data Security and Protection Toolkit.",
  satisfies: "Every portable device or removable storage that holds or accesses patient information — laptops, tablets, phones (including delivery drivers’ phones) and USB sticks — is encrypted and protected by a password or PIN, set to lock automatically, and kept up to date; devices are wiped before disposal.\n\nGood evidence: a yearly confirmation by the branch manager, or a note of where the IT provider’s device / encryption record is held.",
  references: IT_REFS
};

// -----------------------------------------------------------------------------
// WORKPLACE HEALTH & SAFETY (2026-10). Pharmace holds no data for these.
//   hs-riskassess, coshh-assess — documents uploaded yearly (or a note of
//                                  where they are held, e.g. a H&S provider).
//   hs-elinsurance              — the certificate, until the policy expires.
//   hs-firstaid, hs-poster      — a yearly photo task to a named person.
// -----------------------------------------------------------------------------
const HS_REFS = [{
  label: "HSE — Managing risks and risk assessment at work",
  url: "https://www.hse.gov.uk/simple-health-safety/risk/index.htm"
}, {
  label: "GPhC — Standards for registered pharmacies (Principle 3: premises)",
  url: GPHC_STANDARDS
}];
const HS_DOC = {
  pharmace: false,
  task: false,
  upload: "documents",
  sources: ["upload", "link", "na"],
  fallbackDefault: "upload",
  forceValidity: true,
  noTaskReason: "Not a staff-app task: a key person is emailed a secure link to upload the documents (several files at once).",
  linkPlaceholder: "e.g. Held on our health & safety provider’s portal",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  }
};
GUIDANCE["hs-riskassess"] = {
  ...HS_DOC,
  noPharmaceReason: "Pharmace doesn’t hold the pharmacy’s risk assessments, so the evidence is the assessments themselves.",
  uploadAsk: "Please upload the pharmacy’s current health & safety risk assessments — the general workplace assessment and any specific ones (fire, manual handling, lone working, display screens, new or expectant mothers, young workers) — showing the date each was last reviewed. You can attach several files.",
  explanation: "Employers must look at what could harm staff and visitors and take sensible steps to prevent it. With five or more employees the significant findings must be written down; a written, reviewed assessment shows the risks have been thought through.",
  satisfies: "Suitable and sufficient risk assessments cover the pharmacy’s significant hazards — slips and trips, manual handling (deliveries, stock), display screen use, lone working, violence and aggression, sharps (for services such as vaccinations), new or expectant mothers and young workers — plus a separate fire risk assessment. Each records the hazards, who could be harmed, the controls in place and any actions, is reviewed at least yearly or after a change or incident, and is shared with staff.\n\nGood evidence: the current assessments, uploaded yearly, each showing its review date — or a note of where they are held.",
  references: [{
    label: "Management of Health and Safety at Work Regulations 1999 — regulation 3",
    url: "https://www.legislation.gov.uk/uksi/1999/3242/regulation/3"
  }, ...HS_REFS]
};
GUIDANCE["coshh-assess"] = {
  ...HS_DOC,
  noPharmaceReason: "Pharmace doesn’t hold COSHH assessments, so the evidence is the assessments themselves.",
  uploadAsk: "Please upload your current COSHH assessments for the hazardous substances used in the pharmacy — for example cleaning products, methylated spirit / alcohol hand rub, cytotoxic or sensitising medicines, and anything used to make up medicines — with the date each was reviewed. Safety data sheets can be included. You can attach several files.",
  explanation: "Some substances in a pharmacy — cleaning chemicals, solvents, cytotoxic or sensitising medicines — can harm staff if handled wrongly. A COSHH assessment for each says how it is stored, handled and disposed of, and what to do if someone is exposed.",
  satisfies: "Every hazardous substance used or kept in the pharmacy has a COSHH assessment: what it is and its hazards (from the safety data sheet), who could be exposed and how, the controls (storage, gloves, ventilation, spill kit), first aid, and disposal. Assessments are reviewed at least yearly or when a new product is introduced, and staff know where they are.\n\nGood evidence: the current COSHH assessments, uploaded yearly, each showing its review date — or a note of where they are held.",
  references: [{
    label: "HSE — COSHH: Control of Substances Hazardous to Health",
    url: "https://www.hse.gov.uk/coshh/"
  }, ...HS_REFS]
};
GUIDANCE["hs-elinsurance"] = {
  pharmace: false,
  defaultEvidence: "document",
  upload: "documents",
  sources: ["upload", "task", "link", "na"],
  fallbackDefault: "upload",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record insurance, so the evidence is the current certificate.",
  onlyReason: "A key person is emailed a link to upload the current certificate (or a team member photographs it on display); it lasts until the policy expires, and the renewal request goes out 30 days before.",
  linkPlaceholder: "e.g. Group policy held at head office",
  validity: {
    mode: "expiry-date",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload the current employers’ liability insurance certificate for the pharmacy, showing the insurer, the policy dates and the cover (at least £5 million).",
  instructions: "Take a photo of the current employers’ liability insurance certificate where it is displayed for staff (or attach the certificate), with the insurer, policy dates and cover readable.",
  explanation: "Employers’ liability insurance pays for claims from employees injured or made ill at work. It is a legal requirement for almost every employer, and staff must be able to see the current certificate.",
  satisfies: "The pharmacy holds employers’ liability insurance with an authorised insurer, for at least £5 million, and the current certificate is displayed where staff can read it (or made available to them electronically). It is renewed before it expires.\n\nGood evidence: the current certificate, uploaded or photographed on display, with the policy expiry date recorded so the next request goes out 30 days before.",
  references: [{
    label: "HSE — Employers’ liability insurance",
    url: "https://www.hse.gov.uk/simple-health-safety/insurance/index.htm"
  }, {
    label: "Employers’ Liability (Compulsory Insurance) Act 1969",
    url: "https://www.legislation.gov.uk/ukpga/1969/57/contents"
  }]
};
GUIDANCE["hs-firstaid"] = {
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record first aid arrangements; a yearly photo shows the kit and who to go to.",
  onlyReason: "Once a year a named person photographs the first aid kit and the notice saying who the first aider / appointed person is.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take a photo of the first aid kit open, showing it is fully stocked and in date, and a photo of the notice telling staff who the first aider or appointed person is and where the kit is kept. Restock anything missing or out of date before you take the photo.",
  explanation: "If someone is hurt or taken ill at work, staff need to get first aid quickly. The law requires a stocked first aid kit, someone appointed to take charge, and staff told the arrangements.",
  satisfies: "Following the pharmacy’s first-aid needs assessment: a suitably stocked, in-date first aid kit that is easy to reach; an appointed person (or a trained first aider where the assessment says one is needed) on duty whenever the pharmacy is open; and a notice telling staff who that is and where the kit is. The kit is checked and restocked regularly, and accidents are recorded in the accident book.\n\nGood evidence: a yearly photo of the stocked kit and of the first-aid notice.",
  references: [{
    label: "HSE — First aid at work",
    url: "https://www.hse.gov.uk/firstaid/"
  }, {
    label: "The Health and Safety (First-Aid) Regulations 1981",
    url: "https://www.legislation.gov.uk/uksi/1981/917/contents"
  }]
};
GUIDANCE["hs-poster"] = {
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace can’t show a poster on the wall; a yearly photo does.",
  onlyReason: "Once a year a named person photographs the HSE ‘Health and safety law’ poster on display (or, if staff are given the leaflet instead, the record of who received it).",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take a photo of the HSE ‘Health and safety law — what you need to know’ poster where staff can see it, with the boxes for local contacts filled in. If the pharmacy gives staff the matching pocket leaflet instead, photograph the record showing each member of staff received it.",
  explanation: "Employers must tell staff about health and safety law and who to contact, by displaying the approved HSE poster or giving each employee the matching leaflet.",
  satisfies: "The current HSE-approved ‘Health and safety law’ poster is displayed where staff can easily read it, with the local contact details filled in — or every employee has been given the equivalent leaflet.\n\nGood evidence: a yearly photo of the poster on display (or of the leaflet issue record).",
  references: [{
    label: "HSE — Health and safety law poster",
    url: "https://www.hse.gov.uk/simple-health-safety/display.htm"
  }, {
    label: "The Health and Safety Information for Employees Regulations 1989",
    url: "https://www.legislation.gov.uk/uksi/1989/682/contents"
  }]
};

// -----------------------------------------------------------------------------
// DATA PROTECTION (2026-10). Pharmace holds no data for these.
//   gdpr-ico     — ICO registration certificate, until it expires.
//   gdpr-privacy — two parts: a yearly photo on display, and the notice itself.
//   gdpr-breach, gdpr-sar — procedures: Pharmace SOP link or upload, yearly.
// -----------------------------------------------------------------------------
const DP_REFS = [{
  label: "ICO — Guide to the UK GDPR",
  url: "https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/"
}, {
  label: "NHS England — Data Security and Protection Toolkit",
  url: "https://www.dsptoolkit.nhs.uk/"
}, {
  label: "GPhC — Standards for registered pharmacies (Principle 1.6 and 4: records and confidentiality)",
  url: GPHC_STANDARDS
}];
const DP_SOP = {
  pharmace: false,
  task: false,
  sop: true,
  upload: "documents",
  sources: ["sop", "upload"],
  fallbackDefault: "sop",
  forceValidity: true,
  noTaskReason: "For a procedure, use an email request to upload documents (several files at once) instead of a staff-app task.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  }
};
GUIDANCE["gdpr-ico"] = {
  pharmace: false,
  task: false,
  upload: "documents",
  sources: ["upload", "link", "na"],
  fallbackDefault: "upload",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record ICO registration, so the evidence is the current registration certificate.",
  noTaskReason: "Not a staff-app task: a key person is emailed a secure link to upload the current ICO registration certificate.",
  linkPlaceholder: "e.g. Registered centrally — ICO number ZA123456",
  validity: {
    mode: "expiry-date",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload the current ICO registration certificate (data protection fee) for the pharmacy, showing the registration number and the expiry date.",
  explanation: "Every organisation that handles personal information must pay the ICO data protection fee each year, unless exempt — and a pharmacy handling patient records is not exempt. Not paying can lead to a fine.",
  satisfies: "The pharmacy (or the company that owns it) is registered with the Information Commissioner’s Office and has paid the annual data protection fee; the registration is renewed before it expires and the entry on the ICO public register is current.\n\nGood evidence: the current ICO registration certificate, showing the registration number and expiry date — head office records the expiry date and the next request goes out 30 days before. Where one registration covers the whole company, a note of the registration number is enough.",
  references: [{
    label: "ICO — Data protection fee",
    url: "https://ico.org.uk/for-organisations/data-protection-fee/"
  }, ...DP_REFS]
};
GUIDANCE["privacy--display"] = {
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace can’t show what patients see; a yearly photo shows the privacy notice is on display.",
  onlyReason: "Once a year a named person photographs the patient privacy notice where patients can see it.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take a photo of the patient privacy notice (or the short ‘how we use your information’ poster pointing to the full notice) where patients can see it — for example by the counter or in the consultation room.",
  explanation: "Patients have a right to know how the pharmacy uses their information. Displaying the privacy notice — or a short version pointing to the full one — is how most patients will see it.",
  satisfies: "A privacy notice, or a short version saying where to find the full one, is displayed where patients can see it.\n\nGood evidence: a yearly photo of the notice on display.",
  references: DP_REFS
};
GUIDANCE["privacy--notice"] = {
  pharmace: false,
  task: false,
  upload: "documents",
  sources: ["upload", "link"],
  fallbackDefault: "upload",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t hold the privacy notice; upload it, or link the page on the pharmacy’s website.",
  noTaskReason: "Not a staff-app task: upload the current notice, or link the web page where it is published.",
  linkPlaceholder: "e.g. Privacy notice on our website",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload the pharmacy’s current patient privacy notice — the full version explaining what information you hold, why, who you share it with, how long you keep it and patients’ rights.",
  explanation: "The full privacy notice is what tells patients exactly how their information is used and what their rights are. It must be accurate and kept up to date.",
  satisfies: "A current privacy notice that explains, in plain language: who the pharmacy is and how to contact it (and its Data Protection Officer, if it has one); what information is held and why; the lawful basis; who it is shared with (GP, NHS England, NHSBSA, etc.); how long it is kept; patients’ rights (including access and complaint to the ICO); and national data opt-out. It is reviewed at least yearly.\n\nGood evidence: the current notice uploaded, or a link to where it is published on the pharmacy’s website.",
  references: DP_REFS
};
GUIDANCE["gdpr-breach"] = {
  ...DP_SOP,
  noPharmaceReason: "No Pharmace module records the breach procedure. Link the SOP held in Pharmace’s SOP module, or ask the branch to upload it.",
  uploadAsk: "Please upload your current data breach / information governance incident procedure — how staff report a breach, who decides whether it is reportable, and how it is reported (through the DSPT incident tool / to the ICO within 72 hours) — with its signature sheet if you have one.",
  explanation: "When patient information is lost, sent to the wrong person or seen by someone who shouldn’t see it, staff must know to report it straight away, so it can be contained and — if there is a risk to patients — reported to the ICO within 72 hours.",
  satisfies: "A current procedure telling staff what a data breach is (e.g. a bag label or prescription given to the wrong patient, a lost delivery sheet, a misdirected email), to report it immediately to the pharmacist / information governance lead, how it is contained and recorded, and how reportable breaches are notified within 72 hours (in England through the DSPT incident reporting tool, which passes them to the ICO). Staff have read and signed it, and it is reviewed regularly.\n\nGood evidence: the procedure linked from Pharmace’s SOP module (in date, with staff read & signed), or a current copy uploaded by the branch.",
  references: [{
    label: "ICO — Personal data breaches",
    url: "https://ico.org.uk/for-organisations/report-a-breach/"
  }, ...DP_REFS]
};
GUIDANCE["gdpr-sar"] = {
  ...DP_SOP,
  noPharmaceReason: "No Pharmace module records the subject access procedure. Link the SOP held in Pharmace’s SOP module, or ask the branch to upload it.",
  uploadAsk: "Please upload your current subject access request (SAR) procedure — how a request for a patient’s records is recognised, logged, identity checked and answered within one month — with its signature sheet if you have one.",
  explanation: "Patients can ask for a copy of the information the pharmacy holds about them. The pharmacy must recognise the request, check who is asking and reply — usually free of charge — within one month.",
  satisfies: "A current procedure covering: recognising a subject access request (it can be made verbally or in writing, to any member of staff); logging it and checking the requester’s identity; gathering the information from the PMR and other records; withholding anything that would identify others or cause serious harm; and replying within one calendar month, usually free of charge. Staff have read and signed it, and it is reviewed regularly.\n\nGood evidence: the procedure linked from Pharmace’s SOP module (in date, with staff read & signed), or a current copy uploaded by the branch.",
  references: DP_REFS
};

// -----------------------------------------------------------------------------
// DSPT (2026-10). Pharmace holds no data for these.
//   dspt-published — the "Standards Met" confirmation, until the next 30 June
//                    (head office records 30 June as the expiry date; the
//                    renewal request goes out in mid-May).
//   dspt-iao       — the named IG lead accepts the role in the staff app yearly.
//   dspt-training  — the staff training record, uploaded yearly.
// -----------------------------------------------------------------------------
const DSPT_REFS = [{
  label: "NHS England — Data Security and Protection Toolkit",
  url: "https://www.dsptoolkit.nhs.uk/"
}];
GUIDANCE["dspt-published"] = {
  pharmace: false,
  task: false,
  upload: "documents",
  sources: ["upload", "link", "na"],
  fallbackDefault: "upload",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t hold the DSPT submission, so the evidence is the toolkit’s confirmation.",
  noTaskReason: "Not a staff-app task: a key person is emailed a secure link to upload the DSPT confirmation.",
  linkPlaceholder: "e.g. Submitted for the group by head office — ODS code FXX00",
  validity: {
    mode: "expiry-date",
    days: 365,
    warnDays: 45
  },
  uploadAsk: "Please upload the confirmation that this year’s Data Security and Protection Toolkit (DSPT) has been published for the pharmacy — a screenshot of the DSPT status page showing ‘Standards Met’ (or ‘Standards Exceeded’) and the date, or the confirmation email.",
  explanation: "Every NHS community pharmacy must complete and publish the Data Security and Protection Toolkit each year, by 30 June. It shows the pharmacy protects patient information properly, and it is needed to keep access to NHS systems and data.",
  satisfies: "The pharmacy’s DSPT assessment for the current year has been completed and published by the 30 June deadline with a status of ‘Standards Met’ (or ‘Exceeded’), and anything marked as an improvement plan has been followed up.\n\nGood evidence: a screenshot or confirmation email showing the published status and date. Head office sets the expiry date to the next 30 June, so the renewal request goes out in mid-May. Where head office submits for the whole group, a note saying so is enough.",
  references: DSPT_REFS
};
GUIDANCE["dspt-iao"] = {
  pharmace: false,
  task: true,
  defaultEvidence: "confirm",
  evidenceOnly: "confirm",
  sources: ["task"],
  fallbackDefault: "task",
  guardian: true,
  roleTitle: "IG lead",
  roleIcon: "🔐",
  noPharmaceReason: "Pharmace doesn’t record who the information governance lead is; the named person confirms they accept the role.",
  onlyReason: "A task goes to the person named as IG lead at each branch. They confirm they accept the role; it is asked again every year.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "You have been named as the information governance (IG) lead — the information asset owner — for this pharmacy. The role means: making sure patient information is kept secure, that staff complete their data security training, that data breaches are reported and handled, and that the DSPT is completed each year. To accept the role, acknowledge this task. Otherwise, please let your line manager know.",
  explanation: "Someone at each pharmacy must be responsible for how patient information is looked after — the information governance lead / information asset owner. The DSPT asks who it is, and staff need to know who to go to about data security.",
  satisfies: "A named person at each pharmacy — usually the pharmacist manager — has accepted responsibility for information governance: keeping patient information secure, staff training, handling breaches and the yearly DSPT. Staff know who it is.\n\nGood evidence: the named person’s confirmation in the staff app that they accept the role, renewed every year and whenever the person changes.",
  references: DSPT_REFS
};
GUIDANCE["dspt-training"] = {
  pharmace: false,
  task: false,
  upload: "documents",
  sources: ["upload", "link", "na"],
  fallbackDefault: "upload",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record data security training, so the evidence is the training record.",
  noTaskReason: "Not a staff-app task: a key person is emailed a secure link to upload the training record for the whole team.",
  linkPlaceholder: "e.g. Training records held on our e-learning provider’s portal",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload this year’s data security awareness training record for the pharmacy — a list or report showing each member of staff (including part-time and delivery staff) and the date they completed the training, or their certificates. You can attach several files.",
  explanation: "Most data breaches come from simple mistakes. Everyone who handles patient information must complete data security awareness training every year, and the DSPT asks the pharmacy to confirm they have.",
  satisfies: "Every member of staff with access to patient information — pharmacists, technicians, dispensers, counter staff, delivery drivers and regular locums — has completed data security awareness training (for example the NHS ‘Data Security Awareness’ e-learning) within the last 12 months, and new starters do it before they start handling patient data.\n\nGood evidence: the training record or certificates, uploaded yearly, showing every staff member and their completion date.",
  references: DSPT_REFS
};

// -----------------------------------------------------------------------------
// WASTE & ESSENTIAL SERVICES (2026-10). Pharmace holds no data for these.
//   waste-contract    — contract / carrier licence + recent consignment notes,
//                       uploaded yearly (or a note of a group contract).
//   waste-segregation — a yearly photo task.
//   tos-disposal, tos-signposting — two parts each: a yearly confirmation by
//                       the manager, and the SOP (Pharmace SOP link or upload).
// -----------------------------------------------------------------------------
const WASTE_REFS = [{
  label: "GOV.UK — Healthcare waste: safe management (HTM 07-01)",
  url: "https://www.gov.uk/government/publications/guidance-on-the-safe-management-of-healthcare-waste"
}, {
  label: "GPhC — Standards for registered pharmacies",
  url: GPHC_STANDARDS
}];
const TOS_REFS = [{
  label: "Community Pharmacy England — Essential services",
  url: "https://cpe.org.uk/national-pharmacy-services/essential-services/"
}, {
  label: "The NHS (Pharmaceutical and Local Pharmaceutical Services) Regulations 2013 — Schedule 4 (terms of service)",
  url: "https://www.legislation.gov.uk/uksi/2013/349/schedule/4"
}];
GUIDANCE["waste-contract"] = {
  pharmace: false,
  task: false,
  upload: "documents",
  sources: ["upload", "link", "na"],
  fallbackDefault: "upload",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t hold waste contracts, so the evidence is the contract and the waste paperwork.",
  noTaskReason: "Not a staff-app task: a key person is emailed a secure link to upload the contract and consignment notes (several files at once).",
  linkPlaceholder: "e.g. Group waste contract held at head office",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload: the current waste collection contract (or the carrier’s waste carrier registration), and the most recent consignment notes / waste transfer notes for medicines and clinical waste collected from the pharmacy. You can attach several files.",
  explanation: "Pharmacy waste — unwanted medicines, cytotoxic waste, sharps — must be collected by a registered waste carrier, and the paperwork for each collection kept. This is the pharmacy’s duty of care: it stays responsible for the waste until it is disposed of properly.",
  satisfies: "A current contract with a registered waste carrier for collecting the pharmacy’s medicines and clinical waste (including patient-returned medicines, cytotoxic waste and sharps), and the consignment or transfer note for each collection kept — normally for at least three years.\n\nGood evidence: the contract or the carrier’s registration, plus recent consignment / transfer notes, uploaded yearly — or a note of where a group contract is held.",
  references: [{
    label: "GOV.UK — Waste duty of care code of practice",
    url: "https://www.gov.uk/government/publications/waste-duty-of-care-code-of-practice"
  }, ...WASTE_REFS]
};
GUIDANCE["waste-segregation"] = {
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record how waste is kept; a yearly photo shows it is separated and labelled.",
  onlyReason: "Once a year a named person photographs the pharmacy’s waste bins, showing they are separated and labelled.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take a photo of the pharmacy’s waste containers showing they are kept separately and labelled: medicines waste (usually blue-lidded), cytotoxic / cytostatic waste (purple-lidded), sharps bins, and denatured controlled drug waste. Make sure lids are closed and the containers are kept away from public areas.",
  explanation: "Different kinds of medicines waste have to be kept apart and sent for the right kind of disposal. Mixing them can be dangerous to staff and the carrier, and breaks waste law.",
  satisfies: "Medicines and clinical waste is separated at the point it is produced into the right labelled containers — non-hazardous medicines (usually blue-lidded), cytotoxic and cytostatic medicines (purple-lidded), sharps (sharps bins) and denatured controlled drugs — kept securely away from the public, not overfilled, and collected regularly by the waste carrier.\n\nGood evidence: a yearly photo of the separated, labelled containers.",
  references: WASTE_REFS
};
const TOS_CONFIRM = {
  pharmace: false,
  defaultEvidence: "confirm",
  evidenceOnly: "confirm",
  sources: ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  }
};
const TOS_SOP = {
  pharmace: false,
  task: false,
  sop: true,
  upload: "documents",
  sources: ["sop", "upload"],
  fallbackDefault: "sop",
  forceValidity: true,
  noTaskReason: "For an SOP, use an email request to upload documents (several files at once) instead of a staff-app task.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  }
};
GUIDANCE["disposal--offered"] = {
  ...TOS_CONFIRM,
  noPharmaceReason: "Pharmace doesn’t record medicines returned for disposal; the branch manager confirms each year that the service is offered.",
  onlyReason: "Once a year the branch manager confirms in the staff app that unwanted medicines are accepted.",
  instructions: "Please confirm that the pharmacy accepts unwanted medicines returned by patients and households, free of charge, whenever it is open; that staff know how to handle them (sharps and cytotoxic medicines kept separate, controlled drugs recorded and denatured); and that patient-returned medicines are never re-used. Confirm only when this is true.",
  explanation: "Accepting unwanted medicines from patients is an Essential Service every NHS community pharmacy must provide, so medicines are disposed of safely rather than kept at home, thrown away or flushed.",
  satisfies: "The pharmacy accepts unwanted medicines from patients and households free of charge, sorts them into the right waste containers (keeping sharps and cytotoxic medicines separate), records and denatures returned controlled drugs, never re-uses returned medicines, and has them collected by the waste carrier.\n\nGood evidence: a yearly confirmation by the branch manager.",
  references: [...TOS_REFS, ...WASTE_REFS.slice(0, 1)]
};
GUIDANCE["disposal--sop"] = {
  ...TOS_SOP,
  noPharmaceReason: "No Pharmace module records the disposal procedure. Link the SOP held in Pharmace’s SOP module, or ask the branch to upload it.",
  uploadAsk: "Please upload your current SOP for accepting and disposing of patients’ unwanted medicines (including sharps, cytotoxic medicines and returned controlled drugs), with its signature sheet if you have one.",
  explanation: "A written procedure makes sure every member of staff handles returned medicines safely and the same way.",
  satisfies: "A current SOP covering how returned medicines are accepted, checked (sharps, cytotoxic, controlled drugs), separated into the right containers, recorded where needed, stored and collected. Staff have read and signed it, and it is reviewed regularly.\n\nGood evidence: the SOP linked from Pharmace’s SOP module (in date, with staff read & signed), or a current copy uploaded by the branch.",
  references: TOS_REFS
};
GUIDANCE["signpost--offered"] = {
  ...TOS_CONFIRM,
  noPharmaceReason: "Pharmace doesn’t record signposting; the branch manager confirms each year that the service is provided.",
  onlyReason: "Once a year the branch manager confirms in the staff app that signposting and self-care support are given.",
  instructions: "Please confirm that staff give advice on self-care for minor illnesses, and signpost patients to other services when the pharmacy can’t help (GP, NHS 111, urgent care, local support services); that the pharmacy keeps an up-to-date list of local services to refer to; and that significant advice or referrals are recorded where needed. Confirm only when this is true.",
  explanation: "Supporting people to look after themselves, and pointing them to the right service when the pharmacy can’t help, are Essential Services every NHS community pharmacy must provide.",
  satisfies: "Staff give self-care advice for minor illnesses and long-term conditions, and signpost patients to other health and social care services when needed, using an up-to-date directory of local services (e.g. the NHS Service Finder / local directory). Significant advice or referrals are recorded where appropriate.\n\nGood evidence: a yearly confirmation by the branch manager.",
  references: TOS_REFS
};
GUIDANCE["signpost--sop"] = {
  ...TOS_SOP,
  noPharmaceReason: "No Pharmace module records the signposting procedure. Link the SOP held in Pharmace’s SOP module, or ask the branch to upload it.",
  uploadAsk: "Please upload your current SOP for signposting and support for self-care, with its signature sheet if you have one.",
  explanation: "A written procedure makes sure every member of staff gives consistent self-care advice and knows where to refer patients.",
  satisfies: "A current SOP covering self-care advice, when and how to signpost or refer patients to other services, keeping the local services directory up to date, and recording significant advice or referrals. Staff have read and signed it, and it is reviewed regularly.\n\nGood evidence: the SOP linked from Pharmace’s SOP module (in date, with staff read & signed), or a current copy uploaded by the branch.",
  references: TOS_REFS
};

// -----------------------------------------------------------------------------
// TRAINING (2026-10). No Pharmace training records yet (staff_training_defs.js
// describes them, but per-person records don't exist), so each is the
// branch's training record uploaded yearly — or a note of where it is held.
// -----------------------------------------------------------------------------
const TRN_REFS = [{
  label: "GPhC — Standards for registered pharmacies (Principle 2: staff)",
  url: GPHC_STANDARDS
}];
const TRN_DOC = {
  pharmace: false,
  task: false,
  upload: "documents",
  sources: ["upload", "link", "na"],
  fallbackDefault: "upload",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t hold staff training records yet, so the evidence is the branch’s own training record.",
  noTaskReason: "Not a staff-app task: a key person is emailed a secure link to upload the training record for the whole team (several files at once).",
  linkPlaceholder: "e.g. Training records held on our e-learning provider’s portal",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  }
};
GUIDANCE["trn-counter"] = {
  ...TRN_DOC,
  uploadAsk: "Please upload your staff training record (training matrix or certificates) showing, for every member of staff who works on the medicines counter or in the dispensary: their role, the GPhC-accredited course they are on or have completed, the date they enrolled and the date they completed it. You can attach several files.",
  explanation: "Anyone who sells medicines or works in the dispensary must be trained for that role. The GPhC requires them to be enrolled on an accredited course within three months of starting, and to complete it.",
  satisfies: "Every member of staff working on the medicines counter or in the dispensary has completed, or is enrolled on, a GPhC-accredited course for their role (e.g. medicines counter assistant, dispensing assistant) — enrolled within three months of starting the role — and is supervised until they complete it. Training is recorded for each person.\n\nGood evidence: the training matrix or certificates, uploaded yearly, showing each person’s course, enrolment date and completion date.",
  references: TRN_REFS
};
GUIDANCE["trn-safeguarding"] = {
  ...TRN_DOC,
  uploadAsk: "Please upload your safeguarding training record (training matrix or certificates) showing every member of staff, the level of safeguarding training they have completed (e.g. CPPE Level 1 for support staff, Level 2 for pharmacists and technicians) and the date. You can attach several files.",
  explanation: "Pharmacy staff are often the first to notice signs that a child or vulnerable adult is at risk. Safeguarding training at the right level means they recognise the signs and know who to report concerns to.",
  satisfies: "Every member of staff has safeguarding training for children and adults at the level their role needs — usually Level 1 for support staff and Level 2 for pharmacists and pharmacy technicians (e.g. the CPPE safeguarding e-learning and assessment) — refreshed at least every three years, and knows the local safeguarding contacts.\n\nGood evidence: the training record or certificates, uploaded yearly, showing each person’s level and date.",
  references: [{
    label: "CPPE — Safeguarding children and vulnerable adults",
    url: "https://www.cppe.ac.uk/"
  }, ...TRN_REFS]
};
GUIDANCE["trn-cpd"] = {
  ...TRN_DOC,
  uploadAsk: "Please upload the team’s ongoing training / CPD record for the last year — the training matrix or log showing the learning each member of staff has done (e-learning, SOP updates, service training, team briefings) and the date. The same training matrix you upload for counter training is fine. You can attach several files.",
  explanation: "Training doesn’t stop once a course is finished. The GPhC expects the whole team — not just pharmacists and technicians — to keep their knowledge and skills up to date, with time set aside for learning.",
  satisfies: "Every member of staff takes part in ongoing learning relevant to their role — for example e-learning, training on new services or SOPs, and team briefings — with protected time where possible, and it is recorded. Development needs are discussed (e.g. at appraisals).\n\nGood evidence: the team’s training / CPD log for the last year, uploaded yearly.",
  references: TRN_REFS
};

// Revalidation: derived from the professional registration rows (see the compliance
// page loader) — nothing to set up; shown as automatic "professional registrations".
GUIDANCE["reg-revalidation"] = {
  task: false,
  sources: ["na"],
  auto: true,
  noTaskReason: "Nothing to send: this comes from the professional registrations collected from every pharmacist and technician.",
  onlyReason: "Comes automatically from the professional registration rows — the GPhC only renews a registration once that year’s revalidation is submitted.",
  explanation: "Pharmacists and pharmacy technicians must submit revalidation records every year — CPD, a peer discussion and a reflective account — to stay on the register.",
  satisfies: "Every pharmacist and pharmacy technician working at the pharmacy has submitted their revalidation records for the year. Because the GPhC renews a registration only once those records are submitted, a current registration shows it has been done.\n\nGood evidence: a current professional registration for every pharmacist and technician, collected under the registration items.",
  references: [{
    label: "GPhC — Standards for registered pharmacies",
    url: GPHC_STANDARDS
  }]
};

// -----------------------------------------------------------------------------
// NHS SERVICES & ASSURANCE (2026-10). A yearly confirmation task to the branch
// manager for each (no periodic tasks); not applicable where the branch
// doesn’t provide the service (or isn’t a distance-selling pharmacy).
// -----------------------------------------------------------------------------
const NHS_REFS = [{
  label: "Community Pharmacy England — services and contract",
  url: "https://cpe.org.uk/"
}, {
  label: "GPhC — Standards for registered pharmacies",
  url: GPHC_STANDARDS
}];
const confirmItem = ({
  who = "the branch manager",
  na = true,
  naPlaceholder,
  ...rest
}) => ({
  pharmace: false,
  defaultEvidence: "confirm",
  evidenceOnly: "confirm",
  sources: na ? ["task", "na"] : ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t hold data for this; the branch manager confirms it each year.",
  onlyReason: `Once a year ${who} confirms this in the staff app.${na ? " Branches it doesn’t apply to are marked not applicable." : ""}`,
  naPlaceholder,
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  references: NHS_REFS,
  ...rest
});
GUIDANCE["adv-pf"] = confirmItem({
  naPlaceholder: "e.g. This branch is not signed up to Pharmacy First.",
  instructions: "Please confirm that, for Pharmacy First: the pharmacy is signed up on MYS; there is a consultation room in use; an otoscope is available for the ear infection pathway; every pharmacist providing the service has read and signed the current PGDs and clinical protocol and completed the required training / self-declaration; the IT system used to record consultations (and send notifications to the GP) is working; and the NHS website profile lists the service. Confirm only when all of this is true.",
  explanation: "Pharmacy First lets pharmacists treat seven common conditions and supply medicines under PGDs. To provide it, the pharmacy must meet the service requirements — premises, equipment, signed PGDs, trained pharmacists and the right IT.",
  satisfies: "Where the pharmacy provides Pharmacy First: signed up on MYS; a consultation room; an otoscope; every pharmacist providing it has signed the current PGDs and clinical protocol and completed the training / self-declaration; consultations are recorded on an approved IT system and the GP is notified; and the service is shown on the NHS website profile.\n\nGood evidence: a yearly confirmation by the branch manager. Not applicable where the branch isn’t signed up."
});
GUIDANCE["sup-frp"] = confirmItem({
  naPlaceholder: "e.g. This branch is not signed up to Pharmacy First.",
  instructions: "Please confirm that Pharmacy First consultations at this pharmacy follow the national clinical pathways and the PGDs — including the gateway points and when to refer — that every consultation is recorded on the clinical IT system with the GP notified, and that the pharmacists providing the service know the current versions. Confirm only when this is true.",
  explanation: "Each Pharmacy First condition has a national clinical pathway with ‘gateway points’ that decide whether a medicine can be supplied or the patient must be referred. Following them exactly keeps patients safe and keeps the supply within the PGD.",
  satisfies: "Every Pharmacy First consultation follows the national clinical pathway and PGD for that condition, including the gateway points and referral criteria; it is recorded on the clinical IT system and the GP is notified; and pharmacists use the current versions of the pathways and PGDs.\n\nGood evidence: a yearly confirmation by the branch manager. Not applicable where the branch isn’t signed up."
});
GUIDANCE["adv-dms"] = confirmItem({
  naPlaceholder: "e.g. This branch does not receive DMS referrals.",
  instructions: "Please confirm that Discharge Medicines Service referrals are checked for every working day (on the referral system or NHSmail), that each one is actioned as the service specification says — checking the discharge medicines against the first prescription after discharge, and the follow-up consultation with the patient — and that every stage is recorded. Confirm only when this is true.",
  explanation: "When a patient is discharged from hospital with changes to their medicines, the hospital can refer them to the pharmacy. Acting on the referral quickly prevents errors when their next prescription arrives.",
  satisfies: "Discharge Medicines Service referrals are checked every working day and actioned in three stages: the discharge information is reviewed when received, the first new prescription is checked against it (and differences resolved with the GP), and a consultation with the patient is offered — each stage recorded.\n\nGood evidence: a yearly confirmation by the branch manager. Not applicable where the branch doesn’t receive referrals."
});
GUIDANCE["cpaf-questionnaire"] = confirmItem({
  na: false,
  instructions: "Please confirm that the Community Pharmacy Assurance Framework (CPAF) questionnaire has been completed on time whenever NHS England asked for it (the screening questionnaire, and the full questionnaire if selected), and that any actions from it have been followed up. Confirm only when this is true.",
  explanation: "NHS England uses the CPAF questionnaire to check that pharmacies meet their contract. When it is asked for, it must be completed on time; not doing so can lead to a visit or contract action.",
  satisfies: "Whenever NHS England asks for it, the CPAF screening questionnaire — and the full questionnaire if the pharmacy is selected — is completed accurately by the deadline, and any actions are followed up.\n\nGood evidence: a yearly confirmation by the branch manager."
});
GUIDANCE["cpaf-gateway"] = confirmItem({
  na: false,
  instructions: "Please confirm that the pharmacy keeps the evidence NHS England may sample under the assurance framework and quality criteria — for example SOPs, audit results, training records, the practice leaflet, and service records — and could produce it if asked. Confirm only when this is true.",
  explanation: "As part of assurance, NHS England may ask a pharmacy to show the evidence behind what it has declared. Keeping it ready avoids problems if the pharmacy is sampled.",
  satisfies: "The evidence behind the pharmacy’s CPAF answers and quality declarations is kept and can be produced on request.\n\nGood evidence: a yearly confirmation by the branch manager."
});
GUIDANCE["cpaf-pages"] = confirmItem({
  na: false,
  instructions: "Please check the pharmacy’s NHS website profile and Directory of Services (DoS) entry (via NHS Profile Manager) and confirm that the opening hours, contact details and services listed are correct — including bank holiday hours. Update anything that is wrong before confirming.",
  explanation: "Patients, NHS 111 and other services use the NHS website and the Directory of Services to find the pharmacy. Wrong opening hours or services send patients to a closed pharmacy or the wrong place.",
  satisfies: "The pharmacy’s NHS website profile and Directory of Services entry show the correct address, contact details, opening hours (including bank holidays) and services, and are updated whenever they change.\n\nGood evidence: a yearly confirmation by the branch manager that the profile has been checked."
});
GUIDANCE["sup-distance"] = confirmItem({
  // Also evidenced by the SOP(s) covering distance selling, linked from Pharmace's SOP module.
  sop: true,
  sources: ["task", "sop", "na"],
  onlyReason: "Once a year the branch manager confirms this in the staff app — or link the SOP(s) that cover distance selling from Pharmace’s SOP module. Branches that don’t provide services at a distance are marked not applicable.",
  naPlaceholder: "e.g. This branch does not provide services at a distance.",
  instructions: "Please confirm that the pharmacy’s online and distance services meet the GPhC guidance for pharmacies providing services at a distance, including on the internet: the risks have been assessed; higher-risk medicines (e.g. those liable to misuse, needing monitoring, or for weight loss) are only supplied after the extra safeguards — identity checks, verifying information with the GP, and a prescriber who can see the patient’s records; and the website shows the professional registration details. Confirm only when this is true.",
  explanation: "Supplying medicines without meeting the patient face to face carries extra risks. The GPhC expects distance-selling pharmacies to put specific safeguards in place, especially for higher-risk medicines.",
  satisfies: "Where the pharmacy provides services at a distance: a risk assessment of those services; safeguards for higher-risk medicines (identity verification, sharing and checking information with the patient’s GP, prescribing only with access to relevant records, and limits on quantities); and the website showing the pharmacy’s professional registration and contact details.\n\nGood evidence: a yearly confirmation by the branch manager. Not applicable to branches that don’t provide services at a distance.",
  references: [{
    label: "GPhC — Standards and guidance for registered pharmacies (includes the distance-selling guidance)",
    url: GPHC_STANDARDS
  }]
});

// -----------------------------------------------------------------------------
// PREMISES TEMPERATURE — two parts (see TEMP_PARTS). No GPhC rule fixes a
// method; the standards require medicines to be stored as the manufacturer
// says (most "do not store above 25°C") and premises to be suitable, and the
// Workplace Regulations require a reasonable temperature (normally ≥16°C).
// Good practice (NHS medicines-storage SOPs): a min/max thermometer read and
// reset every working day, with action when it goes over 25°C. A twice-yearly
// photo of a strip only shows one moment, so the records are asked for monthly.
// -----------------------------------------------------------------------------
const TEMP_REFS = [{
  label: "GPhC — Standards for registered pharmacies (Principle 3 premises; 4.3 medicines stored appropriately)",
  url: GPHC_STANDARDS
}, {
  label: "Workplace (Health, Safety and Welfare) Regulations 1992 — regulation 7 (temperature)",
  url: "https://www.legislation.gov.uk/uksi/1992/3004/regulation/7"
}, {
  label: "Example NHS SOP — clinical room temperature monitoring (daily min/max, 25°C)",
  url: "https://www.elft.nhs.uk/sites/default/files/2022-01/sop_fridge_and_clinical_room_temperature_monitoring_for_safe_storage_of_medicine_5.0.pdf"
}];
GUIDANCE["temp--thermometer"] = {
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record room temperatures; a photo shows the thermometer is in place.",
  onlyReason: "A named person at each branch photographs the min/max thermometer (or data logger) once a year.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take a photo of the min/max thermometer (or data logger) in the dispensary, with the display readable. If medicines are also kept in a separate stock room, photograph the one there too.",
  explanation: "To know medicines are kept below 25°C, the pharmacy needs a thermometer that records the highest and lowest temperature since it was last reset — a single reading or a strip only shows the temperature at that moment.",
  satisfies: "A digital min/max thermometer or data logger in the dispensary — and in any separate room where medicines are stored — placed away from direct sunlight, radiators and the fridge vent.\n\nGood evidence: a yearly photo of each thermometer in place with its display readable.",
  references: TEMP_REFS
};
GUIDANCE["temp--records"] = {
  // Task (monthly photo), held in another system (e.g. a data logger's portal), or not applicable.
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task", "link", "na"],
  fallbackDefault: "task",
  forceValidity: true,
  linkPlaceholder: "e.g. Temperatures recorded on our data logger’s online portal",
  naPlaceholder: "e.g. No medicines are stored at this site.",
  noPharmaceReason: "Pharmace doesn’t record room temperatures; a monthly photo of the record shows they are being taken and kept in range.",
  onlyReason: "Every month a named person photographs the month’s room-temperature record (or the data logger’s summary) — or note where the records are held (e.g. a data logger’s portal). Sites it doesn’t apply to are marked not applicable.",
  validity: {
    mode: "days",
    days: 31,
    warnDays: 3
  },
  instructions: "Take a photo of this month’s room-temperature record (or the data logger’s summary), with the readings legible. The record should show the current, highest and lowest temperature each working day and that the thermometer was reset. If any reading was above 25°C (or below 16°C), make sure the record says what was done.",
  explanation: "Most medicines must not be stored above 25°C, and staff need a reasonable working temperature (normally at least 16°C). Reading the thermometer each working day shows the temperature stays in range — especially in summer, when a single check twice a year would miss the hot spells.",
  satisfies: "The room temperature where medicines are kept is read every working day — current, highest and lowest since the last reading — the thermometer is reset, and the reading is written down. It stays at or below 25°C (and normally at least 16°C for staff). When it goes above 25°C, action is taken (cooling, moving stock, checking manufacturers’ advice for affected medicines) and recorded.\n\nGood evidence: a monthly photo of the temperature record or data-logger summary showing every working day filled in, readings within range, and actions written down for any that weren’t.\n\nSuggestion: no GPhC rule sets the method, but this is what NHS medicines-storage SOPs ask for. A twice-yearly photo of a strip thermometer only shows one moment and wouldn’t show a heatwave, so it isn’t enough on its own.",
  references: TEMP_REFS
};
GUIDANCE["env-consultation"] = {
  // Not from Pharmace: a once-a-year photo task to a named person at each branch.
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  fallbackDefault: "task",
  forceValidity: true,
  noPharmaceReason: "Only a photo shows the consultation room is in place and private, so this is a task to a named person rather than Pharmace data.",
  onlyReason: "A named person at each branch photographs the consultation room once a year.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take a photo of the consultation room showing it is available for use: a closed door (or screened area), seating, and nothing stored in it that stops it being used for private conversations.",
  satisfies: "A consultation room (or private area) is available for private conversations with patients and for NHS services: it is clearly signposted, has a closable door or screening so conversations can’t be overheard, has seating, and is kept clear rather than used as a store room.\n\nGood evidence: a photo taken once a year by a named person at the branch, showing the room in use as a consultation room.",
  references: [{
    label: "GPhC — Standards for registered pharmacies (Principle 3: premises)",
    url: GPHC_STANDARDS
  }]
};
GUIDANCE["fridge-log"] = {
  // Pharmace fridge monitoring where branches use it; elsewhere the branch is
  // emailed a link to upload its fridge log, analysed for the same figures.
  task: false,
  pharmaceName: "fridge monitoring",
  upload: "fridge",
  fallbackDefault: "upload",
  noTaskReason: "A staff-app task or photo can’t evidence a whole year of readings. Use Pharmace fridge monitoring, or email a key person a link to upload the fridge temperature log (or data logger export) the branch keeps.",
  validity: {
    mode: "days",
    days: 31,
    warnDays: 5
  },
  uploadAsk: "Please upload your fridge temperature log: the spreadsheet (or data logger export) where fridge readings are recorded — one row per reading, with the date, which fridge, the current / minimum / maximum temperature, who took it and any action taken. If you have data logger reports or fridge audits, add those too.",
  satisfies: "Every medicines fridge is checked and its minimum, maximum and current temperature recorded each day the pharmacy is open; readings stay between 2 °C and 8 °C, and any reading outside that range is acted on (rechecked, stock assessed, reported) and the action recorded.\n\nGood evidence: Pharmace fridge records, or the branch’s own fridge log or data logger export, uploaded and analysed for the same figures — the share of open days checked and the share of readings in range.",
  references: [{
    label: "GPhC — Standards for registered pharmacies (Principle 4: medicines management)",
    url: GPHC_STANDARDS
  }]
};
GUIDANCE["env-clean"] = {
  // Pharmace cleaning where branches use it; elsewhere the branch is emailed a
  // link to upload its own cleaning log, analysed for the same figures.
  task: false,
  pharmaceName: "cleaning",
  upload: "cleaning",
  fallbackDefault: "upload",
  noTaskReason: "A staff-app task or photo can’t evidence a whole cleaning record. Use Pharmace cleaning, or email a key person a link to upload the cleaning log the branch keeps.",
  validity: {
    mode: "days",
    days: 31,
    warnDays: 5
  },
  uploadAsk: "Please upload your cleaning log: the spreadsheet or rota where cleaning is recorded — one row per clean (date, area, who cleaned it, and how often it should be done), or a rota with the areas down the side and the dates across the top. If you have a cleaning schedule or audit reports, add those too.",
  satisfies: "Every area of the pharmacy is cleaned to a schedule (daily, weekly, monthly…), each clean is recorded with the date and who did it, and cleans are done on time — the premises are kept clean, safe and suitable for providing healthcare.\n\nGood evidence: Pharmace cleaning records, or the branch’s own cleaning log uploaded every month, showing at least 90% of cleans done on time over the last 12 months, no areas overdue, and nothing missing for whole months.",
  references: [{
    label: "GPhC — Standards for registered pharmacies (Principle 3: premises)",
    url: GPHC_STANDARDS
  }]
};
// Complaints procedure — the four parts (see COMPLAINT_PARTS).
const COMPLAINTS_REFS = [{
  label: "GPhC — Standards for registered pharmacies",
  url: GPHC_STANDARDS
}, {
  label: "Parliamentary and Health Service Ombudsman",
  url: "https://www.ombudsman.org.uk/"
}];
GUIDANCE["complaints--sop"] = {
  pharmace: false,
  task: false,
  sop: true,
  upload: "documents",
  noPharmaceReason: "No Pharmace module records the complaints procedure itself. Link the SOP held in Pharmace’s SOP module, or ask the branch to upload it.",
  noTaskReason: "For an SOP, use an email request to upload documents (several files at once) instead of a staff-app task.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload your current complaints procedure / SOP, and its signature sheet if you have one. You can attach several files.",
  satisfies: "A written complaints procedure (SOP) that says how patients can complain, how complaints are acknowledged, investigated and responded to, who is responsible, and how they can escalate (NHS England / Ombudsman). Staff have read it, and it is reviewed regularly.",
  references: COMPLAINTS_REFS
};
GUIDANCE["complaints--system"] = {
  task: false,
  pharmaceName: "patient feedback",
  fallbackDefault: "link",
  upload: "feedback",
  sources: ["link", "upload", "na"],
  noTaskReason: "Whether the feedback system is in use is read from Pharmace; a site that uses another system can link to it, or a key person can upload evidence of it.",
  uploadAsk: "Please upload evidence of the feedback and complaints system you use instead of Pharmace: an export of the feedback and complaints received (spreadsheet, or a shared link) and, in Part 2, screenshots or documents showing how patients can give feedback and complain (webpage, form, poster, procedure).",
  validity: {
    mode: "none"
  },
  satisfies: "Patients can easily give feedback and make a complaint. In Pharmace: the patient feedback system is in use at the branch (QR code / webpage receiving feedback), complaints are switched on, and a named complaint recipient is set so every complaint is acknowledged and followed up.",
  references: COMPLAINTS_REFS
};
GUIDANCE["complaints--poster"] = {
  pharmace: false,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  noPharmaceReason: "Pharmace can send the poster to the branch, but only a photo shows it is actually on display.",
  onlyReason: "The only acceptable evidence is a photo of the poster on display, taken by a team member in the staff app.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Take a photo of the complaints / patient feedback poster where patients can see it, with the QR code and contact details readable.",
  satisfies: "A notice telling patients how to give feedback or complain — the Pharmace feedback poster with its QR code, or the pharmacy’s own complaints notice — is displayed where patients can see it.",
  references: COMPLAINTS_REFS
};
GUIDANCE["complaints--records"] = {
  task: false,
  pharmaceName: "complaints recording",
  upload: "complaints",
  fallbackDefault: "upload",
  noTaskReason: "A staff-app task can’t evidence a complaints record. Use Pharmace’s complaints data, an upload request, a link, or mark it not applicable.",
  validity: {
    mode: "days",
    days: 90,
    warnDays: 7
  },
  uploadAsk: "Please upload your complaints log: the spreadsheet where complaints are recorded (date received, what it was about, how it was received, date closed / responded, outcome). If you have complaint reviews or annual complaints reports, add those too.",
  satisfies: "Every complaint is recorded with the date received, what it was about, the response and when it was closed; complaints are acknowledged promptly and resolved in good time, and the learning is acted on.\n\nGood evidence: Pharmace complaints data (number, time to close, each complaint and its outcome), or the branch’s complaints log uploaded regularly.",
  references: COMPLAINTS_REFS
};
// Logs uploaded in two parts and analysed.
const LOG_TEXT = {
  feedback: {
    many: "complaints",
    log: "feedback and complaints export",
    records: "feedback and complaints records",
    type: "Complaint type",
    drug: "Medicine",
    hint: "an export of the feedback and complaints received from the system they use",
    reports: "screenshots or documents showing how patients give feedback and complain (webpage, form, poster, procedure)",
    figures: <>the number of complaints, <b>how long each took to close</b>, <b>any still open after 28 days</b>, the commonest types and the trend</>,
    cols: "date received, what it was about, date closed, outcome"
  },
  complaints: {
    many: "complaints",
    log: "complaints log",
    records: "complaints records",
    type: "Complaint type",
    drug: "Medicine",
    hint: "the spreadsheet(s) where complaints are recorded",
    reports: "complaint reviews or annual complaints reports",
    figures: <>the number of complaints, <b>how long each took to close</b> and the % closed within 28 days, <b>any still open after 28 days</b>, the commonest types and the trend</>,
    cols: "date received, what it was about, how it was received, date closed, outcome"
  },
  "near-miss": {
    many: "near misses",
    log: "near-miss log",
    records: "near-miss records",
    type: "Error type",
    drug: "Drug",
    hint: "the spreadsheet(s) where near misses are written down",
    reports: "monthly reviews or trend reports their system exports",
    figures: <>near misses per month, the trend, the commonest error types and drugs, <b>months and spells with nothing recorded</b>, and how often a learning point is written down</>,
    cols: "date, type of error, drug, who spotted it, action/learning"
  },
  cleaning: {
    many: "cleans",
    log: "cleaning log",
    records: "cleaning records",
    type: "Area",
    drug: "Frequency",
    groups: false,
    hint: "the spreadsheet or rota where cleaning is recorded",
    reports: "the cleaning schedule, rota or cleaning audit reports",
    figures: <>the same figures as Pharmace’s cleaning module — <b>% of cleans done on time over 12 months</b>, <b>cleans missed</b> (omissions), cleans done <b>later than the area’s schedule</b> (out of range), <b>areas overdue now</b>, and months with nothing logged</>,
    cols: "date, area, who cleaned it, how often — or a rota with dates across the top"
  },
  lfpse: {
    many: "incidents",
    log: "patient safety incident log",
    records: "patient safety incident records",
    type: "Incident type",
    drug: "Medicine",
    hint: "the spreadsheet(s) where patient safety incidents are recorded",
    reports: "LFPSE submission confirmations or exports, incident reviews",
    figures: <>incidents per month and the trend, <b>how many were referred to LFPSE</b>, the degree of harm, <b>any with moderate harm or worse not referred</b>, the commonest incident types, and patterns such as a day of the week</>,
    cols: "date, type of incident, medicine, degree of harm, reported to LFPSE / reference, action taken"
  }
};
const LINK_HOSTS = "Google Sheets, OneDrive, SharePoint or Dropbox";
GUIDANCE["rp-log"].pharmaceName = "RP sign-in";
GUIDANCE["reg-pharmacist"] = {
  // Not Pharmace, not an email: a staff-app task to EVERY pharmacist at the
  // branch for a copy of their professional registration, read and checked by AI.
  pharmace: false,
  task: false,
  team: true,
  teamKind: "pharmacist",
  sources: ["team"],
  fallbackDefault: "team",
  noTaskReason: "Not a single-person task: every pharmacist at the branch gets their own.",
  noPharmaceReason: "Pharmace doesn’t hold each pharmacist’s registration details, so each pharmacist sends a copy of their own.",
  onlyReason: "A task goes to every pharmacist at the branch; each one attaches a copy of their own professional registration.",
  validity: {
    mode: "expiry-date",
    warnDays: 30
  },
  satisfies: "Every pharmacist must hold current registration with the regulator applicable to this pharmacy. Evidence must show the relevant register, name, number and current registration/renewal information. PSNI evidence requires head-office register verification; GPhC document checks retain their existing rules.",
  references: [{
    label: "GPhC — Standards for registered pharmacies (Principle 2: staff)",
    url: GPHC_STANDARDS
  }]
};
GUIDANCE["reg-technician"] = {
  // Same as pharmacists: a staff-app task to EVERY pharmacy technician at the
  // branch for a copy of their professional registration, read and checked by AI.
  pharmace: false,
  task: false,
  team: true,
  teamKind: "technician",
  sources: ["team"],
  fallbackDefault: "team",
  noTaskReason: "Not a single-person task: every pharmacy technician at the branch gets their own.",
  noPharmaceReason: "Pharmace doesn’t hold each technician’s registration details, so each pharmacy technician sends a copy of their own.",
  onlyReason: "A task goes to every pharmacy technician at the branch; each one attaches a copy of their own professional registration.",
  validity: {
    mode: "expiry-date",
    warnDays: 30
  },
  satisfies: "Every pharmacy technician working at the pharmacy is on the GPhC register, with a registration that is current (renewed each year), and the pharmacy keeps evidence of it.\n\nGood evidence: a copy of each technician’s registration confirmation from myGPhC or their entry on the GPhC online register, showing their name, registration number (7 digits starting with 5) and the expiry date — read and checked automatically, with the next request timed from that expiry date.",
  references: [{
    label: "GPhC — Standards for registered pharmacies (Principle 2: staff)",
    url: GPHC_STANDARDS
  }]
};
GUIDANCE["indemnity"] = {
  // Not recorded in Pharmace: a key person is emailed to upload the current
  // certificate; it lasts until the policy's expiry date.
  pharmace: false,
  task: false,
  upload: "documents",
  sources: ["upload", "link", "na"],
  fallbackDefault: "upload",
  noPharmaceReason: "Pharmace doesn’t record professional indemnity cover, so the evidence has to be the current certificate or schedule.",
  noTaskReason: "Not a staff-app task: a key person is emailed a secure link to upload the current indemnity certificate.",
  validity: {
    mode: "expiry-date",
    days: 365,
    warnDays: 30
  },
  uploadAsk: "Please upload the current professional indemnity insurance certificate (or policy schedule) for the pharmacy, showing the insurer, the cover and the policy expiry date.",
  satisfies: "Professional indemnity insurance is in force for the pharmacy and the pharmacists working there, at a level appropriate to the services provided, and renewed before it expires.\n\nGood evidence: the current certificate or policy schedule, showing the insurer, the cover and the expiry date. Head office records the expiry date when the document arrives, and the next request goes out automatically before then.",
  references: [{
    label: "GPhC — Standards for registered pharmacies",
    url: GPHC_STANDARDS
  }]
};

// -----------------------------------------------------------------------------
// SPEAK UP / WHISTLEBLOWING — four parts (see SPEAKUP_PARTS). GPhC standards
// for registered pharmacies 2.4 (openness, honesty, learning) and 2.5 (staff
// empowered to give feedback and raise concerns); NHS community pharmacies in
// England must also have a whistleblowing policy and a named Freedom to Speak
// Up Guardian (Terms of Service clinical governance; NHS England, Freedom to
// speak up in primary care). No real concern is ever uploaded here: Part 4
// is a confirmation by the person responsible, not the concern itself.
// -----------------------------------------------------------------------------
const SPEAKUP_REFS = {
  gphc: {
    label: "GPhC — Standards for registered pharmacies (2.4 openness, 2.5 raising concerns)",
    url: GPHC_STANDARDS
  },
  gphcWb: {
    label: "GPhC — Whistleblowing",
    url: "https://www.pharmacyregulation.org/patients-and-public/reporting-concerns/how-we-deal-concerns/guidance-pharmacy-professionals-facing-concern/whistleblowing"
  },
  cpe: {
    label: "Community Pharmacy England — Raising concerns (whistleblowing)",
    url: "https://cpe.org.uk/quality-and-regulations/clinical-governance/raising-concerns-whistle-blowing/"
  },
  template: {
    label: "Community Pharmacy England — template whistleblowing policy (Word)",
    url: "https://cpe.org.uk/wp-content/uploads/2017/09/Template-Community-Pharmacy-adapted-from-NHS-England-Guidance-FINAL.docx"
  },
  nhsePc: {
    label: "NHS England — Freedom to speak up in primary care (2016)",
    url: "https://www.england.nhs.uk/wp-content/uploads/2016/11/whistleblowing-guidance.pdf"
  },
  nhseNat: {
    label: "NHS England — national Freedom to Speak Up policy and guidance",
    url: "https://www.england.nhs.uk/ourwork/freedom-to-speak-up/developing-freedom-to-speak-up-arrangements-in-the-nhs/"
  },
  ngo: {
    label: "National Guardian’s Office",
    url: "https://nationalguardian.org.uk/"
  },
  pida: {
    label: "Public Interest Disclosure Act 1998",
    url: "https://www.legislation.gov.uk/ukpga/1998/23/contents"
  },
  prescribed: {
    label: "GOV.UK — Whistleblowing: list of prescribed people and bodies",
    url: "https://www.gov.uk/government/publications/blowing-the-whistle-list-of-prescribed-people-and-bodies--2"
  }
};
GUIDANCE["speakup--policy"] = {
  pharmace: false,
  task: false,
  sop: true,
  upload: "documents",
  sources: ["sop", "upload", "link"],
  fallbackDefault: "sop",
  noPharmaceReason: "No Pharmace module records the policy itself. Link it from Pharmace’s SOP module, or ask a key person to upload it.",
  noTaskReason: "For a policy, use an email request to upload documents (several files at once) instead of a staff-app task.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  explanation: "A written speak-up (whistleblowing) policy that tells every member of staff — including locums and temporary staff — how to raise a concern, who to go to, and what will happen next. It can be called a speak-up, raising-concerns or whistleblowing policy; a separate SOP isn’t needed if the policy covers the procedure.",
  uploadAsk: "Please upload your current speak-up / whistleblowing policy (and, if separate, the procedure for handling concerns). It should show who owns it, when it was approved and when it is next reviewed. You can attach several files.",
  satisfies: "A current, locally completed speak-up / whistleblowing policy, available to all staff and locums, that sets out:\n• who to raise a concern with first (a named person and working contact details);\n• an alternative if that person is involved — the Freedom to Speak Up Guardian for NHS pharmacies in England, or someone outside the line-management chain;\n• external routes: the GPhC, NHS England and other prescribed bodies, and the protection of the Public Interest Disclosure Act 1998;\n• a commitment that concerns are taken seriously, kept confidential where possible, and that nobody will suffer for raising one;\n• how concerns are acknowledged, investigated and acted on, how the person is kept informed, and how records are kept;\n• an owner, an approval date and a review date.\n\nGood evidence: the policy linked from Pharmace’s SOP module and in date, or a current copy uploaded. Suggestion: start from Community Pharmacy England’s template, adapted from NHS England’s guidance, and fill in your local names and contacts.",
  references: [SPEAKUP_REFS.gphc, SPEAKUP_REFS.cpe, SPEAKUP_REFS.template, SPEAKUP_REFS.nhsePc, SPEAKUP_REFS.pida, SPEAKUP_REFS.prescribed]
};
GUIDANCE["speakup--guardian"] = {
  // A staff-app task to the named person, always emailed with a thank-you,
  // what the role involves, background reading and the printable poster
  // naming them. They put the poster up and send a photo of it in the staff
  // room; that completes the task and accepts the role. Sent again every year.
  pharmace: false,
  task: true,
  defaultEvidence: "photo",
  evidenceOnly: "photo",
  sources: ["task"],
  fallbackDefault: "task",
  guardian: true,
  roleTitle: "Guardian",
  roleIcon: "🛡",
  posterNote: true,
  posterGuardian: true,
  replacesInstructions: ["This task is to confirm that you have been accepted in the role of Freedom to Speak Up Guardian. Please mark as confirmed should this be acceptable to you, if not please discuss with your manager.", "Thank you for agreeing to be the Freedom to Speak Up Guardian. To accept this role, acknowledge this task. Otherwise, please let your line manager know."],
  noPharmaceReason: "Pharmace doesn’t record who the Freedom to Speak Up Guardian is; the named person accepts the role and photographs the speak-up poster in the staff room.",
  onlyReason: "A task goes to the person named as Guardian at each branch, always with an email thanking them, explaining the role and attaching a printable poster naming them. They put the poster up and send a photo of it in the staff room. Asked again every year.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  instructions: "Thank you for being the Freedom to Speak Up Guardian for this pharmacy. Completing this task confirms you accept the role. If you don’t want to take it on, please tell your line manager instead.\n1. Print the speak-up poster attached to the email you have been sent (it names you as the Guardian). If the branch already has its own speak-up poster showing you as the Guardian, use that instead.\n2. Put the poster up in the staff room, where everyone — including locums — will see it.\n3. Take a photo of the staff room with the poster clearly visible, attach it to this task and mark it complete.",
  explanation: "NHS community pharmacies in England must name a Freedom to Speak Up Guardian: someone staff can go to who is independent of their line management and is not their direct employer. Staff also need to see who that is without searching for the policy, so the Guardian puts up a poster naming them in the staff room and sends a photo of it. Both are renewed every year.",
  satisfies: "A named Freedom to Speak Up Guardian who is independent of the line-management chain and is not the direct employer, who has accepted the role, and whose contact details staff can use in confidence — shown on a speak-up notice in the staff room where everyone, including locums, will see it. Suitable people include a regional manager or the superintendent in a larger business (when they are outside the person’s own line management), or a Guardian shared with another provider.\n\nGood evidence: the Guardian’s completed staff-app task with a photo of the staff room showing the speak-up poster that names them — renewed every year, and again whenever the Guardian changes. Their email address and phone number come from their staff record, so check those are up to date: they are printed on the poster. A poster on its own is not enough — Parts 1 and 3 are still needed.",
  references: [SPEAKUP_REFS.cpe, SPEAKUP_REFS.nhsePc, SPEAKUP_REFS.nhseNat, SPEAKUP_REFS.ngo, SPEAKUP_REFS.gphc]
};
GUIDANCE["speakup--briefed"] = {
  pharmace: false,
  task: false,
  sop: true,
  upload: "documents",
  sources: ["sop", "upload", "link"],
  fallbackDefault: "sop",
  noPharmaceReason: "Use Pharmace’s SOP module: link the speak-up policy and every member of staff it is assigned to must read and sign it.",
  noTaskReason: "Briefing records cover the whole team, so a key person uploads them, or Pharmace’s SOP read & signed records are used.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  explanation: "Having a policy isn’t enough: every member of staff, including locums and temporary staff, must know how to raise a concern — including when the concern is about their own manager.",
  sourceText: {
    sop: {
      title: "Pharmace SOP — read & signed by all staff",
      text: "Link the speak-up policy held in Pharmace’s SOP module. The branch is evidenced only when the SOP is in date AND every member of staff it is assigned to has read and signed it. Nobody is sent anything extra; the SOP module chases the signatures."
    }
  },
  uploadAsk: "Please upload your speak-up briefing records: the signed briefing or induction sheet showing that staff have been told about the speak-up policy and who to contact, and the locum / temporary-staff induction sheet that includes the speak-up route. You can attach several files.",
  satisfies: "Every member of staff has been briefed on the speak-up policy and local contacts — at induction, and again when the policy or contacts change — and can explain how to raise a concern, including one about their manager. Locums and temporary staff are told the route as part of their induction.\n\nGood evidence: Pharmace SOP records showing every assigned member of staff has read and signed the speak-up policy, or uploaded dated briefing / induction records including a locum induction sheet. Suggestions: ask a quick check question in a team meeting (“Who would you contact if your concern was about your manager?”) and talk through a scenario once a year. No particular paid course or certificate is required.",
  references: [SPEAKUP_REFS.nhsePc, SPEAKUP_REFS.gphc, SPEAKUP_REFS.cpe]
};
GUIDANCE["speakup--handling"] = {
  // Same model as the Guardian: one person per site accepts the role of
  // investigator / assessor of concerns; renewed every year. No concern is ever uploaded.
  pharmace: false,
  task: true,
  defaultEvidence: "confirm",
  evidenceOnly: "confirm",
  sources: ["task"],
  fallbackDefault: "task",
  guardian: true,
  roleTitle: "Investigator / assessor",
  roleShort: "Investigator",
  roleIcon: "🔎",
  noPharmaceReason: "Concerns are confidential and are not recorded in Pharmace; the named investigator / assessor confirms they accept the role.",
  onlyReason: "A task goes to the person chosen as investigator / assessor at each pharmacy. They acknowledge it to accept the role, or tell their line manager if they decline; it is asked again every year.",
  validity: {
    mode: "days",
    days: 365,
    warnDays: 30
  },
  replacesInstructions: ["Confirm that a named person is responsible for receiving concerns, that a confidential record of concerns is kept, and that every concern raised in the last three months (if any) has been acknowledged, looked into, acted on where needed, and the person who raised it kept informed. Don’t include any details of the concerns themselves."],
  instructions: "Thank you for agreeing to be the investigator / assessor for speak-up concerns at this pharmacy: receiving concerns, looking into them, keeping a confidential record and keeping the person who raised them informed. To accept this role, acknowledge this task. Otherwise, please let your line manager know.",
  explanation: "Every pharmacy needs a named person who receives concerns, looks into them, makes sure action is taken and keeps the person who spoke up informed — with a confidential record. The chosen person confirms each year that they accept the role. The concerns themselves are never brought into the platform.",
  satisfies: "A named investigator / assessor at each pharmacy who has accepted responsibility for receiving, investigating and following up concerns; a confidential record of each concern, the action taken, the outcome and the feedback given, kept securely and separately; and nobody who has raised a concern treated unfairly as a result.\n\nGood evidence: the named person’s acknowledgement in the staff app that they accept the role — renewed every year, and again whenever the person changes. A pharmacy where no concern has been raised still passes: the aim is to show it is ready to respond, not that cases exist. Suggestion: keep the confidential record outside the platform (for example with the superintendent) and review it for themes once a year, without names. Where possible choose someone other than the Guardian, so a concern about one of them still has a route.",
  references: [SPEAKUP_REFS.gphc, SPEAKUP_REFS.nhsePc, SPEAKUP_REFS.pida, SPEAKUP_REFS.gphcWb]
};

// -----------------------------------------------------------------------------
// DISTANCE SELLING PHARMACY (DSP) CRITERIA — shown only when at least one
// branch is flagged as a DSP (Sites button), and only in those branches'
// columns. Sources: GPhC guidance for services at a distance (Feb 2025) and
// the NHS (Pharmaceutical and Local Pharmaceutical Services) Regulations 2013
// terms of service for distance selling premises.
// -----------------------------------------------------------------------------
const GPHC_DIST = "https://assets.pharmacyregulation.org/files/2025-02/gphc-guidance-registered-pharmacies-providing-pharmacy-services-distance-february-2025.pdf";
const CPE_DSP = "https://cpe.org.uk/quality-and-regulations/terms-of-service/distance-selling-pharmacies";
const REG_64 = "https://www.legislation.gov.uk/uksi/2013/349/regulation/64";
const NHS_REGS = "https://www.legislation.gov.uk/uksi/2013/349/contents";
const REF_GPHC = {
  label: "GPhC — Guidance for registered pharmacies providing pharmacy services at a distance (Feb 2025)",
  url: GPHC_DIST
};
const REF_STD = {
  label: "GPhC — Standards for registered pharmacies",
  url: GPHC_STANDARDS
};
const REF_CPE = {
  label: "Community Pharmacy England — Distance selling pharmacies (terms of service)",
  url: CPE_DSP
};
const REF_REG64 = {
  label: "NHS (Pharmaceutical and Local Pharmaceutical Services) Regulations 2013 — regulation 64 (distance selling premises)",
  url: REG_64
};
const REF_REGS = {
  label: "NHS (Pharmaceutical and Local Pharmaceutical Services) Regulations 2013",
  url: NHS_REGS
};
const el = (id, label, requirement, help, link) => ({
  id,
  label,
  requirement,
  help,
  link
});
export const DSP_GROUPS = [{
  id: "dsp-gphc",
  name: "DSP criteria (GPhC)",
  subtitle: "Services at a distance",
  dsp: true,
  items: [{
    id: "dsp-governance",
    name: "Governance & risk",
    elements: [el("dsp-risk-assessment", "Distance-services risk assessment & risk register", "mandatory", "A risk assessment for every service, medicine and device supplied at a distance, with a risk register reviewed at least yearly and whenever processes change.", GPHC_DIST), el("dsp-audit", "Distance services audited, with reactive reviews", "mandatory", "Regular audits of SOPs, staffing, prescribing decisions, secure delivery, the website/app, data security, complaints and third parties — and a review after any incident, complaint, data breach or change in the law.", GPHC_DIST), el("dsp-agreements", "Written agreements with third parties", "mandatory", "Service-level agreements with prescribers, couriers and platforms; prescriber registration (and indemnity, if outside the UK) checked.", GPHC_DIST), el("dsp-accountability", "Accountable pharmacist named for each part of the service", "recommended", "Clear accountability for each step (e.g. hub-and-spoke, click-and-collect); external assurance where owner, superintendent, RP and prescriber overlap.", GPHC_DIST)]
  }, {
    id: "dsp-transparency",
    name: "Website & transparency",
    elements: [el("dsp-website", "Website shows the required information", "mandatory", "registration number, owner, superintendent, address, phone and email, how to check registration, how to complain, privacy policy — checked automatically by reading the website.", GPHC_DIST)]
  }, {
    id: "dsp-safety",
    name: "Patient safety",
    elements: [el("dsp-identity", "Identity checks appropriate to the medicine", "mandatory", "Identity verified for P and POM supplies (e.g. credit-reference or photo ID), more robustly for high-risk medicines.", GPHC_DIST), el("dsp-flags", "Automated checks for inappropriate requests", "mandatory", "Systems flag multiple orders to one address, multiple accounts, shared payment details, unsafe combinations, repeat infection requests and orders that are too large or frequent.", GPHC_DIST), el("dsp-high-risk", "High-risk medicines not supplied on a questionnaire alone", "mandatory", "Antimicrobials, misuse-prone, overdose-risk, monitored long-term, pregnancy-prevention, weight-loss and ▼ medicines: information independently verified, GP details and consent obtained; weight/BMI verified for weight-loss medicines.", GPHC_DIST), el("dsp-records", "Clinical and supply records kept", "mandatory", "Records of clinical decisions (including refusals and why), consent to the delivery method, dispatch dates, proof of delivery and complaints.", GPHC_DIST)]
  }, {
    id: "dsp-supply",
    name: "Supply & delivery",
    elements: [el("dsp-delivery", "Tracked delivery with proof of delivery", "mandatory", "Parcels tracked to the right person, delays monitored, carrier terms checked, couriers monitored.", GPHC_DIST), el("dsp-packaging", "Packaging tested for tamper-evidence, cold chain and CDs", "mandatory", "Packaging tested or audited for tamper-proofing and temperature control; suitability of delivery for fridge items and controlled drugs assessed.", GPHC_DIST)]
  }, {
    id: "dsp-staff-systems",
    name: "Staff & systems",
    elements: [el("dsp-training", "Staff trained for remote services", "recommended", "Training in non-face-to-face communication, information security and the systems used; prescribers' competence assured.", GPHC_DIST), el("dsp-continuity", "Business continuity plan covering IT failure", "mandatory", "A plan for IT/system outages, including how patients are told about delays; software robust, backed up and auditable.", GPHC_DIST)]
  }]
}, {
  id: "dsp-nhs",
  name: "DSP criteria (NHS)",
  subtitle: "Terms of service",
  dsp: true,
  items: [{
    id: "dsp-nhs-service",
    name: "Service provision",
    elements: [el("dsp-nhs-no-f2f", "No face-to-face Essential or Directed services at the premises", "mandatory", "Essential services, and since 1 October 2025 Advanced/Enhanced services, are never provided to anyone at or near the pharmacy; written procedures show how they are provided remotely (NHS England may ask for them).", CPE_DSP), el("dsp-nhs-england", "Uninterrupted service to anyone in England", "mandatory", "Essential services available to any person in England throughout opening hours — no area or patient-group limits, including for fridge items such as insulin.", CPE_DSP), el("dsp-nhs-prompt", "Prescriptions dispensed with reasonable promptness (incl. CDs)", "mandatory", "Every NHS prescription, including controlled drugs, dispensed and delivered with reasonable promptness.", CPE_DSP), el("dsp-nhs-free-delivery", "No charge for delivering NHS prescriptions", "mandatory", "NHS prescription delivery is free; NHS services cannot be charged for.", CPE_DSP)]
  }, {
    id: "dsp-nhs-comms",
    name: "Website & communication",
    elements: [el("dsp-nhs-hlp", "Website has an interactive healthy-living page", "mandatory", "An interactive healthy-lifestyle page, clearly promoted when people first visit the website (checked automatically).", CPE_DSP), el("dsp-nhs-consult", "Confidential telephone and live video consultations", "mandatory", "Arrangements for staff and patients to talk confidentially by telephone and by live video.", CPE_DSP), el("dsp-nhs-publicity", "Publicity doesn't limit the service to areas or groups", "mandatory", "Website and publicity must not suggest the NHS service is only for certain areas or patient groups (checked automatically).", CPE_DSP)]
  }]
}];
// Website-review checks behind each automatically reviewed row.
const WEB_ROW_CHECKS = {
  "dsp-website": ["gphcNumber", "owner", "superintendent", "address", "contact", "checkRegister", "complaints", "privacy"],
  "dsp-nhs-hlp": ["healthyLiving"],
  "dsp-nhs-publicity": ["restriction"]
};
export function webRowStatus(elementId, site, now = new Date()) {
  const keys = WEB_ROW_CHECKS[elementId] || [];
  if (!site?.websiteUrl) return {
    status: "missing",
    note: "No website address yet — add it under Sites"
  };
  const r = site.webReview;
  if (!r) return {
    status: "missing",
    note: "Website not reviewed yet"
  };
  if (r.error) return {
    status: "expired",
    note: `Website couldn’t be read: ${r.error}`,
    review: r
  };
  const checks = (r.checks || []).filter(c => keys.includes(c.key));
  const failed = checks.filter(c => !c.found);
  const ageDays = (now - new Date(r.reviewedAt)) / DAY;
  if (failed.length) return {
    status: "missing",
    note: `${elementId === "dsp-nhs-publicity" ? "Possible restriction wording found" : `Missing: ${failed.map(c => WEB_SHORT[c.key] || c.key).join(", ")}`} (${fmtDate(r.reviewedAt)})`,
    review: r,
    checks
  };
  if (ageDays > 35) return {
    status: "due-soon",
    note: `Passed on ${fmtDate(r.reviewedAt)} — review overdue`,
    review: r,
    checks
  };
  return {
    status: "current",
    note: `${checks.length} check${checks.length === 1 ? "" : "s"} passed · reviewed ${fmtDate(r.reviewedAt)}`,
    review: r,
    checks
  };
}
const WEB_SHORT = {
  gphcNumber: "registration number",
  owner: "owner",
  superintendent: "superintendent",
  address: "address",
  contact: "phone & email",
  checkRegister: "how to check registration",
  complaints: "how to complain",
  privacy: "privacy policy",
  healthyLiving: "healthy-living page",
  restriction: "restriction wording"
};
const dspGuide = (kind, {
  days = 365,
  warn = 30,
  ask = "",
  ev,
  instructions = "",
  satisfies,
  refs
}) => ({
  pharmace: false,
  noPharmaceReason: "No Pharmace module records this. Use the options below.",
  ...(kind === "web" ? {
    task: false,
    web: true,
    fallbackDefault: "web",
    noTaskReason: "Checked automatically by reading the website — no task needed."
  } : kind === "task" ? {
    defaultEvidence: ev || "document",
    instructions
  } : {
    task: false,
    upload: "documents",
    sop: kind === "sop",
    fallbackDefault: kind === "sop" ? "sop" : "upload",
    noTaskReason: "Use an email request to upload documents (several files at once) instead of a staff-app task."
  }),
  validity: kind === "web" ? {
    mode: "days",
    days: 31,
    warnDays: 3
  } : {
    mode: "days",
    days,
    warnDays: warn
  },
  uploadAsk: ask,
  satisfies,
  references: refs
});
Object.assign(GUIDANCE, {
  "dsp-risk-assessment": dspGuide("docs", {
    ask: "Please upload your current risk assessment for the services you provide at a distance, and your risk register (with the date it was last reviewed).",
    satisfies: "A written risk assessment for each service, medicine and medical device supplied at a distance — covering staffing and training, how you communicate with patients, business continuity, records and growth in scale — and a risk register that is kept up to date and reviewed at least once a year or whenever processes or systems change.",
    refs: [REF_GPHC, REF_STD]
  }),
  "dsp-audit": dspGuide("docs", {
    ask: "Please upload your most recent audit(s) of the distance services (and any reviews carried out after an incident, complaint or change).",
    satisfies: "Audits at appropriate intervals covering SOPs, staffing and training, communication, prescription systems, clinical decisions, secure delivery, the website or app, data security (including card payments), feedback and complaints, and third parties — with staff involved — plus a reactive review whenever problems, complaints, data breaches, legal or service changes occur.",
    refs: [REF_GPHC]
  }),
  "dsp-agreements": dspGuide("docs", {
    ask: "Please upload your current written agreements with third parties (prescribers / online doctor services, couriers, platform providers), and the checks made on prescribers' registration and indemnity.",
    satisfies: "Clear written service-level agreements with every third party involved, so roles are clear and patients know who to contact; prescribers' registration (and, for prescribers outside the UK, their right to prescribe for UK patients and indemnity) checked; no work with providers who avoid UK regulation.",
    refs: [REF_GPHC]
  }),
  "dsp-accountability": dspGuide("docs", {
    ask: "Please upload the document that shows which pharmacist is accountable for each part of the service.",
    satisfies: "A clear record of which pharmacist is accountable for each part of the service; where the owner, superintendent, responsible pharmacist and prescriber are the same person, external assurance (e.g. another clinician auditing safety) is considered.",
    refs: [REF_GPHC]
  }),
  "dsp-website": dspGuide("web", {
    satisfies: "The website prominently shows the pharmacy's professional registration number, the owner's and superintendent pharmacist's names, the pharmacy's physical address, phone and email, where medicines are dispensed if different, how to check registration, how to give feedback or raise concerns, and a privacy policy (plus prescriber details if online consultations are offered).\n\nGood evidence: the automatic website review — re-run on the 1st of every month — finds each item and quotes where it appears.",
    refs: [REF_GPHC]
  }),
  "dsp-identity": dspGuide("sop", {
    ask: "Please upload the SOP that describes how patients' identity is verified for P and POM supplies.",
    satisfies: "Identity checks appropriate to the medicine supplied (e.g. credit-reference checks or photo ID), with more robust checks for high-risk medicines; the SOP may refer to the Identity Verification and Authentication Standard for Digital Health and Care Services.",
    refs: [REF_GPHC]
  }),
  "dsp-flags": dspGuide("sop", {
    ask: "Please upload the SOP (or system description / screenshots) showing the automated checks used to flag inappropriate requests.",
    satisfies: "Automated systems flag multiple orders to the same address, multiple accounts used by one person, different people using the same payment details, inappropriate combinations, repeated requests for infection medicines, and requests that are too large or too frequent — and flagged orders are reviewed by a pharmacist.",
    refs: [REF_GPHC]
  }),
  "dsp-high-risk": dspGuide("sop", {
    ask: "Please upload the SOP for high-risk medicines (how information is independently verified, GP contact and consent, and BMI verification for weight-loss medicines).",
    satisfies: "Medicines not suitable for a questionnaire alone — antimicrobials, misuse-prone medicines (opioids, sedatives, laxatives, gabapentinoids, stimulants), overdose-risk medicines, monitored long-term condition medicines, pregnancy-prevention medicines, weight-loss medicines and ▼ medicines — are only supplied after independent verification, with the regular prescriber's details and consent to contact them; weight, height and/or BMI independently verified for weight management (a phone call is not enough).",
    refs: [REF_GPHC]
  }),
  "dsp-records": dspGuide("docs", {
    days: 180,
    ask: "Please upload a recent record sample or audit showing clinical decisions (including refusals), consent to delivery method, dispatch dates and proof of delivery.",
    satisfies: "Records of the accountable staff, advice given, clinical decisions with reasons (including refused sales), consent to the delivery method, dispatch dates, proof of successful delivery and complaints; relevant information shared with the person's GP.",
    refs: [REF_GPHC]
  }),
  "dsp-delivery": dspGuide("docs", {
    days: 180,
    ask: "Please upload evidence of tracked delivery and proof of delivery (e.g. courier tracking report or delivery audit for a recent month).",
    satisfies: "Every parcel is tracked so it reaches the right person, delays are monitored and acted on, carrier terms are checked, and couriers and other third parties are monitored.",
    refs: [REF_GPHC]
  }),
  "dsp-packaging": dspGuide("docs", {
    ask: "Please upload your latest packaging test / audit (tamper-evidence and temperature control for fridge lines) and how controlled drugs are packed and sent.",
    satisfies: "Packaging tested or audited for tamper-evidence and temperature control; the suitability and timescale of dispatch and delivery assessed for refrigerated medicines and controlled drugs.",
    refs: [REF_GPHC]
  }),
  "dsp-training": dspGuide("docs", {
    ask: "Please upload training records for staff covering remote communication, information security and the systems used.",
    satisfies: "Staff trained, where appropriate, in information security, communicating with patients who are not face to face, specialised equipment and new technology; employed prescribers' clinical competence assured.",
    refs: [REF_GPHC, REF_STD]
  }),
  "dsp-continuity": dspGuide("sop", {
    ask: "Please upload your business continuity plan, including what happens if IT systems fail and how patients are told about delays.",
    satisfies: "A business continuity plan that covers IT disruption and how patients are told about supply delays; software that is robust, regularly backed up and auditable; equipment of high specification, calibrated and serviced.",
    refs: [REF_GPHC]
  }),
  "dsp-nhs-no-f2f": dspGuide("sop", {
    ask: "Please upload the procedures showing how Essential (and Directed) services are provided without face-to-face contact at the premises.",
    satisfies: "Essential and Directed services are never provided to a person present at or in the vicinity of the pharmacy; since 1 October 2025 this includes Advanced and Enhanced services (COVID-19/flu vaccination exempt until 31 March 2026). Written procedures show how each service is provided remotely, and NHS England may ask to see them.",
    refs: [REF_CPE, REF_REG64, REF_REGS]
  }),
  "dsp-nhs-england": dspGuide("sop", {
    ask: "Please upload the procedure / policy showing Essential services are available to anyone in England throughout opening hours (including delivery of fridge items).",
    satisfies: "Procedures allow uninterrupted provision of Essential services during opening hours to anyone in England who asks — no restriction to an area, GP practice or patient group (e.g. no delivery radius that excludes insulin or other cold-chain items), and phone lines open for the whole of opening hours.",
    refs: [REF_CPE, REF_REGS]
  }),
  "dsp-nhs-prompt": dspGuide("task", {
    ev: "confirm",
    days: 90,
    warn: 7,
    instructions: "Confirm that every NHS prescription received in the last quarter — including controlled drugs — was dispensed and sent with reasonable promptness, and that none were refused.",
    satisfies: "Every NHS prescription is dispensed with reasonable promptness, including controlled drugs — a DSP may not refuse to dispense a Controlled Drug prescription.",
    refs: [REF_CPE, REF_REGS]
  }),
  "dsp-nhs-free-delivery": dspGuide("task", {
    ev: "confirm",
    instructions: "Confirm that no charge is made for delivering NHS prescriptions (on the website, at checkout or by phone).",
    satisfies: "Delivery of NHS prescriptions is free — NHS pharmaceutical services cannot be charged for unless legislation allows it.",
    refs: [REF_CPE]
  }),
  "dsp-nhs-hlp": dspGuide("web", {
    satisfies: "The pharmacy has a website for patients and the public with an interactive healthy-lifestyle page that is clearly promoted when people first visit (Healthy Living Pharmacy requirement for DSPs).\n\nGood evidence: the automatic website review finds the healthy-living page.",
    refs: [REF_CPE]
  }),
  "dsp-nhs-consult": dspGuide("task", {
    ev: "document",
    instructions: "Attach a screenshot or document showing the confidential telephone and live video consultation arrangements (the system used, and where calls are taken so they can't be overheard).",
    satisfies: "Arrangements are in place at the pharmacy that let staff and patients communicate confidentially by telephone or live audio, and by live video. A consultation room is optional.",
    refs: [REF_CPE]
  }),
  "dsp-nhs-publicity": dspGuide("web", {
    satisfies: "The website and other publicity do not suggest the NHS service is only available to people in particular areas of England or to particular groups.\n\nGood evidence: the automatic website review finds no restricting wording (any it finds is quoted).",
    refs: [REF_CPE]
  })
});

// -----------------------------------------------------------------------------
// PHARMACY QUALITY SCHEME 2026/27 (NHS England, community pharmacy). Shown for
// every branch; set a row to "Not applicable" for a branch not taking part.
// Declaration window: 9am 1 Feb – 11:59pm 26 Feb 2027 on Manage Your Service
// (MYS). Most criteria must be met by 31 Mar 2027. Evidence is kept for 3
// years for NHSBSA post-payment verification.
// -----------------------------------------------------------------------------
const PQS_CPE = "https://cpe.org.uk/quality-and-regulations/pharmacy-quality-scheme";
const PQS_NHSBSA = "https://www.nhsbsa.nhs.uk/sites/default/files/2026-06/PQS%2026_27%20Final%20Draft%201%20June1.pdf";
const REF_PQS = {
  label: "NHS England / NHSBSA — Pharmacy Quality Scheme guidance 2026/27",
  url: PQS_NHSBSA
};
const REF_PQS_CPE = {
  label: "Community Pharmacy England — Pharmacy Quality Scheme",
  url: PQS_CPE
};
const REF_PQS_WB = {
  label: "Community Pharmacy England — PQS workbook 2026/27",
  url: "https://cpe.org.uk/wp-content/uploads/2026/06/PQS-Workbook-2026-27.pdf"
};
const REF_PQS_CHK = {
  label: "Community Pharmacy England — PQS 2026/27 checklist",
  url: "https://cpe.org.uk/wp-content/uploads/2026/06/PQS-2026.27-checklist.pdf"
};
const REF_CPPE = {
  label: "CPPE — Fundamentals of respiratory therapeutics and the Asthma e-assessment",
  url: "https://www.cppe.ac.uk"
};
const PQS_REFS = [REF_PQS, REF_PQS_CPE, REF_PQS_WB, REF_PQS_CHK];
const PQS_KEEP = "\n\nKeep this evidence for 3 years: NHSBSA may ask for it in post-payment verification.";
export const PQS_GROUPS = [{
  id: "pqs",
  name: "PQS 2026/27",
  subtitle: "Pharmacy Quality Scheme · declare 1–26 Feb 2027",
  pqs: true,
  items: [{
    id: "pqs-gateway",
    name: "Gateway — palliative & end-of-life care",
    elements: [el("pqs-peolc-questionnaire", "PEoLC questionnaire completed on MYS — by 31 Dec 2026", "mandatory", "Gateway criterion: the palliative and end-of-life care questionnaire completed on the MYS portal by 11:59pm on 31 December 2026. Evidence: the MYS confirmation email.", PQS_CPE), el("pqs-profile-manager", "Profile Manager shows palliative care stockholder — by 31 Mar 2027", "mandatory", "Gateway criterion for pharmacies that hold the 16 palliative care medicines (and can support parenteral haloperidol): NHS Profile Manager updated to show this, with the account verified. Evidence: a screenshot of the update made on or after 2 June 2026. Not applicable if the pharmacy isn’t a stockholder.", PQS_CPE), el("pqs-peolc-action-plan", "PEoLC action plan in place — by 31 Mar 2027", "mandatory", "Gateway criterion for every pharmacy: a written palliative and end-of-life care action plan — local services, local stockholders and how to check the Directory of Services, where to get parenteral haloperidol, and other support for patients and carers. Kept at the pharmacy.", PQS_CPE)]
  }, {
    id: "pqs-domain1",
    name: "Domain 1 — Respiratory & urgent supply",
    elements: [el("pqs-asthma-training", "Pharmacists: CPPE asthma training & e-assessment — by 31 Mar 2027", "mandatory", "Every pharmacist working at the pharmacy on the day of declaration has completed CPPE Fundamentals of respiratory therapeutics (Unit 4, asthma) and passed the Asthma e-assessment between 19 Feb 2025 and 31 Mar 2027. Evidence: each certificate and a list of names and dates.", PQS_CPE), el("pqs-urgent-supply-sop", "Urgent repeat supply SOPs updated (time-critical medicines & CDs) — by 31 Mar 2027", "mandatory", "SOPs reviewed and updated for urgent supply of time-critical medicines and urgent requests for controlled drugs, and the team familiar with them.", PQS_CPE)]
  }, {
    id: "pqs-domain2",
    name: "Domain 2 — Pharmacy First clinical audit",
    elements: [el("pqs-pf-audit", "Pharmacy First clinical audit completed — by 31 Mar 2027", "mandatory", "A clinical audit of at least 10 Pharmacy First patients, an action plan, a peer discussion with a buddy pharmacist, the SOP updated where needed, and the anonymised data submitted on MYS.", PQS_CPE)]
  }, {
    id: "pqs-declaration",
    name: "Declaration",
    elements: [el("pqs-declare", "PQS declaration submitted on MYS — 1 to 26 Feb 2027", "mandatory", "The PQS declaration made on MYS between 9am on 1 February and 11:59pm on 26 February 2027, claiming only the criteria that are met (or will be by 31 March 2027). Evidence: the MYS confirmation email.", PQS_CPE)]
  }]
}];
const pqsGuide = (kind, {
  ask = "",
  instructions = "",
  explanation,
  satisfies,
  refs = PQS_REFS,
  days = 365,
  warn = 30,
  ev
}) => ({
  pharmace: false,
  forceValidity: true,
  noPharmaceReason: "Pharmace doesn’t record PQS evidence. Use the options below — set the row to “Not applicable” at a branch not taking part.",
  ...(kind === "task" ? {
    defaultEvidence: ev || "document",
    instructions
  } : {
    task: false,
    upload: "documents",
    sop: kind === "sop",
    fallbackDefault: kind === "sop" ? "sop" : "upload",
    noTaskReason: "Use an email request so the branch can upload documents (several files at once) instead of a staff-app task."
  }),
  validity: {
    mode: "days",
    days,
    warnDays: warn
  },
  uploadAsk: ask,
  explanation,
  satisfies: satisfies + PQS_KEEP,
  references: refs
});
Object.assign(GUIDANCE, {
  "pqs-peolc-questionnaire": pqsGuide("docs", {
    ask: "Please upload the confirmation email from the NHSBSA Manage Your Service (MYS) portal showing the PQS palliative and end-of-life care questionnaire has been submitted. Deadline: 31 December 2026.",
    explanation: "Gateway criterion — no PQS payment is made unless every gateway criterion is met. The questionnaire on MYS tells NHS England about each pharmacy’s palliative and end-of-life care arrangements.",
    satisfies: "The palliative and end-of-life care (PEoLC) questionnaire completed on the MYS portal by 11:59pm on 31 December 2026.\n\nGood evidence: the MYS confirmation email (or a screenshot of the submitted questionnaire) dated on or before 31 December 2026."
  }),
  "pqs-profile-manager": pqsGuide("docs", {
    ask: "If this pharmacy stocks the 16 palliative care medicines (and can support parenteral haloperidol), please upload a screenshot of NHS Profile Manager showing it is listed as a palliative care medication stockholder — the update must have been made on or after 2 June 2026. Deadline: 31 March 2027. If the pharmacy isn’t a stockholder, reply to say so.",
    explanation: "Gateway criterion for stockholding pharmacies. The NHS Directory of Services (fed by Profile Manager) is how NHS 111, GPs and palliative teams find a pharmacy that holds end-of-life medicines.",
    satisfies: "For pharmacies that hold all 16 medicines on the national palliative care formulary and can support access to parenteral haloperidol: the NHS Profile Manager entry updated to show the pharmacy as a palliative care medication stockholder, with the Profile Manager account verified, by 31 March 2027. Not required if the pharmacy isn’t a stockholder — set this branch to “Not applicable”.\n\nGood evidence: a screenshot of Profile Manager showing the stockholder flag, taken after an update made on or after 2 June 2026."
  }),
  "pqs-peolc-action-plan": pqsGuide("docs", {
    ask: "Please upload the pharmacy’s palliative and end-of-life care (PEoLC) action plan for the PQS. Deadline: 31 March 2027.",
    explanation: "Gateway criterion for every pharmacy. The action plan makes sure any member of the team can help a patient or carer get end-of-life medicines quickly, even if this pharmacy doesn’t stock them.",
    satisfies: "A written PEoLC action plan, kept at the pharmacy, covering: local palliative care services (including on-call and delivery arrangements); the local palliative care stockholding pharmacies and how to check them on the NHS Directory of Services; where parenteral haloperidol can be obtained; and other support available to patients and carers. The team know where it is.\n\nGood evidence: the completed action plan (the CPE workbook template is fine), dated, and reviewed by 31 March 2027."
  }),
  "pqs-asthma-training": pqsGuide("docs", {
    refs: [REF_PQS, REF_CPPE, REF_PQS_CPE, REF_PQS_WB],
    ask: "Please upload, for every pharmacist working here, their CPPE certificate for Fundamentals of respiratory therapeutics (Unit 4 — asthma) and the Asthma e-assessment, plus a list of the pharmacists’ names and the dates each was completed. For a new or returning pharmacist who hasn’t done it yet, upload their training plan. Deadline: 31 March 2027.",
    explanation: "Domain 1 criterion. Asthma is one of the most common long-term conditions seen in community pharmacy; this training supports better inhaler technique, spotting poor control and referring when needed.",
    satisfies: "Every pharmacist working at the pharmacy on the day the declaration is made has completed CPPE Fundamentals of respiratory therapeutics, Unit 4 (about 3 hours), and passed the Asthma e-assessment, both between 19 February 2025 and 31 March 2027. A pharmacist who is new or returning has a training plan and completes it within 30 days of the declaration or by 31 March 2027.\n\nGood evidence: each pharmacist’s CPPE certificate, and a table of names with the date each completed the training and the assessment."
  }),
  "pqs-urgent-supply-sop": pqsGuide("sop", {
    ask: "Please upload the pharmacy’s updated SOP(s) for urgent supply of repeat medicines — covering time-critical medicines and urgent requests for controlled drugs — with the date reviewed and the staff signature sheet. Deadline: 31 March 2027.",
    explanation: "Domain 1 criterion. Patients who run out of time-critical medicines (for example Parkinson’s medicines, insulin, anticoagulants, anti-epileptics) or controlled drugs need a clear, safe route to an urgent supply.",
    satisfies: "The SOP(s) for urgent supply have been reviewed and updated by 31 March 2027 to cover time-critical medicines and urgent requests for controlled drugs — including when an emergency supply is appropriate, using NHS Pharmacy First / the NHS 111 referral route, contacting the prescriber, and what to do when a CD can’t legally be supplied — and every member of the team is familiar with them.\n\nGood evidence: the SOP linked from Pharmace’s SOP module (in date, with staff read & signed), or the updated SOP and signature sheet uploaded by the branch. A note of the team review and examples of PMR interventions help."
  }),
  "pqs-declare": pqsGuide("docs", {
    days: 365,
    warn: 14,
    ask: "Please upload the confirmation email from the MYS portal showing the PQS 2026/27 declaration has been submitted. The declaration window is 9am 1 February to 11:59pm 26 February 2027.",
    explanation: "Payment depends on the declaration: the PQS claim is made on MYS in the declaration window, for the criteria the pharmacy meets (or will meet by 31 March 2027).",
    satisfies: "The PQS 2026/27 declaration submitted on MYS between 9am on 1 February and 11:59pm on 26 February 2027, claiming only criteria that are, or will be, met — with the gateway criteria met. Points: Band 2 pharmacies (1,801+ items a month) earn the full points per domain; Band 1 pharmacies earn 75%.\n\nGood evidence: the MYS confirmation email for the declaration."
  }),
  "pqsaudit--data": pqsGuide("docs", {
    ask: "Please upload the completed Pharmacy First clinical audit (at least 10 patients, with the audit start and end dates) and the confirmation email from MYS showing the anonymised audit data has been submitted. Deadline: 31 March 2027.",
    explanation: "Domain 2 criterion. The audit checks Pharmacy First consultations against the clinical pathways and protocols; the MYS submission is what NHS England checks, so the MYS email is essential.",
    satisfies: "A Pharmacy First clinical audit of at least 10 patients, with the start and end dates recorded, using the national audit template, and the anonymised data submitted through the MYS data-collection tool by 31 March 2027.\n\nGood evidence: the completed audit sheet and the MYS submission confirmation email."
  }),
  "pqsaudit--plan": pqsGuide("docs", {
    ask: "Please upload the action plan written from the Pharmacy First audit findings. Deadline: 31 March 2027.",
    explanation: "The audit only improves care if the findings lead to changes; the action plan records what the pharmacy will do differently.",
    satisfies: "A written action plan based on the audit findings — what was found, the actions to be taken, who is responsible and by when.\n\nGood evidence: the dated action plan."
  }),
  "pqsaudit--peer": pqsGuide("docs", {
    ask: "Please upload the record of the peer discussion about the Pharmacy First audit with your buddy pharmacist (date, who took part, what was discussed and agreed). Deadline: 31 March 2027.",
    explanation: "Reflecting on the audit with a pharmacist from another pharmacy (about 1–2 hours) helps share learning and test the action plan.",
    satisfies: "A peer discussion of the audit findings and action plan with a buddy pharmacist, lasting about 1–2 hours, recorded with the date, the people involved and the learning and actions agreed.\n\nGood evidence: the completed peer-discussion record (the CPE workbook template is fine)."
  }),
  "pqsaudit--sop": pqsGuide("sop", {
    ask: "If the Pharmacy First audit found the SOP needed changing, please upload the updated Pharmacy First SOP with the date reviewed and the staff signature sheet. If no change was needed, reply to say so. Deadline: 31 March 2027.",
    explanation: "Where the audit shows the way Pharmacy First is delivered needs to change, the SOP is updated so the whole team works the new way.",
    satisfies: "The Pharmacy First SOP reviewed in the light of the audit and updated where the findings require it, with the team briefed. If no change was needed, that is recorded in the action plan.\n\nGood evidence: the updated SOP linked from Pharmace’s SOP module (in date, read & signed), or uploaded by the branch — or the action plan showing no change was needed."
  })
});
GUIDANCE["pqs-pf-audit"] = {
  pharmace: false,
  explanation: "Domain 2 criterion, made of four parts. It is in place at a branch only when every part is.",
  satisfies: "A Pharmacy First clinical audit of at least 10 patients with an action plan, a peer discussion with a buddy pharmacist, the SOP updated where needed, and the anonymised data submitted on MYS — all by 31 March 2027." + PQS_KEEP,
  references: PQS_REFS
};
const sourceInfo = (k, guide) => guide?.sourceText?.[k] ? guide.sourceText[k] : k === "team" && guide?.teamKind === "technician" ? {
  title: "Task to every pharmacy technician at the branch",
  text: SOURCE_INFO.team.text.replace("New pharmacists", "New pharmacy technicians")
} : k === "upload" && guide?.upload === "documents" ? SOURCE_INFO.docs : k === "upload" && guide?.upload === "near-miss" ? SOURCE_INFO.nm : k === "upload" && guide?.upload === "lfpse" ? SOURCE_INFO.lf : k === "upload" && guide?.upload === "cleaning" ? SOURCE_INFO.cl : k === "upload" && guide?.upload === "complaints" ? SOURCE_INFO.cp : k === "upload" && guide?.upload === "feedback" ? SOURCE_INFO.fb : SOURCE_INFO[k];
const SOURCE_INFO = {
  sop: {
    title: "Pharmace SOPs",
    text: "Link this item to one or more SOPs held in Pharmace’s SOP module. Each branch is evidenced when it holds the SOP and it is in date; you can also see how many staff have read and signed it. Nobody is sent anything."
  },
  pharmace: {
    title: "Pharmace data detected",
    text: "The status comes straight from the records staff already keep in Pharmace, so nobody is asked to do anything extra. The grid shows it live."
  },
  nm: {
    title: "Email request to upload data",
    text: "A named person at the branch is emailed a secure link with two parts: (1) the near-miss log they keep — spreadsheet(s), or a shared link (Google Sheets, OneDrive, SharePoint, Dropbox) — and (2) any summary reports their system exports. The log is read automatically: numbers, trends and gaps."
  },
  cl: {
    title: "Email request to upload the cleaning log",
    text: "A named person at the branch is emailed a secure link (no staff-app task, no photo) with two parts: (1) the cleaning log or rota they keep — spreadsheet(s), a CSV/Excel export from another system, or a shared link — and (2) any cleaning schedule or audit reports. The log is read automatically for the same figures as Pharmace: on-time %, missed cleans, late cleans and overdue areas."
  },
  lf: {
    title: "Email request to upload data",
    text: "A named person at the branch is emailed a secure link with two parts: (1) the patient safety incident log they keep — spreadsheet(s), a CSV/Excel export from another system, or a shared link — and (2) any LFPSE confirmations or reports. The log is read automatically: numbers, patterns, harm and referrals to LFPSE."
  },
  cp: {
    title: "Email request to upload data",
    text: "A named person at the branch is emailed a secure link with two parts: (1) the complaints log they keep — spreadsheet(s), a CSV/Excel export, or a shared link — and (2) any complaint reviews or reports. It is asked for again at the renewal interval, and the log is read automatically: numbers, time to close, overdue complaints."
  },
  web: {
    title: "Automatic website review",
    text: "The pharmacy’s website is read automatically and checked for what the rules require, with the exact wording quoted. Re-checked on the 1st of every month; nobody is sent anything."
  },
  fb: {
    title: "Email request to upload",
    text: "A key person at the branch is emailed a secure link to upload evidence of the feedback and complaints system they use: an export of feedback and complaints (spreadsheet or shared link — read automatically for patterns and trends) and any screenshots or documents (several files at once)."
  },
  team: {
    title: "Task to every pharmacist at the branch",
    text: "Each person with the chosen role gets their own task in the staff app, and an email with their own secure upload link — they send a copy of their professional registration either way (whichever they do first closes the other). The AI reads it — name, registration number, expiry date — and the checks are done in code. The expiry date sets when that person’s next task goes out. New pharmacists get their first task automatically."
  },
  none: {
    title: "Leave as they are",
    text: "Those sites show “Pharmace not used” until they start recording it in Pharmace."
  },
  docs: {
    title: "Task + email with upload link",
    text: "A named person at each branch gets a task in the staff app AND an email with a secure upload link. They do one or the other: uploading through the link (several files at once — for example a signed SOP and its signature sheet) removes the task; completing the task with the document attached closes the link. Every file is kept."
  },
  upload: {
    title: "Email request to upload data",
    text: "A named person at each branch is emailed a secure link to upload the spreadsheet they keep (or paste a shared link — Google Sheets, OneDrive, SharePoint, Dropbox). No staff-app task. For the RP record the sheet is read automatically and shown just like Pharmace data."
  },
  task: {
    title: "Task + email to a team member",
    text: "A named person at each branch gets a task in the staff app and an email. Where a photo or document is needed, the email carries a secure upload link too: they do one or the other — uploading through the link removes the task, completing the task closes the link. Confirm-only items are simply completed in the app. When the evidence runs out, a new request goes automatically."
  },
  link: {
    title: "Held in another system",
    text: "The evidence is kept somewhere else — another system (e.g. a third-party CD register), a shared drive or a supplier portal. The grid shows where it is held, with a link if there is one; nobody is sent a task."
  },
  na: {
    title: "Not applicable",
    text: "This item does not apply to these branches (for example, a service you do not offer). The grid shows it as N/A and nobody is asked for anything."
  }
};
const RESPONSE_INFO = {
  confirm: {
    title: "Acknowledgement only",
    text: "The staff member checks it and completes the task. No photo needed."
  },
  photo: {
    title: "Photo required",
    text: "They must attach a photo when they complete the task."
  },
  document: {
    title: "Document required",
    text: "They must attach the document (or a clear photo of it)."
  }
};
const panel = {
  border: "1px solid #F0F0F0",
  borderRadius: 10,
  padding: 14,
  background: "#fff",
  overflowY: "auto",
  maxHeight: "64vh",
  minWidth: 0
};
const panelTitle = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "#5D5D5D",
  margin: "0 0 8px"
};
const help = {
  fontSize: 12,
  color: "#5D5D5D",
  margin: "4px 0 0",
  lineHeight: 1.45
};
const choice = (on, disabled) => ({
  display: "block",
  border: `1.5px solid ${on ? "#0E8CFF" : "#F0F0F0"}`,
  background: disabled ? "#FAFAFA" : on ? "#E6F7FF" : "#fff",
  borderRadius: 8,
  padding: "10px 12px",
  marginBottom: 8,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1
});

// Mirrors emailContent() in compliance_working_grid.js (first task).
function emailPreview({
  label,
  explanation,
  instructions,
  link,
  evidenceType
}) {
  const needsFile = evidenceType !== "confirm",
    word = evidenceType === "document" ? "document" : "photo";
  if (needsFile) return dualEmailPreview({
    label,
    todo: todoText(instructions, evidenceType),
    explanation,
    link,
    word
  });
  const topic = `Compliance check: ${label}`;
  const due = new Date(Date.now() + 7 * DAY).toLocaleDateString("en-GB");
  return [`Subject: ${topic}`, `Action needed in the Pharmace staff app`, `You have a new task in the Pharmace staff app, due by ${due}.`, `WHAT IS REQUIRED:\n${todoText(instructions, evidenceType)}`, `WHY: ${explanation || "This is part of the pharmacy’s GPhC / statutory compliance record."}`, `HOW TO COMPLETE IT:\n1. Open the Pharmace staff app.\n2. Open the task “${topic}”.\n3. ${needsFile ? `Do what is required (above), take the ${word}, attach it to the task and mark it complete.` : "Check what is required (above), then mark the task complete."}`, needsFile ? `Please don’t reply to this email with the ${word}: it can only be accepted through the staff app, and a task completed without one will be returned to you.` : "Please don’t reply to this email: the task can only be completed in the staff app.", link ? `More information: ${link}` : ""].filter(Boolean).join("\n\n");
}

// Green "?" at the top right of a settings panel; opens plain-English help.
function HelpButton({
  open,
  onClick,
  label
}) {
  return <button type="button" onClick={onClick} aria-label={label} aria-expanded={open} title={label} style={{
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: "none",
    background: open ? "#389E0D" : "#389E0D",
    color: "#fff",
    fontWeight: 800,
    fontSize: 14,
    lineHeight: "24px",
    padding: 0,
    cursor: "pointer",
    flexShrink: 0,
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
  }}>?</button>;
}
function HelpBox({
  children,
  onClose
}) {
  return <div role="note" style={{
    background: "#F6FFED",
    border: "1px solid #B7EB8F",
    borderRadius: 8,
    padding: "10px 12px",
    margin: "8px 0 10px",
    fontSize: 13,
    color: "#237804",
    lineHeight: 1.5
  }}>
    {children}
    <div style={{
      textAlign: "right",
      marginTop: 6
    }}><button type="button" onClick={onClose} style={{
        fontSize: 12
      }}>Close help</button></div>
  </div>;
}
const helpH = {
  fontWeight: 700,
  margin: "8px 0 2px"
};
const firstPara = t => String(t || "").split(/\n\s*\n/)[0].trim();
function TextOrEdit({
  value,
  onChange,
  rows = 4,
  placeholder,
  editing
}) {
  if (editing) return <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder} style={{
    width: "100%",
    fontFamily: "inherit",
    fontSize: 13
  }} />;
  return value ? <div style={{
    fontSize: 13,
    color: "#262626",
    whiteSpace: "pre-wrap",
    lineHeight: 1.5
  }}>{value}</div> : <div style={{
    fontSize: 13,
    color: "#8C8C8C",
    fontStyle: "italic"
  }}>{placeholder}</div>;
}
const defaultTeamRole = (kind, r) => kind === "technician" ? /technician/i.test(r) && !/student|trainee|apprentice/i.test(r) : /pharmacist/i.test(r) && !/technician|student|trainee|pre-?reg|foundation|assistant|dispenser|apprentice/i.test(r);
function TeamSettings({
  s,
  set,
  roles,
  kind
}) {
  const word = kind === "technician" ? "pharmacy technician" : "pharmacist";
  const chosen = s.teamRoles.length ? s.teamRoles : (roles || []).filter(r => defaultTeamRole(kind, r.roleName)).map(r => r.roleName);
  const after = Number(s.teamOffsetDays) >= 0;
  return <>
    <div style={panelTitle}>What happens</div>
    <div style={{
      fontSize: 13,
      lineHeight: 1.5
    }}>Every {word} at the branch gets <b>their own task in the staff app and an email with their own secure upload link</b>: send a copy of your professional registration — a screenshot or PDF from myGPhC, or of your entry on the online register. They do one or the other: uploading through the link removes the task, completing the task closes the link. A copy sent through the link is checked straight away, so if it can’t be accepted they are told why and can try again.</div>
    <div style={{
      fontSize: 13,
      lineHeight: 1.5,
      marginTop: 8,
      background: "#F6FFED",
      color: "#237804",
      padding: 8,
      borderRadius: 6
    }}>
      <b>Each copy is checked automatically.</b> The AI reads the name, registration number, register and expiry date; code then checks the name matches the staff member, the number is 7 digits starting with {kind === "technician" ? "5" : "2"}, and the registration hasn’t expired.
      <ul style={{
        margin: "4px 0 0",
        paddingLeft: 18
      }}><li><b>Confirmed</b> — the expiry date is recorded and sets the next task.</li><li><b>Not accepted</b> (not a registration, someone else’s, expired) — the task goes straight back to them with the reason.</li><li><b>Unclear</b> (e.g. no date legible) — head office checks it and enters the date.</li></ul></div>
    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>When the next task goes</div>
    <div style={{
      fontSize: 13
    }}>Send the next task{" "}
      <input type="number" min="0" max="365" value={Math.abs(Number(s.teamOffsetDays) || 0)} onChange={e => set({
        teamOffsetDays: (after ? 1 : -1) * Math.abs(Number(e.target.value) || 0)
      })} style={{
        width: 60
      }} /> days{" "}
      <select value={after ? "after" : "before"} onChange={e => set({
        teamOffsetDays: (e.target.value === "after" ? 1 : -1) * Math.abs(Number(s.teamOffsetDays) || 0)
      })}>
        <option value="after">after</option><option value="before">before</option></select> the expiry date on their registration</div>
    <div style={help}>{after ? "After the expiry date the cell shows amber (renewed registration awaited) until the task goes; a renewed copy then sets the next date." : "The task goes before the registration expires, so the renewed copy can be sent in time."}</div>
    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>Who receives it</div>
    {roles == null ? <div style={help}>Loading roles…</div> : <div style={{
      border: "1px solid #F0F0F0",
      borderRadius: 6,
      padding: 6,
      maxHeight: 160,
      overflowY: "auto"
    }}>
      {roles.map(r => {
        const on = chosen.includes(r.roleName);
        return <label key={r.roleName} style={{
          display: "block",
          fontSize: 13,
          padding: "2px 0"
        }}>
        <input type="checkbox" checked={on} onChange={e => set({
            teamRoles: e.target.checked ? [...chosen, r.roleName] : chosen.filter(x => x !== r.roleName)
          })} /> {r.roleName} <span style={{
            color: "#5D5D5D",
            fontSize: 11
          }}>({r.people} {r.people === 1 ? "person" : "people"})</span></label>;
      })}
      {!roles.length && <div style={help}>No roles found.</div>}</div>}
    <div style={help}>{s.teamRoles.length ? "Only the ticked roles receive a task." : `Ticked automatically: roles with “${word}” in the name (not students, trainees or dispensers). Change the ticks to choose exactly.`} New people given one of these roles get their first task automatically.</div>
    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>Email</div>
    <div style={help}>Sent automatically with every task — the email carries the person’s own upload link.</div>
  </>;
}

// Speak up, Part 2: choose the Guardian at each site from ALL its staff (any
// role); they get one task to accept the role, renewed every 365 days.
function GuardianSettings({
  s,
  set,
  token,
  branches,
  picks,
  setPicks,
  setPickNames,
  cellOf,
  label,
  explanation,
  roleTitle = "Guardian",
  posterGuardian = false,
  instructions = ""
}) {
  const [staffBy, setStaffBy] = useState({});
  useEffect(() => {
    let off = false;
    for (const b of branches) post(token, `${API_BASE}/api/headoffice/handover-tasks/staff`, {
      dbName: b.dbName
    }).then(d => {
      if (off) return;
      const list = (d.staff || []).slice().sort((x, y) => String(x.name).localeCompare(String(y.name)));
      setStaffBy(m => ({
        ...m,
        [b.dbName]: list
      }));
      setPickNames(m => ({
        ...m,
        ...Object.fromEntries(list.map(p => [`${b.dbName}|${p.staffId}`, p.name]))
      }));
    }).catch(() => {
      if (!off) setStaffBy(m => ({
        ...m,
        [b.dbName]: []
      }));
    });
    return () => {
      off = true;
    };
  }, [token, branches, setPickNames]);
  const stateOf = b => {
    const c = cellOf(b.dbName);
    if (!c?.recipientStaffId || picks[b.dbName] !== c.recipientStaffId) return picks[b.dbName] ? {
      t: "New — task sent when you save",
      col: "#0958D9"
    } : {
      t: "",
      col: "#5D5D5D"
    };
    if (c.evidence?.at && (c.status === "current" || c.status === "due-soon")) return {
      t: `✔ Accepted ${fmtDate(c.evidence.at)}`,
      col: "#389E0D"
    };
    if (c.task?.status === "open") return {
      t: `Awaiting acceptance · sent ${fmtDate(c.task.sentAt)}`,
      col: "#AD4E00"
    };
    return {
      t: "Not accepted yet",
      col: "#AD4E00"
    };
  };
  return <>
    <div style={panelTitle}>What happens</div>
    <div style={{
      fontSize: 13,
      lineHeight: 1.5
    }}>Choose <b>one person at each site</b> — anyone on that site’s staff list, whatever their role. They get a task in the staff app inviting them to accept the role. <b>Acknowledging the task is the evidence</b>: no photo is needed. If they decline, they tell their line manager, and you choose someone else. They are asked again <b>every 365 days</b>.</div>
    <div style={{
      ...help,
      marginTop: 6
    }}>If you change the person, the new person is asked to accept, and the previous acceptance no longer counts.</div>

    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>The task they receive</div>
    {posterGuardian ? <pre style={{
      whiteSpace: "pre-wrap",
      fontFamily: "inherit",
      fontSize: 13,
      background: "#FAFAFA",
      border: "1px solid #F0F0F0",
      borderRadius: 6,
      padding: 8,
      margin: 0
    }}>{instructions}</pre> : <textarea value={s.instructions} onChange={e => set({
      instructions: e.target.value
    })} rows={3} style={{
      width: "100%",
      fontFamily: "inherit",
      fontSize: 13
    }} />}
    <div style={help}>{posterGuardian ? "A photo of the staff room with the poster visible is required — a task completed without one is returned" : "No photo required"} · renewed every 365 days{Number(s.warnDays) ? ` (the new task goes ${s.warnDays} days before)` : ""}.</div>

    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>Choose the {roleTitle.toLowerCase()} at each site</div>
    <table style={{
      width: "100%",
      fontSize: 13,
      borderCollapse: "collapse"
    }}><tbody>
      {branches.map(b => {
          const list = staffBy[b.dbName];
          const st = stateOf(b);
          return <tr key={b.dbName} style={{
            borderTop: "1px solid #EDF2F7",
            verticalAlign: "top"
          }}>
          <td style={{
              padding: "6px 6px 6px 0",
              width: "34%",
              fontWeight: 600
            }}>{b.pharmacyName}</td>
          <td style={{
              padding: "6px 0"
            }}>
            <select value={picks[b.dbName] || ""} onChange={e => setPicks(m => ({
                ...m,
                [b.dbName]: e.target.value
              }))} style={{
                width: "100%"
              }} disabled={!list}>
              <option value="">{!list ? "Loading staff…" : list.length ? "— choose a person —" : "No staff found"}</option>
              {(list || []).map(p => <option key={p.staffId} value={p.staffId}>{p.name}{p.email ? ` (${p.email})` : " (no email)"}</option>)}
            </select>
            {st.t && <div style={{
                fontSize: 12,
                color: st.col,
                marginTop: 2
              }}>{st.t}</div>}
          </td></tr>;
        })}
    </tbody></table>

    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>Email</div>
    {posterGuardian ? <>
      <div style={{
        fontSize: 13,
        lineHeight: 1.5,
        background: "#E6F7FF",
        border: "1px solid #91D5FF",
        borderRadius: 6,
        padding: 8
      }}>
        Each Guardian is <b>always</b> emailed with the task: a thank-you, what the role involves, background reading, and a <b>printable A4 speak-up poster (PDF)</b> with the branch name at the top and the Guardian’s name, email and phone as the point of contact. They print it (unless the branch has its own), put it up in the staff room and photograph it in the staff app — or with the upload link in the email. One reminder goes if it becomes overdue.</div>
      <details style={{
        marginTop: 8
      }}>
        <summary style={{
          fontSize: 12,
          cursor: "pointer",
          color: "#0E8CFF"
        }}>Preview the email they receive</summary>
        <pre style={{
          whiteSpace: "pre-wrap",
          fontFamily: "inherit",
          fontSize: 12,
          background: "#FAFAFA",
          border: "1px solid #F0F0F0",
          borderRadius: 6,
          padding: 8,
          margin: "6px 0 0"
        }}>{guardianEmailPreview(branches[0]?.pharmacyName)}</pre>
      </details>
    </> : <label style={{
      display: "block",
      fontSize: 13
    }}><input type="checkbox" checked={s.emailReminders} onChange={e => set({
        emailReminders: e.target.checked
      })} /> Also email the task to them, plus one reminder if it becomes overdue</label>}
    <details style={{
      marginTop: 8
    }}>
      <summary style={{
        fontSize: 12,
        cursor: "pointer",
        color: "#0E8CFF"
      }}>Preview the task as they will see it</summary>
      <pre style={{
        whiteSpace: "pre-wrap",
        fontFamily: "inherit",
        fontSize: 12,
        background: "#FAFAFA",
        border: "1px solid #F0F0F0",
        borderRadius: 6,
        padding: 8,
        margin: "6px 0 0"
      }}>
        {taskPreview({
          label,
          explanation,
          instructions: posterGuardian ? instructions : s.instructions,
          evidenceType: posterGuardian ? "photo" : "confirm"
        })}</pre>
    </details>
  </>;
}
function TaskSettings({
  s,
  set,
  roles,
  label,
  explanation,
  link,
  only = null,
  poster = false
}) {
  const needsFile = s.evidenceType !== "confirm";
  return <>
    <div style={panelTitle}>What the staff member must provide</div>
    {only && <div style={{
      ...help,
      marginBottom: 6
    }}>{only === "confirm" ? "Only a confirmation is asked for — no photo or document." : `Only a ${only} is accepted for this item.`}</div>}
    {(only ? [only] : ["confirm", "photo", "document"]).map(k => <label key={k} style={choice(s.evidenceType === k)}>
        <input type="radio" checked={s.evidenceType === k} onChange={() => set({
        evidenceType: k
      })} /> <b>{RESPONSE_INFO[k].title}</b>
        <div style={help}>{RESPONSE_INFO[k].text}</div>
      </label>)}
    {needsFile && <div style={{
      ...help,
      background: "#FFF7E6",
      color: "#AD4E00",
      padding: 8,
      borderRadius: 6
    }}>
      If they complete the task without attaching one, it is not accepted: they automatically get a new task and an email asking for it.</div>}

    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>How long the evidence lasts</div>
    <label style={{
      display: "block",
      fontSize: 13,
      margin: "4px 0"
    }}><input type="radio" checked={s.mode === "none"} onChange={() => set({
        mode: "none"
      })} /> No expiry: once evidenced it stays current</label>
    <label style={{
      display: "block",
      fontSize: 13,
      margin: "4px 0"
    }}><input type="radio" checked={s.mode === "days"} onChange={() => set({
        mode: "days"
      })} /> Repeat every{" "}
      <input type="number" min="1" max="3650" value={s.days} disabled={s.mode !== "days"} onChange={e => set({
        days: e.target.value
      })} style={{
        width: 64
      }} /> days
      <span style={{
        color: "#5D5D5D"
      }}> (365 = yearly), counted from the day it is completed</span></label>
    <label style={{
      display: "block",
      fontSize: 13,
      margin: "4px 0"
    }}><input type="radio" checked={s.mode === "expiry-date"} onChange={() => set({
        mode: "expiry-date"
      })} /> Until an expiry date recorded with the evidence <span style={{
        color: "#5D5D5D"
      }}>(e.g. an insurance policy)</span></label>
    {s.mode !== "none" && <div style={{
      fontSize: 13,
      marginTop: 6
    }}>Send the renewal task{" "}
      <input type="number" min="0" max="365" value={s.warnDays} onChange={e => set({
        warnDays: e.target.value
      })} style={{
        width: 56
      }} /> days before it runs out</div>}

    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>Who receives it</div>
    <label style={{
      display: "block",
      fontSize: 13
    }}><input type="checkbox" checked={s.autoAssign} onChange={e => set({
        autoAssign: e.target.checked
      })} /> <b>Automatically assign a recipient</b> by role</label>
    {s.autoAssign ? <div style={{
      marginTop: 6
    }}>
      <select value={s.roleName} onChange={e => set({
        roleName: e.target.value
      })} style={{
        width: "100%"
      }}>
        <option value="">— choose a role —</option>
        {(roles || []).map(r => <option key={r.roleName} value={r.roleName}>{r.roleName} ({r.people} {r.people === 1 ? "person" : "people"} at {r.branches} branch{r.branches === 1 ? "" : "es"})</option>)}
      </select>
      <div style={help}>When you cascade, each branch gets the first active person with this role, or keeps its current recipient if they have it. Branches with nobody in the role are listed so you can choose someone.</div>
    </div> : <div style={help}>Choose the person at each branch by clicking its cell in the grid.</div>}

    {poster ? <>
      <div style={{
        ...panelTitle,
        marginTop: 16
      }}>Poster to print</div>
      <label style={{
        display: "block",
        fontSize: 13
      }}><input type="checkbox" checked={s.posterEmail} onChange={e => set({
          posterEmail: e.target.checked
        })} /> <b>Email a printable speak-up poster</b> to the same staff member with each task</label>
      <div style={help}>A one-page A4 PDF: the pharmacy name at the top, why speaking up matters and how to raise a concern in the middle, and the Freedom to Speak Up Guardian named in Part 2 — with their email address and phone number from staff records — as the point of contact. If there isn’t a notice in the staff area they print it, put it up, then photograph it for the task. You can also download it, or email it on its own, from any branch’s cell.</div>
      {!s.posterEmail && <div style={help}>Without the poster, the email (with the upload link) still goes — just without the PDF.</div>}
    </> : <>
      <div style={{
        ...panelTitle,
        marginTop: 16
      }}>Email</div>
      {needsFile ? <div style={{
        fontSize: 13,
        lineHeight: 1.5,
        background: "#E6F7FF",
        color: "#0958D9",
        padding: 8,
        borderRadius: 6
      }}>
        <b>Task + email, automatically.</b> With every task the staff member also gets an email with the same request and a secure upload link, plus one reminder if it becomes overdue. They do one or the other: uploading through the link removes the task; completing the task closes the link.</div> : <label style={{
        display: "block",
        fontSize: 13
      }}><input type="checkbox" checked={s.emailReminders} onChange={e => set({
          emailReminders: e.target.checked
        })} /> Email each task to the staff member, plus one reminder if it becomes overdue</label>}
      <div style={help}>Every email is recorded on the staff member’s file (Single staff → Messages). Staff with no email address on file get the task in the app only.</div>
    </>}

    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>What needs to be done</div>
    <textarea value={s.instructions} onChange={e => set({
      instructions: e.target.value
    })} rows={3} style={{
      width: "100%",
      fontFamily: "inherit",
      fontSize: 13
    }} placeholder="e.g. Photograph the RP notice so the name and registration number can be read." />
    <details style={{
      marginTop: 6
    }}>
      <summary style={{
        fontSize: 12,
        cursor: "pointer",
        color: "#0E8CFF"
      }}>Preview the task and email as the staff member will see them</summary>
      <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: "#5D5D5D",
        margin: "8px 0 2px"
      }}>TASK IN THE STAFF APP</div>
      <pre style={{
        whiteSpace: "pre-wrap",
        fontFamily: "inherit",
        fontSize: 12,
        background: "#FAFAFA",
        border: "1px solid #F0F0F0",
        borderRadius: 6,
        padding: 8,
        margin: 0
      }}>
        {taskPreview({
          label,
          explanation,
          instructions: s.instructions,
          link,
          evidenceType: s.evidenceType
        })}
        {needsFile ? "\nA task completed without a photo or document will be returned to you." : ""}</pre>
      {(needsFile || (poster ? s.posterEmail : s.emailReminders)) && <>
        <div style={{
          fontSize: 11,
          fontWeight: 700,
          color: "#5D5D5D",
          margin: "8px 0 2px"
        }}>EMAIL{needsFile ? " (with the upload link)" : ""}{poster && s.posterEmail ? " (with the poster PDF attached)" : ""}</div>
        {poster && s.posterEmail && <pre style={{
          whiteSpace: "pre-wrap",
          fontFamily: "inherit",
          fontSize: 12,
          background: "#E6F7FF",
          border: "1px solid #91D5FF",
          borderRadius: 6,
          padding: 8,
          margin: "0 0 4px"
        }}>A printable speak-up poster for your pharmacy is attached, naming the Freedom to Speak Up Guardian. If there isn’t a speak-up notice in the staff area, print it, put it up, then take the photo for this task.</pre>}
        <pre style={{
          whiteSpace: "pre-wrap",
          fontFamily: "inherit",
          fontSize: 12,
          background: "#E6F7FF",
          border: "1px solid #BAE7FF",
          borderRadius: 6,
          padding: 8,
          margin: 0
        }}>
          {emailPreview({
            label,
            explanation,
            instructions: s.instructions,
            link,
            evidenceType: s.evidenceType
          })}</pre>
      </>}
    </details>
  </>;
}
function uploadEmailPreview(label, ask) {
  const due = new Date(Date.now() + 7 * DAY).toLocaleDateString("en-GB");
  return [`Subject: Please upload: ${label}`, "Upload requested by head office", `Head office needs the ${label.toLowerCase()} for your pharmacy by ${due}.`, `WHAT TO SEND:\n${ask || "Upload the spreadsheet you keep for this record."}`, "HOW:\n1. Open the secure link below (it works on a phone or a computer).\n2. Attach the spreadsheet (Excel or CSV), or paste a link to a Google Sheets, OneDrive, SharePoint or Dropbox spreadsheet shared as “Anyone with the link”.\n3. Press Send. You will see straight away whether it could be read.", "Tip: if you keep the record in Google Sheets, OneDrive, SharePoint or Dropbox, send the link instead of a file. We will then re-read the sheet automatically on the 1st of every month, picking up new sessions and any earlier gaps you have since filled in — so you won’t need to upload again while the link keeps working.", "[ Upload the spreadsheet ]  ← secure link, valid 45 days", "You don’t need the staff app for this, and please don’t reply to this email with the file — use the link."].join("\n\n");
}
function nmEmailPreview(label, ask, kind = "near-miss") {
  const T = LOG_TEXT[kind];
  const due = new Date(Date.now() + 7 * DAY).toLocaleDateString("en-GB");
  return [`Subject: Please upload: ${label}`, "Upload requested by head office", `Head office needs the ${T.records} for your pharmacy by ${due}.`, `WHAT TO SEND:\n${ask}`, `PART 1 — the ${T.log} itself (required): the spreadsheet (Excel or CSV) — or paste a link to a ${LINK_HOSTS} spreadsheet shared as “Anyone with the link”. Several files are fine. If your system is a website, export the log as CSV or Excel first.\nPART 2 — summary reports (if you have any): ${T.reports} — PDF, Word or Excel. Leave this empty if you don’t have any.`, `[ Upload the ${T.records} ]  ← secure link, valid 45 days`, "You don’t need the staff app for this, and please don’t reply to this email with the file — use the link."].join("\n\n");
}
function RecipientPicker({
  s,
  set,
  roles
}) {
  return <>
    <label style={{
      display: "block",
      fontSize: 13
    }}><input type="checkbox" checked={s.autoAssign} onChange={e => set({
        autoAssign: e.target.checked
      })} /> <b>Automatically assign a recipient</b> by role</label>
    {s.autoAssign ? <select value={s.roleName} onChange={e => set({
      roleName: e.target.value
    })} style={{
      width: "100%",
      marginTop: 6
    }}>
      <option value="">— choose a role —</option>
      {(roles || []).map(r => <option key={r.roleName} value={r.roleName}>{r.roleName} ({r.people} {r.people === 1 ? "person" : "people"} at {r.branches} branch{r.branches === 1 ? "" : "es"})</option>)}
    </select> : <div style={help}>Choose the person at each branch by clicking its cell in the grid.</div>}
    <div style={help}>Every request and reminder is recorded on the staff member’s file (Single staff → Messages).</div>
  </>;
}
function UploadSettings({
  s,
  set,
  roles,
  label,
  kind
}) {
  if (LOG_TEXT[kind]) {
    const T = LOG_TEXT[kind];
    return <>
    <div style={panelTitle}>What happens</div>
    <div style={{
        fontSize: 13,
        lineHeight: 1.5
      }}>The person receives an <b>email with a secure link</b> (no staff-app task). The upload page has two parts:</div>
    <ol style={{
        fontSize: 13,
        lineHeight: 1.5,
        margin: "6px 0",
        paddingLeft: 20
      }}>
      <li><b>The {T.log}</b> (required) — {T.hint}, a CSV/Excel export from another system, or a shared link ({LINK_HOSTS}).</li>
      <li><b>Summary reports</b> (if any) — {T.reports} (PDF, Word, Excel…). Several files at once.</li>
    </ol>
    <div style={{
        fontSize: 13,
        lineHeight: 1.5,
        background: "#F6FFED",
        color: "#237804",
        padding: 8,
        borderRadius: 6
      }}>
      <b>The log is read automatically.</b> The AI only works out which column is which ({T.cols}){T.groups === false ? "" : " and groups the types"}; the figures are calculated: {T.figures}. Shown in the branch’s cell and on the Compliance view.</div>
    <div style={{
        fontSize: 13,
        lineHeight: 1.5,
        marginTop: 8,
        background: "#E6F7FF",
        color: "#0958D9",
        padding: 8,
        borderRadius: 6
      }}>
      <b>Shared links: re-read on the 1st of every month.</b> If the branch sends a link ({LINK_HOSTS}), it is read again automatically on the 1st of each month, so new entries — and any gaps filled in since — come through without another upload. Every upload is kept; entries that appear in more than one are counted once.</div>
    <div style={{
        ...help,
        background: "#FFF7E6",
        color: "#AD4E00",
        padding: 8,
        borderRadius: 6,
        marginTop: 8
      }}>Staff need an email address on file to receive the link. Head office can also upload for a branch from its cell.</div>
    <div style={{
        ...panelTitle,
        marginTop: 16
      }}>What to ask for</div>
    <textarea value={s.uploadAsk} onChange={e => set({
        uploadAsk: e.target.value
      })} rows={4} style={{
        width: "100%",
        fontFamily: "inherit",
        fontSize: 13
      }} />
    <div style={{
        ...panelTitle,
        marginTop: 16
      }}>How often</div>
    <label style={{
        display: "block",
        fontSize: 13,
        margin: "4px 0"
      }}><input type="radio" checked={s.mode === "none"} onChange={() => set({
          mode: "none"
        })} /> Once</label>
    <label style={{
        display: "block",
        fontSize: 13,
        margin: "4px 0"
      }}><input type="radio" checked={s.mode === "days"} onChange={() => set({
          mode: "days"
        })} /> Ask again every{" "}
      <input type="number" min="1" max="3650" value={s.days} disabled={s.mode !== "days"} onChange={e => set({
          days: e.target.value
        })} style={{
          width: 64
        }} /> days
      <span style={{
          color: "#5D5D5D"
        }}> (31 = monthly), counted from each upload of the log</span></label>
    {s.mode !== "none" && <div style={{
        fontSize: 13,
        marginTop: 6
      }}>Send the next request{" "}
      <input type="number" min="0" max="365" value={s.warnDays} onChange={e => set({
          warnDays: e.target.value
        })} style={{
          width: 56
        }} /> days before it is due</div>}
    <div style={{
        ...help
      }}>Each request tells them the date of the latest entry already held, so the next upload starts there with no gaps.</div>
    <div style={{
        ...panelTitle,
        marginTop: 16
      }}>Who receives it</div>
    <RecipientPicker s={s} set={set} roles={roles} />
    <details style={{
        marginTop: 10
      }}>
      <summary style={{
          fontSize: 12,
          cursor: "pointer",
          color: "#0E8CFF"
        }}>Preview the email</summary>
      <pre style={{
          whiteSpace: "pre-wrap",
          fontFamily: "inherit",
          fontSize: 12,
          background: "#E6F7FF",
          border: "1px solid #91D5FF",
          borderRadius: 6,
          padding: 8,
          margin: "6px 0 0"
        }}>{nmEmailPreview(label, s.uploadAsk, kind)}</pre>
    </details>
  </>;
  }
  if (kind === "documents") return <>
    <div style={panelTitle}>What happens</div>
    <div style={{
      fontSize: 13,
      lineHeight: 1.5
    }}>The person receives <b>a task in the staff app and an email with a secure upload link</b>, together. They do one or the other: uploading through the link removes the task, and completing the task (with the document attached) closes the link. On the upload page they attach the document(s) — <b>several files at once</b> if needed (PDF, Word, photos of a signed copy). Every file is kept and listed in the branch’s cell; the newest upload starts the renewal clock.</div>
    <div style={{
      ...help,
      background: "#FFF7E6",
      color: "#AD4E00",
      padding: 8,
      borderRadius: 6,
      marginTop: 8
    }}>Staff need an email address on file to receive the link. Head office can also upload files for a branch from its cell.</div>
    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>What to ask for</div>
    <textarea value={s.uploadAsk} onChange={e => set({
      uploadAsk: e.target.value
    })} rows={4} style={{
      width: "100%",
      fontFamily: "inherit",
      fontSize: 13
    }} />
    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>How often</div>
    <label style={{
      display: "block",
      fontSize: 13,
      margin: "4px 0"
    }}><input type="radio" checked={s.mode === "none"} onChange={() => set({
        mode: "none"
      })} /> Once</label>
    <label style={{
      display: "block",
      fontSize: 13,
      margin: "4px 0"
    }}><input type="radio" checked={s.mode === "days"} onChange={() => set({
        mode: "days"
      })} /> Ask again every{" "}
      <input type="number" min="1" max="3650" value={s.days} disabled={s.mode !== "days"} onChange={e => set({
        days: e.target.value
      })} style={{
        width: 64
      }} /> days
      <span style={{
        color: "#5D5D5D"
      }}> (365 = yearly review), counted from each upload</span></label>
    <label style={{
      display: "block",
      fontSize: 13,
      margin: "4px 0"
    }}><input type="radio" checked={s.mode === "expiry-date"} onChange={() => set({
        mode: "expiry-date"
      })} /> Until the expiry date on the document <span style={{
        color: "#5D5D5D"
      }}>(e.g. an insurance certificate — head office enters the date when it arrives)</span></label>
    {s.mode !== "none" && <div style={{
      fontSize: 13,
      marginTop: 6
    }}>Send the next request{" "}
      <input type="number" min="0" max="365" value={s.warnDays} onChange={e => set({
        warnDays: e.target.value
      })} style={{
        width: 56
      }} /> days before it is due</div>}
    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>Who receives it</div>
    <label style={{
      display: "block",
      fontSize: 13
    }}><input type="checkbox" checked={s.autoAssign} onChange={e => set({
        autoAssign: e.target.checked
      })} /> <b>Automatically assign a recipient</b> by role</label>
    {s.autoAssign ? <select value={s.roleName} onChange={e => set({
      roleName: e.target.value
    })} style={{
      width: "100%",
      marginTop: 6
    }}>
      <option value="">— choose a role —</option>
      {(roles || []).map(r => <option key={r.roleName} value={r.roleName}>{r.roleName} ({r.people} {r.people === 1 ? "person" : "people"} at {r.branches} branch{r.branches === 1 ? "" : "es"})</option>)}
    </select> : <div style={help}>Choose the person at each branch by clicking its cell in the grid.</div>}
    <div style={help}>Every request and reminder is recorded on the staff member’s file (Single staff → Messages).</div>
    <details style={{
      marginTop: 10
    }}>
      <summary style={{
        fontSize: 12,
        cursor: "pointer",
        color: "#0E8CFF"
      }}>Preview the email</summary>
      <pre style={{
        whiteSpace: "pre-wrap",
        fontFamily: "inherit",
        fontSize: 12,
        background: "#E6F7FF",
        border: "1px solid #91D5FF",
        borderRadius: 6,
        padding: 8,
        margin: "6px 0 0"
      }}>{docUploadEmailPreview(label, s.uploadAsk)}</pre>
    </details>
  </>;
  return <>
    <div style={panelTitle}>What happens</div>
    <div style={{
      fontSize: 13,
      lineHeight: 1.5
    }}>The person receives an <b>email with a secure link</b> (no staff-app task). They attach the spreadsheet or paste a shared link ({LINK_HOSTS}).
      {kind === "rp-signin" && <> The sheet is <b>read automatically</b>: the AI only works out which column is which, and the figures (trading days covered, sessions, signed in now) are calculated exactly as for Pharmace RP sign-in, then shown in the same panel on the Compliance view.</>}</div>
    <div style={{
      fontSize: 13,
      lineHeight: 1.5,
      marginTop: 8,
      background: "#E6F7FF",
      color: "#0958D9",
      padding: 8,
      borderRadius: 6
    }}>
      <b>Shared links: re-read on the 1st of every month.</b> If the branch sends a link ({LINK_HOSTS}) rather than a file, the sheet is read again automatically on the 1st of each month.
      New sessions are added, and any earlier gaps the branch has since filled in are picked up, so the record keeps itself up to date without another upload.
      Every upload and every re-read that finds changes is kept; sessions that appear more than once are counted once. If a re-read fails (for example the sheet is no longer shared), the cell shows it and the usual request goes out when it is due.</div>
    <div style={{
      ...help,
      background: "#FFF7E6",
      color: "#AD4E00",
      padding: 8,
      borderRadius: 6,
      marginTop: 8
    }}>Staff need an email address on file to receive the link. Head office can also upload a sheet for a branch from its cell.</div>

    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>What to ask for</div>
    <textarea value={s.uploadAsk} onChange={e => set({
      uploadAsk: e.target.value
    })} rows={4} style={{
      width: "100%",
      fontFamily: "inherit",
      fontSize: 13
    }} />

    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>How often</div>
    <label style={{
      display: "block",
      fontSize: 13,
      margin: "4px 0"
    }}><input type="radio" checked={s.mode === "none"} onChange={() => set({
        mode: "none"
      })} /> Once</label>
    <label style={{
      display: "block",
      fontSize: 13,
      margin: "4px 0"
    }}><input type="radio" checked={s.mode === "days"} onChange={() => set({
        mode: "days"
      })} /> Ask again every{" "}
      <input type="number" min="1" max="3650" value={s.days} disabled={s.mode !== "days"} onChange={e => set({
        days: e.target.value
      })} style={{
        width: 64
      }} /> days
      <span style={{
        color: "#5D5D5D"
      }}> (30 = monthly), counted from each upload</span></label>
    {s.mode !== "none" && <div style={{
      fontSize: 13,
      marginTop: 6
    }}>Send the next request{" "}
      <input type="number" min="0" max="365" value={s.warnDays} onChange={e => set({
        warnDays: e.target.value
      })} style={{
        width: 56
      }} /> days before it is due</div>}

    <div style={{
      ...panelTitle,
      marginTop: 16
    }}>Who receives it</div>
    <label style={{
      display: "block",
      fontSize: 13
    }}><input type="checkbox" checked={s.autoAssign} onChange={e => set({
        autoAssign: e.target.checked
      })} /> <b>Automatically assign a recipient</b> by role</label>
    {s.autoAssign ? <select value={s.roleName} onChange={e => set({
      roleName: e.target.value
    })} style={{
      width: "100%",
      marginTop: 6
    }}>
      <option value="">— choose a role —</option>
      {(roles || []).map(r => <option key={r.roleName} value={r.roleName}>{r.roleName} ({r.people} {r.people === 1 ? "person" : "people"} at {r.branches} branch{r.branches === 1 ? "" : "es"})</option>)}
    </select> : <div style={help}>Choose the person at each branch by clicking its cell in the grid.</div>}
    <div style={help}>Every request and reminder is recorded on the staff member’s file (Single staff → Messages).</div>

    <details style={{
      marginTop: 10
    }}>
      <summary style={{
        fontSize: 12,
        cursor: "pointer",
        color: "#0E8CFF"
      }}>Preview the email</summary>
      <pre style={{
        whiteSpace: "pre-wrap",
        fontFamily: "inherit",
        fontSize: 12,
        background: "#E6F7FF",
        border: "1px solid #91D5FF",
        borderRadius: 6,
        padding: 8,
        margin: "6px 0 0"
      }}>{uploadEmailPreview(label, s.uploadAsk)}</pre>
    </details>
  </>;
}
function RowDialog(props) {
  if (isSopCascade(props.row)) return <SopCascadeConfig {...props} />;
  if (isSopManagement(props.row)) return <SopManagementConfig {...props} />;
  return <StandardRowDialog {...props} />;
}
function StandardRowDialog({
  row,
  groups,
  token,
  branches = [],
  detect,
  sites = {},
  cellOf = () => null,
  onClose,
  onSaved
}) {
  branches = branches.filter(b => rowApplicability(row, locationOf(b, {
    sites
  })).applicable);
  const custom = !!row.custom;
  const det = detect ? detect(row) : {
    module: null,
    withData: []
  };
  const pharmaceAvailable = !!det.module && det.withData.length > 0;
  const guide = GUIDANCE[row.elementId] || {};
  // Pharmace is a tick box for the sites that record the item there; the
  // other sources are one choice for the remaining sites (or every site when
  // Pharmace isn't used).
  const others = dedupeDocs(guide.sources || [guide.web ? "web" : null, guide.sop ? "sop" : null, guide.task === false ? null : "task", guide.upload ? "upload" : null, "link", "na"].filter(Boolean), guide);
  const missing = branches.filter(b => !det.withData.includes(b.dbName));
  const firstChoice = guide.fallbackDefault && others.includes(guide.fallbackDefault) ? guide.fallbackDefault : guide.sop ? "sop" : guide.task === false && guide.upload ? "upload" : others[0];
  const savedOther = row.defaultSource && row.defaultSource !== "pharmace" && others.includes(row.defaultSource) ? row.defaultSource : row.defaultSource === "pharmace" && row.fallbackTask === false ? "none" : row.defaultSource === "pharmace" && others.includes(row.fallbackSource) ? row.fallbackSource : firstChoice;
  const [trainingAttachment, setTrainingAttachment] = useState(null);
  const [label, setLabel] = useState(row.label || "");
  const [groupId, setGroupId] = useState(row.groupId || groups[0]?.id);
  const [itemId, setItemId] = useState(row.itemId || "");
  const [usePharmace, setUsePharmace] = useState(!row.new && pharmaceAvailable && (!row.defaultSource || row.defaultSource === "pharmace" || !others.includes(row.defaultSource)));
  const [other, setOther] = useState(row.new ? "task" : savedOther);
  const source = usePharmace ? "pharmace" : other === "none" ? firstChoice : other; // the row's default
  // The non-Pharmace source whose settings are shown (null: none needed).
  const rest = usePharmace ? other === "none" ? null : other : source;
  const siteWord = n => `${n} site${n === 1 ? "" : "s"}`;
  const pName = guide.pharmaceName || det.module || "recording";
  const [about, setAbout] = useState({
    explanation: niGuidanceValue(row, "explanation", row.explanation) || guide.explanation || row.help || "",
    satisfies: niGuidanceValue(row, "satisfies", row.satisfies) || guide.satisfies || "",
    references: String(row.elementId || "").startsWith("ni-") ? [...(row.references || []), ...(guide.references || [])].filter((r, i, all) => all.findIndex(x => x.url === r.url) === i) : row.references && row.references.length ? row.references : guide.references || [row.link ? {
      label: "Guidance",
      url: row.link
    } : null, {
      label: "GPhC — Standards for registered pharmacies",
      url: GPHC_STANDARDS
    }].filter(Boolean)
  });
  const [editAbout, setEditAbout] = useState(custom && row.new);
  // Speak up Guardian: one person per site, chosen from all its staff.
  const [picks, setPicks] = useState(() => Object.fromEntries(branches.map(b => [b.dbName, cellOf(b.dbName)?.recipientStaffId || ""])));
  const [pickNames, setPickNames] = useState({});
  const [t, setT] = useState({
    evidenceType: guide.evidenceOnly || row.evidenceType || guide.defaultEvidence || "photo",
    mode: (guide.forceValidity ? guide.validity?.mode : row.validity?.mode) || guide.validity?.mode || "none",
    days: (guide.forceValidity ? guide.validity?.days : row.validity?.days) || guide.validity?.days || 365,
    warnDays: row.validity?.warnDays ?? guide.validity?.warnDays ?? 14,
    autoAssign: !!row.autoAssign?.enabled,
    roleName: row.autoAssign?.roleName || "",
    emailReminders: row.emailReminders !== false,
    posterEmail: row.posterEmail !== false,
    instructions: ((guide.replacesInstructions || []).includes(row.instructions) ? "" : niGuidanceValue(row, "instructions", row.instructions)) || guide.instructions || "",
    fallbackTask: row.fallbackTask !== false,
    defaultLinkUrl: row.defaultLinkUrl || "",
    defaultLinkLabel: row.defaultLinkLabel || "",
    naReason: row.naReason || "",
    uploadAsk: row.uploadAsk || guide.uploadAsk || "",
    sopTitles: row.sopTitles || [],
    inspectorNote: row.inspectorNote || "",
    training: row.training || {
      enabled: false,
      url: "",
      filename: ""
    },
    teamRoles: row.teamRoles || [],
    teamOffsetDays: row.teamOffsetDays ?? 30
  });
  const set = p => setT(x => ({
    ...x,
    ...p
  }));
  const [roles, setRoles] = useState(null);
  const [err, setErr] = useState(""),
    [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(null); // "about" | "source" | "settings"
  const toggleHelp = k => setHelpOpen(h => h === k ? null : k);
  const [review, setReview] = useState(null); // cascade dry run
  const [ticked, setTicked] = useState(new Set());
  const [done, setDone] = useState(null);
  const group = groups.find(g => g.id === groupId) || groups[0];
  useEffect(() => {
    if (custom && !(group?.items || []).some(i => i.id === itemId)) setItemId(group?.items?.[0]?.id || "");
  }, [groupId]); // eslint-disable-line
  useEffect(() => {
    post(token, `${GRID}/roles`, {}).then(d => setRoles(d.roles || [])).catch(() => setRoles([]));
  }, [token]);
  const name = custom ? label : row.label;
  const usesTask = rest === "task" || rest === "upload";
  const [sopList, setSopList] = useState(null),
    [sopQ, setSopQ] = useState("");
  useEffect(() => {
    if (guide.sop) post(token, `${GRID}/sops`, {}).then(d => setSopList(d.sops || [])).catch(() => setSopList([]));
  }, [token]); // eslint-disable-line

  const payload = () => ({
    rowId: row.new ? undefined : row.rowId,
    label: custom ? label : undefined,
    groupId,
    itemId,
    evidenceType: guide.evidenceOnly || t.evidenceType,
    validity: {
      mode: t.mode,
      days: Number(t.days),
      warnDays: Number(t.warnDays)
    },
    defaultSource: source,
    explanation: about.explanation,
    satisfies: about.satisfies,
    references: about.references.filter(r => /^https?:\/\//i.test(r.url)),
    link: about.references.find(r => /^https?:\/\//i.test(r.url))?.url || "",
    instructions: t.instructions,
    emailReminders: t.emailReminders,
    posterEmail: t.posterEmail,
    autoAssign: {
      enabled: t.autoAssign && !!t.roleName,
      roleName: t.roleName
    },
    fallbackTask: usePharmace ? other !== "none" : t.fallbackTask,
    defaultLinkUrl: normLink(t.defaultLinkUrl, t.defaultLinkLabel).url,
    defaultLinkLabel: normLink(t.defaultLinkUrl, t.defaultLinkLabel).label,
    naReason: t.naReason,
    updatedBy: userName(),
    fallbackSource: other === "none" ? firstChoice : other,
    uploadKind: guide.upload || "",
    uploadAsk: t.uploadAsk,
    training: t.training,
    trainingAttachment,
    sopTitles: t.sopTitles,
    inspectorNote: t.inspectorNote,
    ...(guide.team ? {
      teamRoles: t.teamRoles,
      teamKind: guide.teamKind || "pharmacist",
      teamOffsetDays: Number(t.teamOffsetDays)
    } : {})
  });
  const validate = () => {
    if (t.training?.enabled && !trainingAttachment && !/^https?:\/\/[^\s]+$/i.test(t.training.url || "")) return "Add a training link or upload a training document.";
    if (custom && !label.trim()) return "Give the item a name.";
    if (rest === "link") {
      const lk = normLink(t.defaultLinkUrl, t.defaultLinkLabel);
      if (!lk.url && !lk.label) return "Say where the information is held (a link is optional).";
    }
    if (rest === "sop" && !t.sopTitles.length) return "Choose at least one Pharmace SOP to link.";
    if (usesTask && !guide.guardian && t.autoAssign && !t.roleName) return "Choose the role to assign, or untick automatic assignment.";
    return "";
  };
  const save = async thenCascade => {
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const r = await post(token, `${GRID}/row/save`, guide.guardian ? {
        ...payload(),
        autoAssign: {
          enabled: false,
          roleName: ""
        },
        defaultSource: "task",
        evidenceType: guide.evidenceOnly || "confirm",
        validity: {
          mode: "days",
          days: 365,
          warnDays: Number(t.warnDays) || 30
        }
      } : payload());
      if (guide.guardian) {
        const sent = [],
          saved = [],
          problems = [];
        for (const b of branches) {
          const pick = picks[b.dbName];
          if (!pick) continue;
          const cur = cellOf(b.dbName) || {};
          const changed = pick !== (cur.recipientStaffId || "") || cur.source !== "task";
          if (changed) {
            await post(token, `${GRID}/cell/save`, {
              rowId: r.rowId,
              dbName: b.dbName,
              source: "task",
              autoChase: true,
              rowLabel: name,
              recipientStaffId: pick,
              recipientName: pickNames[`${b.dbName}|${pick}`] || ""
            });
            saved.push(b.pharmacyName);
          }
          const needs = changed || !cur.evidence?.at && cur.task?.status !== "open";
          if (thenCascade && needs) {
            try {
              await post(token, `${GRID}/cell/send-task`, {
                rowId: r.rowId,
                dbName: b.dbName,
                label: name
              });
              sent.push(b.pharmacyName);
            } catch (e) {
              problems.push(`${b.pharmacyName}: ${e.message}`);
            }
          }
        }
        const none = branches.filter(b => !picks[b.dbName]).length;
        onSaved([`Saved.${saved.length ? ` ${guide.roleTitle} set at ${saved.length} site${saved.length === 1 ? "" : "s"}.` : ""}`, sent.length ? `Acceptance task sent at ${sent.join(", ")}.` : thenCascade ? "No new tasks needed." : "No tasks sent.", none ? `${none} site${none === 1 ? " has" : "s have"} no ${guide.roleTitle.toLowerCase()} chosen yet.` : "", problems.length ? `Not sent — ${problems.join("; ")}` : ""].filter(Boolean).join(" "));
        return;
      }
      // Evidenced by a Pharmace SOP: nothing is sent, so the link is applied to
      // every branch straight away (branches set to something else keep it).
      if (source === "sop" && !row.new && !t.training?.enabled) {
        const dbs = branches.filter(b => {
          const cs = cellOf(b.dbName)?.source;
          return !cs || cs === "sop";
        }).map(b => b.dbName);
        if (dbs.length) await post(token, `${GRID}/cell/save`, {
          rowId: r.rowId || row.rowId,
          dbNames: dbs,
          source: "sop",
          rowLabel: name
        });
        onSaved(`Saved — SOP linked at ${dbs.length} branch${dbs.length === 1 ? "" : "es"}. Nothing is sent to staff.`);
        return;
      }
      if (!thenCascade) {
        onSaved(row.new ? `“${label}” added.` : "Settings saved. Branches already set up keep their own choice until you cascade.");
        return;
      }
      const p = await post(token, `${GRID}/row/cascade-preview`, {
        rowId: r.rowId,
        pharmaceDbNames: det.withData,
        onlyDbNames: row.dsp ? branches.map(b => b.dbName) : undefined
      });
      setReview({
        ...p,
        rowId: r.rowId
      });
      setTicked(new Set(p.plan.filter(x => x.changes || x.firstTask || x.trainingTask).map(x => x.dbName)));
      setSaving(false);
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };
  const apply = async () => {
    setSaving(true);
    setErr("");
    try {
      const r = await post(token, `${GRID}/row/cascade`, {
        rowId: review.rowId,
        dbNames: [...ticked],
        pharmaceDbNames: det.withData,
        onlyDbNames: row.dsp ? branches.map(b => b.dbName) : undefined,
        rowLabel: name,
        updatedBy: userName()
      });
      setDone(r);
      setSaving(false);
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };
  const del = async () => {
    if (!(await appConfirm(`Delete “${row.label}” and its set-up at every branch?`))) return;
    try {
      await post(token, `${GRID}/row/delete`, {
        rowId: row.rowId
      });
      onSaved("Row deleted.");
    } catch (e) {
      setErr(e.message);
    }
  };
  const srcLabel = s => (sourceInfo(s, guide)?.title || "Not set").replace("Pharmace data detected", "Pharmace");

  // ---- cascade dry run / result -------------------------------------------
  if (review) {
    const willSend = review.plan.filter(p => ticked.has(p.dbName) && p.firstTask);
    const uploads = willSend.filter(p => p.proposed.source === "upload").length;
    const tasks = willSend.length - uploads;
    const emails = willSend.filter(p => p.emailOnFirstTask && p.proposed.hasEmail).length;
    const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
    return <Modal title={`Save & cascade — ${name}`} onClose={() => done ? onSaved(`Cascaded to ${done.results.length} branch${done.results.length === 1 ? "" : "es"}; ${done.tasksSent} task${done.tasksSent === 1 ? "" : "s"} sent.`) : onClose()} width={1100}>
        {!done ? <>
          <p style={{
          fontSize: 13,
          margin: "0 0 10px"
        }}>The settings are saved. <b>Nothing has been sent yet.</b> This is what each ticked branch will get. Untick any branch you want to leave as it is.</p>
          <div style={{
          overflowX: "auto",
          border: "1px solid #F0F0F0",
          borderRadius: 8
        }}>
            <table style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 13
          }}>
              <thead><tr style={{
                background: "#F5F5F5",
                textAlign: "left"
              }}>
                <th style={{
                  padding: 8
                }}><input type="checkbox" aria-label="All branches" checked={ticked.size === review.plan.length} onChange={e => setTicked(e.target.checked ? new Set(review.plan.map(p => p.dbName)) : new Set())} /></th>
                <th style={{
                  padding: 8
                }}>Branch</th><th style={{
                  padding: 8
                }}>Now</th><th style={{
                  padding: 8
                }}>Will be</th><th style={{
                  padding: 8
                }}>Recipient</th><th style={{
                  padding: 8
                }}>Sent now</th><th style={{
                  padding: 8
                }}>Note</th></tr></thead>
              <tbody>{review.plan.map(p => <tr key={p.dbName} style={{
                borderTop: "1px solid #EDF2F7",
                opacity: ticked.has(p.dbName) ? 1 : 0.5
              }}>
                  <td style={{
                  padding: 8
                }}><input type="checkbox" aria-label={p.pharmacyName} checked={ticked.has(p.dbName)} onChange={e => {
                    const n = new Set(ticked);
                    e.target.checked ? n.add(p.dbName) : n.delete(p.dbName);
                    setTicked(n);
                  }} /></td>
                  <td style={{
                  padding: 8,
                  fontWeight: 600
                }}>{p.pharmacyName}</td>
                  <td style={{
                  padding: 8,
                  color: "#5D5D5D"
                }}>{srcLabel(p.current.source)}{p.current.recipientName ? ` → ${p.current.recipientName}` : ""}</td>
                  <td style={{
                  padding: 8
                }}>{srcLabel(p.proposed.source)}</td>
                  <td style={{
                  padding: 8
                }}>{p.proposed.source === "task" || p.proposed.source === "upload" || p.trainingTask ? p.proposed.recipientName ? <>{p.proposed.recipientName}{p.proposed.recipientRole ? <span style={{
                      color: "#5D5D5D"
                    }}> · {p.proposed.recipientRole}</span> : ""}{!p.proposed.hasEmail && <span style={{
                      color: "#AD4E00"
                    }}> · no email on file</span>}</> : <span style={{
                    color: "#A8071A"
                  }}>Nobody yet</span> : "—"}</td>
                  <td style={{
                  padding: 8
                }}>{p.firstTask ? p.proposed.source === "upload" && guide.upload !== "documents" ? "Upload request email" : `Task${p.emailOnFirstTask && p.proposed.hasEmail ? p.proposed.source === "upload" || p.proposed.source === "task" && (guide.evidenceOnly || row.evidenceType || guide.defaultEvidence || "photo") !== "confirm" ? " + email with upload link" : " + email" : ""}` : p.proposed.source === "upload" && p.proposed.recipientName && !p.proposed.hasEmail ? <span style={{
                    color: "#A8071A"
                  }}>Can’t send — no email</span> : "—"}</td>
                  <td style={{
                  padding: 8,
                  color: "#5D5D5D",
                  fontSize: 12
                }}>{p.note}{p.trainingTask && <div>Additional training task</div>}</td>
                </tr>)}</tbody>
            </table>
          </div>
          {review.plan.some(p => ticked.has(p.dbName) && p.trainingTask) && <div style={{
          marginTop: 8,
          fontSize: 13
        }}>Additional training tasks will be sent to the selected recipients. An identical training task already sent for this request is not sent again.</div>}
          <div style={{
          marginTop: 10,
          padding: 10,
          borderRadius: 8,
          background: willSend.length ? "#FFF7E6" : "#F5F5F5",
          color: willSend.length ? "#AD4E00" : "#434343",
          fontSize: 13
        }}>
            {willSend.length ? <>Applying will send {[tasks ? <b key="t">{plural(tasks, "staff-app task")}</b> : null, uploads ? <b key="u">{plural(uploads, "upload request")}</b> : null].filter(Boolean).reduce((a, x) => a.length ? [...a, " and ", x] : [x], [])}{emails ? <> ({plural(emails, "email")} in all)</> : ""} straight away.</> : "Applying changes the set-up only; nothing will be sent."}
          </div>
          {err && <div role="alert" style={{
          color: "#A8071A",
          marginTop: 8
        }}>{err}</div>}
          <div style={{
          display: "flex",
          gap: 8,
          marginTop: 12
        }}>
            <button disabled={saving || !ticked.size} onClick={apply} style={{
            fontWeight: 700,
            background: "#0E8CFF",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "8px 14px"
          }}>Apply to {ticked.size} branch{ticked.size === 1 ? "" : "es"}</button>
            <button onClick={() => setReview(null)}>← Back to settings</button>
          </div>
        </> : <>
          <p style={{
          fontSize: 13
        }}>Done. {done.tasksSent} task{done.tasksSent === 1 ? "" : "s"} sent.</p>
          <table style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13
        }}><tbody>{done.results.map(r => <tr key={r.dbName} style={{
              borderTop: "1px solid #EDF2F7"
            }}>
              <td style={{
                padding: 6,
                fontWeight: 600
              }}>{r.pharmacyName}</td><td style={{
                padding: 6
              }}>{srcLabel(r.source)}{r.recipientName ? ` → ${r.recipientName}` : ""}</td>
              <td style={{
                padding: 6
              }}>{r.task === "sent" && r.source === "upload" && guide.upload !== "documents" ? r.email === "sent" ? "Upload request emailed" : r.email === "no_address" ? "No email on file — request not delivered" : "Email failed" : r.task === "sent" ? `Task sent${r.email === "sent" ? " + emailed" : r.email === "no_address" ? " (no email on file)" : r.email === "failed" ? " (email failed)" : ""}` : r.task === "failed" ? <span style={{
                  color: "#A8071A"
                }}>Task failed: {r.error}</span> : r.task || ""}</td>
              <td style={{
                padding: 6,
                color: "#5D5D5D",
                fontSize: 12
              }}>{r.note}{r.trainingSent > 0 && <div>{r.trainingSent} training task{r.trainingSent === 1 ? "" : "s"} sent</div>}{r.error && <div style={{
                  color: "#A8071A"
                }}>{r.error}</div>}</td></tr>)}</tbody></table>
          <div style={{
          marginTop: 12
        }}><button style={{
            fontWeight: 700
          }} onClick={() => onSaved(`Cascaded to ${done.results.length} branch${done.results.length === 1 ? "" : "es"}; ${done.tasksSent} task${done.tasksSent === 1 ? "" : "s"} sent.`)}>Close</button></div>
        </>}
      </Modal>;
  }

  // ---- three panels ----------------------------------------------------------
  return <Modal title={row.new ? "Add a data item" : `Settings — ${row.label}`} onClose={onClose} width={1240}>
      <div style={{
      display: "grid",
      gridTemplateColumns: "minmax(260px, 1fr) minmax(260px, 0.9fr) minmax(320px, 1.2fr)",
      gap: 12,
      background: "#FAFAFA",
      padding: 10,
      borderRadius: 10
    }}>
        {/* LEFT — about the item */}
        <section style={panel} aria-label="About this item">
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 6
        }}>
            <div style={{
            ...panelTitle,
            margin: 0,
            flex: 1
          }}>1 · About this item</div>
            <button onClick={() => setEditAbout(!editAbout)} style={{
            fontSize: 12
          }}>{editAbout ? "Done editing" : "Edit"}</button>
            <HelpButton open={helpOpen === "about"} onClick={() => toggleHelp("about")} label="Help: what this item is" />
          </div>
          {helpOpen === "about" && <HelpBox onClose={() => setHelpOpen(null)}>
            <div><b>{name || "This item"}</b>{row.groupName ? <> is part of <b>{row.groupName}</b>{row.itemName ? <> → {row.itemName}</> : null}</> : null}
              {row.requirement ? <> and is <b>{row.requirement}</b>{row.requirement === "mandatory" ? " — an inspector will expect to see it" : ""}</> : null}.</div>
            {about.explanation && <><div style={helpH}>In plain terms</div><div>{about.explanation}</div></>}
            {about.satisfies && <><div style={helpH}>What “good” looks like</div><div>{firstPara(about.satisfies)}</div></>}
            <div style={helpH}>What this panel is for</div>
            <div>It records what the item means and how it is met, with links to the rules behind it. Staff see the explanation in any request they are sent. Use <b>Edit</b> to change the wording, and the <b>Note for the inspector</b> to explain how you meet it — it is shown with this item on the Compliance view at every site.</div>
          </HelpBox>}
          {custom ? <>
            <label style={field}>Item name</label>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Public liability certificate displayed" style={{
            width: "100%"
          }} />
            <div style={{
            display: "flex",
            gap: 6
          }}>
              <div style={{
              flex: 1
            }}><label style={field}>Heading</label>
                <select value={groupId} onChange={e => setGroupId(e.target.value)} style={{
                width: "100%"
              }}>{groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}</select></div>
              <div style={{
              flex: 1
            }}><label style={field}>Sub-heading</label>
                <select value={itemId} onChange={e => setItemId(e.target.value)} style={{
                width: "100%"
              }}>{(group?.items || []).map(i => <option key={i.id} value={i.id}>{i.name}</option>)}</select></div>
            </div>
          </> : <div style={{
          margin: "8px 0 4px"
        }}>
            <div style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#262626"
          }}>{row.label}</div>
            {row.part && <div style={{
            fontSize: 12,
            color: "#0958D9",
            fontWeight: 600
          }}>Part of: {row.parentLabel}</div>}
            <div style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>{row.groupName}{row.itemName ? ` · ${row.itemName}` : ""}
              {row.requirement && <span style={{
              marginLeft: 6,
              padding: "1px 6px",
              borderRadius: 8,
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              background: row.requirement === "mandatory" ? "#FFF1F0" : "#F0F5FF",
              color: row.requirement === "mandatory" ? "#A8071A" : "#1D39C4"
            }}>{row.requirement}</span>}</div>
          </div>}
          <label style={field}>What this is about</label>
          <TextOrEdit editing={editAbout} value={about.explanation} onChange={v => setAbout({
          ...about,
          explanation: v
        })} placeholder="Why this matters, in a sentence or two. Staff see this in their task." />
          <label style={field}>What satisfies it</label>
          <TextOrEdit editing={editAbout} rows={6} value={about.satisfies} onChange={v => setAbout({
          ...about,
          satisfies: v
        })} placeholder="What has to be in place, and what counts as good evidence." />
          <label style={field}>References</label>
          {about.references.map((r, i) => editAbout ? <div key={i} style={{
          display: "flex",
          gap: 4,
          marginBottom: 4
        }}>
                <input value={r.label} placeholder="Title" onChange={e => setAbout({
            ...about,
            references: about.references.map((x, j) => j === i ? {
              ...x,
              label: e.target.value
            } : x)
          })} style={{
            flex: 1,
            minWidth: 0
          }} />
                <input value={r.url} placeholder="https://…" onChange={e => setAbout({
            ...about,
            references: about.references.map((x, j) => j === i ? {
              ...x,
              url: e.target.value
            } : x)
          })} style={{
            flex: 1.3,
            minWidth: 0
          }} />
                <button aria-label="Remove reference" onClick={() => setAbout({
            ...about,
            references: about.references.filter((_, j) => j !== i)
          })}>✕</button>
              </div> : <div key={i} style={{
          fontSize: 13,
          margin: "3px 0"
        }}>↗ <a href={r.url} target="_blank" rel="noreferrer">{r.label || r.url}</a></div>)}
          {editAbout && about.references.length < 10 && <button style={{
          fontSize: 12
        }} onClick={() => setAbout({
          ...about,
          references: [...about.references, {
            label: "",
            url: ""
          }]
        })}>+ Add reference</button>}
          {!about.references.length && !editAbout && <div style={{
          fontSize: 13,
          color: "#8C8C8C",
          fontStyle: "italic"
        }}>No references yet.</div>}
          <div style={{
          marginTop: 14,
          padding: 10,
          background: "#FFFBE6",
          border: "1px solid #FFE58F",
          borderRadius: 8
        }}>
            <label style={{
            ...field,
            margin: "0 0 4px"
          }}>Note for the inspector</label>
            <textarea value={t.inspectorNote} onChange={e => set({
            inspectorNote: e.target.value
          })} rows={3} style={{
            width: "100%",
            fontFamily: "inherit",
            fontSize: 13
          }} placeholder="e.g. These are covered in our SOPs, with current copies uploaded regularly." />
            <div style={help}>Shown with this item on the Compliance view and inspector reports, at every branch. Branch-specific notes can be added in each branch’s cell.</div>
          </div>
        </section>

        {/* MIDDLE — where the evidence comes from */}
        <section style={panel} aria-label="Where the evidence comes from">
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 8
        }}>
            <div style={{
            ...panelTitle,
            margin: 0,
            flex: 1
          }}>2 · Where the evidence comes from</div>
            <HelpButton open={helpOpen === "source"} onClick={() => toggleHelp("source")} label="Help: where the evidence comes from" />
          </div>
          {helpOpen === "source" && <HelpBox onClose={() => setHelpOpen(null)}>
            <div><b>The aim:</b> to bring the evidence for every compliance item, at every site, into one place — so it can be analysed, kept up to date and shown against the applicable standards.</div>
            <div style={helpH}>Your sites</div>
            {pharmaceAvailable && !missing.length ? <div>All {branches.length} sites are covered by Pharmace ({det.module}). <b>No action is needed except to save this panel</b> (Save &amp; cascade).</div> : pharmaceAvailable ? <div><b>{det.withData.length} of {branches.length}</b> sites are covered by Pharmace ({det.module}) — no action is needed for those. <b>A decision is needed for the remaining {siteWord(missing.length)}</b>: {missing.map(b => b.pharmacyName).join(", ")}.</div> : <div>{det.module ? `Pharmace (${det.module}) has no records for this item at any site yet,` : "Pharmace doesn’t record this item,"} so <b>a decision is needed for all {siteWord(branches.length)}</b>.</div>}
            {(missing.length > 0 || !pharmaceAvailable) && <>
              <div style={helpH}>Questions to answer for {pharmaceAvailable ? "those sites" : "each site"}</div>
              <ul style={{
              margin: "2px 0",
              paddingLeft: 18
            }}>
                <li><b>Does this item apply there at all?</b> If not, choose <i>Not applicable</i> and say why.</li>
                <li><b>Is the data kept somewhere else?</b>
                  <ul style={{
                  margin: "2px 0",
                  paddingLeft: 16
                }}>
                    <li>On a third-party website or system — export it as CSV or Excel; it can then be uploaded into the management platform.</li>
                    <li>In a shared spreadsheet (Google Sheets, OneDrive, SharePoint, Dropbox) — share the link once; it is re-read automatically every month.</li>
                    <li>In the site’s own spreadsheet or documents — staff upload it through a secure link.</li>
                    <li>Somewhere it can’t be uploaded (a supplier portal, a shared drive) — use <i>Held in another system</i>.</li>
                  </ul></li>
              </ul></>}
            <div style={helpH}>The steps</div>
            <ol style={{
            margin: "2px 0",
            paddingLeft: 18
          }}>
              <li><b>Identify where the data comes from</b> — tick Pharmace where it’s used, and choose how the other sites are handled.</li>
              <li><b>If needed, send automatic prompts</b> — the relevant staff member at each site is emailed a secure upload link (set up in panel 3).</li>
            </ol>
            <div style={helpH}>What happens next</div>
            <div>Whatever the source, the data counts towards the applicable regulatory readiness, is reviewed by AI where it is a spreadsheet, and is visible to you on the Compliance view. Once set up, collection is either <b>fully automatic</b> (Pharmace, or a linked shared spreadsheet) or <b>emails go out automatically</b> to the right staff whenever an upload is due.</div>
          </HelpBox>}
          {/* Not available for this item → not shown at all (less clutter). */}
          {pharmaceAvailable && <label style={choice(usePharmace, !pharmaceAvailable)}>
            <input type="checkbox" disabled={!pharmaceAvailable} checked={usePharmace} onChange={e => {
            setUsePharmace(e.target.checked);
            if (!e.target.checked && other === "none") setOther(firstChoice);
          }} /> <b>{SOURCE_INFO.pharmace.title}</b>
            <span style={{
            fontSize: 11,
            marginLeft: 6,
            color: pharmaceAvailable ? "#389E0D" : "#5D5D5D"
          }}>
              {pharmaceAvailable ? `found at ${det.withData.length} of ${branches.length} site${branches.length === 1 ? "" : "s"}` : "not available"}</span>
            <div style={help}>{!pharmaceAvailable ? guide.noPharmaceReason || (det.module ? `Pharmace (${det.module}) has no records for this item at any site yet, so it can't be used as the evidence.` : "Pharmace doesn’t record anything that can evidence this item.") : SOURCE_INFO.pharmace.text}</div>
          </label>}
          <div style={{
          fontSize: 13,
          fontWeight: 600,
          color: "#262626",
          margin: "10px 0 6px",
          lineHeight: 1.4
        }}>
            {usePharmace ? missing.length ? <>{siteWord(missing.length)} {missing.length === 1 ? "does" : "do"} not use Pharmace {pName}{missing.length <= 4 ? <span style={{
              fontWeight: 400,
              color: "#5D5D5D"
            }}> ({missing.map(b => b.pharmacyName).join(", ")})</span> : null}. For {missing.length === 1 ? "that site" : "those sites"}, how do you wish to manage the data?</> : <>Every site records this in Pharmace. If a site stops, or a new one is added, manage its data by:</> : pharmaceAvailable ? <>Without Pharmace — how do you wish to manage the data at every site?</> : <>How do you wish to manage the data?</>}
          </div>
          <div style={usePharmace ? {
          borderLeft: "3px solid #BAE7FF",
          paddingLeft: 10,
          marginLeft: 4
        } : undefined}>
            {others.map(k => <label key={k} style={choice(other === k)}>
                <input type="radio" checked={other === k} onChange={() => setOther(k)} /> <b>{sourceInfo(k, guide).title}</b>
                <div style={help}>{sourceInfo(k, guide).text}</div>
              </label>)}
            {usePharmace && <label style={{
            ...choice(other === "none"),
            padding: "6px 12px"
          }}>
              <input type="radio" checked={other === "none"} onChange={() => setOther("none")} /> <span style={{
              fontSize: 13
            }}>{SOURCE_INFO.none.title}</span>
              <div style={help}>{SOURCE_INFO.none.text}</div>
            </label>}
            {guide.onlyReason && <div style={{
            ...help,
            marginBottom: 8,
            fontStyle: "italic"
          }}>{guide.onlyReason}</div>}
          </div>
          <div style={{
          ...help,
          marginTop: 10
        }}>This is the default for every site. <b>Save & cascade</b> applies it to the sites you tick; a single site can still be changed by clicking its cell.</div>
          <TrainingSettings value={t.training} attachment={trainingAttachment} onChange={training => set({
          training
        })} onAttachment={setTrainingAttachment} />
          {t.training?.enabled && !usesTask && rest !== "team" && <div style={{
          marginTop: 8,
          fontSize: 13
        }}>
            <label><input type="checkbox" checked={t.autoAssign} onChange={e => set({
              autoAssign: e.target.checked
            })} /> Automatically assign a training recipient</label>
            <select aria-label="Training recipient role" value={t.roleName} onChange={e => set({
            roleName: e.target.value
          })} style={{
            display: 'block',
            width: '100%',
            marginTop: 6
          }}>
              <option value="">Choose role…</option>{(roles || []).map(r => {
              const name = typeof r === 'string' ? r : r.roleName || r.name;
              return <option key={name} value={name}>{name}</option>;
            })}
            </select>
          </div>}

        </section>

        {/* RIGHT — settings for the chosen source */}
        <section style={panel} aria-label="Settings">
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 8
        }}>
            <div style={{
            ...panelTitle,
            margin: 0,
            flex: 1
          }}>3 · Settings</div>
            <HelpButton open={helpOpen === "settings"} onClick={() => toggleHelp("settings")} label="Help: settings" />
          </div>
          {helpOpen === "settings" && <HelpBox onClose={() => setHelpOpen(null)}>
            <div>This panel sets up the choice made in panel 2.</div>
            {usePharmace && <><div style={helpH}>Pharmace sites</div><div>Nothing to set: the status is read live from Pharmace.{missing.length ? " The list shows which sites use it and what the others will use." : ""}</div></>}
            {rest === "upload" && <><div style={helpH}>Upload requests</div><div>Choose what to ask for, how often, and who receives it — or tick <b>Automatically assign a recipient</b> and pick a role, so each site’s request goes to the right person. Requests and reminders are emailed with a secure link (no staff app needed) and logged on the staff member’s record. A site that sends a shared spreadsheet link doesn’t need asking again: it is re-read on the 1st of each month.</div></>}
            {rest === "task" && <><div style={helpH}>Tasks</div><div>Choose what the staff member must provide (acknowledgement, photo or document), how long it lasts and who receives it. Tasks are sent to the staff app and by email; renewals go out automatically before the evidence runs out.</div></>}
            {rest === "link" && <><div style={helpH}>Link</div><div>Enter where the evidence is kept. Every site you cascade to gets this link; one site can be changed by clicking its cell in the grid.</div></>}
            {rest === "na" && <><div style={helpH}>Not applicable</div><div>Give the reason — it is kept with the setting so an inspector can see why.</div></>}
            {rest === "sop" && <><div style={helpH}>Pharmace SOPs</div><div>Pick the SOP(s) that cover this item. Each site is checked for holding them in date, with read &amp; signed counts.</div></>}
            <div style={helpH}>Saving</div>
            <div><b>Save &amp; cascade</b> shows a dry run of every site first — what each will get and who will be emailed. Nothing is sent until you press Apply. <b>Save settings only</b> stores the settings without changing the sites.</div>
          </HelpBox>}
          {usePharmace && <>
            <div style={{
            fontWeight: 700,
            fontSize: 13,
            marginBottom: 4
          }}>{SOURCE_INFO.pharmace.title} — {det.module}</div>
            <ul style={{
            fontSize: 13,
            margin: "0 0 10px",
            paddingLeft: 18
          }}>
              {branches.map(b => <li key={b.dbName} style={{
              color: det.withData.includes(b.dbName) ? "#389E0D" : rest ? "#0958D9" : "#5D5D5D"
            }}>{det.withData.includes(b.dbName) ? "✓ Pharmace" : `→ ${rest ? sourceInfo(rest, guide).title.replace(/^Email request to upload (data|documents)$/, "Upload request") : "Pharmace not used"}`} · {b.pharmacyName}</li>)}
            </ul>
          </>}
          {rest && usePharmace && <div style={{
          borderTop: "1px solid #F0F0F0",
          margin: "4px 0 10px",
          paddingTop: 10,
          fontWeight: 700,
          fontSize: 13
        }}>
            {missing.length ? `For ${missing.length === 1 ? missing[0].pharmacyName : `the ${missing.length} sites without Pharmace`}` : "For any site without Pharmace"}: {sourceInfo(rest, guide).title}</div>}
          {rest === "sop" && <>
            <div style={{
            fontSize: 13,
            lineHeight: 1.5
          }}>Choose the SOP(s) in Pharmace that cover this item. They are matched by title at every branch.</div>
            <input value={sopQ} onChange={e => setSopQ(e.target.value)} placeholder="Search SOPs…" style={{
            width: "100%",
            margin: "8px 0 6px"
          }} />
            <div style={{
            maxHeight: 260,
            overflowY: "auto",
            border: "1px solid #F0F0F0",
            borderRadius: 6
          }}>
              {sopList == null ? <div style={{
              padding: 8,
              fontSize: 12,
              color: "#5D5D5D"
            }}>Loading SOPs…</div> : !sopList.length ? <div style={{
              padding: 8,
              fontSize: 12,
              color: "#5D5D5D"
            }}>No SOPs found in Pharmace for these branches.</div> : sopList.filter(x => !sopQ || x.title.toLowerCase().includes(sopQ.toLowerCase())).map(x => {
              const on = t.sopTitles.some(y => y.toLowerCase() === x.title.toLowerCase());
              return <label key={x.title} style={{
                display: "flex",
                gap: 6,
                alignItems: "flex-start",
                padding: "5px 8px",
                borderBottom: "1px solid #F5F5F5",
                fontSize: 13,
                background: on ? "#E6F7FF" : undefined
              }}>
                      <input type="checkbox" checked={on} onChange={e => set({
                  sopTitles: e.target.checked ? [...t.sopTitles, x.title] : t.sopTitles.filter(y => y.toLowerCase() !== x.title.toLowerCase())
                })} />
                      <span>{x.title}<span style={{
                    color: "#5D5D5D",
                    fontSize: 11
                  }}> · at {x.branches} of {branches.length} branch{branches.length === 1 ? "" : "es"}{x.category ? ` · ${x.category}` : ""}</span></span>
                    </label>;
            })}
            </div>
            {!!t.sopTitles.length && <div style={{
            ...help,
            marginTop: 6
          }}>Linked: <b>{t.sopTitles.join(", ")}</b>. A branch shows green when it holds every linked SOP in date, amber/red when one is missing or past its review date. Read &amp; signed counts are shown in each branch’s cell.</div>}
          </>}
          {rest === "team" && <TeamSettings s={t} set={set} roles={roles} kind={guide.teamKind || "pharmacist"} />}
          {rest === "web" && <>
            <div style={{
            fontSize: 13,
            lineHeight: 1.5
          }}>The website of each DSP site is <b>read automatically</b> — the home page plus its about, contact, privacy, complaints and healthy-living pages — and checked for:</div>
            <ul style={{
            fontSize: 13,
            margin: "6px 0",
            paddingLeft: 18
          }}>{(WEB_ROW_CHECKS[row.elementId] || []).map(k => <li key={k}>{k === "restriction" ? "no wording that limits the NHS service to certain areas or groups" : WEB_SHORT[k]}</li>)}</ul>
            <div style={{
            fontSize: 13,
            lineHeight: 1.5,
            background: "#F6FFED",
            color: "#237804",
            padding: 8,
            borderRadius: 6
          }}>The AI only says whether each item is there and quotes the words; the quote is then checked against the page, so nothing is taken on trust. Re-checked automatically on the <b>1st of every month</b>, or on demand from a site’s cell.</div>
            <div style={{
            ...panelTitle,
            marginTop: 14
          }}>Websites</div>
            {branches.map(b => <div key={b.dbName} style={{
            fontSize: 13,
            margin: "3px 0"
          }}><b>{b.pharmacyName}</b>: {sites[b.dbName]?.websiteUrl ? <a href={sites[b.dbName].websiteUrl} target="_blank" rel="noreferrer">{sites[b.dbName].websiteUrl}</a> : <span style={{
              color: "#A8071A"
            }}>no website address — add it under Sites</span>}</div>)}
          </>}
          {rest === "upload" && <UploadSettings s={t} set={set} roles={roles} label={name} kind={guide.upload} />}
          {rest === "task" && (guide.guardian ? <GuardianSettings s={t} set={set} token={token} branches={branches} picks={picks} setPicks={setPicks} setPickNames={setPickNames} cellOf={cellOf} label={name} explanation={about.explanation} roleTitle={guide.roleTitle} posterGuardian={!!guide.posterGuardian} instructions={guide.instructions || ""} /> : <TaskSettings s={t} set={set} roles={roles} label={name} explanation={about.explanation} link={about.references[0]?.url} only={guide.evidenceOnly || null} poster={!!guide.poster} />)}
          {rest === "link" && <>
            <label style={field}>Where the information is held (shown in the grid)</label>
            <input value={t.defaultLinkLabel} onChange={e => set({
            defaultLinkLabel: e.target.value
          })} placeholder={guide.linkPlaceholder || "e.g. Insurance certificates folder"} style={{
            width: "100%"
          }} />
            <label style={field}>Link (optional)</label>
            <input value={t.defaultLinkUrl} onChange={e => set({
            defaultLinkUrl: e.target.value
          })} placeholder="https://…" style={{
            width: "100%"
          }} />
            <div style={help}>A link isn’t needed when the record is kept in another system — just say where it is held. Every branch you cascade to gets this; a branch can be changed by clicking its cell.</div>
          </>}
          {rest === "na" && <>
            <label style={field}>Why it doesn’t apply</label>
            <textarea value={t.naReason} onChange={e => set({
            naReason: e.target.value
          })} rows={3} style={{
            width: "100%",
            fontFamily: "inherit"
          }} placeholder={guide.naPlaceholder || "e.g. We do not hold Schedule 2 controlled drugs."} />
            <div style={help}>Recorded with the setting so an inspector can see the reason.</div>
          </>}
        </section>
      </div>

      {err && <div role="alert" style={{
      color: "#A8071A",
      marginTop: 10
    }}>{err}</div>}
      <div style={{
      display: "flex",
      gap: 8,
      marginTop: 12,
      alignItems: "center"
    }}>
        <button disabled={saving} onClick={() => save(true)} style={{
        fontWeight: 700,
        background: "#0E8CFF",
        color: "#fff",
        border: "none",
        borderRadius: 6,
        padding: "8px 14px"
      }}>{guide.guardian ? "Save & send acceptance tasks" : source === "sop" ? "Save & link at every branch" : "Save & cascade to branches…"}</button>
        <button disabled={saving} onClick={() => save(false)}>{row.new ? "Add item" : "Save settings only"}</button>
        <button onClick={onClose}>Cancel</button>
        <span style={{
        fontSize: 12,
        color: "#5D5D5D"
      }}>{guide.guardian ? "Sends the acceptance task to each newly chosen person (and anyone who hasn’t been asked yet). “Save settings only” saves the choices without sending." : "Cascading shows you every branch first; nothing is sent until you apply."}</span>
        <span style={{
        flex: 1
      }} />
        {custom && !row.new && <button onClick={del} style={{
        color: "#A8071A"
      }}>Delete item</button>}
      </div>
    </Modal>;
}

// Near-miss log analysis (from an uploaded / linked spreadsheet). Every figure
// is calculated in near_miss_analysis.js; the AI only mapped the columns and
// grouped the error types. Also used on the Compliance view.
const NM_STATUS = {
  ok: {
    bg: "#F6FFED",
    fg: "#389E0D",
    label: "Recorded every month"
  },
  gaps: {
    bg: "#FFF1F0",
    fg: "#A8071A",
    label: "Gaps in recording"
  },
  lapsed: {
    bg: "#FFF7E6",
    fg: "#AD4E00",
    label: "Lapsed — nothing recent"
  },
  never: {
    bg: "#F5F5F5",
    fg: "#5D5D5D",
    label: "No entries"
  }
};
const monthLabel = ym => new Date(`${ym}-01T12:00:00Z`).toLocaleDateString("en-GB", {
  month: "short",
  timeZone: "UTC"
});
const LF_STATUS = {
  ok: {
    bg: "#F6FFED",
    fg: "#389E0D",
    label: "Logged and referred"
  },
  gaps: {
    bg: "#FFF1F0",
    fg: "#A8071A",
    label: "Serious incidents not referred"
  },
  lapsed: {
    bg: "#FFF7E6",
    fg: "#AD4E00",
    label: "Nothing logged recently"
  },
  never: {
    bg: "#F5F5F5",
    fg: "#5D5D5D",
    label: "No incidents"
  }
};
const CP_STATUS = {
  ok: {
    bg: "#F6FFED",
    fg: "#389E0D",
    label: "Complaints resolved"
  },
  gaps: {
    bg: "#FFF1F0",
    fg: "#A8071A",
    label: "Complaints open too long"
  },
  lapsed: {
    bg: "#FFF7E6",
    fg: "#AD4E00",
    label: "Nothing recent"
  },
  never: {
    bg: "#F5F5F5",
    fg: "#5D5D5D",
    label: "No complaints"
  }
};
const HARM_COLOUR = {
  "No harm": "#B7EB8F",
  "Low harm": "#FFE58F",
  "Moderate harm": "#FFA940",
  "Severe harm": "#F5222D",
  Fatal: "#820014"
};

// Cleaning log uploaded by a branch: the same figures as Pharmace cleaning.
const FR_STATUS = {
  ok: {
    bg: "#F6FFED",
    fg: "#389E0D",
    label: "Fridges checked and in range"
  },
  gaps: {
    bg: "#FFF1F0",
    fg: "#A8071A",
    label: "Missed days or readings out of range"
  },
  lapsed: {
    bg: "#FFF7E6",
    fg: "#AD4E00",
    label: "No recent readings"
  }
};
const CL_STATUS = {
  ok: {
    bg: "#F6FFED",
    fg: "#389E0D",
    label: "Cleaning on schedule"
  },
  gaps: {
    bg: "#FFF1F0",
    fg: "#A8071A",
    label: "Missed or overdue cleans"
  },
  lapsed: {
    bg: "#FFF7E6",
    fg: "#AD4E00",
    label: "Log lapsed"
  }
};
// Fridge temperature log uploaded by a branch: the same figures as Pharmace.
export function FridgePanel({
  a,
  compact = false,
  reports = null
}) {
  if (!a) return null;
  const st = FR_STATUS[a.status] || FR_STATUS.lapsed;
  const months = a.months || [];
  const max = Math.max(1, ...months.map(m => m.readings));
  const col = (v, t) => v == null ? "#64748b" : v >= t ? "#389E0D" : v >= t - 10 ? "#AD4E00" : "#A8071A";
  const tile = (k, v, c) => <div key={k} style={{
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    padding: "6px 8px"
  }}><div style={{
      fontSize: 10,
      color: "#64748b"
    }}>{k}</div><div style={{
      fontSize: 18,
      fontWeight: 800,
      color: c || "#0f172a"
    }}>{v}</div></div>;
  const out = (a.fridges || []).reduce((n, f) => n + (f.outOfRange || 0), 0);
  return <div style={{
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: 10,
    background: "#fff",
    marginTop: 8
  }}>
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap"
    }}>
      <span style={{
        background: st.bg,
        color: st.fg,
        borderRadius: 10,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 700
      }}>{st.label}</span>
      <span style={{
        fontSize: 13
      }}><b>{a.total}</b> readings logged · <b>{a.last30}</b> in the last 30 days · last reading {fmtDate(a.periodTo)}</span>
    </div>
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 8,
      marginTop: 10
    }}>
      {tile(`Checked on open days (target ${a.onTimeThreshold}%)`, a.pctOnTimeYear != null ? `${a.pctOnTimeYear}%` : "—", col(a.pctOnTimeYear, a.onTimeThreshold))}
      {tile(`Readings in 2–8 °C (target ${a.inRangeThreshold}%)`, a.pctInRangeYear != null ? `${a.pctInRangeYear}%` : "—", col(a.pctInRangeYear, a.inRangeThreshold))}
      {tile("Readings out of range", out, out ? "#A8071A" : null)}
      {tile("Fridges", (a.fridges || []).length)}
    </div>
    <div aria-label="Readings per month" style={{
      display: "flex",
      alignItems: "flex-end",
      gap: 3,
      height: 60,
      margin: "10px 0 2px",
      borderBottom: "1px solid #e2e8f0"
    }}>
      {months.map(m => {
        const before = !!a.periodFrom && m.month < a.periodFrom.slice(0, 7);
        return <div key={m.month} title={`${monthLabel(m.month)} ${m.month.slice(0, 4)}: ${before ? "before the log starts" : `${m.readings} readings${m.inRangePct != null ? `, ${m.inRangePct}% in range` : ""}`}`} style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          height: "100%"
        }}>
        <span style={{
            fontSize: 9,
            color: before ? "#cbd5e1" : m.readings ? "#334155" : "#dc2626"
          }}>{before ? "–" : m.readings}</span>
        <div style={{
            width: "100%",
            height: `${m.readings / max * 42}px`,
            minHeight: m.readings ? 2 : 0,
            background: m.inRangePct != null && m.inRangePct < a.inRangeThreshold ? "#b45309" : "#0369a1",
            borderRadius: "3px 3px 0 0",
            outline: m.readings || before ? "none" : "1px dashed #fca5a5"
          }} /></div>;
      })}
    </div>
    <div style={{
      display: "flex",
      gap: 3
    }}>{months.map(m => <span key={m.month} style={{
        flex: 1,
        fontSize: 9,
        color: "#64748b",
        textAlign: "center"
      }}>{monthLabel(m.month).slice(0, 1)}</span>)}</div>
    <div style={{
      fontSize: 10,
      color: "#64748b",
      marginTop: 2
    }}>Readings per month, last 12 months · amber = below the in-range target · dashed red = nothing logged</div>
    <div style={{
      fontWeight: 600,
      fontSize: 12,
      margin: "10px 0 4px"
    }}>What the figures show</div>
    <ul style={{
      margin: 0,
      paddingLeft: 18,
      fontSize: 12,
      lineHeight: 1.5
    }}>{(a.findings || []).map((f, i) => <li key={i}>{f}</li>)}</ul>
    {!compact && <div style={{
      marginTop: 10,
      border: "1px solid #e2e8f0",
      borderRadius: 6,
      maxHeight: 200,
      overflow: "auto"
    }}>
      <table style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 12
      }}>
        <thead><tr style={{
            background: "#f1f5f9",
            position: "sticky",
            top: 0
          }}>{["Fridge", "Readings", "Days checked", "Missed days", "In range", "Last reading", ""].map(h => <th key={h} style={{
              textAlign: "left",
              padding: "4px 8px"
            }}>{h}</th>)}</tr></thead>
        <tbody>{(a.fridges || []).map(x => <tr key={x.fridge} style={{
            borderTop: "1px solid #f1f5f9"
          }}>
          <td style={{
              padding: "3px 8px",
              fontWeight: 600
            }}>{x.fridge}</td><td style={{
              padding: "3px 8px"
            }}>{x.readings}</td>
          <td style={{
              padding: "3px 8px"
            }}>{x.pctChecked != null ? `${x.pctChecked}%` : "—"}</td>
          <td style={{
              padding: "3px 8px",
              color: x.missedDays ? "#A8071A" : undefined
            }}>{x.missedDays}</td>
          <td style={{
              padding: "3px 8px"
            }}>{x.pctInRange != null ? `${x.pctInRange}%` : "—"}</td>
          <td style={{
              padding: "3px 8px",
              whiteSpace: "nowrap"
            }}>{x.lastReading ? fmtDate(x.lastReading.slice(0, 10)) : "—"}</td>
          <td style={{
              padding: "3px 8px",
              whiteSpace: "nowrap",
              color: x.lastInRange === false ? "#A8071A" : "#389E0D",
              fontWeight: 600
            }}>{x.lastInRange === false ? "Last reading out of range" : "In range"}</td></tr>)}</tbody>
      </table></div>}
    {!compact && (a.records || []).length > 0 && <div className="rc-dark-scroll" style={{
      marginTop: 10,
      border: "1px solid #e2e8f0",
      borderRadius: 6,
      maxHeight: 240,
      overflowY: "scroll"
    }}>
      <table style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 12
      }}>
        <thead><tr style={{
            background: "#f1f5f9",
            position: "sticky",
            top: 0
          }}>{["Date", "Time", "Fridge", "Current °C", "Min °C", "Max °C", "By", "Comments"].map(h => <th key={h} style={{
              textAlign: "left",
              padding: "4px 8px"
            }}>{h}</th>)}</tr></thead>
        <tbody>{a.records.map((x, i) => <tr key={i} style={{
            borderTop: "1px solid #f1f5f9",
            background: x.inRange ? undefined : "#fef2f2"
          }}>
          <td style={{
              padding: "3px 8px",
              whiteSpace: "nowrap"
            }}>{fmtDate(x.date)}</td><td style={{
              padding: "3px 8px"
            }}>{x.time || "—"}</td><td style={{
              padding: "3px 8px"
            }}>{x.fridge}</td>
          <td style={{
              padding: "3px 8px"
            }}>{x.current ?? "—"}</td><td style={{
              padding: "3px 8px"
            }}>{x.min ?? "—"}</td><td style={{
              padding: "3px 8px",
              color: x.inRange ? undefined : "#A8071A",
              fontWeight: x.inRange ? 400 : 700
            }}>{x.max ?? "—"}</td>
          <td style={{
              padding: "3px 8px"
            }}>{x.staff || "—"}</td><td style={{
              padding: "3px 8px"
            }}>{x.notes || ""}</td></tr>)}</tbody>
      </table>
      {a.recordsTotal > a.records.length && <div style={{
        fontSize: 10,
        color: "#64748b",
        padding: "4px 8px"
      }}>Latest {a.records.length} of {a.recordsTotal} readings shown — the uploaded file holds them all.</div>}
    </div>}
    {reports && reports.length > 0 && <div style={{
      fontSize: 12,
      marginTop: 8
    }}><b>Reports:</b> {reports.slice(0, 8).map((r, i) => <a key={i} href={r.fileUrl} target="_blank" rel="noreferrer" style={{
        marginRight: 8
      }}>📄 {r.filename}</a>)}</div>}
    {a.mapping && <div style={{
      fontSize: 10,
      color: "#94a3b8",
      marginTop: 6
    }}>One row per reading; columns {a.mapping.via === "ai" ? "worked out by AI" : "matched by heading"}; all figures calculated from the rows.</div>}
  </div>;
}
export function CleaningPanel({
  a,
  compact = false,
  reports = null
}) {
  if (!a) return null;
  const st = CL_STATUS[a.status] || CL_STATUS.lapsed;
  const months = a.months || [];
  const max = Math.max(1, ...months.map(m => m.cleans));
  const pctCol = a.pctOnTimeYear == null ? "#5D5D5D" : a.pctOnTimeYear >= a.onTimeThreshold ? "#389E0D" : a.pctOnTimeYear >= a.onTimeThreshold - 10 ? "#AD4E00" : "#A8071A";
  const tile = (k, v, col) => <div key={k} style={{
    border: "1px solid #F0F0F0",
    borderRadius: 6,
    padding: "6px 8px"
  }}><div style={{
      fontSize: 10,
      color: "#5D5D5D"
    }}>{k}</div><div style={{
      fontSize: 18,
      fontWeight: 800,
      color: col || "#262626"
    }}>{v}</div></div>;
  return <div style={{
    border: "1px solid #F0F0F0",
    borderRadius: 8,
    padding: 10,
    background: "#fff",
    marginTop: 8
  }}>
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap"
    }}>
      <span style={{
        background: st.bg,
        color: st.fg,
        borderRadius: 10,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 700
      }}>{st.label}</span>
      <span style={{
        fontSize: 13
      }}><b>{a.total}</b> cleans logged · <b>{a.last30}</b> in the last 30 days · last clean {fmtDate(a.periodTo)}</span>
    </div>
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 8,
      marginTop: 10
    }}>
      {tile(`Done on time (12 months, target ${a.onTimeThreshold}%)`, a.pctOnTimeYear != null ? `${a.pctOnTimeYear}%` : "—", pctCol)}
      {tile("Missed cleans", a.missedCleans, a.missedCleans ? "#A8071A" : null)}
      {tile("Done late (out of schedule)", a.lateCleans, a.lateCleans ? "#AD4E00" : null)}
      {tile("Areas overdue now", `${a.overdueAreas} of ${a.areasTotal}`, a.overdueAreas ? "#A8071A" : null)}
    </div>
    <div aria-label="Cleans per month" style={{
      display: "flex",
      alignItems: "flex-end",
      gap: 3,
      height: 60,
      margin: "10px 0 2px",
      borderBottom: "1px solid #F0F0F0"
    }}>
      {months.map(m => {
        const before = !!a.periodFrom && m.month < a.periodFrom.slice(0, 7);
        return <div key={m.month} title={`${monthLabel(m.month)} ${m.month.slice(0, 4)}: ${before ? "before the log starts" : m.cleans}`} style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          height: "100%"
        }}>
        <span style={{
            fontSize: 9,
            color: before ? "#D9D9D9" : m.cleans ? "#434343" : "#CF1322"
          }}>{before ? "–" : m.cleans}</span>
        <div style={{
            width: "100%",
            height: `${m.cleans / max * 42}px`,
            minHeight: m.cleans ? 2 : 0,
            background: "#0E8CFF",
            borderRadius: "3px 3px 0 0",
            outline: m.cleans || before ? "none" : "1px dashed #FFA39E"
          }} /></div>;
      })}
    </div>
    <div style={{
      display: "flex",
      gap: 3
    }}>{months.map(m => <span key={m.month} style={{
        flex: 1,
        fontSize: 9,
        color: "#5D5D5D",
        textAlign: "center"
      }}>{monthLabel(m.month).slice(0, 1)}</span>)}</div>
    <div style={{
      fontSize: 10,
      color: "#5D5D5D",
      marginTop: 2
    }}>Cleans per month, last 12 months · dashed red = nothing logged · – = before the log starts</div>
    <div style={{
      fontWeight: 600,
      fontSize: 12,
      margin: "10px 0 4px"
    }}>What the figures show</div>
    <ul style={{
      margin: 0,
      paddingLeft: 18,
      fontSize: 12,
      lineHeight: 1.5
    }}>{(a.findings || []).map((f, i) => <li key={i}>{f}</li>)}</ul>
    {!compact && <div style={{
      marginTop: 10,
      border: "1px solid #F0F0F0",
      borderRadius: 6,
      maxHeight: 260,
      overflow: "auto"
    }}>
      <table style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 12
      }}>
        <thead><tr style={{
            background: "#F5F5F5",
            position: "sticky",
            top: 0
          }}>{["Area", "How often", "Last cleaned", "Cleans", "On time", "Missed", ""].map(h => <th key={h} style={{
              textAlign: "left",
              padding: "4px 8px"
            }}>{h}</th>)}</tr></thead>
        <tbody>{(a.areas || []).map(x => <tr key={x.area} style={{
            borderTop: "1px solid #F5F5F5"
          }}>
          <td style={{
              padding: "3px 8px",
              fontWeight: 600
            }}>{x.area}</td>
          <td style={{
              padding: "3px 8px"
            }} title={x.frequencySource === "worked out" ? "Worked out from the gaps between cleans" : "From the log"}>{x.frequency}{x.frequencySource === "worked out" ? "*" : ""}</td>
          <td style={{
              padding: "3px 8px",
              whiteSpace: "nowrap"
            }}>{x.lastCleaned ? fmtDate(x.lastCleaned) : "Never"}</td>
          <td style={{
              padding: "3px 8px"
            }}>{x.cleans}</td>
          <td style={{
              padding: "3px 8px"
            }}>{x.onTimePct != null ? `${x.onTimePct}%` : "—"}</td>
          <td style={{
              padding: "3px 8px",
              color: x.missed ? "#A8071A" : undefined
            }}>{x.missed}</td>
          <td style={{
              padding: "3px 8px",
              whiteSpace: "nowrap",
              color: x.overdue ? "#A8071A" : "#389E0D",
              fontWeight: 600
            }}>{x.neverLogged ? "Never logged" : x.overdue ? "Overdue" : "Up to date"}</td></tr>)}</tbody>
      </table>
      {(a.areas || []).some(x => x.frequencySource === "worked out") && <div style={{
        fontSize: 10,
        color: "#5D5D5D",
        padding: "4px 8px"
      }}>* frequency worked out from the gaps between cleans (no frequency in the log)</div>}
    </div>}
    {reports && reports.length > 0 && <div style={{
      fontSize: 12,
      marginTop: 8
    }}><b>Reports:</b> {reports.slice(0, 8).map((r, i) => <a key={i} href={r.fileUrl} target="_blank" rel="noreferrer" style={{
        marginRight: 8
      }}>📄 {r.filename}</a>)}</div>}
    {a.mapping && <div style={{
      fontSize: 10,
      color: "#8C8C8C",
      marginTop: 6
    }}>{a.mapping.layout === "grid" ? "Rota layout (areas × dates)" : "One row per clean"}; columns {a.mapping.via === "ai" ? "worked out by AI" : "matched by heading"}; all figures calculated from the rows.</div>}
  </div>;
}
export function NearMissPanel({
  a,
  compact = false,
  reports = null
}) {
  if (!a) return null;
  if (a.kind === "cleaning") return <CleaningPanel a={a} compact={compact} reports={reports} />;
  const lf = a.kind === "lfpse" || a.profile === "lfpse";
  const cp = a.kind === "complaints" || a.profile === "complaints";
  const many = lf ? "incidents" : cp ? "complaints" : "near misses";
  const st = (lf ? LF_STATUS : cp ? CP_STATUS : NM_STATUS)[a.status] || NM_STATUS.never;
  const months = (a.months || []).slice(compact ? -12 : -24);
  const max = Math.max(1, ...months.map(m => m.count));
  const bar = (pct, color = "#0E8CFF") => <span style={{
    display: "inline-block",
    height: 8,
    width: `${Math.max(2, pct)}%`,
    background: color,
    borderRadius: 4,
    verticalAlign: "middle"
  }} />;
  return <div style={{
    border: "1px solid #F0F0F0",
    borderRadius: 8,
    padding: 10,
    background: "#fff",
    marginTop: 8
  }}>
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap"
    }}>
      <span style={{
        background: st.bg,
        color: st.fg,
        borderRadius: 10,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 700
      }}>{st.label}</span>
      <span style={{
        fontSize: 13
      }}><b>{a.total}</b> {many} · <b>{a.last30}</b> in the last 30 days · last entry {fmtDate(a.lastEntry)}</span>
      {a.trend?.direction && <span style={{
        fontSize: 12,
        color: "#5D5D5D"
      }}>· trend {a.trend.direction === "up" ? "▲" : a.trend.direction === "down" ? "▼" : "▬"} {a.trend.recentAvg}/month (was {a.trend.priorAvg})</span>}
    </div>
    {cp && a.handling && <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 8,
      marginTop: 10
    }}>
      {[["Closed", a.handling.closed], ["Open", a.handling.open, a.handling.overdueOpen ? "#A8071A" : null], ["Avg days to close", a.handling.avgDaysToClose ?? "—"], [`Closed within ${a.handling.targetDays} days`, a.handling.pctWithinTarget != null ? `${a.handling.pctWithinTarget}%` : "—"]].map(([k, v, col]) => <div key={k} style={{
        border: "1px solid #F0F0F0",
        borderRadius: 6,
        padding: "6px 8px"
      }}><div style={{
          fontSize: 10,
          color: "#5D5D5D"
        }}>{k}</div><div style={{
          fontSize: 18,
          fontWeight: 800,
          color: col || "#262626"
        }}>{v}</div></div>)}
      {a.handling.overdueOpen > 0 && <div style={{
        gridColumn: "1 / -1",
        fontSize: 12,
        color: "#A8071A"
      }}><b>{a.handling.overdueOpen}</b> open for more than {a.handling.targetDays} days:
        {" "}{a.handling.overdueList.slice(0, 5).map(x => `${fmtDate(x.date)}${x.category ? ` (${x.category})` : ""}`).join(", ")}</div>}
    </div>}
    {lf && a.lfpse && <div style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
      marginTop: 10
    }}>
      <div><div style={{
          fontWeight: 600,
          fontSize: 12,
          marginBottom: 2
        }}>Referred to LFPSE</div>
        <div style={{
          fontSize: 22,
          fontWeight: 800,
          color: a.lfpse.pct >= 90 ? "#389E0D" : a.lfpse.pct >= 60 ? "#AD4E00" : "#A8071A"
        }}>{a.lfpse.pct}%</div>
        <div style={{
          fontSize: 12,
          color: "#5D5D5D"
        }}>{a.lfpse.referred} referred · {a.lfpse.notReferred} not · {a.lfpse.unknown} not recorded</div>
        {a.lfpse.notReferredSeriousCount > 0 && <div style={{
          fontSize: 12,
          color: "#A8071A",
          marginTop: 4
        }}><b>{a.lfpse.notReferredSeriousCount}</b> with moderate harm or worse not referred:
          <ul style={{
            margin: "2px 0 0",
            paddingLeft: 16
          }}>{a.lfpse.notReferredSerious.slice(0, 5).map((x, i) => <li key={i}>{fmtDate(x.date)} — {x.category || "incident"} ({x.harm})</li>)}</ul></div>}
      </div>
      <div><div style={{
          fontWeight: 600,
          fontSize: 12,
          marginBottom: 2
        }}>Degree of harm</div>
        {a.harm?.levels?.map(h => <div key={h.label} style={{
          fontSize: 12,
          display: "grid",
          gridTemplateColumns: "1fr 90px 34px",
          gap: 4,
          alignItems: "center"
        }}>
          <span>{h.label}</span><span>{bar(h.count / Math.max(1, ...a.harm.levels.map(x => x.count)) * 100, HARM_COLOUR[h.label])}</span><span style={{
            textAlign: "right"
          }}>{h.count}</span></div>)}
        {a.harm?.unknown > 0 && <div style={{
          fontSize: 11,
          color: "#5D5D5D"
        }}>Not recorded: {a.harm.unknown}</div>}
      </div>
    </div>}
    <div aria-label="Entries per month" style={{
      display: "flex",
      alignItems: "flex-end",
      gap: 3,
      height: 70,
      margin: "10px 0 2px",
      borderBottom: "1px solid #F0F0F0"
    }}>
      {months.map(m => <div key={m.month} title={`${monthLabel(m.month)} ${m.month.slice(0, 4)}: ${m.count}${m.partial ? " (so far)" : ""}`} style={{
        flex: 1,
        minWidth: 6,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        height: "100%"
      }}>
        <span style={{
          fontSize: 9,
          color: m.count ? "#434343" : "#CF1322"
        }}>{m.count}</span>
        <div style={{
          width: "100%",
          height: `${m.count / max * 52}px`,
          minHeight: m.count ? 2 : 0,
          background: m.count ? m.partial ? "#91D5FF" : "#0E8CFF" : "transparent",
          borderRadius: "3px 3px 0 0",
          outline: m.count ? "none" : "1px dashed #FFA39E"
        }} />
      </div>)}
    </div>
    <div style={{
      display: "flex",
      gap: 3
    }}>{months.map(m => <span key={m.month} style={{
        flex: 1,
        minWidth: 6,
        fontSize: 9,
        color: "#5D5D5D",
        textAlign: "center",
        overflow: "hidden"
      }}>{monthLabel(m.month).slice(0, 1)}</span>)}</div>
    <div style={{
      fontSize: 10,
      color: "#5D5D5D",
      marginTop: 2
    }}>Per month, {monthLabel(months[0]?.month || "2026-01")} {months[0]?.month.slice(0, 4)} – now · dashed red = nothing recorded · pale = this month so far</div>
    <div style={{
      fontWeight: 600,
      fontSize: 12,
      margin: "10px 0 4px"
    }}>What the figures show</div>
    <ul style={{
      margin: 0,
      paddingLeft: 18,
      fontSize: 12,
      lineHeight: 1.5
    }}>{(a.findings || []).map((f, i) => <li key={i}>{f}</li>)}</ul>
    {!compact && <div style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
      marginTop: 10
    }}>
      <div><div style={{
          fontWeight: 600,
          fontSize: 12,
          marginBottom: 2
        }}>{lf ? "Incident types" : cp ? "Complaint types" : "Error types"}</div>
        {(a.byGroup || []).map(g => <div key={g.label} title={`${g.pct}% of entries with an error type`} style={{
          fontSize: 12,
          display: "grid",
          gridTemplateColumns: "1fr 90px 34px",
          gap: 4,
          alignItems: "center"
        }}><span>{g.label}</span><span>{bar(g.count / (a.byGroup[0]?.count || 1) * 100)}</span><span style={{
            textAlign: "right"
          }}>{g.count}</span></div>)}
        {!a.byGroup?.length && <div style={{
          fontSize: 12,
          color: "#8C8C8C"
        }}>Not recorded</div>}</div>
      <div><div style={{
          fontSize: 12,
          fontWeight: 600,
          marginBottom: 2
        }}>{cp ? "How complaints were received" : lf ? "Medicines most often involved" : "Drugs most often involved"}</div>
        {((cp ? a.byStage : a.byDrug) || []).slice(0, 6).map(g => <div key={g.label} style={{
          fontSize: 12,
          display: "grid",
          gridTemplateColumns: "1fr 90px 34px",
          gap: 4,
          alignItems: "center"
        }}><span>{g.label}</span><span>{bar(g.count / (((cp ? a.byStage : a.byDrug) || [])[0]?.count || 1) * 100, "#2F54EB")}</span><span style={{
            textAlign: "right"
          }}>{g.count}</span></div>)}
        {!(cp ? a.byStage : a.byDrug)?.length && <div style={{
          fontSize: 12,
          color: "#8C8C8C"
        }}>Not recorded</div>}</div>
    </div>}
    {!compact && a.byWeekday && a.total >= 5 && <div style={{
      marginTop: 8
    }}><div style={{
        fontWeight: 600,
        fontSize: 12,
        marginBottom: 2
      }}>By day of the week</div>
      <div style={{
        display: "flex",
        gap: 4,
        alignItems: "flex-end",
        height: 34
      }}>{a.byWeekday.map(d => {
          const mx = Math.max(1, ...a.byWeekday.map(x => x.count));
          return <div key={d.day} title={`${d.day}: ${d.count}`} style={{
            flex: 1,
            textAlign: "center"
          }}>
        <div style={{
              height: d.count / mx * 22,
              minHeight: d.count ? 2 : 0,
              background: "#8C8C8C",
              borderRadius: 2
            }} /><div style={{
              fontSize: 9,
              color: "#5D5D5D"
            }}>{d.day.slice(0, 3)} {d.count}</div></div>;
        })}</div></div>}
    {a.completeness && <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginTop: 8
    }}>Filled in: {lf ? "incident" : cp ? "complaint" : "error"} type {a.completeness.category}%{cp ? "" : ` · drug ${a.completeness.drug}%`} · cause {a.completeness.cause}% · {cp ? "outcome" : "action/learning"} {a.completeness.action}%</div>}
    {reports && reports.length > 0 && <div style={{
      fontSize: 12,
      marginTop: 8
    }}><b>Summary reports:</b> {reports.slice(0, 8).map((r, i) => <a key={i} href={r.fileUrl} target="_blank" rel="noreferrer" style={{
        marginRight: 8
      }}>📄 {r.filename}</a>)}</div>}
    {a.mapping?.via === "pharmace" && <div style={{
      fontSize: 10,
      color: "#8C8C8C",
      marginTop: 6
    }}>From Pharmace patient feedback{a.mapping.groupsVia === "ai" ? "; complaint types grouped by AI from each complaint’s wording" : "; complaint types grouped by keywords"}; all figures calculated from the records.</div>}
    {a.mapping && a.mapping.via !== "pharmace" && <div style={{
      fontSize: 10,
      color: "#8C8C8C",
      marginTop: 6
    }}>Columns {a.mapping.via === "ai" ? "worked out by AI" : "matched by heading"}{a.mapping.groupsVia === "ai" ? ", error types grouped by AI" : ""}; all figures calculated from the rows.</div>}
  </div>;
}

// Every person a "team" item applies to at one branch, with their copy.
const PERSON_STATUS = {
  current: "Confirmed",
  "due-soon": "Renewal awaited",
  "needs-expiry": "Check needed",
  awaiting: "Task sent",
  chasing: "Renewal task sent",
  missing: "Not sent yet",
  expired: "Expired"
};
function TeamPanel({
  c,
  row,
  branch,
  token,
  onSaved
}) {
  const t = c.team;
  const [busy, setBusy] = useState(""),
    [err, setErr] = useState(""),
    [dates, setDates] = useState({}),
    [nums, setNums] = useState({});
  const act = async (key, url, body, msg) => {
    setBusy(key);
    setErr("");
    try {
      const r = await post(token, `${GRID}/${url}`, {
        rowId: row.rowId,
        dbName: branch.dbName,
        label: row.label,
        by: userName(),
        ...body
      });
      await onSaved(typeof msg === "function" ? msg(r) : msg);
    } catch (e) {
      setErr(e.message);
    }
    setBusy("");
  };
  const upload = async (p, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setErr("Files must be 8 MB or smaller");
      return;
    }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result).split(",")[1] || "");
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    await act(`up${p.staffId}`, "cell/team-upload", {
      staffId: p.staffId,
      file: {
        filename: file.name,
        dataBase64: b64
      },
      recordedBy: userName()
    }, r => r.rejected ? `Not accepted for ${p.name}: ${(r.check?.problems || []).join("; ")}` : `Copy for ${p.name} read: ${r.check?.verdict === "confirmed" ? "confirmed" : "check needed"}.`);
  };
  return <div style={{
    ...box,
    background: "#FAFAFA"
  }}>
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8
    }}>
      <div style={{
        fontWeight: 600,
        fontSize: 13,
        flex: 1
      }}>{t ? `${t.total} ${t.total === 1 ? "person" : "people"} at this branch · ${t.confirmed} confirmed` : "No tasks sent yet"}</div>
      <button disabled={!!busy} onClick={() => act("all", "cell/team-send", {}, r => r.sent.length ? `Task sent to ${r.sent.map(x => x.to).join(", ")}.` : r.people ? "Nobody needs a task right now." : "Nobody with the chosen role at this branch.")}>{busy === "all" ? "Sending…" : "Send to everyone who needs one"}</button>
    </div>
    {err && <div style={{
      color: "#A8071A",
      fontSize: 12,
      marginTop: 4
    }}>{err}</div>}
    {t && !t.total && <div style={{
      fontSize: 12,
      color: "#AD4E00",
      marginTop: 6
    }}>Nobody at this branch has one of the chosen roles — check staff roles, or the roles ticked in the item’s settings (⚙).</div>}
    {t?.people?.map(p => {
      const st = STATUS_STYLE[p.status] || STATUS_STYLE.unset;
      const ev = p.evidence;
      return <div key={p.staffId} style={{
        borderTop: "1px solid #F0F0F0",
        padding: "8px 0",
        fontSize: 12
      }}>
      <div style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap"
        }}>
        <b style={{
            fontSize: 13
          }}>{p.name}</b><span style={{
            color: "#5D5D5D"
          }}>{p.roleName}</span>
        <span style={{
            background: st.bg,
            color: st.fg,
            borderRadius: 8,
            padding: "1px 8px",
            fontWeight: 600
          }}>{PERSON_STATUS[p.status] || p.status}</span>
        <span style={{
            flex: 1
          }} />
        <button disabled={!!busy} onClick={() => act(`s${p.staffId}`, "cell/team-send", {
            staffId: p.staffId,
            force: true
          }, `Task sent to ${p.name}.`)} style={{
            fontSize: 11
          }}>{p.task?.status === "open" ? "Task open" : "Send task now"}</button>
        <label style={{
            fontSize: 11,
            cursor: "pointer",
            border: "1px solid #D9D9D9",
            borderRadius: 4,
            padding: "1px 6px",
            background: "#fff"
          }}>Upload a copy<input type="file" accept="image/*,.pdf" style={{
              display: "none"
            }} onChange={e => upload(p, e.target.files?.[0])} /></label>
      </div>
      {ev && <div style={{
          marginTop: 3,
          color: "#434343"
        }}>
        {ev.registrationNumber && <>Registration {ev.registrationNumber} · </>}{ev.nameOnDocument && <>“{ev.nameOnDocument}” · </>}{p.expiresAt ? <>expires <b>{fmtDate(p.expiresAt)}</b></> : "no expiry date yet"}
        {p.nextTaskAt && <> · next task {fmtDate(p.nextTaskAt)}</>}{ev.fileUrl && <> · <a href={ev.fileUrl} target="_blank" rel="noreferrer">view copy ↗</a></>}
        <span style={{
            color: "#8C8C8C"
          }}> · {ev.reviewedBy ? `confirmed by ${ev.reviewedBy}` : ev.verdict === "confirmed" ? "checked automatically" : "automatic check unsure"}</span></div>}
      {p.awaitingRenewal && <div style={{
          color: "#AD4E00"
        }}>The registration shown has passed its expiry date — the renewed copy is requested on {fmtDate(p.nextTaskAt)}.</div>}
      {p.status === "needs-expiry" && <div style={{
          marginTop: 4,
          background: "#FFFBE6",
          border: "1px solid #FFE58F",
          borderRadius: 6,
          padding: 6
        }}>
        <div style={{
            color: "#AD4E00"
          }}>Please check this copy: {(ev?.problems || []).join("; ") || "the automatic check wasn’t sure"}.</div>
        <div style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            marginTop: 4,
            flexWrap: "wrap"
          }}>
          Expiry date <input type="date" value={dates[p.staffId] || ""} onChange={e => setDates({
              ...dates,
              [p.staffId]: e.target.value
            })} />
          Registration no. <input value={nums[p.staffId] ?? ev?.registrationNumber ?? ""} onChange={e => setNums({
              ...nums,
              [p.staffId]: e.target.value
            })} style={{
              width: 80
            }} />
          <button disabled={!!busy || !dates[p.staffId]} onClick={() => act(`c${p.staffId}`, "cell/team-review", {
              staffId: p.staffId,
              action: "confirm",
              expiresAt: dates[p.staffId],
              registrationNumber: nums[p.staffId] ?? ev?.registrationNumber ?? ""
            }, `${p.name}’s registration confirmed.`)}>Confirm</button>
          <button disabled={!!busy} onClick={async () => {
              const reason = (await appPrompt(`Why can’t ${p.name}’s copy be accepted? (sent to them)`, "It isn’t clear enough to read — please send a clearer copy."));
              if (reason) act(`r${p.staffId}`, "cell/team-review", {
                staffId: p.staffId,
                action: "reject",
                reason
              }, `Returned to ${p.name}.`);
            }}>Return to them</button>
        </div></div>}
      {p.lastRejection && p.status !== "current" && <div style={{
          color: "#A8071A",
          marginTop: 2
        }}>Last copy not accepted: {p.lastRejection.join("; ")}</div>}
      {p.task?.status === "open" && <div style={{
          color: "#5D5D5D",
          marginTop: 2
        }}>Task sent {fmtDate(p.task.sentAt)}{p.task.reason === "renewal" ? " (renewal)" : p.task.reason === "rejected" ? " (sent back)" : ""}, due {fmtDate(p.task.dueDate)}.</div>}
    </div>;
    })}
    <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginTop: 6
    }}>Each copy is read automatically; the expiry date it shows sets that person’s next task ({(row.teamOffsetDays ?? 30) >= 0 ? `${row.teamOffsetDays ?? 30} days after` : `${Math.abs(row.teamOffsetDays)} days before`} it).</div>
  </div>;
}

// The latest website review for one site: each check, with the words found.
function WebChecks({
  review,
  keys = null
}) {
  if (!review) return null;
  if (review.error) return <div style={{
    fontSize: 12,
    color: "#A8071A"
  }}>Couldn’t read the website ({fmtDate(review.reviewedAt)}): {review.error}</div>;
  const list = (review.checks || []).filter(c => !keys || keys.includes(c.key));
  return <div>
    {list.map(c => <div key={c.key} style={{
      fontSize: 12,
      padding: "3px 0",
      borderBottom: "1px solid #F5F5F5"
    }}>
      <span style={{
        fontWeight: 700,
        color: c.found ? "#389E0D" : "#A8071A"
      }}>{c.found ? "✓" : "✗"}</span> <b>{c.label}</b>
      {c.quote && <div style={{
        color: "#5D5D5D",
        fontStyle: "italic",
        marginLeft: 14
      }}>“{c.quote}”{c.url && <> — <a href={c.url} target="_blank" rel="noreferrer">page ↗</a></>}</div>}
      {c.note && <div style={{
        color: "#5D5D5D",
        marginLeft: 14
      }}>{c.note}</div>}
    </div>)}
    <div style={{
      fontSize: 11,
      color: "#8C8C8C",
      marginTop: 4
    }}>Reviewed {new Date(review.reviewedAt).toLocaleString("en-GB")} · {review.pagesRead?.length || 0} page{review.pagesRead?.length === 1 ? "" : "s"} read · {review.via === "ai" ? "checked by AI, quotes verified on the page" : "checked by keyword rules"}</div>
  </div>;
}
function WebReviewPanel({
  elementId,
  site,
  token,
  dbName,
  onSaved
}) {
  const [busy, setBusy] = useState(false),
    [err, setErr] = useState("");
  const w = webRowStatus(elementId, site);
  const run = async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await post(token, `${GRID}/sites/review`, {
        dbName
      });
      await onSaved(r.review.error ? `Website couldn’t be read: ${r.review.error}` : `Website reviewed: ${r.review.passed} of ${r.review.total} checks passed.`);
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  };
  return <div style={{
    ...box,
    background: "#FAFAFA"
  }}>
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8
    }}>
      <div style={{
        fontWeight: 600,
        fontSize: 13,
        flex: 1
      }}>Website review{site?.websiteUrl ? <> — <a href={site.websiteUrl} target="_blank" rel="noreferrer">{site.websiteUrl}</a></> : ""}</div>
      <button disabled={busy || !site?.websiteUrl} onClick={run}>{busy ? "Reading the website…" : "Review now"}</button>
    </div>
    <div style={{
      fontSize: 12,
      color: STATUS_STYLE[w.status]?.fg,
      margin: "4px 0 6px"
    }}>{w.note}</div>
    {!site?.websiteUrl && <div style={{
      fontSize: 12,
      color: "#AD4E00"
    }}>Add this site’s website address under <b>Sites</b> (top of the grid).</div>}
    {err && <div style={{
      fontSize: 12,
      color: "#A8071A"
    }}>{err}</div>}
    <WebChecks review={site?.webReview} keys={WEB_ROW_CHECKS[elementId]} />
    <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginTop: 6
    }}>Re-checked automatically on the 1st of every month.</div>
  </div>;
}
// Which branches are distance selling pharmacies, and their websites.
function SitesDialog({
  branches,
  sites,
  token,
  displayName,
  masked,
  onClose,
  onChanged
}) {
  const [edit, setEdit] = useState(() => Object.fromEntries(branches.map(b => [b.dbName, {
    dsp: !!sites[b.dbName]?.dsp,
    websiteUrl: sites[b.dbName]?.websiteUrl || ""
  }])));
  const [busy, setBusy] = useState(""),
    [err, setErr] = useState(""),
    [open, setOpen] = useState(null);
  const save = async b => {
    setBusy(b.dbName);
    setErr("");
    try {
      await post(token, `${GRID}/sites/save`, {
        dbName: b.dbName,
        ...edit[b.dbName],
        updatedBy: userName()
      });
      await onChanged(`${b.pharmacyName} saved.`);
    } catch (e) {
      setErr(`${b.pharmacyName}: ${e.message}`);
    }
    setBusy("");
  };
  const review = async b => {
    setBusy(b.dbName);
    setErr("");
    try {
      if ((sites[b.dbName]?.websiteUrl || "") !== edit[b.dbName].websiteUrl || !!sites[b.dbName]?.dsp !== edit[b.dbName].dsp) await post(token, `${GRID}/sites/save`, {
        dbName: b.dbName,
        ...edit[b.dbName],
        updatedBy: userName()
      });
      const r = await post(token, `${GRID}/sites/review`, {
        dbName: b.dbName
      });
      setOpen(b.dbName);
      await onChanged(r.review.error ? `${b.pharmacyName}: website couldn’t be read — ${r.review.error}` : `${b.pharmacyName}: website reviewed, ${r.review.passed} of ${r.review.total} checks passed.`);
    } catch (e) {
      setErr(`${b.pharmacyName}: ${e.message}`);
    }
    setBusy("");
  };
  const changed = b => (sites[b.dbName]?.websiteUrl || "") !== edit[b.dbName].websiteUrl || !!sites[b.dbName]?.dsp !== edit[b.dbName].dsp;
  return <Modal title="Sites — location and distance services" onClose={onClose} width={900}>
    <p style={{
      fontSize: 13,
      margin: "0 0 10px",
      lineHeight: 1.5
    }}>Tick each branch that is a <b>distance selling pharmacy (DSP)</b> — an internet / mail-order pharmacy that may not provide NHS essential services face to face.
      Once at least one branch is ticked, the <b>DSP criteria (GPhC)</b> and <b>DSP criteria (NHS)</b> tabs appear on the Compliance view and in this grid, for the ticked branches only. Enter each DSP’s website so it can be reviewed automatically.</p>
    <table style={{
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 13
    }}>
      <thead><tr style={{
          background: "#F5F5F5",
          textAlign: "left"
        }}><th style={{
            padding: 8
          }}>Branch</th><th style={{
            padding: 8
          }}>Detected location</th><th style={{
            padding: 8
          }}>DSP</th><th style={{
            padding: 8
          }}>Website</th><th style={{
            padding: 8
          }}>Last website review</th><th /></tr></thead>
      <tbody>{branches.map(b => {
          const s = sites[b.dbName] || {};
          const e = edit[b.dbName];
          const r = s.webReview;
          return <React.Fragment key={b.dbName}>
        <tr style={{
              borderTop: "1px solid #F0F0F0"
            }}>
          <td style={{
                padding: 8,
                fontWeight: 600
              }}>{displayName(b.pharmacyName, masked)}</td>
          <td style={{
                padding: 8
              }}>{badge(locationOf(b, {
                  sites
                }))}<div style={{
                  fontSize: 11,
                  color: "#475569"
                }}>{locationOf(b, {
                    sites
                  })?.postcode || "No pharmacy postcode"}</div></td>
          <td style={{
                padding: 8
              }}><input type="checkbox" aria-label={`${b.pharmacyName} is a DSP`} checked={e.dsp} onChange={x => setEdit({
                  ...edit,
                  [b.dbName]: {
                    ...e,
                    dsp: x.target.checked
                  }
                })} /></td>
          <td style={{
                padding: 8
              }}><input value={e.websiteUrl} onChange={x => setEdit({
                  ...edit,
                  [b.dbName]: {
                    ...e,
                    websiteUrl: x.target.value
                  }
                })} placeholder={e.dsp ? "https://www.…" : "(only needed for a DSP)"} style={{
                  width: "100%"
                }} /></td>
          <td style={{
                padding: 8,
                fontSize: 12
              }}>{r ? r.error ? <span style={{
                  color: "#A8071A"
                }}>Couldn’t read ({fmtDate(r.reviewedAt)})</span> : <button onClick={() => setOpen(open === b.dbName ? null : b.dbName)} style={{
                  fontSize: 12
                }}>{r.passed}/{r.total} passed · {fmtDate(r.reviewedAt)} {open === b.dbName ? "▾" : "▸"}</button> : <span style={{
                  color: "#8C8C8C"
                }}>—</span>}</td>
          <td style={{
                padding: 8,
                whiteSpace: "nowrap"
              }}>
            <button disabled={busy === b.dbName || !changed(b)} onClick={() => save(b)}>Save</button>{" "}
            <button disabled={busy === b.dbName || !e.websiteUrl.trim() || locationOf(b, {
                  sites
                })?.regulator !== "GPHC"} onClick={() => review(b)}>{busy === b.dbName ? "Working…" : "Review website"}</button></td>
        </tr>
        {open === b.dbName && r && <tr><td colSpan={5} style={{
                padding: "4px 12px 10px",
                background: "#FAFAFA"
              }}><WebChecks review={r} /></td></tr>}
      </React.Fragment>;
        })}</tbody>
    </table>
    {err && <div role="alert" style={{
      color: "#A8071A",
      marginTop: 8
    }}>{err}</div>}
    <div style={{
      fontSize: 12,
      color: "#5D5D5D",
      marginTop: 10
    }}>The website review reads only the address entered (and pages on the same site), and is repeated automatically on the 1st of every month for every DSP.</div>
    <div style={{
      marginTop: 12
    }}><button onClick={onClose} style={{
        fontWeight: 600
      }}>Close</button></div>
  </Modal>;
}

// Pharmace patient feedback at one branch: the system checks (part 2) or the
// complaints themselves (part 4) — number, time to close, each one viewable.
function FeedbackPartPanel({
  id,
  f,
  token,
  dbName
}) {
  const [list, setList] = useState(null),
    [err, setErr] = useState(""),
    [open, setOpen] = useState(null),
    [trend, setTrend] = useState(null);
  useEffect(() => {
    if (id !== "complaints--records") return;
    post(token, `${GRID}/complaints`, {
      dbName
    }).then(d => {
      setList(d.complaints || []);
      setTrend(d.analysis || null);
    }).catch(e => setErr(e.message));
  }, [id, token, dbName]);
  if (!f) return <div style={{
    ...box,
    background: "#FAFAFA",
    fontSize: 13,
    color: "#5D5D5D"
  }}>No Pharmace feedback data for this branch.</div>;
  const tick = (ok, text, fix) => <div style={{
    fontSize: 13,
    margin: "3px 0",
    color: ok === null ? "#5D5D5D" : ok ? "#389E0D" : "#A8071A"
  }}>{ok === null ? "?" : ok ? "✓" : "✗"} {text}{!ok && fix ? <span style={{
      color: "#5D5D5D"
    }}> — {fix}</span> : null}</div>;
  if (id === "complaints--system") return <div style={{
    ...box,
    background: "#FAFAFA"
  }}>
    <div style={{
      fontWeight: 600,
      fontSize: 13,
      marginBottom: 4
    }}>Pharmace patient feedback at this branch</div>
    {tick(f.inUse, f.inUse ? `In use — ${f.submissions} feedback response${f.submissions === 1 ? "" : "s"}, the latest ${fmtDate(f.lastSubmissionAt)}` : "Not in use — no feedback received yet", "display the feedback poster / QR code")}
    {tick(f.complaintsEnabled, f.complaintsEnabled === null ? "Complaints setting couldn’t be read" : "Complaints switched on (patients can tick “this is a complaint”)", "tick “Complaint option” in Patient feedback → Webpage design")}
    {tick(f.recipientSet, f.recipientSet === null ? "Complaint recipient couldn’t be read" : "A complaint recipient is set for this branch", "choose a recipient in Patient feedback → Branches")}
  </div>;
  const dt = d => d ? new Date(d).toLocaleDateString("en-GB") : "—";
  return <div style={{
    ...box,
    background: "#FAFAFA"
  }}>
    <div style={{
      fontWeight: 600,
      fontSize: 13,
      marginBottom: 6
    }}>Complaints received through Pharmace</div>
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 6
    }}>
      {[["Complaints", f.complaints, `${f.last12m} in the last 12 months`], ["Open", f.open, f.overdueOpen ? `${f.overdueOpen} over ${f.targetDays} days` : "none overdue", f.overdueOpen ? "#A8071A" : null], ["Days to close", f.avgDaysToClose ?? "—", f.medianDaysToClose != null ? `average · median ${f.medianDaysToClose}` : "average"], [`Within ${f.targetDays} days`, f.pctWithinTarget != null ? `${f.pctWithinTarget}%` : "—", `of ${f.closed} closed`]].map(([k, v, sub, col]) => <div key={k} style={{
        border: "1px solid #F0F0F0",
        background: "#fff",
        borderRadius: 6,
        padding: "6px 8px"
      }}><div style={{
          fontSize: 10,
          color: "#5D5D5D"
        }}>{k}</div>
          <div style={{
          fontSize: 18,
          fontWeight: 800,
          color: col || "#262626"
        }}>{v}</div><div style={{
          fontSize: 10,
          color: "#5D5D5D"
        }}>{sub}</div></div>)}
    </div>
    {err && <div style={{
      color: "#A8071A",
      fontSize: 12,
      marginTop: 6
    }}>{err}</div>}
    {list == null && !err ? <div style={{
      fontSize: 12,
      color: "#5D5D5D",
      marginTop: 6
    }}>Loading complaints…</div> : list && <div style={{
      marginTop: 8,
      border: "1px solid #F0F0F0",
      borderRadius: 6,
      maxHeight: 300,
      overflow: "auto",
      background: "#fff"
    }}>
      <table style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 12
      }}>
        <thead><tr style={{
            background: "#F5F5F5",
            position: "sticky",
            top: 0
          }}>{["Received", "Status", "Days", "Complaint"].map(h => <th key={h} style={{
              textAlign: "left",
              padding: "4px 8px"
            }}>{h}</th>)}</tr></thead>
        <tbody>{list.map(c => <React.Fragment key={c.id}>
          <tr onClick={() => setOpen(open === c.id ? null : c.id)} style={{
              borderTop: "1px solid #F5F5F5",
              cursor: "pointer",
              background: open === c.id ? "#E6F7FF" : undefined
            }} title="Click to see the complaint and its outcome">
            <td style={{
                padding: "4px 8px",
                whiteSpace: "nowrap"
              }}>{open === c.id ? "▾ " : "▸ "}{dt(c.submittedAt)}</td>
            <td style={{
                padding: "4px 8px",
                color: c.open ? c.days > 28 ? "#A8071A" : "#AD4E00" : "#389E0D"
              }}>{c.open ? "Open" : `Closed ${dt(c.closedAt)}`}</td>
            <td style={{
                padding: "4px 8px"
              }}>{c.days}</td>
            <td style={{
                padding: "4px 8px",
                maxWidth: 260,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>{c.comments || "—"}</td></tr>
          {open === c.id && <tr><td colSpan={4} style={{
                padding: "6px 10px 10px",
                background: "#FAFAFA",
                fontSize: 12,
                lineHeight: 1.5
              }}>
            <div style={{
                  whiteSpace: "pre-wrap"
                }}><b>Complaint:</b> {c.comments || "—"}</div>
            <div><b>Acknowledged:</b> {c.acknowledgementSentAt ? dt(c.acknowledgementSentAt) : "not recorded"}{c.reminderCount ? ` · ${c.reminderCount} reminder${c.reminderCount === 1 ? "" : "s"} sent to the branch` : ""}</div>
            {c.outcome ? <>
              {c.outcome.responseToPatient && <div style={{
                    whiteSpace: "pre-wrap"
                  }}><b>Response to patient{c.outcome.responseDateTime ? ` (${c.outcome.responseDateTime})` : ""}:</b> {c.outcome.responseToPatient}</div>}
              {c.outcome.investigationOutcome && <div style={{
                    whiteSpace: "pre-wrap"
                  }}><b>Investigation:</b> {c.outcome.investigationOutcome}</div>}
              {c.outcome.actionTaken && <div style={{
                    whiteSpace: "pre-wrap"
                  }}><b>Action taken:</b> {c.outcome.actionTaken}</div>}
              <div><b>Superintendent informed:</b> {c.outcome.superintendentInformed ? "yes" : "no"}</div>
            </> : <div style={{
                  color: "#AD4E00"
                }}>No outcome recorded yet.</div>}
            {c.reviewNote && <div><b>Head office note:</b> {c.reviewNote}</div>}
          </td></tr>}
        </React.Fragment>)}
          {!list.length && <tr><td colSpan={4} style={{
              padding: 8,
              color: "#5D5D5D"
            }}>No complaints received through Pharmace.</td></tr>}</tbody>
      </table></div>}
    {trend && <><div style={{
        fontWeight: 600,
        fontSize: 13,
        margin: "10px 0 0"
      }}>Patterns and trends</div><NearMissPanel a={trend} /></>}
  </div>;
}

// The photo (or a link for other files), shown where the evidence is described.
const isImageUrl = u => /\.(jpe?g|png|gif|webp)(\?|#|$)/i.test(String(u || ""));
function EvidencePreview({
  url,
  label = "Evidence"
}) {
  const [big, setBig] = useState(false);
  if (!url) return null;
  if (!isImageUrl(url)) return <div style={{
    marginTop: 4
  }}><a href={url} target="_blank" rel="noreferrer">Open the file ↗</a></div>;
  return <>
    <div style={{
      marginTop: 6
    }}>
      <img src={url} alt={label} onClick={() => setBig(true)} title="Click to enlarge" style={{
        maxWidth: "100%",
        maxHeight: 220,
        borderRadius: 6,
        border: "1px solid #F0F0F0",
        cursor: "zoom-in",
        display: "block"
      }} />
      <div style={{
        fontSize: 11,
        color: "#5D5D5D",
        marginTop: 2
      }}>Click to enlarge · <a href={url} target="_blank" rel="noreferrer">open in a new tab ↗</a></div>
    </div>
    {big && <div role="dialog" aria-label={label} onClick={() => setBig(false)} style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,42,0.85)",
      zIndex: 10100,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
      cursor: "zoom-out"
    }}>
      <img src={url} alt={label} style={{
        maxWidth: "95vw",
        maxHeight: "92vh",
        borderRadius: 8,
        boxShadow: "0 10px 40px rgba(0,0,0,0.5)"
      }} />
    </div>}
  </>;
}

// Every upload for this branch, each clickable to show the rows read from it,
// plus the combined record the figures are worked out from.
function UploadsBrowser({
  c,
  row,
  branch,
  token
}) {
  const [open, setOpen] = useState(null); // id | "combined"
  const [rows, setRows] = useState({}); // id -> { loading, error, sessions, summary }
  const a = c.evidence?.analysis;
  const list = c.uploads?.length ? c.uploads : a ? [{
    id: "current",
    at: c.evidence.at,
    by: c.evidence.by,
    via: c.evidence.via,
    fileUrl: c.evidence.fileUrl,
    filename: c.evidence.filename,
    rowsRead: a.rowsRead,
    periodFrom: a.periodFrom,
    periodTo: a.periodTo
  }] : [];
  if (!list.length && !a) return null;
  // (documents: no analysis — the file list below)
  const show = async u => {
    if (open === u.id) {
      setOpen(null);
      return;
    }
    setOpen(u.id);
    if (u.id === "current" || rows[u.id]) return;
    setRows(r => ({
      ...r,
      [u.id]: {
        loading: true
      }
    }));
    try {
      const d = await post(token, `${GRID}/cell/upload-detail`, {
        rowId: row.rowId,
        dbName: branch.dbName,
        uploadId: u.id
      });
      setRows(r => ({
        ...r,
        [u.id]: {
          sessions: d.upload.sessions,
          records: d.upload.records,
          summary: d.upload.summary
        }
      }));
    } catch (e) {
      setRows(r => ({
        ...r,
        [u.id]: {
          error: e.message
        }
      }));
    }
  };
  const dt = d => d ? new Date(d).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }) : "—";
  const isNm = !!LOG_TEXT[a?.kind];
  const lf = a?.kind === "lfpse";
  const cpk = a?.kind === "complaints";
  const clk = a?.kind === "cleaning";
  const nmTable = (records, note) => clk ? <div style={{
    margin: "6px 0 8px",
    border: "1px solid #F0F0F0",
    borderRadius: 6,
    maxHeight: 260,
    overflow: "auto",
    background: "#fff"
  }}>
    {note && <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      padding: "4px 8px",
      borderBottom: "1px solid #EDF2F7"
    }}>{note}</div>}
    <table style={{
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 12
    }}>
      <thead><tr style={{
          background: "#F5F5F5",
          position: "sticky",
          top: 0
        }}>{["Date", "Area", "How often", "By", "Done"].map(h => <th key={h} style={{
            textAlign: "left",
            padding: "4px 8px"
          }}>{h}</th>)}</tr></thead>
      <tbody>{(records || []).map((x, i) => <tr key={i} style={{
          borderTop: "1px solid #F5F5F5"
        }}>
        <td style={{
            padding: "3px 8px",
            whiteSpace: "nowrap"
          }}>{fmtDate(x.date)}{x.time ? ` ${x.time}` : ""}</td><td style={{
            padding: "3px 8px"
          }}>{x.area}{x.task ? ` — ${x.task}` : ""}</td>
        <td style={{
            padding: "3px 8px"
          }}>{x.frequency || ""}</td><td style={{
            padding: "3px 8px"
          }}>{x.staff || ""}</td>
        <td style={{
            padding: "3px 8px",
            color: x.done === false ? "#CF1322" : "#389E0D"
          }}>{x.done === false ? "Not done" : "✓"}</td></tr>)}
        {!(records || []).length && <tr><td colSpan={5} style={{
            padding: 8,
            color: "#5D5D5D"
          }}>No rows.</td></tr>}</tbody>
    </table></div> : <div style={{
    margin: "6px 0 8px",
    border: "1px solid #F0F0F0",
    borderRadius: 6,
    maxHeight: 260,
    overflow: "auto",
    background: "#fff"
  }}>
    {note && <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      padding: "4px 8px",
      borderBottom: "1px solid #EDF2F7"
    }}>{note}</div>}
    <table style={{
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 12
    }}>
      <thead><tr style={{
          background: "#F5F5F5",
          position: "sticky",
          top: 0
        }}>{(cpk ? ["Received", "About", "Closed", "How received", "Outcome"] : lf ? ["Date", "Incident type", "Medicine", "Harm", "LFPSE", "Action"] : ["Date", "Error type", "Drug", "Spotted", "Action / learning"]).map(h => <th key={h} style={{
            textAlign: "left",
            padding: "4px 8px"
          }}>{h}</th>)}</tr></thead>
      <tbody>{(records || []).map((x, i) => <tr key={i} style={{
          borderTop: "1px solid #F5F5F5"
        }}>
        <td style={{
            padding: "3px 8px",
            whiteSpace: "nowrap"
          }}>{fmtDate(x.date)}{x.time ? ` ${x.time}` : ""}</td><td style={{
            padding: "3px 8px"
          }}>{x.category || x.description || "—"}</td>
        <td style={{
            padding: "3px 8px"
          }}>{cpk ? x.closed ? fmtDate(x.closed) : <span style={{
              color: "#CF1322"
            }}>open</span> : x.drug || ""}</td>{lf ? <><td style={{
              padding: "3px 8px"
            }}>{x.harm || ""}</td><td style={{
              padding: "3px 8px"
            }}>{x.lfpse || <span style={{
                color: "#CF1322"
              }}>—</span>}</td></> : <td style={{
            padding: "3px 8px"
          }}>{x.stage || ""}</td>}<td style={{
            padding: "3px 8px"
          }}>{x.action || <span style={{
              color: "#CF1322"
            }}>none</span>}</td></tr>)}
        {!(records || []).length && <tr><td colSpan={6} style={{
            padding: 8,
            color: "#5D5D5D"
          }}>No rows.</td></tr>}</tbody>
    </table></div>;
  const table = (sessions, note) => <div style={{
    margin: "6px 0 8px",
    border: "1px solid #F0F0F0",
    borderRadius: 6,
    maxHeight: 260,
    overflow: "auto",
    background: "#fff"
  }}>
    {note && <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      padding: "4px 8px",
      borderBottom: "1px solid #EDF2F7"
    }}>{note}</div>}
    <table style={{
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 12
    }}>
      <thead><tr style={{
          background: "#F5F5F5",
          position: "sticky",
          top: 0
        }}>{["Pharmacist", "Registration no.", "Signed in", "Signed out"].map(h => <th key={h} style={{
            textAlign: "left",
            padding: "4px 8px"
          }}>{h}</th>)}</tr></thead>
      <tbody>{(sessions || []).map((x, i) => <tr key={i} style={{
          borderTop: "1px solid #F5F5F5"
        }}>
        <td style={{
            padding: "3px 8px"
          }}>{x.pharmacistName || "—"}</td><td style={{
            padding: "3px 8px"
          }}>{x.registration || ""}</td>
        <td style={{
            padding: "3px 8px",
            whiteSpace: "nowrap"
          }}>{dt(x.signInTime)}</td>
        <td style={{
            padding: "3px 8px",
            whiteSpace: "nowrap"
          }}>{x.open ? "Still open" : dt(x.signOutTime)}</td></tr>)}
        {!(sessions || []).length && <tr><td colSpan={4} style={{
            padding: 8,
            color: "#5D5D5D"
          }}>No rows.</td></tr>}</tbody>
    </table></div>;
  const btn = on => ({
    fontSize: 12,
    padding: "2px 8px",
    border: "1px solid #D9D9D9",
    borderRadius: 4,
    background: on ? "#0E8CFF" : "#fff",
    color: on ? "#fff" : "#262626",
    cursor: "pointer"
  });
  if (!a) return <div style={{
    marginTop: 8
  }}>
    <div style={{
      fontWeight: 600,
      fontSize: 13
    }}>Files uploaded so far ({list.length})</div>
    <table style={{
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 12,
      marginTop: 4
    }}><tbody>
      {list.map(u => <tr key={u.id} style={{
          borderTop: "1px solid #F0F0F0"
        }}>
        <td style={{
            padding: "4px 4px",
            whiteSpace: "nowrap"
          }}>{fmtDate(u.at)}</td>
        <td style={{
            padding: "4px 4px"
          }}>{u.by || ""}{u.via === "upload-head-office" ? " (head office)" : u.via?.startsWith("upload") ? " (email link)" : ""}</td>
        <td style={{
            padding: "4px 4px"
          }}>{u.fileUrl ? <a href={u.fileUrl} target="_blank" rel="noreferrer">{isImageUrl(u.fileUrl) ? "🖼 " : "📄 "}{u.filename || "file"} ↗</a> : u.filename}</td>
      </tr>)}
    </tbody></table>
    {(c.evidence?.files || []).filter(f => isImageUrl(f.fileUrl)).slice(0, 4).map(f => <EvidencePreview key={f.fileUrl} url={f.fileUrl} label={f.filename} />)}
  </div>;
  return <div style={{
    marginTop: 8
  }}>
    <div style={{
      fontWeight: 600,
      fontSize: 13
    }}>Data uploaded so far</div>
    {a && <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      margin: "4px 0"
    }}>
      <button style={btn(open === "combined")} onClick={() => setOpen(open === "combined" ? null : "combined")}>{open === "combined" ? "Hide" : "Show"} combined record</button>
      <span style={{
        fontSize: 12,
        color: "#5D5D5D"
      }}>{isNm ? `${a.total} distinct ${LOG_TEXT[a.kind].many}${a.uploads > 1 ? ` from ${a.uploads} log uploads` : ""} · ${fmtDate(a.periodFrom)}–${fmtDate(a.periodTo)}` : `${a.rowsRead} distinct sessions${a.uploads > 1 ? ` from ${a.uploads} uploads` : ""} · ${fmtDate(a.periodFrom)}–${fmtDate(a.periodTo)} · last 30 days ${a.daysCovered}/${a.tradingDays} days covered`}</span>
    </div>}
    {open === "combined" && a && (isNm ? nmTable(a.records, a.recordsTotal > (a.records || []).length ? `Newest ${a.records.length} of ${a.recordsTotal} entries` : "Every entry, duplicates across uploads counted once") : table(a.sessions, a.totalSessions > (a.sessions || []).length ? `Newest ${a.sessions.length} of ${a.totalSessions} sessions` : "Every session, duplicates across uploads counted once"))}
    <table style={{
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 12,
      marginTop: 4
    }}><tbody>
      {list.map(u => <React.Fragment key={u.id}>
        <tr style={{
            borderTop: "1px solid #F0F0F0"
          }}>
          <td style={{
              padding: "4px 4px",
              whiteSpace: "nowrap"
            }}>{fmtDate(u.at)}</td>
          <td style={{
              padding: "4px 4px"
            }}>{u.by || ""}{u.via === "google-sheet-auto" ? " (monthly re-read)" : u.via === "upload-head-office" ? " (head office)" : u.via?.startsWith("google") ? " (shared link)" : u.via?.startsWith("upload") ? " (email link)" : ""}</td>
          <td style={{
              padding: "4px 4px"
            }}>{u.part === "report" ? <>📄 Summary report · {u.filename}</> : <>{u.part === "raw" ? "Log · " : ""}{u.rowsRead != null ? `${u.rowsRead} rows` : ""}{u.periodFrom ? ` · ${fmtDate(u.periodFrom)}–${fmtDate(u.periodTo)}` : ""}</>}</td>
          <td style={{
              padding: "4px 4px",
              textAlign: "right",
              whiteSpace: "nowrap"
            }}>
            {u.part !== "report" && <button style={btn(open === u.id)} onClick={() => show(u)}>{open === u.id ? "Hide" : "Show rows"}</button>}{" "}
            {u.fileUrl && <a href={u.fileUrl} target="_blank" rel="noreferrer" title={u.filename || ""}>file ↗</a>}
          </td>
        </tr>
        {open === u.id && <tr><td colSpan={4}>
          {u.id === "current" ? table(a.sessions, "This upload was made before uploads were kept separately; it becomes the first stored upload next time one arrives.") : rows[u.id]?.loading ? <div style={{
                fontSize: 12,
                color: "#5D5D5D",
                padding: 6
              }}>Loading…</div> : rows[u.id]?.error ? <div style={{
                fontSize: 12,
                color: "#A8071A",
                padding: 6
              }}>{rows[u.id].error}</div> : isNm ? nmTable(rows[u.id]?.records, `Exactly as read from ${u.filename || "the file"}`) : table(rows[u.id]?.sessions, rows[u.id]?.summary?.mapping ? `Exactly as read from ${u.filename || "the file"} · columns: ${Object.entries(rows[u.id].summary.mapping.columns || {}).filter(([, v]) => v).map(([k, v]) => `${k} “${v}”`).join(", ")}` : null)}
        </td></tr>}
      </React.Fragment>)}
    </tbody></table>
  </div>;
}

// Speak up, Part 2: has the named person accepted the Guardian role (and sent the poster photo)?
function GuardianPanel({
  c,
  view,
  guide = {}
}) {
  const ok = (view.status === "current" || view.status === "due-soon") && c.evidence?.at;
  return <div style={{
    ...box,
    background: ok ? "#F6FFED" : "#FFFBE6",
    borderColor: ok ? "#D9F7BE" : "#FFE58F"
  }}>
    <div style={{
      fontWeight: 600,
      fontSize: 13,
      marginBottom: 4
    }}>{guide.roleTitle === "Guardian" ? "Freedom to Speak Up Guardian" : guide.roleTitle}</div>
    {!c.recipientName ? <div style={{
      fontSize: 13,
      color: "#AD4E00"
    }}>Choose the person below, then save — they are sent a task asking them to accept the role.</div> : ok ? <div style={{
      fontSize: 13,
      color: "#389E0D"
    }}>✔ <b>{c.recipientName}</b> {guide.posterGuardian ? c.evidence?.fileUrl ? "accepted the role and sent the photo of the poster in the staff room" : "accepted the role (before a poster photo was asked for — the photo is asked for at renewal)" : "confirmed and acknowledged the role"} on {fmtDate(c.evidence.at)}.{c.expiresAt ? ` They are asked again on ${fmtDate(new Date(new Date(c.expiresAt).getTime() - 30 * DAY))}.` : ""}</div> : c.task?.status === "open" ? <div style={{
      fontSize: 13,
      color: "#AD4E00"
    }}>Waiting for <b>{c.recipientName}</b> to {guide.posterGuardian ? "put up the poster and send the photo" : "confirm they accept the role"} (task sent {fmtDate(c.task.sentAt)}).</div> : <div style={{
      fontSize: 13,
      color: "#AD4E00"
    }}><b>{c.recipientName}</b> hasn’t confirmed the role yet.</div>}
    {guide.posterNote && <div style={{
      fontSize: 12,
      color: "#5D5D5D",
      marginTop: 4
    }}>Their name, email and phone number (from their staff record) are printed on the speak-up poster that is always emailed to them with the task.</div>}
  </div>;
}
// Speak up, Part 2: the printable poster naming the Guardian (also attached to their task email).
function PosterPanel({
  token,
  branch,
  guardian,
  recipientName
}) {
  const [busy, setBusy] = useState(""),
    [note, setNote] = useState(""),
    [err, setErr] = useState("");
  const run = async send => {
    setBusy(send ? "send" : "get");
    setErr("");
    setNote("");
    try {
      const r = await post(token, `${GRID}/cell/speakup-poster`, {
        dbName: branch.dbName,
        send
      });
      if (send) setNote(r.emailed ? `Poster emailed to ${r.to}.` : `Couldn’t email ${r.to || "them"}${r.email?.status === "no_address" ? " — no email address on their staff record" : ""}.`);else {
        const bytes = Uint8Array.from(atob(r.dataBase64), ch => ch.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], {
          type: "application/pdf"
        }));
        const a = document.createElement("a");
        a.href = url;
        a.download = r.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy("");
    }
  };
  return <div style={{
    ...box,
    background: "#E6F7FF",
    borderColor: "#91D5FF"
  }}>
    <div style={{
      fontWeight: 600,
      fontSize: 13,
      marginBottom: 4
    }}>Speak-up poster to print</div>
    {!guardian ? <div style={{
      fontSize: 13,
      color: "#AD4E00"
    }}>Choose the Freedom to Speak Up Guardian for this branch first — the poster gives them as the point of contact.</div> : <>
      <div style={{
        fontSize: 13
      }}>Point of contact: <b>{guardian.name}</b>{guardian.email ? ` · ${guardian.email}` : ""}{guardian.phone ? ` · ${guardian.phone}` : ""}</div>
      {!guardian.confirmed && <div style={{
        fontSize: 12,
        color: "#AD4E00",
        marginTop: 2
      }}>They haven’t accepted the role and sent the poster photo yet.</div>}
      {(!guardian.email || !guardian.phone) && <div style={{
        fontSize: 12,
        color: "#AD4E00",
        marginTop: 2
      }}>No {!guardian.email && !guardian.phone ? "email address or phone number" : !guardian.email ? "email address" : "phone number"} on their staff record — add it there so it prints on the poster.</div>}
      <div style={{
        display: "flex",
        gap: 8,
        marginTop: 8,
        flexWrap: "wrap"
      }}>
        <button onClick={() => run(false)} disabled={!!busy}>{busy === "get" ? "Preparing…" : "Download poster (PDF)"}</button>
        <button onClick={() => run(true)} disabled={!!busy || !recipientName}>{busy === "send" ? "Sending…" : `Email the poster again to ${recipientName || "the Guardian"}`}</button>
      </div>
      {!recipientName && <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginTop: 4
      }}>Choose the Guardian below to email it to them.</div>}
    </>}
    {note && <div style={{
      fontSize: 12,
      color: "#389E0D",
      marginTop: 6
    }}>{note}</div>}
    {err && <div style={{
      fontSize: 12,
      color: "#A8071A",
      marginTop: 6
    }}>{err}</div>}
  </div>;
}
function CellDialog(props) {
  if (isSopCascade(props.row)) return <Modal title={props.row.label} onClose={props.onClose} width={1100}><SopCascadeDetails row={props.row} dbName={props.branch.dbName} token={props.token} readOnly={true} onChanged={props.onSaved} /></Modal>;
  if (isSopManagement(props.row)) return <Modal title={props.row.label} onClose={props.onClose} width={1000}><SopManagementDetails row={props.row} dbName={props.branch.dbName} token={props.token} readOnly={true} onChanged={props.onSaved} /></Modal>;
  return <StandardCellDialog {...props} />;
}
function StandardCellDialog({
  row,
  branch,
  branches,
  view,
  token,
  liveModule,
  displayName,
  masked,
  feedback = null,
  site = null,
  guardian = null,
  onClose,
  onSaved
}) {
  branches = branches.filter(b => rowApplicability(row, b.jurisdiction).applicable);
  const jurisdictionDetail = detailsFor(row, branch.jurisdiction || site?.jurisdiction);
  const c = view.c || {};
  const baseGuide = GUIDANCE[row.elementId] || {};
  const guide = row.elementId === 'reg-pharmacist' && jurisdictionDetail.regulator === 'PSNI' ? {
    ...baseGuide,
    satisfies: jurisdictionDetail.help,
    references: [{
      label: jurisdictionDetail.reference,
      url: jurisdictionDetail.link
    }]
  } : baseGuide;
  const [upFile, setUpFile] = useState(null),
    [upLink, setUpLink] = useState(""),
    [upResult, setUpResult] = useState(null);
  const [upFiles, setUpFiles] = useState([]);
  const [cellNote, setCellNote] = useState(c.inspectorNote || "");
  const docs = guide.upload === "documents";
  const nm = !!LOG_TEXT[guide.upload];
  const LT = LOG_TEXT[guide.upload] || LOG_TEXT["near-miss"];
  const [upReports, setUpReports] = useState([]);
  const autoModule = row.elementId ? liveModule?.(row.elementId) : null;
  const taskOffered = !(guide.task === false || guide.sources && !guide.sources.includes("task"));
  // A documents upload and a task are one request (task + email link), so an
  // item offering both shows only the task.
  const docsAsTask = docs && taskOffered;
  const [source, setSource] = useState((s0 => s0 === "upload" && docsAsTask ? "task" : s0)(c.source || defaultSourceOf(row) || (autoModule ? "pharmace" : guide.web ? "web" : guide.team ? "team" : "task")));
  const [moduleName, setModuleName] = useState(c.moduleName || autoModule || "");
  const [recipient, setRecipient] = useState(c.recipientStaffId || "");
  const [staff, setStaff] = useState(null);
  const [linkUrl, setLinkUrl] = useState(c.linkUrl || ""),
    [linkLabel, setLinkLabel] = useState(c.linkLabel || "");
  const [autoChase, setAutoChase] = useState(c.autoChase !== false);
  const [applyAll, setApplyAll] = useState(false);
  const [err, setErr] = useState(""),
    [msg, setMsg] = useState(""),
    [saving, setSaving] = useState(false);
  const [evDate, setEvDate] = useState(isoDay(new Date())),
    [evExpiry, setEvExpiry] = useState(""),
    [evNote, setEvNote] = useState("");
  const [evFile, setEvFile] = useState(null),
    [setExp, setSetExp] = useState("");
  const mode = row.validity?.mode || "none";
  // "Valid until" for this branch: a hand-set date, else what the row's rule
  // works out from the evidence (shown, not saved).
  const [validUntil, setValidUntil] = useState(isoDay(c.validUntil));
  useEffect(() => {
    setValidUntil(isoDay(c.validUntil));
  }, [c.validUntil, c.evidence?.nextDueAt]);
  const ruleDate = !c.validUntil && c.expiresAt ? c.expiresAt : null;
  const [w, setW] = useState({
    explanation: row.explanation || row.help || "",
    instructions: row.instructions || "",
    link: row.link || ""
  });
  const canTask = source === "task" || source === "upload" || source === "link" || source === "pharmace";
  const readB64 = f => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1] || "");
    r.onerror = rej;
    r.readAsDataURL(f);
  });
  const days = row.validity?.days || 365,
    warn = row.validity?.warnDays ?? 14;
  const effectiveExpiry = validUntil ? new Date(`${validUntil}T00:00:00`) : c.expiresAt ? new Date(c.expiresAt) : null;
  const renewalOn = effectiveExpiry ? new Date(effectiveExpiry.getTime() - warn * DAY) : null;
  const recipientName = (staff || []).find(x => x.staffId === recipient)?.name || (recipient === c.recipientStaffId ? c.recipientName : "");
  // Saving a task cell with a person but no evidence and no open task sends
  // the first task straight away (the 6-hourly check would send it anyway).
  const willSendFirst = (source === "task" || source === "upload") && !!recipient && autoChase && !c.evidence?.at && c.task?.status !== "open";
  useEffect(() => {
    let off = false;
    post(token, `${API_BASE}/api/headoffice/handover-tasks/staff`, {
      dbName: branch.dbName
    }).then(d => {
      if (!off) setStaff(d.staff || []);
    }).catch(e => {
      if (!off) {
        setStaff([]);
        setErr(`Could not load staff: ${e.message}`);
      }
    });
    return () => {
      off = true;
    };
  }, [token, branch.dbName]);
  const save = async andSend => {
    setSaving(true);
    setErr("");
    setMsg("");
    try {
      const person = (staff || []).find(s => s.staffId === recipient);
      if (andSend && !person) throw new Error("Choose who should receive the task");
      if (source === "task" && !w.explanation.trim()) throw new Error("Add a short explanation of why this matters - it goes in the task");
      // Link details only matter for "held in another system"; tidy them.
      const lk = source === "link" ? normLink(linkUrl, linkLabel) : {
        url: "",
        label: ""
      };
      if (source === "link" && !lk.url && !lk.label) throw new Error("Say where the information is held (a link is optional)");
      const linkUrlS = lk.url,
        linkLabelS = lk.label;
      // Wording is shared by every branch's tasks for this row.
      await post(token, `${GRID}/row/wording`, {
        rowId: row.rowId,
        ...w,
        link: /^https?:\/\//i.test(String(w.link || "").trim()) ? String(w.link).trim() : "",
        groupId: row.groupId,
        itemId: row.itemId
      });
      if (applyAll) await post(token, `${GRID}/cell/save`, {
        rowId: row.rowId,
        dbNames: branches.map(b => b.dbName),
        source,
        moduleName,
        linkUrl: linkUrlS,
        linkLabel: linkLabelS,
        autoChase,
        rowLabel: row.label
      });
      await post(token, `${GRID}/cell/save`, {
        rowId: row.rowId,
        dbName: branch.dbName,
        source,
        moduleName,
        linkUrl: linkUrlS,
        linkLabel: linkLabelS,
        autoChase,
        rowLabel: row.label,
        inspectorNote: cellNote,
        validUntil: source === "na" ? "" : validUntil || "",
        ...(canTask ? {
          recipientStaffId: recipient || null,
          recipientName: person?.name || ""
        } : {})
      });
      let note = applyAll ? `Saved for all ${branches.length} branches (recipient set for ${branch.pharmacyName} only).` : "Saved.";
      if (andSend) {
        await post(token, `${GRID}/cell/send-task`, {
          rowId: row.rowId,
          dbName: branch.dbName,
          label: row.label
        });
        note += source === "upload" && !docs ? ` Upload request emailed to ${person.name}.` : ` Task and email sent to ${person.name}.`;
      }
      await onSaved(note);
      onClose();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };
  const sendNow = async () => {
    setSaving(true);
    setErr("");
    try {
      await post(token, `${GRID}/cell/send-task`, {
        rowId: row.rowId,
        dbName: branch.dbName,
        label: row.label
      });
      await onSaved(`Task sent to ${c.recipientName}.`);
      onClose();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };
  const recordEvidence = async () => {
    setSaving(true);
    setErr("");
    try {
      let attachment = null;
      if (evFile) {
        if (evFile.size > 8 * 1024 * 1024) throw new Error("Files must be 8 MB or smaller");
        const b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result).split(",")[1] || "");
          r.onerror = rej;
          r.readAsDataURL(evFile);
        });
        attachment = {
          filename: evFile.name,
          dataBase64: b64
        };
      }
      if (mode === "expiry-date" && !evExpiry) throw new Error("This row lasts until an expiry date — enter it");
      await post(token, `${GRID}/cell/record-evidence`, {
        rowId: row.rowId,
        dbName: branch.dbName,
        date: evDate,
        expiresAt: evExpiry || null,
        note: evNote,
        attachment,
        recordedBy: userName() || "Head office",
        rowLabel: row.label
      });
      await onSaved("Evidence recorded.");
      onClose();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };
  const sheetAction = async what => {
    setSaving(true);
    setErr("");
    try {
      if (what === "unlink" && !(await appConfirm("Stop re-reading this shared spreadsheet each month? Uploads already received are kept."))) {
        setSaving(false);
        return;
      }
      const r = await post(token, `${GRID}/cell/${what === "unlink" ? "sheet-unlink" : "sheet-reread"}`, {
        rowId: row.rowId,
        dbName: branch.dbName,
        by: userName()
      });
      await onSaved(what === "unlink" ? "Shared spreadsheet unlinked." : `Shared spreadsheet re-read: ${r.message}.`);
      setSaving(false);
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };
  const uploadNow = async () => {
    setSaving(true);
    setErr("");
    setUpResult(null);
    try {
      let body;
      if (nm) {
        const all = [...upFiles, ...upReports];
        if (!upFiles.length && !upLink.trim() && !upReports.length) throw new Error(`Add the ${LT.log} (Part 1) or a report (Part 2)`);
        if (all.length > 10) throw new Error("Up to 10 files at a time");
        if (all.some(f => f.size > 8 * 1024 * 1024)) throw new Error("Each file must be 8 MB or smaller");
        const enc = l => Promise.all(l.map(async f => ({
          filename: f.name,
          dataBase64: await readB64(f)
        })));
        body = {
          raw: await enc(upFiles),
          reports: await enc(upReports),
          link: upLink.trim() || undefined
        };
      } else if (docs) {
        if (!upFiles.length) throw new Error("Choose one or more files");
        if (upFiles.length > 10) throw new Error("Up to 10 files at a time");
        if (upFiles.some(f => f.size > 8 * 1024 * 1024)) throw new Error("Each file must be 8 MB or smaller");
        body = {
          files: await Promise.all(upFiles.map(async f => ({
            filename: f.name,
            dataBase64: await readB64(f)
          })))
        };
      } else if (upFile) {
        if (upFile.size > 8 * 1024 * 1024) throw new Error("Files must be 8 MB or smaller");
        const b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result).split(",")[1] || "");
          r.onerror = rej;
          r.readAsDataURL(upFile);
        });
        body = {
          file: {
            filename: upFile.name,
            dataBase64: b64
          }
        };
      } else if (upLink.trim()) body = {
        link: upLink.trim()
      };else throw new Error("Attach a spreadsheet or paste a shared spreadsheet link");
      const r = await post(token, `${GRID}/cell/upload`, {
        rowId: row.rowId,
        dbName: branch.dbName,
        rowLabel: row.label,
        recordedBy: userName() || "Head office",
        ...body
      });
      setUpResult(nm ? null : r.analysis || null);
      setSaving(false);
      if (nm) {
        setUpFiles([]);
        setUpReports([]);
        setUpLink("");
      }
      await onSaved(nm ? r.summary || "Uploaded." : r.analysis ? `Spreadsheet read: ${r.analysis.rowsRead} RP sessions.` : r.files ? `${r.files.length} file${r.files.length === 1 ? "" : "s"} uploaded.` : "File saved.");
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };
  const saveExpiry = async () => {
    setSaving(true);
    setErr("");
    try {
      await post(token, `${GRID}/cell/set-expiry`, {
        rowId: row.rowId,
        dbName: branch.dbName,
        expiresAt: setExp
      });
      await onSaved("Expiry date saved.");
      onClose();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };
  const st = STATUS_STYLE[view.status] || STATUS_STYLE.unset;
  return <Modal title={`${row.label} — ${displayName(branch.pharmacyName, masked)}`} onClose={onClose} width={1180}>
      {/* Two halves: left = where the evidence comes from and its schedule;
          right = who is asked, wording, note, save, uploads and evidence. */}
      <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
      gap: 20,
      alignItems: "start"
    }}>
      <div style={{
        minWidth: 0
      }}>
      <div style={{
          fontSize: 12,
          color: "#5D5D5D"
        }}>{validitySummary(row)}</div>
      <div style={{
          marginTop: 8,
          display: "inline-block",
          background: st.bg,
          color: st.fg,
          borderRadius: 10,
          padding: "3px 10px",
          fontSize: 12,
          fontWeight: 600
        }}>
        {st.label}{view.line2 && view.line2 !== st.label ? ` · ${view.line2}` : ""}</div>

      <label style={field}>Data source at this branch</label>
      <div style={{
          display: "grid",
          gap: 4,
          fontSize: 13
        }}>
        {guide.pharmace === false ? null : <label><input type="radio" checked={source === "pharmace"} onChange={() => setSource("pharmace")} /> Pharmace module {autoModule ? <span style={{
              color: "#5D5D5D"
            }}>(live: {autoModule})</span> : null}</label>}
        {guide.web && <label><input type="radio" checked={source === "web"} onChange={() => setSource("web")} /> Automatic website review — the site’s website is read and checked</label>}
        {guide.team && <label><input type="radio" checked={source === "team"} onChange={() => setSource("team")} /> Task to every {guide.teamKind === "technician" ? "pharmacy technician" : "pharmacist"} at the branch — each sends a copy of their registration</label>}
        {guide.sop && <label><input type="radio" checked={source === "sop"} onChange={() => setSource("sop")} /> Pharmace SOPs — linked in the item’s settings ({(row.sopTitles || []).join(", ") || "none chosen yet"})</label>}
        {!taskOffered ? null : <label><input type="radio" checked={source === "task"} onChange={() => setSource("task")} /> Task + email to a team member — for a photo or document the email carries an upload link too; they do one or the other</label>}
        {guide.upload && !docsAsTask && <label><input type="radio" checked={source === "upload"} onChange={() => setSource("upload")} /> {docs ? "Task + email with an upload link — they upload one or more files with the link, or attach them to the task in the staff app" : nm ? `Email request to upload data — a secure link for the ${LT.log} and any summary reports` : "Email request to upload data — they get a secure link to upload the spreadsheet"}</label>}
        {(!guide.sources || guide.sources.includes("link")) && <label><input type="radio" checked={source === "link"} onChange={() => setSource("link")} /> Held in another system — say where (a link is optional)</label>}
        {(!guide.sources || guide.sources.includes("na")) && <label><input type="radio" checked={source === "na"} onChange={() => setSource("na")} /> Not applicable at this branch</label>}
        {guide.onlyReason && <div style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>{guide.onlyReason}</div>}
      </div>

      {!["task", "link", "upload"].includes(source) && <ComplianceNextDue cell={c} rowId={row.rowId} dbName={branch.dbName} token={token} readOnly={true} onChanged={onSaved} />}
      <TrainingResults cell={c} />
      {source === "team" && <TeamPanel c={c} row={row} branch={branch} token={token} onSaved={onSaved} />}
      {source === "web" && <WebReviewPanel elementId={row.elementId} site={site} token={token} dbName={branch.dbName} onSaved={onSaved} />}
      {guide.guardian && source === "task" && <GuardianPanel c={c} view={view} guide={guide} />}
      {GUIDANCE[row.elementId]?.posterGuardian && <PosterPanel token={token} branch={branch} guardian={guardian} recipientName={c.recipientName} />}
      {source === "pharmace" && FEEDBACK_MODULE[row.elementId] && <FeedbackPartPanel id={row.elementId} f={feedback} token={token} dbName={branch.dbName} />}
      {source === "pharmace" && !FEEDBACK_MODULE[row.elementId] && <>
        <label style={field}>Module</label>
        <input value={moduleName} onChange={e => setModuleName(e.target.value)} placeholder="e.g. Cleaning, CD register" style={{
            width: "100%"
          }} />
        {!autoModule && <div style={{
            fontSize: 11,
            color: "#AD4E00",
            marginTop: 4
          }}>This item isn’t wired to a live Pharmace feed yet, so the grid can’t show a done/not-done status for it.</div>}
      </>}
      {source === "link" && <>
        <label style={field}>Where the information is held</label>
        <input value={linkLabel} onChange={e => setLinkLabel(e.target.value)} placeholder={guide.linkPlaceholder || "e.g. Fire safety log (SharePoint)"} style={{
            width: "100%"
          }} />
        <label style={field}>Link (optional)</label>
        <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://…" style={{
            width: "100%"
          }} />
        {c.linkUrl && <div style={{
            fontSize: 12,
            marginTop: 4
          }}><a href={c.linkUrl} target="_blank" rel="noreferrer">Open current link ↗</a></div>}
      </>}
      {source === "sop" && <div style={{
          ...box,
          background: "#FAFAFA"
        }}>
        <div style={{
            fontWeight: 600,
            fontSize: 13,
            marginBottom: 4
          }}>Linked SOPs at this branch</div>
        {!(row.sopTitles || []).length ? <div style={{
            fontSize: 13,
            color: "#AD4E00"
          }}>No SOPs linked yet — choose them in the item’s settings (⚙).</div> : !c.sop ? <div style={{
            fontSize: 13,
            color: "#5D5D5D"
          }}>Save to see the status at this branch.</div> : <table style={{
            width: "100%",
            fontSize: 13,
            borderCollapse: "collapse"
          }}><tbody>{c.sop.matched.map(m => <tr key={m.title} style={{
                borderTop: "1px solid #EDF2F7"
              }}>
              <td style={{
                  padding: "4px 4px"
                }}>{m.title}{m.version ? ` (v${m.version})` : ""}</td>
              <td style={{
                  padding: "4px 4px",
                  color: !m.held ? "#A8071A" : !m.inDate ? "#AD4E00" : "#389E0D"
                }}>{!m.held ? "Not held at this branch" : !m.inDate ? `Past review date (${fmtDate(m.expiryDate)})` : `In place${m.expiryDate ? ` · review by ${fmtDate(m.expiryDate)}` : ""}`}</td>
              <td style={{
                  padding: "4px 4px",
                  color: c.sop.requireSigned && m.held && !(m.assigned > 0 && m.signed >= m.assigned) ? "#A8071A" : "#5D5D5D"
                }}>{m.held && m.assigned ? `${m.signed}/${m.assigned} staff read & signed` : m.held && c.sop.requireSigned ? "Not assigned to any staff yet" : ""}</td></tr>)}</tbody></table>}
        {c.sop?.requireSigned && <div style={{
            fontSize: 12,
            color: "#5D5D5D",
            marginTop: 6
          }}>For this part every staff member the SOP is assigned to must have read and signed it.</div>}
      </div>}
      {source === "pharmace" && <>
        <label style={field}>Review by (optional)</label>
        <div style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            fontSize: 13
          }}>
          <input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} />
          {validUntil && <button onClick={() => setValidUntil("")}>Clear</button>}
          <span style={{
              color: "#5D5D5D",
              fontSize: 12
            }}>Set a date if this branch’s arrangement should be checked again; the reviewer gets a task {warn} days before.</span>
        </div>
      </>}
      {(source === "task" || source === "link" || source === "upload") && <div style={{
          ...box,
          background: "#FAFAFA"
        }}>
        <div style={{
            fontWeight: 600,
            fontSize: 13,
            marginBottom: 4
          }}>Schedule at this branch</div>
        <div style={{
            fontSize: 13,
            lineHeight: 1.6
          }}>
          {mode === "days" && <div>Evidence is needed every <b>{days} days</b>, counted from the date of each upload.</div>}
          {mode === "expiry-date" && <div>Evidence lasts until the <b>expiry date</b> on the {row.evidenceType === "photo" ? "item photographed" : "document"} (entered when it arrives).</div>}
          {mode === "none" && <div>No expiry — once evidenced it stays current (set an override below if it needs renewing).</div>}
          {c.evidence?.at ? <>
            <div>Last evidence: <b>{fmtDate(c.evidence.at)}</b>{c.evidence.by ? ` by ${c.evidence.by}` : ""}{c.evidence.fileUrl && !c.evidence.analysis && !isImageUrl(c.evidence.fileUrl) && <> · <a href={c.evidence.fileUrl} target="_blank" rel="noreferrer">view</a></>}</div>
            {!c.evidence.analysis && isImageUrl(c.evidence.fileUrl) && <EvidencePreview url={c.evidence.fileUrl} label={`${row.label} — ${branch.pharmacyName}`} />}
            {effectiveExpiry ? <div>Valid until: <b>{fmtDate(effectiveExpiry)}</b>{c.validUntil ? " (set by hand)" : c.evidence?.nextDueAt ? " (stored next due date)" : mode === "days" ? ` (${fmtDate(c.evidence.at)} + ${days} days)` : ""}</div> : mode === "expiry-date" ? <div style={{
                color: "#AD4E00"
              }}>Valid until: expiry date not entered yet — add it under Evidence below.</div> : null}
            {effectiveExpiry && <div>Renewal task: <b>{fmtDate(renewalOn)}</b> ({warn} days before){recipientName ? ` to ${recipientName}` : " — choose who receives it"}{!autoChase ? " · automatic sending is off" : ""}</div>}
          </> : <>
            <div>No evidence yet.{" "}
              {c.task?.status === "open" ? <>{c.task.kind === "both" ? "Task and upload email" : c.task.kind === "upload" ? "Upload request" : "Task"} sent to {c.task.recipientName} on {fmtDate(c.task.sentAt)}, due {fmtDate(c.task.dueDate)}.</> : recipientName ? autoChase ? <>The first task goes to <b>{recipientName}</b> when you save.</> : <>Automatic sending is off — use “Send task now”.</> : <>Choose who receives the task below.</>}</div>
            {mode === "days" && <div>When the photo/document arrives it is valid for {days} days from that date, and the renewal task goes out {warn} days before it runs out.</div>}
          </>}
          {c.task?.status === "open" && c.evidence?.at && <div>Renewal task open — sent {fmtDate(c.task.sentAt)} to {c.task.recipientName}, due {fmtDate(c.task.dueDate)}.</div>}
        </div>
        {(c.source === "upload" || c.evidence?.analysis) && <UploadsBrowser c={c} row={row} branch={branch} token={token} />}
        {c.evidence?.at ? <ComplianceNextDue cell={c} rowId={row.rowId} dbName={branch.dbName} token={token} readOnly={true} onChanged={onSaved} /> : <>
        <details style={{
              marginTop: 6
            }} open={!!validUntil}>
          <summary style={{
                fontSize: 12,
                cursor: "pointer",
                color: "#5D5D5D"
              }}>Override the valid-until date for this branch</summary>
          <div style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
                fontSize: 13,
                marginTop: 4
              }}>
            <input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} />
            {validUntil && <button onClick={() => setValidUntil("")}>Clear</button>}
            <span style={{
                  color: "#5D5D5D",
                  fontSize: 12
                }}>Only if this branch needs a different date. Cleared automatically when new evidence arrives.</span>
          </div>
        </details>
        </>}
      </div>}
      </div>
      <div style={{
        minWidth: 0
      }}>
      {canTask && <>
        <label style={field}>{source === "upload" ? "Who receives the upload request (needs an email address)" : source === "task" ? "Who receives the task" : source === "pharmace" ? "Who reviews this when it’s due (optional)" : "Who to ask if it needs renewing (optional)"}</label>
        {staff == null ? <div style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>Loading staff…</div> : <select value={recipient} onChange={e => setRecipient(e.target.value)} style={{
            width: "100%"
          }}>
            <option value="">— choose a team member —</option>
            {staff.map(s => <option key={s.staffId} value={s.staffId}>{s.name}{s.email ? ` (${s.email})` : ""}</option>)}
          </select>}
        <label style={{
            display: "block",
            fontSize: 13,
            marginTop: 8
          }}>
          <input type="checkbox" checked={autoChase} onChange={e => setAutoChase(e.target.checked)} />{" "}
          {source === "upload" ? `Email the request automatically — the first one if nothing has been uploaded yet, then again ${warn} days before the next one is due` : source === "task" ? `Send tasks automatically — the first one if there’s no evidence yet, then a renewal ${warn} days before it runs out` : `Send a task automatically when it is due (${warn} days before)`}</label>
      </>}
      {canTask && source !== "upload" && <TaskWording w={w} setW={setW} label={row.label} evidenceType={row.evidenceType || "photo"} linkUrl={source === "link" ? linkUrl : ""} validUntil={validUntil || ruleDate} review={source === "pharmace"} note="same at every branch for this item" />}
      <div style={{
          marginTop: 10,
          padding: 10,
          background: "#FFFBE6",
          border: "1px solid #FFE58F",
          borderRadius: 8
        }}>
        <label style={{
            ...field,
            margin: "0 0 4px"
          }}>Note for the inspector — {branch.pharmacyName}</label>
        {row.inspectorNote && <div style={{
            fontSize: 12,
            color: "#5D5D5D",
            marginBottom: 4
          }}>Item note (all branches): “{row.inspectorNote}”</div>}
        <textarea value={cellNote} onChange={e => setCellNote(e.target.value)} rows={2} style={{
            width: "100%",
            fontFamily: "inherit",
            fontSize: 13
          }} placeholder="Anything specific to this branch, e.g. New version of the SOP being signed this week." />
      </div>
      <label style={{
          display: "block",
          fontSize: 13,
          marginTop: 8
        }}>
        <input type="checkbox" checked={applyAll} onChange={e => setApplyAll(e.target.checked)} /> Use this source for every branch ({branches.length}) — recipients are chosen per branch</label>

      {err && <div role="alert" style={{
          color: "#A8071A",
          marginTop: 8
        }}>{err}</div>}
      {msg && <div role="status" style={{
          color: "#389E0D",
          marginTop: 8
        }}>{msg}</div>}
      <div style={{
          display: "flex",
          gap: 8,
          marginTop: 12,
          flexWrap: "wrap"
        }}>
        <button disabled={saving} onClick={() => save(willSendFirst)} style={{
            fontWeight: 600
          }}>{willSendFirst ? source === "upload" ? "Save & email the request" : "Save & send first task" : "Save"}</button>
        {canTask && !willSendFirst && c.task?.status !== "open" && <button disabled={saving || !recipient} onClick={() => save(true)}>{source === "upload" ? "Save & email request now" : <>Save &amp; send task now</>}</button>}
        <button onClick={onClose}>Cancel</button>
      </div>

      {(source === "upload" || c.source === "upload") && guide.upload && <div style={{
          ...box,
          background: "#E6F7FF",
          borderColor: "#91D5FF"
        }}>
        {nm ? <>
          <div style={{
              fontWeight: 600,
              fontSize: 13
            }}>Upload {LT.records} for this branch</div>
          <div style={{
              fontSize: 12,
              color: "#5D5D5D",
              margin: "2px 0 6px"
            }}>If you already have the branch’s log, add it here instead of waiting for the email.</div>
          <div style={{
              border: "1px solid #91D5FF",
              borderRadius: 6,
              padding: 8,
              background: "#fff"
            }}>
            <div style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#0E8CFF"
              }}>Part 1 — the {LT.log} (spreadsheets, or a CSV/Excel export)</div>
            <input type="file" multiple accept=".xlsx,.xls,.csv,.ods" onChange={e => setUpFiles([...(e.target.files || [])])} />
            <input value={upLink} onChange={e => setUpLink(e.target.value)} placeholder="…or a shared link — Google Sheets, OneDrive, SharePoint, Dropbox (anyone with the link)" style={{
                width: "100%",
                marginTop: 6
              }} />
          </div>
          <div style={{
              border: "1px solid #F0F0F0",
              borderRadius: 6,
              padding: 8,
              background: "#fff",
              marginTop: 6
            }}>
            <div style={{
                fontSize: 12,
                fontWeight: 700
              }}>Part 2 — summary reports (optional)</div>
            <input type="file" multiple accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.odt,.ods,.txt,image/*" onChange={e => setUpReports([...(e.target.files || [])])} />
          </div>
        </> : <>
        <div style={{
              fontWeight: 600,
              fontSize: 13
            }}>{docs ? "Upload documents for this branch" : "Upload a spreadsheet for this branch"}</div>
        <div style={{
              fontSize: 12,
              color: "#5D5D5D",
              margin: "2px 0 6px"
            }}>{docs ? "If you already have the branch’s copy, upload it here (several files at once is fine) instead of waiting for the email." : "If you already have the branch’s record, upload it here instead of waiting for the email."}</div>
        {docs ? <input type="file" multiple accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.odt,.ods,.txt,image/*" onChange={e => setUpFiles([...(e.target.files || [])])} /> : <input type="file" accept=".xlsx,.xls,.csv,.ods" onChange={e => setUpFile(e.target.files?.[0] || null)} />}
        {!docs && <input value={upLink} onChange={e => setUpLink(e.target.value)} placeholder="…or a shared link — Google Sheets, OneDrive, SharePoint, Dropbox (anyone with the link)" style={{
              width: "100%",
              marginTop: 6
            }} />}
        </>}
        {!docs && <div style={{
            fontSize: 11,
            color: "#5D5D5D",
            marginTop: 3
          }}>A shared spreadsheet link is remembered and re-read automatically on the 1st of every month, filling in new {nm ? "entries" : "sessions"} and any earlier gaps.</div>}
        {c.sheetLink?.url && <div style={{
            fontSize: 12,
            marginTop: 8,
            padding: 8,
            borderRadius: 6,
            background: c.sheetLink.lastResult === "failed" ? "#FFF1F0" : "#E6F7FF",
            color: c.sheetLink.lastResult === "failed" ? "#A8071A" : "#0958D9"
          }}>
          <b>🔄 {c.sheetLink.provider || "Shared spreadsheet"} linked</b> — re-read automatically on the 1st of every month. <a href={c.sheetLink.url} target="_blank" rel="noreferrer">Open sheet ↗</a>
          <div>Added {fmtDate(c.sheetLink.addedAt)}{c.sheetLink.addedBy ? ` by ${c.sheetLink.addedBy}` : ""} · last read {fmtDate(c.sheetLink.lastReadAt)}: {c.sheetLink.lastResult === "failed" ? `could not be read — ${c.sheetLink.lastMessage}` : c.sheetLink.lastMessage}</div>
          <div style={{
              marginTop: 4,
              display: "flex",
              gap: 6
            }}>
            <button disabled={saving} onClick={() => sheetAction("reread")}>Re-read now</button>
            <button disabled={saving} onClick={() => sheetAction("unlink")}>Stop re-reading</button>
          </div>
        </div>}
        <button disabled={saving} onClick={uploadNow} style={{
            marginTop: 6
          }}>{docs ? `Upload${upFiles.length ? ` ${upFiles.length} file${upFiles.length === 1 ? "" : "s"}` : ""}` : "Upload & read"}</button>
        {nm && c.evidence?.analysis && <NearMissPanel a={c.evidence.analysis} reports={c.evidence.reports} />}
        {!nm && (upResult || c.evidence?.analysis) && (() => {
            const a = upResult || c.evidence.analysis;
            return <div style={{
              fontSize: 13,
              marginTop: 8,
              lineHeight: 1.5
            }}>
          <b>{a.rowsRead}</b> RP sessions read ({fmtDate(a.periodFrom)} – {fmtDate(a.periodTo)}){a.rowsSkipped ? `, ${a.rowsSkipped} rows skipped` : ""} ·
          last 30 days: <b>{a.daysCovered}/{a.tradingDays}</b> {a.tradingDaysOnly ? "trading " : ""}days covered
          <div style={{
                fontSize: 12,
                color: "#5D5D5D"
              }}>Columns used ({a.mapping?.via === "ai" ? "worked out by AI" : "matched by heading"}): {Object.entries(a.mapping?.columns || {}).filter(([, v]) => v).map(([k, v]) => `${k} = “${v}”`).join(" · ")}</div>
          {upResult?.combined && <div style={{
                fontSize: 12,
                color: "#0E8CFF",
                marginTop: 2
              }}>Combined with earlier uploads: {upResult.combined.rowsRead} distinct sessions from {upResult.combined.uploads} upload{upResult.combined.uploads === 1 ? "" : "s"} · {upResult.combined.daysCovered}/{upResult.combined.tradingDays} days covered</div>}
        </div>;
          })()}

      </div>}
      {(c.source === "task" || c.source === "upload" || c.source === "link" || c.source === "pharmace" && (c.task || c.recipientStaffId)) && <div style={box}>
        <div style={{
            fontWeight: 600,
            fontSize: 13
          }}>Evidence</div>
        {c.evidence?.at ? <div style={{
            fontSize: 13,
            marginTop: 4
          }}>
          {fmtDate(c.evidence.at)} by {c.evidence.by || "—"} ({c.evidence.via === "task" ? "staff app task" : String(c.evidence.via || "").startsWith("upload") ? c.evidence.via === "upload-head-office" ? "uploaded by head office" : "uploaded via the email link" : "recorded by head office"})
          {c.evidence.fileUrl && !isImageUrl(c.evidence.fileUrl) && <> · <a href={c.evidence.fileUrl} target="_blank" rel="noreferrer">{c.evidence.analysis || String(c.evidence.via || "").startsWith("upload") ? "Open the spreadsheet ↗" : "View document ↗"}</a></>}
          {c.expiresAt && <> · expires {fmtDate(c.expiresAt)}</>}
          {c.evidence.note && <div style={{
              color: "#5D5D5D"
            }}>{c.evidence.note}</div>}
        </div> : <div style={{
            fontSize: 13,
            color: "#5D5D5D"
          }}>None yet.</div>}
        {view.status === "needs-expiry" && <div style={{
            marginTop: 8,
            fontSize: 13
          }}>
          Expiry date for this evidence: <input type="date" value={setExp} onChange={e => setSetExp(e.target.value)} />{" "}
          <button disabled={saving || !setExp} onClick={saveExpiry}>Save expiry</button></div>}
        {c.task && <div style={{
            fontSize: 13,
            marginTop: 6
          }}>
          Task: {c.task.status === "open" ? `open — sent to ${c.task.recipientName} on ${fmtDate(c.task.sentAt)}, due ${fmtDate(c.task.dueDate)}` : `${c.task.status} (${c.task.recipientName || ""}, ${fmtDate(c.task.completedAt || c.task.sentAt)})`}</div>}
        {c.recipientStaffId && c.task?.status !== "open" && <button disabled={saving} onClick={sendNow} style={{
            marginTop: 6
          }}>{c.source === "upload" ? `Email upload request to ${c.recipientName} now` : `Send task to ${c.recipientName} now`}</button>}

        <details style={{
            marginTop: 10
          }}>
          <summary style={{
              fontSize: 13,
              cursor: "pointer"
            }}>Record evidence yourself</summary>
          <div style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 6,
              fontSize: 13
            }}>
            <label>Date <input type="date" value={evDate} onChange={e => setEvDate(e.target.value)} /></label>
            {mode === "expiry-date" && <label>Expires <input type="date" value={evExpiry} onChange={e => setEvExpiry(e.target.value)} /></label>}
          </div>
          <input type="file" accept="image/*,.pdf,.doc,.docx" onChange={e => setEvFile(e.target.files?.[0] || null)} style={{
              marginTop: 6
            }} />
          <input value={evNote} onChange={e => setEvNote(e.target.value)} placeholder="Note (optional)" style={{
              width: "100%",
              marginTop: 6
            }} />
          <button disabled={saving} onClick={recordEvidence} style={{
              marginTop: 6
            }}>Record evidence</button>
        </details>
        {!!c.history?.length && <details style={{
            marginTop: 8
          }}>
          <summary style={{
              fontSize: 12,
              cursor: "pointer",
              color: "#5D5D5D"
            }}>History</summary>
          {c.history.slice().reverse().map((h, i) => <div key={i} style={{
              fontSize: 12,
              color: "#5D5D5D"
            }}>
            {new Date(h.at).toLocaleString("en-GB")} — {h.event.replace("-", " ")}{h.to ? ` to ${h.to}` : ""}{h.by ? ` by ${h.by}` : ""}{h.reason ? ` (${h.reason})` : ""}</div>)}
        </details>}
      </div>}
          </div>
      </div>
    </Modal>;
}
Object.assign(GUIDANCE, Object.fromEntries(JURISDICTION_CATALOGUE.niRows.map(row => [row.id, {
  pharmace: false,
  defaultEvidence: 'document',
  upload: 'documents',
  fallbackDefault: 'task',
  satisfies: row.satisfies || row.help,
  explanation: row.explanation || row.help,
  instructions: row.instructions || row.help,
  references: row.references || [{
    label: row.refs.length ? `PSNI ${row.refs.join(', ')}` : 'PSNI / NI guidance',
    url: row.link
  }],
  validity: {
    mode: 'days',
    days: 365,
    warnDays: 30
  }
}])));
Object.assign(GUIDANCE, Object.fromEntries(['tos-hours', 'ni-tos-hours'].map(id => [id, {
  pharmaceName: 'Pharmacy opening hours',
  sources: ['task', 'link'],
  defaultEvidence: 'document',
  evidenceOnly: 'document',
  instructions: 'Provide the current pharmacy opening timetable, including all daily sessions, closed days and any core/supplementary-hours breakdown. Upload the document using the task or secure email link.',
  explanation: 'Show the pharmacy opening timetable, not staff working hours. Configured times do not by themselves prove adherence or absence of unplanned closures.',
  validity: {
    mode: 'none',
    days: 365,
    warnDays: 14
  }
}])));

// Procedure-content checks link selected SOPs, rather than the whole SOP library.
Object.assign(GUIDANCE, Object.fromEntries([['ni-p13-3', 'Link procedures covering ordering, storage, preparation, retail sale, supply, delivery and disposal of medicines.'], ['ni-p13-4', 'Link the procedure defining when non-pharmacist staff may give advice about medicines.'], ['ni-p13-5', 'Link procedures identifying the staff authorised and competent to undertake specified pharmacy tasks. A linked procedure is not, by itself, proof of individual competence.'], ['ni-p13-6', 'Link the procedure for maintaining the required pharmacy records.'], ['ni-p13-7', 'Link the procedure for arrangements during the Responsible Pharmacist’s absence.'], ['ni-p13-8', 'Link the procedure for a change of Responsible Pharmacist and handover arrangements.'], ['ni-p13-10', 'Link the procedure for incidents that may affect the safe and effective running of the pharmacy.']].map(([id, explanation]) => [id, {
  ...GUIDANCE[id],
  pharmace: false,
  sop: true,
  sources: ['sop', 'task', 'link'],
  defaultEvidence: 'document',
  evidenceOnly: 'document',
  explanation,
  satisfies: 'One or more current SOPs cover the procedure required by this item. Select the relevant Pharmace SOPs, provide an external link, or request supporting documents by task and email.',
  instructions: explanation + ' If the procedures are not held in Pharmace, upload the current document(s) using the staff task or the secure email upload link.',
  validity: {
    mode: 'none',
    days: 365,
    warnDays: 14
  },
  fallbackDefault: 'sop'
}])));

// Keep NI wording consistent after source-specific SOP/opening-hours setup.
for (const row of JURISDICTION_CATALOGUE.niRows) {
  GUIDANCE[row.id] = {
    ...GUIDANCE[row.id],
    explanation: row.explanation || row.help,
    satisfies: row.satisfies || row.help,
    instructions: row.instructions || row.help,
    references: row.references || GUIDANCE[row.id].references
  };
}
